var SLA = typeof window !== 'undefined' && window.SL_ASSETS ? window.SL_ASSETS : 'assets/';
/* The upload hint is RENDERED FROM THE VALIDATOR's limit, never hard-coded (D-056).
   It used to read "up to 50 MB" in two places while the validator enforced something
   else — the exact drift that tells a customer one number and rejects them with
   another. SLUpload.limits() is the single source; the literal below is only a
   last-resort fallback if the engine somehow has not loaded. */
function SL_UPLOAD_HINT() {
  try {
    var U = typeof window !== 'undefined' && window.SLUpload;
    if (U && U.limits) return U.limits().uploadHint;
  } catch (e) {}
  return 'PNG · JPG · SVG · PDF';
}
/* The Sticker Lab — Premium Builder
   Three-column product-creation experience. Reuses the calm-builder business
   logic (pricing, contour die-cut) in a single premium screen: left options ·
   center canvas · right tools · sticky bar.

   Background removal: D-034 removed it for Launch V1, and the 2026-09-02
   decision brought it back as a REVERSIBLE VIEW, which is what is here now —
   `shownArt = (bgOn && bgUrl) ? bgUrl : originalArt`, the upload itself never
   altered. This comment said otherwise for a while, which is its own small
   lesson about header comments that describe a decision instead of the code. */
const {
  useState,
  useRef,
  useEffect
} = React;

/* ══════════════════════════════════════════════════════════════════════════
   ONE CORE, TWO SHELLS

   This component is both the sticker ENGINE and the storefront SHELL around
   it. HQ mounts the same file — same geometry, same transforms, same state,
   same `renderProductionSvg` — because a second copy would drift, and every
   milestone since 12D.1 depends on there being exactly one.

   What HQ must not get is the shop. Once the builder became visible inside the
   MOTO bench (13D), its storefront chrome became clickable: quantity, price,
   Save Design, Add to cart, and the "What would you like to do next?" card
   that follows them. Ryan reached a raw browser alert reading "This item can't
   be ordered online yet" from inside a production tool.

   `__SL_HQ_HOST__` is set by hq/production/moto-host.html before these scripts
   load. It was already there and nothing read it. It does now, and it gates
   the STOREFRONT-ONLY regions — the ones that can take payment, take over the
   page, or sell something. It gates no geometry, no control and no state.

   Read once, at module scope: the host a page is running in cannot change
   under it, and a per-render read would invite somebody to make it a prop.
   ══════════════════════════════════════════════════════════════════════════ */
const PRODUCTION_HOST = (typeof window !== 'undefined') && !!window.__SL_HQ_HOST__;
/* WHETHER TODAY'S ORDER RULES APPLY (24 Oct 2026: the 1in individual minimum, the fewest sheets).
   A shell fact, not geometry: the storefront applies them to a new order; the production host
   reopens orders that are already PAID and builds them at the size and count they were bought at. */
const ORDER_RULES_APPLY = !PRODUCTION_HOST;
/* ══════════════════════════════════════════════════════════════════════════
   ONE BUILDER, A SECOND PRODUCT (D-177)

   Cart Graphics is a separate product — its own route, sizes, price, quantity
   rules and finish — built on this builder rather than on a copy of it, for
   the reason the note above gives: a second copy would drift.

   `CG` is the Cart Graphics engine (assets/sl-cart-graphics.js) when, and only
   when, the page declared that product. It is null on the sticker builder, on
   the sheet builder and inside HQ, and every use of it below is behind that
   null: with `CG` absent this file takes exactly the paths it took before, so
   no sticker price, quantity limit, finish or size limit can move.

   Read once, at module scope, like the host flag: the product a page is
   selling cannot change under it.
   ══════════════════════════════════════════════════════════════════════════ */
const CG = (typeof window !== 'undefined' && window.SL_PRODUCT === 'cart_graphics' && window.SLCartGraphics && !window.__SL_HQ_HOST__)
  ? window.SLCartGraphics : null;
/* The launch switch (a theme setting on the Cart Graphics template). While it
   is off a cart graphic can be designed, previewed, priced and saved, and
   cannot be put in the cart. */
/* This product's three border levels are fixed physical widths. Handing the
   table to the shared border rule here, once, is what makes the preview ring,
   the order record and the production file agree; on any other page nothing
   is handed over and the proportional sticker rule stands. */
if (CG && _szCore() && _szCore().setBorderWidthTable) _szCore().setBorderWidthTable(CG.BORDER_WIDTHS_IN);
const CG_CHECKOUT_OPEN = !!(CG && typeof window !== 'undefined' && window.SL_CART_GRAPHICS && window.SL_CART_GRAPHICS.checkoutOpen);
/* WHERE A CUSTOMER ARRIVES FROM THE CART GRAPHICS LANDING PAGE, the size and
   finish chosen on its lab bench travel as ?width=&height=&finish= and become
   this builder's starting values. They are held to the engine's own rules: a
   size it would refuse, or a finish it does not sell, is ignored, never
   clamped. A ?quantity= is held to the same rule. Read once, at load; Reset
   still returns to the product defaults.
   A customer who arrives this way opens on the Cart preview (`landing`), so
   the artwork they brought appears on the cart the moment it has loaded. */
const CG_ENTRY = (function () {
  if (!CG) return null;
  const d = { w: CG.SIZE.defaultWidthIn, h: CG.SIZE.defaultHeightIn, finish: 'matte', qty: CG.QUANTITY.defaultQty, landing: false };
  try {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('width') != null && qs.get('height') != null) {
      const v = CG.validateSize(qs.get('width'), qs.get('height'));
      if (v.ok) { d.w = v.widthIn; d.h = v.heightIn; d.landing = true; }
    }
    const fin = CG.finishById(qs.get('finish'));
    if (fin && fin.available) d.finish = fin.id;
    if (qs.get('quantity') != null) {
      const q = CG.validateQuantity(qs.get('quantity'));
      if (q.ok) d.qty = q.value;
    }
  } catch (e) {}
  return d;
})();
/* ══ THE 50-PACK PROMO (W-PROMO, Ryan 18 Oct 2026) ═══════════════════════════
   The sticker builder selling ONE fixed pack: 50 stickers, 2 or 3 in on the
   longest side, matte or gloss, free shipping, for a fixed price on its own Shopify product.
   The price lives on that product's real $22 / $40 variants, so Shopify
   enforces it and discounts can exclude it; the $0.01 transport line is never
   used for a pack.

   Declared by templates/index.promo50.json through sl-builder.liquid, read
   once here like `CG`. Null everywhere else (sticker builder, Cart Graphics,
   HQ), and every promo branch below is behind it, so with PROMO null this file
   takes exactly the paths it took before. */
const PROMO = (typeof window !== 'undefined' && window.SL_PRODUCT === 'promo50' && window.SL_PROMO && !window.__SL_HQ_HOST__)
  ? window.SL_PROMO : null;
/* "$22", "$40", "$11.99": whole dollars without cents, as the landing page prints them. */
function promoMoney(n) { n = Math.round((+n || 0) * 100) / 100; return '$' + (n % 1 ? n.toFixed(2) : String(n)); }
/* THE PACK'S SIZES (Ryan, 18 Oct 2026): any longest side from 2.0 to 3.0 in, in
   0.1 in steps. The other side follows the art (the preset rule); the price
   rule (promoPriceRule) still decides, per size, whether the pack is offered. */
const PROMO_SIZE_MIN = 2, PROMO_SIZE_MAX = 3, PROMO_SIZE_STEP = 0.1;
function promoSnapSize(v) { v = +v; if (!isFinite(v)) v = PROMO_SIZE_MIN; return +Math.min(PROMO_SIZE_MAX, Math.max(PROMO_SIZE_MIN, Math.round(v * 10) / 10)).toFixed(1); }
function promoIsSize(v) { return typeof v === 'number' && v === promoSnapSize(v); }
/* THE PACK IS ONLY SOLD WHERE IT IS THE BETTER DEAL (Ryan, 18 Oct 2026).
   `regular` is SLPricing's price for 50 at the ordered dimensions. A thin 2in
   design can cost less than the pack on the normal price list (2 x 0.5 in is
   $17.99), and charging $22 for it would charge a customer more than our own
   price: such a size is NOT offered the pack, and is pointed at the regular
   builder instead. Equal: sold, with no savings claim. Above: sold, with the
   real strike-through and saving. No regular price (pricing not loaded): not
   sold, rather than sold unchecked. Compared in whole cents. */
function promoPriceRule(regular, packPrice) {
  const r = Math.round((+regular || 0) * 100), p = Math.round((+packPrice || 0) * 100);
  if (!(r > 0) || !(p > 0)) return { offer: false, cheaper: false, unpriced: true, saveCents: 0 };
  if (r < p) return { offer: false, cheaper: true, unpriced: false, saveCents: 0 };
  return { offer: true, cheaper: false, unpriced: false, saveCents: r - p };
}
/* Why the pack cannot be bought right now, in the customer's words, or '' when
   it can. The window is checked at purchase (here and again in the cart), not
   just advertised: an old tab or a bookmarked link outside it is refused. */
function promoBlockText(now) {
  if (!PROMO) return '';
  const st = Date.parse(PROMO.start || ''), en = Date.parse(PROMO.end || '');
  if (isFinite(st) && now < st) {
    let when = '';
    try { when = new Date(st).toLocaleDateString(undefined, { month: 'long', day: 'numeric' }); } catch (e) {}
    return 'This offer starts' + (when ? ' on ' + when : ' soon') + '.';
  }
  if (isFinite(en) && now >= en) return 'This offer has ended.';
  if (!PROMO.variant50) return 'The 50-sticker pack can’t be ordered online yet.';
  return '';
}
/* Where this customer came from, as the landing page recorded it
   (localStorage `sl_promo_attr`, 30 days) or, failing that, this page's own
   query string. Compact on purpose: it rides on the order as hidden line
   properties. */
const PROMO_ATTR_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid', 'ttclid'];
function promoAttribution() {
  const out = { q: '', landed: '' };
  if (!PROMO) return out;
  let rec = null;
  try {
    rec = JSON.parse(localStorage.getItem('sl_promo_attr') || 'null');
    if (rec && !(rec.exp > Date.now())) rec = null;
  } catch (e) { rec = null; }
  const pairs = [];
  try {
    const qs = new URLSearchParams(location.search);
    PROMO_ATTR_KEYS.forEach(k => {
      const v = (rec && rec.p && rec.p[k]) || qs.get(k);
      if (v) pairs.push(k + '=' + encodeURIComponent(String(v).slice(0, 100)));
    });
  } catch (e) {}
  out.q = pairs.join('&').slice(0, 400);
  out.landed = (rec && rec.t) ? String(rec.t) : '';
  return out;
}
function promoTrack(name, data) {
  try { if (PROMO && window.slTelemetry && window.slTelemetry.track) window.slTelemetry.track(name, data || {}); } catch (e) {}
}
const SL_ROUTES = (typeof window !== 'undefined' && window.SL_BUILDER_ROUTES) || {};
const SL_ROUTE_STICKERS = SL_ROUTES.stickers || '/?view=builder';
const SL_ROUTE_CART_GRAPHICS = SL_ROUTES.cartGraphics || '/?view=cartgraphics';
const SL_UNTITLED = CG ? 'Untitled cart graphic' : 'Untitled sticker';
const SHAPES = [{
  id: 'custom',
  label: 'Die Cut',
  big: '✦',
  ar: 1,
  hint: 'Cut exactly around your art'
}, {
  id: 'circle',
  label: 'Circle',
  big: '●',
  ar: 1
}, {
  id: 'square',
  label: 'Square',
  big: '▢',
  ar: 1,
  roundable: true
}];
/* THE CORNER RADIUS IS PHYSICAL (D-092).
   ---------------------------------------------------------------------
   This used to return a flat '30px' — a count of PREVIEW pixels, with no term
   for the sticker's physical size and none for the box it is drawn in. The
   preview box is device-dependent, so the same order cut a different corner on
   a phone than on a desktop: 3.42 mm apart on a 6in sticker. It also drifted
   across sizes on desktop alone, 8.67% of the edge at 1in down to 6.30% at
   6in, because a constant pixel count is a shrinking fraction of a growing box.

   The rule now lives in SLBuilderCore beside the border rule it mirrors, and
   `box` is required — a radius cannot be resolved without knowing what it is a
   radius OF. `circle` and `custom` are unaffected: 50% and a pill are already
   proportional, which is why only the rounded rectangle drifted. */
function shapeRadius(shapeId, corners, longestIn, boxW, boxH) {
  if (shapeId === 'circle') return '50%';
  if (shapeId === 'custom') return '999px';
  const C = _szCore();
  if (!C || !C.cornerPxFor) return corners === 'sharp' ? '2px' : '30px';
  /* Kept FRACTIONAL on purpose. Rounding to whole preview pixels reintroduced
     the device dependency in miniature: on a 351px box showing a 6in sticker
     one pixel is 0.017in, so a half-pixel rounding moved the cut path by up to
     0.0088in between phones. The radius is a physical quantity; the preview's
     integer grid is not a reason to quantise it. */
  return Math.max(0.5, C.cornerPxFor(corners, longestIn, boxW, boxH)).toFixed(3) + 'px';
}
const SIZES = [{
  id: 2,
  label: '2″',
  sub: 'Small'
}, {
  id: 3,
  label: '3″',
  sub: 'Popular',
  pop: true
}, {
  id: 4,
  label: '4″',
  sub: 'Large'
}, {
  id: 5,
  label: '5″',
  sub: 'Jumbo'
}].filter(s => !PROMO || s.id === 2 || s.id === 3); // promo: these presets are unused; the pack has its own 2.0–3.0 in control
const QTYS = [10, 25, 50, 100, 200, 500, 1000];
const QTY_POP = 100; // "Popular" preset badge
// Pricing is owned by assets/sl-pricing.js — the config-driven source of truth.
// Options (material/laminate/border/proof) do NOT affect die-cut price (Rule 4).
function slPrice(w, h, q) {
  const P = typeof window !== 'undefined' && window.SLPricing;
  const r = P && P.diecut(w, h, q);
  return r && !r.error ? r.price : 0;
}
// Per-sticker price — the engine's OWN perUnit (round2(price/qty)); reused, not re-derived.
function slUnit(w, h, q) {
  const P = typeof window !== 'undefined' && window.SLPricing;
  const r = P && P.diecut(w, h, q);
  return r && !r.error ? r.perUnit : 0;
}
// Shared sticker-size math — owned by sl-builder-core.js (the single source of truth for the 0.25" grid and
// the 0.50"–9.00" limits). These thin wrappers read the engine at call time and fall back to an inline copy
// only if the engine hasn't loaded, so the mobile Size panel never forks the sizing rules.
const SZ_MIN = 0.5, SZ_MAX = 9, SZ_STEP = 0.25;
function _szCore() { return typeof window !== 'undefined' && window.slBuilder || null; }
function snapSz(v) {
  const C = _szCore();
  if (C && C.snapSizeIn) return C.snapSizeIn(v);
  v = +v; if (!isFinite(v)) return SZ_MIN;
  v = Math.round(v / SZ_STEP) * SZ_STEP;
  return +Math.min(SZ_MAX, Math.max(SZ_MIN, v)).toFixed(2);
}
function lockH(widthIn, ratio) { const C = _szCore(); if (C && C.lockHeightIn) return C.lockHeightIn(widthIn, ratio); widthIn = snapSz(widthIn); return snapSz(ratio > 0 ? widthIn / ratio : widthIn); }
function lockW(heightIn, ratio) { const C = _szCore(); if (C && C.lockWidthIn) return C.lockWidthIn(heightIn, ratio); heightIn = snapSz(heightIn); return snapSz(ratio > 0 ? heightIn * ratio : heightIn); }
const MATERIALS = [{
  id: 'vinyl',
  label: 'Vinyl',
  sub: 'Durable & waterproof',
  sw: 'linear-gradient(135deg,#e9eef4,#cdd7e3)'
}, {
  id: 'holo',
  label: 'Holographic',
  sub: 'Rainbow shimmer',
  sw: 'conic-gradient(from 0deg,#ff7eb3,#ffd86b,#7ee8fa,#a685ff,#ff7eb3)'
}, {
  id: 'clear',
  label: 'Clear',
  sub: 'See-through',
  sw: 'linear-gradient(135deg,rgba(200,220,240,.35),rgba(160,190,220,.5))'
}, {
  id: 'kraft',
  label: 'Kraft',
  sub: 'Natural matte',
  sw: 'linear-gradient(135deg,#d8b98a,#b98f5a)'
}];
const LAMINATES = [{
  id: 'matte',
  label: 'Matte',
  sub: 'Soft, premium',
  pop: true
}, {
  id: 'gloss',
  label: 'Glossy',
  sub: 'Shiny, vivid'
}];
const BORDERS = [{
  id: 'white',
  label: 'White',
  sw: '#ffffff'
}, {
  id: 'black',
  label: 'Black',
  sw: '#0a0c10'
}, {
  id: 'custom',
  label: 'Custom',
  sw: 'custom'
}];
/* ============================================================================
   THE OUTLINED FRAME — ONE DEFINITION, USED TWICE.

   The border baker pads the upload before dilating it, so the picture the
   builder actually draws is `cw x ch`, not `iw x ih`. Drawing that padded
   frame with `fit: 'width'` renders the customer's artwork smaller by exactly
   `iw / cw`, and `contourScale` exists to cancel that.

   It used to cancel it with a FITTED CONSTANT — `1 + 2.05 * vfrac` — which is
   not the ratio and does not track it. Measured on a real order
   (SL-1M1BK-H4GS6-TG7QG, 640x432, Standard): the required ratio is 1.084375
   and the constant supplied 1.045100, so the customer's artwork rendered
   3.62% small. Worse, the error MOVED WITH THE BORDER: -5.13% Thin, -3.62%
   Standard, -2.01% Thick, because `closeR`'s 0.03 floor pins `pad` while the
   constant keeps tracking `vfrac`. Choosing a thicker border silently
   rescaled the customer's design.

   The padding is therefore computed once, here, and the two consumers read
   the SAME numbers: the baker builds the frame from them, and `contourScale`
   is the ratio those numbers imply. A constant cannot drift from a ratio it
   IS.
   ============================================================================ */
function slOutlineFrame(iw, ih, vfrac, silLongPx) {
  var w = +iw, h = +ih, f = +vfrac;
  if (!(w > 0) || !(h > 0) || !(f > 0)) return null;
  var long = Math.max(w, h);
  /* THE BORDER FRACTION IS DEFINED AGAINST THE FINISHED STICKER, so it has to
     be measured from the SILHOUETTE and not from the upload frame.

     ── WHAT WENT WRONG WHEN IT WAS NOT ─────────────────────────────────────
     `rad` used to be `vfrac * max(iw, ih)` — a fraction of the UPLOAD. But
     contour-fit reserves room for the border as a fraction of the ORDERED
     size, mapped through the SILHOUETTE bbox, which is
     `vfrac * S / (1 - 2*vfrac)` in silhouette pixels. The two agree only when
     the silhouette happens to fill exactly `1 - 2*vfrac` of its own upload
     (0.930 thick, 0.956 standard, 0.972 thin). Below that, the baker painted a
     border larger than the canvas had reserved and the finished sticker ran
     off its own export raster.

     Real order SL-1M1T3-85QBP-M94S4 (1.25in, Die Cut, thick): the silhouette
     filled 0.801 of the upload, the painted sticker came out 1.13% oversize,
     and it was flush-cut on all four sides — which then made the offset fail
     validation and the file shipped with no CutContour at all.

     Solved rather than corrected: with `rad = vfrac*S/(1-2*vfrac)` the painted
     finished sticker is `S/(1-2*vfrac)`, which is exactly what contour-fit
     reserves, for EVERY silhouette size. One basis, no residual.

     `silLongPx` is the silhouette's long edge in the same pixels as `iw/ih`.
     Absent, this falls back to the old upload basis so a caller that cannot
     measure a silhouette still gets a frame rather than nothing. */
  var denom = 1 - 2 * f;
  var S = (+silLongPx > 0 && +silLongPx <= long) ? +silLongPx : long;
  var rad = Math.max(3, Math.round(denom > 0.05 ? (f * S / denom) : (f * long)));
  // closing radius: bridge concave gaps up to ~2×this so the cut line doesn't dive into
  // narrow notches (between letter legs, under the globe stand) and leave fragile peninsulas.
  var closeR = Math.max(rad, Math.round(0.03 * long));
  var pad = Math.round(Math.max(rad, closeR) + 8);
  var cw = w + pad * 2, ch = h + pad * 2;
  return {
    iw: w, ih: h, rad: rad, closeR: closeR, pad: pad, cw: cw, ch: ch,
    /* which basis produced `rad`, so a production file can say so */
    silLongPx: S, basis: (S === long ? 'upload' : 'silhouette'),
    /* The whole point: the scale that makes the artwork inside the padded
       frame render at the size it would have rendered unpadded. */
    contourScale: cw / w
  };
}

/* ============================================================================
   THE THUMBNAIL'S FRAME — PRESENTATION, AND NOTHING ELSE.

   Re-crops a finished proof canvas to the sticker it actually contains, with
   even breathing room around it. Used for `_artwork_preview`; NEVER for the
   production export, which takes the same canvas at its full size.

   ── WHY THE ALPHA IS THE RIGHT AUTHORITY ────────────────────────────────
   For a die-cut sticker the composite is drawn from `outlinedUrl` — the
   artwork WITH its baked white border — so the opaque region IS the generated
   sticker plus its border. For a shaped sticker the ring and face are painted
   across the whole box, so the opaque region is the box, which is also exactly
   right: there, the box IS the sticker. One rule, no branch, and it needs to
   know nothing about MOTO, the band or the cut line.

   ── WHAT IT DELIBERATELY DOES NOT DO ────────────────────────────────────
   It cannot recover content the composite never had. If a legacy design was
   clipped at the canvas edge, the alpha bbox is the clipped sticker and the
   thumbnail shows the clipped sticker — which is the truth. A thumbnail that
   invented the missing part would be a picture of something nobody is going to
   receive.
   ============================================================================ */
const SL_THUMB_PAD_FRAC = 0.06;      // breathing room, per the long edge

/* ============================================================================
   DISC DILATION, BY DISTANCE RATHER THAN BY STAMPING
   ----------------------------------------------------------------------------
   Dilating a silhouette by a disc of radius R is the same statement as

       a pixel is inside the dilated mask
       when its distance to the nearest opaque pixel is at most R

   which a distance transform answers for every pixel in two linear passes.

   It replaces `diskDraw`, which approximated the same disc by compositing the
   WHOLE canvas onto itself once per sample around two rings --
   max(48, round(R*3)) samples each. Measured on the live builder that was 704
   drawImage calls for one bake: each is deferred, so they bill as 11ms, and
   the accumulated ~1 gigapixel of alpha compositing is then flushed by the
   next getImageData, which is why three single reads of a 1500x1033 canvas
   cost 1519ms, 1491ms and 1186ms. The stamping was never the visible cost and
   the readback was never the real one; they are the same cost, billed late.

   ── WHY R + HALF A PIXEL ────────────────────────────────────────────────
   The reference is not an ideal disc. Its stamps are antialiased and the
   result is thresholded at >60, so coverage reaches about half a pixel past
   the ideal circle. Measured across the corpus, matching the reference means
   asking for R + 0.5: at that offset the two agree to within 0.004-0.14% of
   pixels, at most one pixel of boundary displacement, ~0% area difference,
   and identical component AND hole counts -- including the donut's enclosed
   negative space and a gap closure at R=28 that plain R gets wrong. It is a
   calibration against the accepted behaviour, not a taste.

   Felzenszwalb & Huttenlocher: the lower envelope of the parabolas rooted at
   each sample, computed once per axis. Exact on squared integer distances --
   no approximation, no separable box-kernel substitute. A square is not a
   disc and could not have stood in here.
   ============================================================================ */
const SL_DILATE_EPS = 0.5;

function slEdt1d(f, n, d, v, z) {
  let k = 0;
  v[0] = 0; z[0] = -1e20; z[1] = 1e20;
  for (let q = 1; q < n; q++) {
    let s2;
    for (;;) {
      s2 = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
      if (s2 <= z[k]) k--; else break;
    }
    k++; v[k] = q; z[k] = s2; z[k + 1] = 1e20;
  }
  k = 0;
  for (let q = 0; q < n; q++) {
    while (z[k + 1] < q) k++;
    const dx = q - v[k];
    d[q] = dx * dx + f[v[k]];
  }
}

/**
 * Dilate a binary alpha mask by a disc.
 *
 * @param {Uint8ClampedArray|Uint8Array} alpha  one byte per pixel, 0 or opaque
 * @param {number} w @param {number} h @param {number} R  radius in pixels
 * @returns {Uint8Array} 0 / 255 per pixel
 */
function slDiscDilate(alpha, w, h, R) {
  const n = w * h, INF = 1e20;
  const g = new Float64Array(n);
  for (let p = 0; p < n; p++) g[p] = alpha[p] ? 0 : INF;
  const m = Math.max(w, h);
  const f = new Float64Array(m), d = new Float64Array(m);
  const v = new Int32Array(m), z = new Float64Array(m + 1);
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) f[y] = g[y * w + x];
    slEdt1d(f, h, d, v, z);
    for (let y = 0; y < h; y++) g[y * w + x] = d[y];
  }
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) f[x] = g[row + x];
    slEdt1d(f, w, d, v, z);
    for (let x = 0; x < w; x++) g[row + x] = d[x];
  }
  const rr = (R + SL_DILATE_EPS) * (R + SL_DILATE_EPS);
  const out = new Uint8Array(n);
  for (let p = 0; p < n; p++) out[p] = g[p] <= rr ? 255 : 0;
  return out;
}


/* ============================================================================
   THE CONNECTED STICKER BODY
   ----------------------------------------------------------------------------
   A customer uploads a school logo, removes the background and chooses Border:
   None. What is left is a crest, some large lettering and a line of small
   lettering -- five, ten, twenty separate opaque islands floating in
   transparency. Cut exactly as drawn that is not one sticker; it is twenty
   stickers, and the customer ordered one.

   "Border: None" means NO DECORATIVE OUTLINE. It has never meant "manufacture
   every disconnected island separately". So where the artwork does not already
   form one manufacturable shape, a BODY is computed to carry it -- the vinyl
   the pieces sit on. The customer is not asked; they already said what they
   wanted, and the system owes them a sticker that exists.

   This is NOT a border, is never reported as one, and the customer's chosen
   border stays None.

   ── HOW IT IS BUILT ─────────────────────────────────────────────────────
   A morphological CLOSING at the smallest radius that actually joins the
   significant pieces: dilate by D, then erode by D. Closing is what gives the
   result its manners -- it bridges a gap of up to 2D while leaving everything
   further than D from a gap exactly where it was, so the body hugs the artwork
   instead of inflating it, and every join arrives as a smooth fillet rather
   than as a bar laid between two letters.

   D is SEARCHED, not chosen: the smallest radius on a binary search that
   brings the significant components into one. That is what keeps the substrate
   minimal -- a bigger D would connect just as well and look padded.

   Deliberately not: a convex hull (fills the space between a wide logo's ends
   with vinyl nobody wanted), hairline bridges (a neck that thin tears off the
   backing), straight bars between components (visibly mechanical), or
   restoring the deleted background (the rectangle the customer just removed).

   ── WHAT COUNTS AS A PIECE ──────────────────────────────────────────────
   Significance is by area, against the largest piece: a component is
   significant when it is at least SL_BODY_SIG_FRAC of the biggest one AND at
   least SL_BODY_SIG_PX pixels. Scanner dirt and stray anti-aliasing therefore
   cannot drag the body across the canvas to collect them -- they are simply
   carried if the body happens to reach them, and ignored if it does not.

   ── NEGATIVE SPACE ──────────────────────────────────────────────────────
   Holes are not blindly filled. A hole large enough to read as intent -- the
   middle of a ring, the counter of a letter at size -- stays open. Anything
   under SL_BODY_HOLE_FRAC of the body is an accident of thresholding and is
   closed, because a sticker with pinholes is a sticker that tears.
   ============================================================================ */
const SL_BODY_SIG_FRAC  = 0.02;   /* of the largest piece */
const SL_BODY_SIG_PX    = 64;     /* and an absolute floor */
const SL_BODY_HOLE_FRAC = 0.004;  /* holes smaller than this share of the body close */
const SL_BODY_BRIDGE_FRAC   = 0.022; /* bridge width, per the artwork long edge */
const SL_BODY_BRIDGE_MIN_PX = 10;    /* and never a hairline */
const SL_BODY_FILLET_FRAC   = 0.6;   /* the smoothing is a fraction of a bridge */

/** 4-connected labelling. Returns {labels, sizes} with 0 = background. */
function slLabel(mask, w, h) {
  const n = w * h;
  const labels = new Int32Array(n);
  const q = new Int32Array(n);
  const sizes = [0];
  let cur = 0;
  for (let s = 0; s < n; s++) {
    if (!mask[s] || labels[s]) continue;
    cur++;
    let head = 0, tail = 0, size = 0;
    labels[s] = cur; q[tail++] = s;
    while (head < tail) {
      const pp = q[head++]; size++;
      const px = pp % w, py = (pp - px) / w;
      if (px > 0 && mask[pp - 1] && !labels[pp - 1]) { labels[pp - 1] = cur; q[tail++] = pp - 1; }
      if (px < w - 1 && mask[pp + 1] && !labels[pp + 1]) { labels[pp + 1] = cur; q[tail++] = pp + 1; }
      if (py > 0 && mask[pp - w] && !labels[pp - w]) { labels[pp - w] = cur; q[tail++] = pp - w; }
      if (py < h - 1 && mask[pp + w] && !labels[pp + w]) { labels[pp + w] = cur; q[tail++] = pp + w; }
    }
    sizes.push(size);
  }
  return { labels, sizes, count: cur };
}

/** Erode by a disc: dilate the inverse, then invert. */
function slDiscErode(mask, w, h, R) {
  const n = w * h;
  const inv = new Uint8Array(n);
  for (let p = 0; p < n; p++) inv[p] = mask[p] ? 0 : 255;
  const grown = slDiscDilate(inv, w, h, R);
  const out = new Uint8Array(n);
  for (let p = 0; p < n; p++) out[p] = grown[p] ? 0 : 255;
  return out;
}

/** Closing: bridges gaps up to 2R without moving edges away from them. */
function slDiscClose(mask, w, h, R) {
  if (!(R > 0)) return Uint8Array.from(mask);
  return slDiscErode(slDiscDilate(mask, w, h, R), w, h, R);
}

/* ============================================================================
   THE PREVIEW BORDER'S PIXEL WORK, AS ONE PURE FUNCTION (D-176 performance).

   The bake grows the customer's border in PIXELS: close the silhouette by
   `closeR` (dilate; invert; dilate; invert), grow it by `rad`, fill enclosed
   holes, measure each pixel's depth from the outside, and paint the backing -
   border ring, white kiss-line, sticker face. On a phone-speed CPU that held
   the page's only thread for about four seconds on every design change.

   It is exactly the arithmetic the bake did through its canvases - every mask
   it handled is binary (0/255), which a canvas stores and returns exactly - so
   it is moved here unchanged, as one function the bake can run in a Web
   Worker (slBakeRun) or, if no worker can run, on the page. Same function,
   same bytes, either way.

   in:  clean  the de-jagged artwork alpha, 0/255, cw x ch
   out: { sil  the closed, hole-filled pre-border silhouette alpha (production's
               authoritative silhouette),
          bd   the backing as RGBA - the bake's `bk` canvas, byte for byte } */
function slBakeMasks(clean, cw, ch, closeR, rad, border, face, sep) {
  const n = cw * ch;
  /* 1c. closing: dilate, then erode as invert - dilate - invert */
  const dil = slDiscDilate(clean, cw, ch, closeR);
  const inv = new Uint8Array(n);
  for (let p = 0; p < n; p++) inv[p] = (dil[p] > 60 ? 255 : 0) === 0 ? 255 : 0;
  const ei = slDiscDilate(inv, cw, ch, closeR);
  const sil = new Uint8Array(n);
  for (let p = 0; p < n; p++) sil[p] = ei[p] < 128 ? 255 : 0;
  /* 2. the border, grown from the closed silhouette */
  const gd = slDiscDilate(sil, cw, ch, rad);
  /* 3. enclosed holes are filled: the body is one solid shape */
  const outside = new Uint8Array(n);
  const st = [];
  for (let x = 0; x < cw; x++) {
    const t = x, b = (ch - 1) * cw + x;
    if (gd[t] === 0 && !outside[t]) { outside[t] = 1; st.push(t); }
    if (gd[b] === 0 && !outside[b]) { outside[b] = 1; st.push(b); }
  }
  for (let y = 0; y < ch; y++) {
    const l = y * cw, rr = y * cw + cw - 1;
    if (gd[l] === 0 && !outside[l]) { outside[l] = 1; st.push(l); }
    if (gd[rr] === 0 && !outside[rr]) { outside[rr] = 1; st.push(rr); }
  }
  while (st.length) {
    const p = st.pop();
    const x = p % cw, y = (p - x) / cw;
    if (x > 0) { const q = p - 1; if (!outside[q] && gd[q] === 0) { outside[q] = 1; st.push(q); } }
    if (x < cw - 1) { const q = p + 1; if (!outside[q] && gd[q] === 0) { outside[q] = 1; st.push(q); } }
    if (y > 0) { const q = p - cw; if (!outside[q] && gd[q] === 0) { outside[q] = 1; st.push(q); } }
    if (y < ch - 1) { const q = p + cw; if (!outside[q] && gd[q] === 0) { outside[q] = 1; st.push(q); } }
  }
  for (let p = 0; p < n; p++) if (gd[p] === 0 && !outside[p]) gd[p] = 255;
  /* 4. depth from the outside, up to the border width */
  const depth = new Int32Array(n).fill(-1);
  const q = [];
  let qi = 0;
  for (let p = 0; p < n; p++) if (outside[p]) { depth[p] = 0; q.push(p); }
  while (qi < q.length) {
    const p = q[qi++];
    const d = depth[p];
    if (d >= rad) continue;
    const x = p % cw, y = (p - x) / cw;
    if (x > 0) { const r2 = p - 1; if (depth[r2] === -1) { depth[r2] = d + 1; q.push(r2); } }
    if (x < cw - 1) { const r2 = p + 1; if (depth[r2] === -1) { depth[r2] = d + 1; q.push(r2); } }
    if (y > 0) { const r2 = p - cw; if (depth[r2] === -1) { depth[r2] = d + 1; q.push(r2); } }
    if (y < ch - 1) { const r2 = p + cw; if (depth[r2] === -1) { depth[r2] = d + 1; q.push(r2); } }
  }
  /* 5. the backing: border ring, white kiss-line, sticker face */
  const bd = new Uint8ClampedArray(n * 4);
  const ringOuter = rad - sep;
  for (let p = 0; p < n; p++) {
    if (gd[p] === 0) { bd[p * 4 + 3] = 0; continue; }
    const dep = depth[p];
    if (dep >= 1 && dep <= ringOuter) { bd[p * 4] = border[0]; bd[p * 4 + 1] = border[1]; bd[p * 4 + 2] = border[2]; }
    else if (dep > ringOuter && dep <= rad) { bd[p * 4] = 255; bd[p * 4 + 1] = 255; bd[p * 4 + 2] = 255; }
    else { bd[p * 4] = face[0]; bd[p * 4 + 1] = face[1]; bd[p * 4 + 2] = face[2]; }
    bd[p * 4 + 3] = 255;
  }
  return { sil: sil, bd: bd };
}

/* A PNG data URL WITHOUT HOLDING THE PAGE (D-176 performance). `toDataURL`
   encodes on the page's thread - over a second per preview on a phone-speed
   CPU. `toBlob` encodes off it, and the same PNG read back as a data URL is
   the same string. A canvas that cannot be read (tainted) fails exactly as
   `toDataURL` would. With `window.__slCutWorkerVerify` (QA only) every result
   is compared with `toDataURL` and recorded. */
/* QA only: do two PNGs decode to the same pixels? */
function slPngPixelsSame(a, b) {
  function px(u) { return new Promise(function (res) { var im = new Image(); im.onload = function () {
    var c = document.createElement('canvas'); c.width = im.naturalWidth; c.height = im.naturalHeight;
    var x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(im, 0, 0);
    res(x.getImageData(0, 0, c.width, c.height).data); }; im.onerror = function () { res(null); }; im.src = u; }); }
  return Promise.all([px(a), px(b)]).then(function (r) {
    if (!r[0] || !r[1] || r[0].length !== r[1].length) return false;
    for (var i = 0; i < r[0].length; i++) if (r[0][i] !== r[1][i]) return false;
    return true;
  });
}
function slPngDataUrl(canvas, site) {
  return new Promise(function (resolve, fail) {
    var onPage = function () { try { resolve(canvas.toDataURL('image/png')); } catch (e) { fail(e); } };
    if (!canvas.toBlob || typeof FileReader === 'undefined') { onPage(); return; }
    /* QA: the reference is taken NOW, from the same pixels toBlob snapshots */
    var ref = null;
    if (typeof window !== 'undefined' && window.__slCutWorkerVerify) { try { ref = canvas.toDataURL('image/png'); } catch (eR) { ref = null; } }
    try {
      canvas.toBlob(function (blob) {
        if (!blob) { onPage(); return; }
        var fr = new FileReader();
        fr.onload = function () {
          var url = fr.result;
          if (ref !== null) {
            try {
              var rec = { site: site || null, w: canvas.width, h: canvas.height, same: url === ref, pixelsSame: url === ref ? true : null };
              (window.__slPngVerify = window.__slPngVerify || []).push(rec);
              if (!rec.same) slPngPixelsSame(url, ref).then(function (s) { rec.pixelsSame = s; });
            } catch (eV) {}
          }
          resolve(url);
        };
        fr.onerror = onPage;
        fr.readAsDataURL(blob);
      }, 'image/png');
    } catch (e) { onPage(); }
  });
}

/* The bake's worker: nothing to fetch - it is built from the functions above,
   by their source. One job at a time; a newer bake stops an older one (the
   customer changed the design), and if no worker can run, the page computes
   it exactly as before. */
var slBakeWorker = { state: 'idle', url: null, w: null, job: null, seq: 0, log: [] };
function slBakeRun(clean, cw, ch, closeR, rad, border, face, sep) {
  const onPage = function () { return slBakeMasks(clean, cw, ch, closeR, rad, border, face, sep); };
  if (slBakeWorker.state === 'broken' || typeof Worker === 'undefined' || typeof Blob === 'undefined' ||
      typeof URL === 'undefined' || !URL.createObjectURL) {
    return Promise.resolve(onPage());
  }
  try {
    if (!slBakeWorker.url) {
      const src = 'var SL_DILATE_EPS = ' + JSON.stringify(SL_DILATE_EPS) + ';\n' +
        slEdt1d.toString() + '\n' + slDiscDilate.toString() + '\n' + slBakeMasks.toString() + '\n' +
        'self.onmessage = function (e) { var m = e.data, r;\n' +
        '  try { r = slBakeMasks(m.clean, m.cw, m.ch, m.closeR, m.rad, m.border, m.face, m.sep); }\n' +
        '  catch (err) { self.postMessage({ id: m.id, error: String(err && err.message || err) }); return; }\n' +
        '  self.postMessage({ id: m.id, sil: r.sil, bd: r.bd }, [r.sil.buffer, r.bd.buffer]); };\n';
      slBakeWorker.url = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }));
      slBakeWorker.state = 'ready';
    }
    const cur = slBakeWorker.job;
    if (cur) {                                   /* superseded: stop it, never finish it */
      slBakeWorker.job = null;
      try { slBakeWorker.w.terminate(); } catch (e) {}
      slBakeWorker.w = null;
      cur.abandon({ superseded: true });
    }
    if (!slBakeWorker.w) {
      const w = new Worker(slBakeWorker.url);
      w.onmessage = function (e) {
        const j = slBakeWorker.job, m = e.data || {};
        if (!j || m.id !== j.id) return;
        slBakeWorker.job = null;
        if (m.error) { slBakeWorker.state = 'broken'; j.fallback(); }
        else { slBakeWorker.log.push({ id: j.id, ms: Date.now() - j.t0 }); j.resolve({ sil: m.sil, bd: m.bd }); }
      };
      w.onerror = function (ev) {
        const j = slBakeWorker.job;
        slBakeWorker.job = null;
        slBakeWorker.state = 'broken';
        try { w.terminate(); } catch (e) {}
        slBakeWorker.w = null;
        if (j) j.fallback();
        if (ev && ev.preventDefault) ev.preventDefault();
      };
      slBakeWorker.w = w;
    }
    if (slBakeWorker.log.length > 30) slBakeWorker.log.splice(0, slBakeWorker.log.length - 30);
    const job = { id: ++slBakeWorker.seq, t0: Date.now() };
    const p = new Promise(function (res, fl) {
      job.resolve = res; job.abandon = fl;
      job.fallback = function () { try { res(onPage()); } catch (e) { fl(e); } };
    });
    slBakeWorker.job = job;
    /* a copy goes to the worker; the page keeps `clean` for its own fallback */
    const sent = new Uint8Array(clean);
    slBakeWorker.w.postMessage({ id: job.id, clean: sent, cw: cw, ch: ch, closeR: closeR, rad: rad,
                                 border: border, face: face, sep: sep }, [sent.buffer]);
    return p;
  } catch (e) {
    slBakeWorker.state = 'broken';
    return Promise.resolve(onPage());
  }
}

/**
 * One manufacturable body for artwork that arrived in pieces.
 *
 * @returns {Object|null} null when the artwork is already one piece — Case A,
 *   where inventing substrate would be wrong. Otherwise
 *   { body, radius, pieces, componentsBefore } with `body` a 0/255 mask.
 */
function slConnectedBody(alpha, w, h) {
  const n = w * h;
  const mask = new Uint8Array(n);
  for (let p = 0; p < n; p++) mask[p] = alpha[p] > 8 ? 255 : 0;

  const lab = slLabel(mask, w, h);
  if (lab.count === 0) return null;                    /* nothing to carry */

  let largest = 0;
  for (let i = 1; i <= lab.count; i++) if (lab.sizes[i] > largest) largest = lab.sizes[i];
  const floor = Math.max(SL_BODY_SIG_PX, Math.round(largest * SL_BODY_SIG_FRAC));
  const significant = [];
  for (let i = 1; i <= lab.count; i++) if (lab.sizes[i] >= floor) significant.push(i);

  if (significant.length === 0) return null;
  if (significant.length === 1) {
    /* CASE A -- one real piece. Nothing needs bridging, but anything ELSE
       floating around it would still be cut as its own little sticker, so the
       stray specks are dropped rather than manufactured. If there are none,
       the artwork is already exactly right and is left completely alone. */
    if (lab.count === 1) return null;
    const only = significant[0];
    const solo = new Uint8Array(n);
    for (let p = 0; p < n; p++) if (lab.labels[p] === only) solo[p] = 255;
    return { body: solo, radius: 0, pieces: 1, componentsBefore: lab.count, strays: lab.count - 1 };
  }

  /* one seed pixel per significant piece, to test connectivity cheaply */
  const seeds = new Int32Array(significant.length).fill(-1);
  const index = new Map();
  significant.forEach((id, k) => index.set(id, k));
  for (let p = 0; p < n && seeds.indexOf(-1) >= 0; p++) {
    const id = lab.labels[p];
    if (!id) continue;
    const k = index.get(id);
    if (k !== undefined && seeds[k] < 0) seeds[k] = p;
  }

  const joins = (m) => {
    const l = slLabel(m, w, h);
    const first = l.labels[seeds[0]];
    if (!first) return false;
    for (let k = 1; k < seeds.length; k++) if (l.labels[seeds[k]] !== first) return false;
    return true;
  };

  /* ── WHY NOT SIMPLY CLOSE HARDER ─────────────────────────────────────
     A closing wide enough to span the gap also floods every gap narrower than
     it. Rendered, two discs 620px apart came back as a filled rectangle over
     the whole canvas -- the exact "giant rectangle around the former image
     canvas" this is supposed to avoid -- and a row of letters 24px apart
     became a featureless slab. One global radius cannot both span a wide gap
     and respect a narrow one.

     So the gaps are spanned where they actually are. The significant pieces
     are joined by a minimum spanning tree over their true closest approach,
     and each link is drawn as a capsule -- a thick rounded bar between the two
     nearest points -- wide enough to be pluckable rather than a hairline. Only
     the links are added; everything else keeps the shape it had.

     A light closing afterwards is for manners only: it fillets where a capsule
     meets a letter, so the joins read as designed rather than welded on. It is
     a fraction of the bridge width, so it can never flood a gap. */
  const longEdge = Math.max(w, h);
  const bridgeW = Math.max(SL_BODY_BRIDGE_MIN_PX, Math.round(longEdge * SL_BODY_BRIDGE_FRAC));

  /* boundary pixels per significant piece, thinned, so the closest-approach
     search stays linear enough on a large canvas */
  const pts = significant.map(() => []);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const pp = y * w + x;
      const id = lab.labels[pp];
      if (!id) continue;
      const k = index.get(id);
      if (k === undefined) continue;
      const edgeP = x === 0 || y === 0 || x === w - 1 || y === h - 1 ||
        !mask[pp - 1] || !mask[pp + 1] || !mask[pp - w] || !mask[pp + w];
      if (edgeP && ((x + y) & 1) === 0) pts[k].push(x, y);
    }
  }

  /* closest approach between two pieces, and the pair that achieves it */
  const near = (i, j) => {
    const A = pts[i], B = pts[j];
    let best = Infinity, ax = 0, ay = 0, bx = 0, by = 0;
    const stepA = Math.max(2, (A.length / 2 / 600) | 0) * 2;
    const stepB = Math.max(2, (B.length / 2 / 600) | 0) * 2;
    for (let u = 0; u < A.length; u += stepA) {
      const x1 = A[u], y1 = A[u + 1];
      for (let v = 0; v < B.length; v += stepB) {
        const dx = x1 - B[v], dy = y1 - B[v + 1];
        const d = dx * dx + dy * dy;
        if (d < best) { best = d; ax = x1; ay = y1; bx = B[v]; by = B[v + 1]; }
      }
    }
    return { d: Math.sqrt(best), ax, ay, bx, by };
  };

  /* PRIM: grow one tree, always taking the cheapest link out of it. */
  const m = significant.length;
  const inTree = new Array(m).fill(false);
  inTree[0] = true;
  const links = [];
  for (let added = 1; added < m; added++) {
    let bestLink = null, bestJ = -1;
    for (let i = 0; i < m; i++) {
      if (!inTree[i]) continue;
      for (let j = 0; j < m; j++) {
        if (inTree[j]) continue;
        const cand = near(i, j);
        if (!bestLink || cand.d < bestLink.d) { bestLink = cand; bestJ = j; }
      }
    }
    if (!bestLink) break;
    inTree[bestJ] = true;
    links.push(bestLink);
  }

  /* draw each link as a capsule of width bridgeW */
  const body0 = Uint8Array.from(mask);
  const rad = bridgeW / 2, rad2 = rad * rad;
  links.forEach(L => {
    const dx = L.bx - L.ax, dy = L.by - L.ay;
    const len2 = dx * dx + dy * dy;
    const x0 = Math.max(0, Math.floor(Math.min(L.ax, L.bx) - rad - 1));
    const x1 = Math.min(w - 1, Math.ceil(Math.max(L.ax, L.bx) + rad + 1));
    const y0 = Math.max(0, Math.floor(Math.min(L.ay, L.by) - rad - 1));
    const y1 = Math.min(h - 1, Math.ceil(Math.max(L.ay, L.by) + rad + 1));
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        let t = len2 > 0 ? (((x - L.ax) * dx + (y - L.ay) * dy) / len2) : 0;
        t = t < 0 ? 0 : (t > 1 ? 1 : t);
        const cx = L.ax + t * dx, cy = L.ay + t * dy;
        const ex = x - cx, ey = y - cy;
        if (ex * ex + ey * ey <= rad2) body0[y * w + x] = 255;
      }
    }
  });

  /* manners: a fillet where a bridge meets artwork, never wide enough to flood */
  const fillet = Math.max(1, Math.round(bridgeW * SL_BODY_FILLET_FRAC));
  let body = slDiscClose(body0, w, h, fillet);
  if (!joins(body)) body = slDiscClose(body0, w, h, fillet * 2);
  const best = bridgeW;

  /* Keep only what carries the artwork. A speck the closing happened to
     inflate is not a second sticker, and must not be cut as one. */
  const bl = slLabel(body, w, h);
  const keep = bl.labels[seeds[0]];
  for (let p = 0; p < n; p++) if (bl.labels[p] !== keep) body[p] = 0;

  /* NEGATIVE SPACE: close the pinholes, keep the intent. */
  let area = 0;
  for (let p = 0; p < n; p++) if (body[p]) area++;
  const holeFloor = Math.max(8, Math.round(area * SL_BODY_HOLE_FRAC));
  const seen = new Uint8Array(n), q = new Int32Array(n);
  for (let s = 0; s < n; s++) {
    if (seen[s] || body[s]) continue;
    let head = 0, tail = 0, size = 0, touches = false;
    seen[s] = 1; q[tail++] = s;
    while (head < tail) {
      const pp = q[head++]; size++;
      const px = pp % w, py = (pp - px) / w;
      if (px === 0 || py === 0 || px === w - 1 || py === h - 1) touches = true;
      if (px > 0 && !body[pp - 1] && !seen[pp - 1]) { seen[pp - 1] = 1; q[tail++] = pp - 1; }
      if (px < w - 1 && !body[pp + 1] && !seen[pp + 1]) { seen[pp + 1] = 1; q[tail++] = pp + 1; }
      if (py > 0 && !body[pp - w] && !seen[pp - w]) { seen[pp - w] = 1; q[tail++] = pp - w; }
      if (py < h - 1 && !body[pp + w] && !seen[pp + w]) { seen[pp + w] = 1; q[tail++] = pp + w; }
    }
    if (!touches && size < holeFloor) {
      /* an accident, not a decision: fill it */
      let head2 = 0, tail2 = 0;
      const q2 = new Int32Array(size + 4);
      q2[tail2++] = s; body[s] = 255;
      while (head2 < tail2) {
        const pp = q2[head2++];
        const px = pp % w, py = (pp - px) / w;
        if (px > 0 && !body[pp - 1]) { body[pp - 1] = 255; q2[tail2++] = pp - 1; }
        if (px < w - 1 && !body[pp + 1]) { body[pp + 1] = 255; q2[tail2++] = pp + 1; }
        if (py > 0 && !body[pp - w]) { body[pp - w] = 255; q2[tail2++] = pp - w; }
        if (py < h - 1 && !body[pp + w]) { body[pp + w] = 255; q2[tail2++] = pp + w; }
      }
    }
  }

  return { body, radius: best, pieces: significant.length, componentsBefore: lab.count };
}

function slContentFrame(cv, padFrac) {
  const w = cv.width, h = cv.height;
  if (!(w > 0) || !(h > 0)) return null;
  let d;
  try { d = cv.getContext('2d').getImageData(0, 0, w, h).data; }
  catch (e) { return null; }          // tainted canvas — the caller keeps the original

  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) {
    const row = y * w * 4;
    for (let x = 0; x < w; x++) {
      /* A low threshold on purpose: antialiased border edges and soft shadows
         are part of the sticker, and cropping them off would shave the very
         white border this exists to keep whole. */
      if (d[row + x * 4 + 3] > 8) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  if (x1 < x0 || y1 < y0) return null;                 // nothing opaque; leave it alone

  const cw = x1 - x0 + 1, ch = y1 - y0 + 1;
  const pad = Math.round(Math.max(cw, ch) * (padFrac != null ? padFrac : SL_THUMB_PAD_FRAC));
  /* SQUARE, AND CENTRED IN IT. The tiles that show this are square (116px in
     the cart, a fixed box in HQ), so squaring here is what makes a tall sticker
     and a wide one get the SAME breathing room once `object-fit: contain` has
     had its say. Fitting a tall sticker into a tall frame and then contain-ing
     that into a square tile would pad one axis twice. */
  const side = Math.max(cw, ch) + pad * 2;
  const out = document.createElement('canvas');
  out.width = side;
  out.height = side;
  const octx = out.getContext('2d');
  octx.drawImage(cv, x0, y0, cw, ch,
                 Math.round((side - cw) / 2), Math.round((side - ch) / 2), cw, ch);
  return out;
}

/* ============================================================================
   THE FINISHED STICKER IS WHAT THE CUSTOMER ORDERED  (contour-fit-v1).

   For a die-cut sticker the product is the CUT SHAPE — the artwork plus the
   generated border, out to the CutContour. So that is what the ordered size
   has to measure. It did not: `arForSize` used the artwork CANVAS aspect and
   the artwork region was scaled to span the ordered width, which left the
   border to fall wherever it fell.

   ── WHAT THAT COST, MEASURED ────────────────────────────────────────────
   On the real bear order (1536x1024, silhouette bbox 1489x861 with margins
   L20 R27 T67 B96) ordered at 2.000in:

       border      finished sticker      vs the ordered 2.000in
       thin        1.9948 x 1.1771       -0.005 in
       standard    2.0268 x 1.2091       +0.027 in   border flat-cut L and R
       thick       2.0788 x 1.2611       +0.079 in   border flat-cut L and R

   The canvas is the production viewBox, so an overshoot is not a rounding
   detail — it is a straight edge sliced through a white border that was
   supposed to be a curve. And the ordered HEIGHT was 1.3in against a sticker
   that is really 1.18-1.22in tall: the order described the artwork canvas,
   transparent margins and all, rather than the thing being made.

   ── THE RULE ────────────────────────────────────────────────────────────
   The silhouette, plus one border width on every side, spans the ordered long
   edge. Everything else follows from that:

       a  = (L - 2b) / max(sw, sh)        inches per artwork pixel
       Fw = sw*a + 2b   Fh = sh*a + 2b    the finished sticker
       max(Fw, Fh) = L                    exactly, by construction

   A THICKER BORDER MAKES THE SHORT EDGE LONGER, not shorter. The long edge is
   pinned at L, so growing b shrinks the artwork; but b is added to BOTH axes,
   and adding a constant to both terms of a ratio moves it toward 1. Measured
   above: 1.1771 thin -> 1.2091 standard -> 1.2611 thick. The finished sticker
   gets squarer as the border thickens, and the finished AREA grows slightly.

   ── WHY THE SILHOUETTE BBOX AND NOT THE CANVAS ──────────────────────────
   Transparent margins are not part of the sticker. The bear carries 20px of
   nothing on its left; charging for it, and sizing to it, describes the file
   rather than the product.
   ============================================================================ */
function slContourFit(sil, iw, ih, borderIn, longEdgeIn) {
  if (!sil) return null;
  var sw = +sil.w, sh = +sil.h, L = +longEdgeIn;
  var b = Math.max(0, +borderIn || 0);
  if (!(sw > 0) || !(sh > 0) || !(+iw > 0) || !(+ih > 0) || !(L > 0)) return null;
  /* A border that eats the whole order is not a sticker. Refused rather than
     clamped: a clamp would silently produce a size nobody asked for. */
  if (!(L - 2 * b > 0)) return null;

  var a = (L - 2 * b) / Math.max(sw, sh);
  var Fw = sw * a + 2 * b, Fh = sh * a + 2 * b;
  var artW = +iw * a, artH = +ih * a;
  /* WHERE THE ARTWORK SITS so the FINISHED sticker is centred. The silhouette
     is not centred in its own canvas — the bear is 20px from one edge and 27
     from the other — so centring the artwork would put the sticker off-centre
     by exactly that asymmetry. Inches, x right, y down, origin at the
     production canvas top-left, which is the SVG viewBox convention. */
  var x = Fw / 2 - (+sil.x + sw / 2) * a;
  var y = Fh / 2 - (+sil.y + sh / 2) * a;

  return {
    borderIn: b,
    inchesPerPx: a,
    finishedW: Fw, finishedH: Fh,
    aspect: Fw / Fh,
    artworkRectIn: { x: x, y: y, width: artW, height: artH },
    /* The factor that turns "the artwork spans the canvas" into "the FINISHED
       STICKER spans the canvas". It multiplies `contourScale`; it does not
       replace it. `contourScale` is still exactly cw_f/iw and still cancels
       the baker's padding — that correction and this one answer different
       questions, and folding them together is how the first one got lost. */
    fitFactor: artW / Fw
  };
}

/* ============================================================================
   ONE CORRECTION, SOLVED FROM WHAT WAS ACTUALLY BUILT.

   `slContourFit` solves the finished size from the artwork's RAW ALPHA bbox.
   MOTO does not reconstruct that bbox: the morphological closing bridges
   narrow gaps before the outline is traced, so the reconstructed silhouette
   can sit a pixel or two off the alpha box it came from. That is a small
   error and a real one, and the closed form cannot see it — only the built
   CutContour can.

   So the actual cut path is measured, and if it misses the canvas by more
   than the numerical tolerance, ONE exact correction is solved from the
   measurement and the job is rebuilt. Once. Not a loop: if the second build
   still misses, something is wrong that another iteration will not fix, and
   production is refused with the numbers rather than shipped with a sliced
   border.

   ── WHY IT IS EXACT IN ONE STEP ─────────────────────────────────────────
   The cut path is the reconstructed silhouette P dilated by the border
   distance d, and bbox(P (+) disk(d)) is bbox(P) grown by exactly d per side.
   Scaling the ARTWORK by m scales P by m; d is a physical border width and
   does not move. So

       B(m) = m * (B1 - 2d) + 2d

   on each axis — linear, with a known intercept. Solving it is arithmetic,
   not iteration.

   ── AND WHY IT IS NOT min(canvasW/bboxW, canvasH/bboxH) ─────────────────
   That shrinks until it fits and leaves the ordered size unspent: a 2in
   sticker comes back 1.94in and the customer is short-changed on the
   dimension they chose. The solve is stated on the LONG axis, so the finished
   contour still consumes the selected long edge exactly; the short axis is
   then CHECKED rather than assumed, and a short axis that does not fit means
   the canvas aspect itself disagrees with the reconstruction by more than
   tolerance — which is a refusal, not something to silently absorb.
   ============================================================================ */
function slContourCorrect(bb, cw, ch, distance, tolUnits) {
  if (!bb) return null;
  var d = Math.max(0, +distance || 0);
  var tol = Math.max(0, +tolUnits || 0);
  var bw = bb.maxX - bb.minX, bh = bb.maxY - bb.minY;
  if (!(bw > 0) || !(bh > 0) || !(+cw > 0) || !(+ch > 0)) return null;

  var overflow = {
    left: 0 - bb.minX, top: 0 - bb.minY,
    right: bb.maxX - cw, bottom: bb.maxY - ch
  };
  var worst = Math.max(overflow.left, overflow.top, overflow.right, overflow.bottom);

  /* THE SOLVER AND THE GATE MUST MEASURE THE SAME AXIS.
     ---------------------------------------------------------------------
     `longIsX` was `cw >= ch` — the CANVAS's long axis. The acceptance gate in
     `buildDoubleDip` measures `Math.max(mbb.w, mbb.h)` against
     `Math.max(cw, ch)` — the CONTOUR's long axis. Those are the same axis only
     when the canvas and the reconstructed sticker share an orientation.

     On a SQUARE canvas they need not. Real order SL-1M1TM-JV8KP-Y5ANM was a
     custom 1.25 x 1.25 — square canvas, 301 x 301 — on a 380 x 388 silhouette,
     so the finished sticker is 1.2254 x 1.2500 and is TALLER than it is wide.
     `cw >= ch` is a coin flip on a square canvas and it landed on X: the solver
     drove the width to exactly 301 while the height overshot to ~306.8, and the
     gate refused `cut_long_edge_off_target` on a box the solver believed it had
     just corrected. The recorded correctionScale was 1.003148 — scaling UP a
     sticker that was already too long on the axis that counted. A second pass
     could not help; it was solving the wrong equation.

     So the long axis is taken from the MEASURED CONTOUR, and the target is the
     canvas's long extent — which is what the gate compares against. Where the
     canvas and the contour already agree, `max`/`min` return exactly what
     `longIsX ? cw : ch` returned and this is a no-op; the golden reference
     (SL-1M1BC-GKCFB-GP60J, 3 x 2in, wider-than-tall contour on a 398 x 265
     canvas) is unchanged.

     This does NOT shrink to fit both axes: one axis is still solved exactly and
     the other is still CHECKED by `shortFits`, which is unchanged. A genuine
     aspect disagreement is still a refusal. */
  var longIsX = bw >= bh;
  var canvasLong = Math.max(cw, ch);
  var canvasShort = Math.min(cw, ch);
  var pLong = (longIsX ? bw : bh) - 2 * d;
  var pShort = (longIsX ? bh : bw) - 2 * d;
  if (!(pLong > 0)) return null;

  var m = (canvasLong - 2 * d) / pLong;
  var predShort = m * pShort + 2 * d;

  /* ── CONTAINMENT IS HALF THE INVARIANT; SPENDING THE ORDERED SIZE IS THE
        OTHER HALF ───────────────────────────────────────────────────────
     MOTO's closing can shrink the reconstructed silhouette as easily as grow
     it, and a cut path that lands INSIDE the canvas overflows nothing while
     still being the wrong size: a 2in order comes back 1.99in. Correcting only
     on overflow would ship that, quietly, as a sticker smaller than the one the
     customer chose. So the long edge is checked in both directions and the
     correction runs either way — it is the same solve, and it is exact. */
  var longActual = longIsX ? bw : bh;
  var longMiss = Math.abs(longActual - canvasLong);

  return {
    overflow: overflow,
    worstOverflow: worst,
    longMiss: longMiss,
    needed: worst > tol || longMiss > tol,
    scaleMul: m,
    longIsX: longIsX,
    predictedLong: canvasLong,
    predictedShort: predShort,
    /* PER SIDE, BECAUSE THE STICKER IS CENTRED. The canvas aspect was derived
       from the UNDILATED silhouette, so when MOTO's closing grows the path by a
       constant on every side that constant is proportionally larger on the
       short axis, and the two aspects disagree slightly. Measured on the real
       bear at Standard the short axis lands 0.2 units over a 222-unit canvas —
       0.09%, split to 0.1 per side. Judging that against the whole excess
       instead of the per-side one would refuse production over a tenth of a
       unit, which is a worse outcome than the border it is protecting. A
       GENUINE aspect disagreement is larger than the tolerance either way. */
    shortFits: (predShort - canvasShort) / 2 <= tol,
    centre: { x: (bb.minX + bb.maxX) / 2, y: (bb.minY + bb.maxY) / 2 }
  };
}

/* The numerical tolerance a FITTED contour job is held to, in canvas units.
   Not a licence like the 8% it replaces: it covers the ordered inches rounded
   to one decimal, the preview box rounded to whole pixels, and the path
   reconstruction — and nothing else. */
/* ============================================================================
   THE CONTOUR FIT, MADE OBSERVABLE.

   The fit and its correction run inside an export that takes tens of seconds
   and leaves nothing behind but a finished file. When the real browser
   produced a 2.9837in contour for a 3.0000in order, there was no way to tell
   from the artefact whether the correction pass had run at all, what it had
   measured, or what it had asked for — only that the answer was 16 thou
   short. Inferring any of that from the SVG would be guessing.

   So the report the export already builds is also hung on `window` under a
   double-underscore name. It is READ-ONLY DIAGNOSTIC DATA: nothing in the
   builder consults it, and removing the assignment would change no behaviour.
   ============================================================================ */
function slPublishFit(r) {
  try { if (typeof window !== 'undefined') window.__slContourFit = r; } catch (e) {}
  return r;
}

/* ============================================================================
   THE FINISHED-SIZE ACCEPTANCE TARGET IS PHYSICAL.

   A sticker is accepted on how many INCHES it misses the ordered long edge by,
   because that is the only thing the customer can hold against a ruler. The
   gate therefore states one number — 0.015in — and converts it into canvas
   units using the document's own scale.

   It used to be `max(cw, ch) * 0.004`: a fraction of the CANVAS. That is not a
   physical quantity. It happened to work out to 1.592u on the 398u/3in bear
   (0.012in), but the same 0.4% means a different physical tolerance on every
   document whose units-per-inch differs — the rule silently tightened on small
   stickers and loosened on large ones, and nothing in the number said so.

   `unitsPerInchLong` is measured on the LONG axis, because the long edge is
   what the customer ordered and what the correction solves on. The fallback
   is the old proportional rule, kept only so a caller that cannot state its
   scale is never handed a tolerance of zero — not as a second opinion.
   ============================================================================ */
var SL_SIZE_TOL_IN = 0.015;
/* A STICKER REFUSED FOR ITS SIZE ALONE GETS A FAITHFUL REBUILD (P-45, 20 Oct
   2026). Order #1067's hexagon, no border, ordered 6in: the organic outline
   ran ~0.03in inside the ink at its two pointed corners, the cut came out
   5.977in, and the long-edge gate rightly refused it. The trace-first ladder
   already owns tighter outlines (organic-fine, organic-tight) but climbs them
   only for an ink failure, never for a size one. So a die-cut sticker refused
   for one of these reasons is fitted again, once per outline below, through
   the SAME first pass, correction and gates; the first that passes them all
   is used, and if none does the original refusal stands. A sticker that
   passes today never reaches this. Cart graphics are untouched. */
var SL_SIZE_RESCUE_REASONS = ['cut_long_edge_off_target', 'contour_fit_short_axis_will_not_fit'];
var SL_SIZE_RESCUE_ENGINES = [
  { name: 'organic-fine', eo: { geometricEnabled: false, organicTolFrac: 0.00125, organicFitSigmaFrac: 0.0009 } },
  { name: 'organic-tight', eo: { geometricEnabled: false, organicTolFrac: 0.000625, organicFitSigmaFrac: 0.0006 } }
];                    // inches, on the ordered long edge
/* A SHAPE MOTO WILL NOT CUT ON ITS OWN GOES TO HQ AS A REVIEW (MOTO GVE,
   Golden Rule #3, 27 Oct 2026). #1091: a circle recognised with confidence 1.0
   was refused by the D-175 size guard (1.25% out of round), and the engine fell
   back to a five-segment raster trace that shipped. The engine now answers
   accepted:false, reason "geometry_review_required", with `review`
   (geometry-review-v1: the detected primitive, its exact candidate, the trace
   for comparison). The builder writes NO CutContour for it - the meta row says
   "cut: refused geometry_review_required" as for any refusal - and carries the
   review in the production SVG as <metadata id="sl-geometry-review">, plus the
   line property `_geometry_review=<why>`. Ryan picks Approve Shape / Change
   Shape in HQ, and HQ (hq/production/shape-review.js) draws the exact cut.
   Contract: coordination/IRIS/production-p1/GVE-HQ-ARCHITECTURE.md section 3.
   Not a review reason the size rescue climbs for: a rescue would ship a trace. */
var SL_GEOMETRY_REVIEW_ID = 'sl-geometry-review';
/* The review restated by the transform the builder applies to the cut (a
   uniform scale and a translate), so every number in it is in production.svg
   units. Paths go through the engine's own `transformPath`, as the cut does.
   Inches (`measures`) do not change. Null when anything fails to transport:
   a review in mixed units would put the proposed cut in the wrong place. */
function slTransformGeometryReview(review, T, transformPath) {
  if (!review || typeof review !== 'object') return null;
  const s = +T.sx;
  if (!(s > 0) || Math.abs(+T.sy - s) > 1e-12 || typeof transformPath !== 'function') return null;
  const tx = +T.tx || 0, ty = +T.ty || 0;
  const X = (v) => v * s + tx, Y = (v) => v * s + ty, L = (v) => v * s;
  const params = (p) => {
    if (!p || typeof p !== 'object') return p;
    const q = Object.assign({}, p);
    if (typeof p.cx === 'number') q.cx = X(p.cx);
    if (typeof p.cy === 'number') q.cy = Y(p.cy);
    ['r', 'rx', 'ry', 'w', 'h'].forEach((k) => { if (typeof p[k] === 'number') q[k] = L(p[k]); });
    if (Array.isArray(p.points)) q.points = p.points.map((pt) => [X(+pt[0]), Y(+pt[1])]);
    return q;
  };
  const out = JSON.parse(JSON.stringify(review));
  if (review.viewBox) out.viewBox = { w: L(+review.viewBox.w), h: L(+review.viewBox.h) };
  if (+review.unitsPerInch > 0) out.unitsPerInch = L(+review.unitsPerInch);
  if (review.detected) out.detected = Object.assign({}, out.detected, { params: params(review.detected.params) });
  const cands = [];
  for (const c of (review.candidates || [])) {
    const nc = Object.assign({}, c, { params: params(c.params) });
    if (typeof c.d === 'string' && c.d) {
      let d = null;
      try { d = transformPath(c.d, { sx: s, sy: s, tx: tx, ty: ty }); } catch (e) { d = null; }
      if (!d) return null;
      nc.d = d;
    }
    cands.push(nc);
  }
  out.candidates = cands;
  return out;
}
/* The block itself. JSON in CDATA, because HQ's readReview parses the text as
   JSON with no XML decoding; a "]]>" inside a string is written as "]]>". */
function slGeometryReviewBlock(review) {
  return '  <metadata id="' + SL_GEOMETRY_REVIEW_ID + '"><![CDATA[' +
         JSON.stringify(review).replace(/\]\]>/g, ']]\\u003e') + ']]></metadata>';
}
/* The production SVG carrying the review, just before its closing tag (after
   the Metadata block: outside the printed page, and nothing in it prints). Any input it
   cannot place returns the SVG unchanged. */
function slWithGeometryReview(svg, review) {
  if (typeof svg !== 'string' || !review) return svg;
  const at = svg.lastIndexOf('</svg>');
  if (at < 0) return svg;
  return svg.slice(0, at) + slGeometryReviewBlock(review) + '\n' + svg.slice(at);
}
/* The review's `why`, read back from a production SVG, for the line property. */
function slGeometryReviewOf(svg) {
  const m = /<metadata id="sl-geometry-review"><!\[CDATA\[([\s\S]*?)\]\]><\/metadata>/.exec(String(svg || ''));
  if (!m) return null;
  try { const r = JSON.parse(m[1]); return (r && typeof r.why === 'string' && r.why) ? r.why : 'review'; }
  catch (e) { return null; }
}
/* THE TRACE TOLERANCE (D-176). How far a finished cut may stray from the
   artwork's own pixels, anywhere along its boundary, before MOTO must try a
   more faithful cut or hand the design to a person. Measured, not chosen for
   comfort: the reconstructions Ryan accepted in Illustrator stray up to
   0.016-0.019in (a smoothed bump on the bear, the wave's Standard border), and
   every real defect found sits far above it - the #1062 ellipse 0.076in, the
   circle through the low-res bump 0.085in, the wave's outline ring 0.21in.
   Half a millimetre: inside a cutter's registration, outside any visible gap. */
var SL_TRACE_TOL_IN = 0.020;
/* A DIE-CUT CART GRAPHIC IS TRACED ON A COARSER GRID, SO ITS NOISE IS WIDER.
   Its evidence is 75 px per inch (`CG.dieCutMaskLongEdge`, capped at 3600 px),
   where a sticker's blueprint gives 200+. There the 0.020in above is 1.5
   evidence pixels - inside the trace's own noise, which D-183 measured at up
   to about 0.03in at this scale. A 40in no-border logo was refused on 13
   pixels of its rough outline sitting 2 pixels out. So for a die-cut cart
   graphic only, the tolerance is never finer than this many evidence pixels:
   0.030in at 75 px per inch, still well below every real defect above
   (0.076in and more). Stickers never take this floor. */
var SL_CG_TRACE_NOISE_PX = 2.25;
/* THE PREVIEW SHOWS THE PRODUCTION CUT, NOT A SECOND OPINION (D-176).
   The preview used to run its own, cheaper double dip on a downsampled mask with
   none of production's candidates, guard or tolerances, and drew THAT: on
   order #1062 it drew an ellipse while production refused one, and on the
   low-res fixture a rounded rectangle while production cut a circle. Off: the
   bake shows the raster border it computes anyway - the artwork's own pixels,
   dilated, which never guesses a shape - until the production cut for exactly
   this design arrives and replaces it. Also saves the phone that pass. */
var SL_PREVIEW_INTERIM_MOTO = false;
function slContourTolUnits(cw, ch, unitsPerInchLong) {
  var u = +unitsPerInchLong;
  if (isFinite(u) && u > 0) return SL_SIZE_TOL_IN * u;
  return Math.max(0.75, Math.max(cw, ch) * 0.004);
}

/* ============================================================================
   THE CUT IS BUILT OFF THE PAGE'S THREAD (D-176 performance).

   Measured on a phone-speed CPU (4x and 6x throttling): building the
   production cut in the background - so the preview can show it and Add to
   Cart can ship it at once - held the page's only thread for 40-80 seconds,
   and the builder could not be touched. The work itself is no larger than the
   build Add to Cart has always done; it had simply moved from behind the save
   screen into the middle of editing.

   So the reconstruction runs in a Web Worker. It is THE SAME CODE: the worker
   is assembled from the very engine files this page loaded (fetched by their
   own script URLs, so it can never run a different version) and from the
   builder's own `buildDoubleDip`, passed as its source text. The page reads
   the silhouette pixels and hands them over; the worker returns the same
   result object the page would have built. Nothing about the cut, its
   validation, its tolerances or its size checks differs - only which thread
   computes it.

   FAILURE IS NEVER A DIFFERENT CUT:
     - no worker possible (no Worker, a script missing, CSP, a fetch failed):
       Add to Cart builds on the page exactly as it always did; the preview
       keeps its pixel border instead of freezing the page for a background
       build;
     - the worker fails on a job: that job is rebuilt on the page (Add to
       Cart) or abandoned (a preview build), and the worker is not trusted
       again for this page;
     - a newer preview build arrives: the stale one is stopped, not finished.
       Add to Cart is never stopped, and is never queued behind a preview.
   `window.__slCutWorkerVerify = true` also builds every job on the page and
   records whether the two results are identical (QA and the regression
   harness only). */
var SL_CUT_WORKER_FILES = [
  ['sl-cutpath.js', 'SLCutPath'], ['sl-pathclean.js', 'SLPathClean'],
  ['sl-structure.js', 'SLStructure'], ['sl-structbuild.js', 'SLStructBuild'],
  ['sl-lockrefit.js', 'SLLockRefit'], ['sl-junctions.js', 'SLJunctions'],
  ['sl-relations.js', 'SLRelations'], ['sl-canonical.js', 'SLCanonical'],
  ['sl-smoothturn.js', 'SLSmoothTurn'], ['sl-shapedesc.js', 'SLShapeDesc'],
  ['sl-cutguard.js', 'SLCutGuard']
];
var slCutWorker = { state: 'idle', blobUrl: null, prep: null, w: null, job: null, seq: 0, log: [] };

/* the worker's program: this page's engine files, in page order, then the build */
function slCutWorkerSource(buildFn) {
  if (typeof window === 'undefined' || typeof Worker === 'undefined' || typeof Blob === 'undefined' ||
      typeof fetch !== 'function' || !window.URL || !URL.createObjectURL) {
    return Promise.reject(new Error('worker_unsupported'));
  }
  var scripts = Array.prototype.slice.call(document.querySelectorAll('script[src]'));
  var picks = [];
  for (var i = 0; i < SL_CUT_WORKER_FILES.length; i++) {
    var name = SL_CUT_WORKER_FILES[i][0], glob = SL_CUT_WORKER_FILES[i][1];
    if (!window[glob]) continue;                       /* absent on the page: absent in the worker */
    var re = new RegExp('/' + name.replace('.', '\\.') + '(\\?|$)');
    var el = scripts.filter(function (s) { return re.test(s.src); })[0];
    if (!el) return Promise.reject(new Error('worker_no_script:' + name));
    picks.push({ el: el, src: el.src });
  }
  picks.sort(function (a, b) {
    return (a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING) ? -1 : 1;
  });
  return Promise.all(picks.map(function (p) {
    return fetch(p.src, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('worker_fetch_' + r.status);
      return r.text();
    });
  })).then(function (texts) {
    return 'self.window = self;\n' + texts.join('\n;\n') +
      '\n;var SL_SIZE_TOL_IN = ' + JSON.stringify(SL_SIZE_TOL_IN) + ';' +
      '\nvar SL_TRACE_TOL_IN = ' + JSON.stringify(SL_TRACE_TOL_IN) + ';\n' +
      slContourTolUnits.toString() +
      /* buildDoubleDip calls it for every geometry review; without it a review in the worker threw, the page
         fell back to its own thread and the worker stayed broken for the visit (28 Oct, -49) */
      '\n' + slTransformGeometryReview.toString() +
      '\nvar slBuildCut = ' + buildFn.toString() + ';\n' +
      'self.onmessage = function (e) {\n' +
      '  var m = e.data, sil = { width: m.w, height: m.h, ctx: { getImageData: function () {\n' +
      '    return { data: m.data, width: m.w, height: m.h }; } } };\n' +
      '  var cut;\n' +
      '  try { cut = slBuildCut(m.plan, sil, m.geom, m.opts); }\n' +
      '  catch (err) { self.postMessage({ id: m.id, error: "threw:" + (err && err.message) }); return; }\n' +
      '  try { self.postMessage({ id: m.id, cut: cut }); }\n' +
      '  catch (err2) { self.postMessage({ id: m.id, error: "clone:" + (err2 && err2.message) }); }\n' +
      '};\n';
  });
}

function slCutWorkerSpawn() {
  var w = new Worker(slCutWorker.blobUrl);
  w.onmessage = function (e) {
    var j = slCutWorker.job, m = e.data || {};
    if (!j || m.id !== j.id) return;
    slCutWorker.job = null;
    if (m.error) { slCutWorkerBreak(m.error); j.reject({ failed: m.error }); }
    else j.resolve(m.cut);
  };
  w.onerror = function (ev) {
    var j = slCutWorker.job;
    slCutWorker.job = null;
    slCutWorkerBreak('error:' + ((ev && ev.message) || 'worker'));
    if (j) j.reject({ failed: 'worker_error' });
    if (ev && ev.preventDefault) ev.preventDefault();
  };
  return w;
}

function slCutWorkerBreak(why) {
  slCutWorker.state = 'broken';
  slCutWorker.log.push({ at: Date.now(), broken: String(why) });
  try { if (slCutWorker.w) slCutWorker.w.terminate(); } catch (e) {}
  slCutWorker.w = null;
}

/* Run one build in the worker. Resolves with the cut; rejects with
   { superseded } (a newer preview build replaced it), { busy } (a preview
   build while Add to Cart is building), { unavailable } or { failed }. */
function slCutWorkerRun(buildFn, plan, silComposite, geom, opts, warm) {
  if (slCutWorker.state === 'broken') return Promise.reject({ unavailable: 'broken' });
  if (!slCutWorker.prep) {
    slCutWorker.state = 'preparing';
    slCutWorker.prep = slCutWorkerSource(buildFn).then(function (src) {
      slCutWorker.blobUrl = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }));
      slCutWorker.state = 'ready';
    }, function (e) {
      slCutWorker.state = 'broken';
      slCutWorker.log.push({ at: Date.now(), broken: String((e && e.message) || e) });
      throw e;
    });
  }
  return slCutWorker.prep.then(function () {
    if (slCutWorker.state !== 'ready') throw { unavailable: slCutWorker.state };
    var cur = slCutWorker.job;
    if (cur) {
      if (warm && !cur.warm) throw { busy: true };
      if (cur.warm) {
        /* a stale preview build: stop it rather than finish it */
        slCutWorker.job = null;
        try { slCutWorker.w.terminate(); } catch (e) {}
        slCutWorker.w = null;
        cur.reject({ superseded: true });
      } else {
        /* Add to Cart while Add to Cart builds: run after it */
        return cur.done.then(function () {}, function () {}).then(function () {
          return slCutWorkerRun(buildFn, plan, silComposite, geom, opts, warm);
        });
      }
    }
    var mw = silComposite.width | 0, mh = silComposite.height | 0;
    var px = silComposite.ctx.getImageData(0, 0, mw, mh).data;
    if (!slCutWorker.w) slCutWorker.w = slCutWorkerSpawn();
    var job = { id: ++slCutWorker.seq, warm: !!warm, t0: Date.now() };
    job.done = new Promise(function (res, rej) { job.resolve = res; job.reject = rej; });
    slCutWorker.job = job;
    slCutWorker.w.postMessage({ id: job.id, plan: plan, geom: geom, opts: opts, w: mw, h: mh, data: px },
                              [px.buffer]);
    return job.done.then(function (cut) {
      slCutWorker.log.push({ at: Date.now(), id: job.id, warm: job.warm, ms: Date.now() - job.t0 });
      if (slCutWorker.log.length > 50) slCutWorker.log.splice(0, slCutWorker.log.length - 50);
      return cut;
    });
  });
}

/* ============================================================================
   A CAUGHT ERROR THAT NOBODY CAN SEE IS A CAUGHT ERROR THAT NOBODY CAN FIX.

   The export's two `.catch` handlers exist so that no failure inside the
   double dip can cost the sale — that part is right and is unchanged. What was
   wrong was that they discarded the error, so a ReferenceError in the handler
   and a legitimate "this shape has no cut path" produced the same silence.

   This records, never throws, and never changes a verdict. It writes to the
   console and to a diagnostic global; it deliberately does NOT touch the fit
   report that gets serialised onto the order, because a diagnostic must not
   alter the record it is diagnosing.
   ============================================================================ */
function slNoteHandlerError(where, err, report) {
  try {
    var msg = (err && (err.stack || err.message)) || String(err);
    console.error('[sl-cutpath] ' + where + ' handler threw — double dip discarded:', err);
    if (typeof window !== 'undefined') {
      window.__slContourFitError = {
        where: where, message: (err && err.message) || String(err),
        stack: (err && err.stack) || null, at: Date.now(),
        toleranceUnits: report && report.toleranceUnits,
        canvasUnits: report && report.canvasUnits
      };
    }
    if (typeof window !== 'undefined' && window.slTelemetry && window.slTelemetry.track) {
      window.slTelemetry.track('cutpath_handler_error', { where: where, message: msg.slice(0, 300) });
    }
  } catch (x) { /* a diagnostic may never become the failure */ }
}


const BSIZES = [{
  id: 'thin',
  label: 'Thin',
  desc: 'Slim',
  in: 0.03,
  vfrac: 0.014,
  tpx: 1.3,
  img: SLA + 'border-thin.png'
}, {
  id: 'standard',
  label: 'Standard',
  desc: 'Popular',
  in: 0.05,
  vfrac: 0.022,
  tpx: 2.4,
  pop: true,
  img: SLA + 'border-standard.png'
}, {
  id: 'thick',
  label: 'Thick',
  desc: 'Bold',
  in: 0.10,
  vfrac: 0.035,
  tpx: 4.2,
  img: SLA + 'border-thick.png'
}];
const SURFACES = [{
  id: 'light',
  label: 'White',
  dot: '#ffffff'
}, {
  id: 'dark',
  label: 'Dark',
  dot: '#1a1f2a'
}, {
  id: 'laptop',
  label: 'Laptop',
  dot: '#9aa4b2'
}, {
  id: 'bottle',
  label: 'Bottle',
  dot: '#8fb3d9'
}, {
  id: 'window',
  label: 'Car window',
  dot: '#39485c'
}];
/* Cart Graphics previews: the two inspection backgrounds, and the cart. */
const CG_SURFACES = [SURFACES[0], SURFACES[1], {
  id: 'cart',
  label: 'Cart',
  dot: '#15181d'
}];
/* Five ways to say three shape ids. Rectangle and Oval are Square and Circle
   with the sides left free, which is what unlocking already does for a sticker. */
const CG_SHAPES = [{
  id: 'rectangle', label: 'Rectangle', big: '▭', icon: "M3 7h18v10H3z"
}, {
  id: 'square', label: 'Square', big: '▢', icon: "M5 5h14v14H5z"
}, {
  id: 'circle', label: 'Circle', big: '●', icon: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18"
}, {
  id: 'oval', label: 'Oval', big: '⬭', icon: "M12 6c5 0 9 2.7 9 6s-4 6-9 6-9-2.7-9-6 4-6 9-6z"
}, {
  id: 'custom', label: 'Die Cut', big: '✦', icon: "M12 3l3 5 5.5.8-4 4 1 5.7L12 21l-5.5-2.5 1-5.7-4-4L9 8z"
}];
function Ic({
  d,
  w
}) {
  return /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: w || 2,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: d
  }));
}
// Mobile Trust Banner copy (D-046) — rotating reassurance shown between the sticker preview and the Glass
// Dock. Icons are monochrome stroke paths rendered through <Ic> (same icon system as the rest of the builder).
// Copy is intentionally scoped to professional review / minor cleanup / print optimization — never promises
// unlimited design, full redraws, or "we fix everything".
const TRUST_MSGS = [
  { d: "M12 3l7 2.6v5.2c0 4.3-3 7.4-7 8.9-4-1.5-7-4.6-7-8.9V5.6zM9 11.8l2 2 4-4", title: "Free Artwork Review",
    body: "Every order is personally reviewed before printing. We’ll optimize your artwork for the best possible print quality and take care of common production adjustments whenever possible—at no extra charge." },
  { d: "M4 20l8.5-8.5M14 4l1.2 2.8L18 8l-2.8 1.2L14 12l-1.2-2.8L10 8l2.8-1.2z", title: "Print-Ready Guarantee",
    body: "No need to provide a perfect file. We’ll prepare your artwork for production before it goes to print." },
  { d: "M12 3.6l2.5 5.1 5.6.8-4.1 4 1 5.6L12 16.5 6.9 19.1l1-5.6-4.1-4 5.6-.8z", title: "Premium Materials",
    body: "Printed using premium vinyl and protective laminate for long-lasting durability." },
  { d: "M3 6.5h10.5v8H3zM13.5 9.5H17l3 3.2v2.8h-6.5zM7 18.5a1.7 1.7 0 1 0 0-3.4 1.7 1.7 0 0 0 0 3.4M17.5 18.5a1.7 1.7 0 1 0 0-3.4 1.7 1.7 0 0 0 0 3.4", title: "Fast Production",
    body: "Most custom sticker orders are produced quickly and shipped with care." },
  { d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M8.4 12l2.3 2.3L15.6 9.6", title: "We’re Here To Help",
    body: "Questions about your artwork? Our team reviews every order and will reach out if anything needs your attention before production." }
];
// Build a solid outline around a transparent PNG using stacked directional drop-shadows.
// Used to preview each border thickness on the customer's OWN artwork.
/* The phone's rotating reassurance, for the product on the page. The sticker
   list promises a protective laminate and artwork clean-up; this product is
   unlaminated and is ordered with print-ready artwork, so it gets its own. */
const TRUST_LIST = !CG ? TRUST_MSGS : [
  { d: "M4 7h16v10H4zM8 7V5h8v2", title: "Graphic Only",
    body: "You’re ordering the printed vinyl graphic. The cart, sign board and installation aren’t included." },
  { d: "M12 3l7 2.6v5.2c0 4.3-3 7.4-7 8.9-4-1.5-7-4.6-7-8.9V5.6zM9 11.8l2 2 4-4", title: "Print-Ready Artwork",
    body: "Upload artwork that’s ready to print. We review every file before printing and will contact you if anything needs attention." },
  { d: "M3 6.5h10.5v8H3zM13.5 9.5H17l3 3.2v2.8h-6.5zM7 18.5a1.7 1.7 0 1 0 0-3.4 1.7 1.7 0 0 0 0 3.4M17.5 18.5a1.7 1.7 0 1 0 0-3.4 1.7 1.7 0 0 0 0 3.4", title: "Shipping Included",
    body: "Standard shipping is included in the price. Tax is added at checkout." },
  TRUST_MSGS[4]
];
function outlineFilter(px, color) {
  let f = '';
  const steps = 16;
  for (let k = 0; k < steps; k++) {
    const a = k / steps * Math.PI * 2;
    const dx = +(Math.cos(a) * px).toFixed(2),
      dy = +(Math.sin(a) * px).toFixed(2);
    f += `drop-shadow(${dx}px ${dy}px 0 ${color}) `;
  }
  return f.trim();
}
function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;
  const k = n => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = n => {
    const c = l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return Math.round(255 * c).toString(16).padStart(2, '0');
  };
  return '#' + f(0) + f(8) + f(4);
}
function hueOf(hex) {
  const m = (hex || '').replace('#', '');
  if (m.length < 6) return 210;
  const r = parseInt(m.slice(0, 2), 16) / 255,
    g = parseInt(m.slice(2, 4), 16) / 255,
    b = parseInt(m.slice(4, 6), 16) / 255;
  const mx = Math.max(r, g, b),
    mn = Math.min(r, g, b),
    d = mx - mn;
  if (!d) return 210;
  let h;
  if (mx === r) h = (g - b) / d % 6;else if (mx === g) h = (b - r) / d + 2;else h = (r - g) / d + 4;
  h = Math.round(h * 60);
  return (h + 360) % 360;
}
function PremiumBuilder() {
  // ---- product state (mirrors calm-builder) ----
  const [art, setArt] = useState(null); // {url,name}
  const [artRes, setArtRes] = useState(null); // {w,h} original resolution (rendering)
  const [uploadMeta, setUploadMeta] = useState(null); // SLUpload result (metadata + warnings); DPI is engine-owned
  /* The Home-page file could not be carried across the navigation (D-098).
     An empty builder that LOOKS like a fresh start is the failure this whole
     change exists to remove, so the drop zone says what happened instead. */
  /* WHICH failure, not just "a" failure (D-100). Blaming transfer when the
     format was unsupported — or format when the file never arrived — sends the
     customer to fix the wrong thing.
     null | 'transfer' | 'rebuild' | 'decode' | 'format' */
  const [handoffError, setHandoffError] = useState(null);
  /* AN UPLOAD IS NEVER SILENT (WG2, Ryan 24 Oct 2026). On live the validator's refusal was stored
     and never shown, so a refused file left the empty preview with no word. uploadBusy shows
     "Opening your artwork…" from selection until the picture is on the stage; uploadFail holds
     the customer-facing reason for any failure, shown on the phone and on desktop. */
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadFail, setUploadFail] = useState(null);
  const UPLOAD_FAIL = {
    rejected: 'We couldn’t use this file. Try saving it as a PNG or JPG and upload it again.',
    oversize: 'This image is too large for this device to open. Export it at a smaller size and upload it again.',
    unreadable: 'We couldn’t read that photo from your device. Please choose it again.'
  };
  const uploadFailed = msg => { setUploadBusy(false); setUploadFail(msg); };
  /* READING AN UPLOAD'S BYTES CANNOT HANG SILENTLY (P0, 27 Oct 2026). Ryan's
     real iPhone: `file_selected`, then nothing until "upload stalled" at 25 s,
     and reading the bytes is the only wait in between. The file's own
     `arrayBuffer()` (passed in as `primary`) gets 6 s before a FileReader read
     of the same file is tried alongside it, and the read as a whole 20 s,
     inside the existing 25 s budget (not a longer timer). This only READS
     bytes: nothing is decoded before readFile's verdict. Every step names
     itself, so the next device failure says which one. */
  const slReadUploadBytes = (f, primary, stage) => new Promise((resolve, reject) => {
    const t0 = Date.now();
    let settled = false, viaReaderStarted = false;
    const finish = (fn, arg) => { if (settled) return; settled = true; clearTimeout(slow); clearTimeout(limit); fn(arg); };
    const viaReader = why => {
      if (viaReaderStarted) return;
      viaReaderStarted = true;
      stage('bytes_read_retry', { why: why, ms: Date.now() - t0 });
      try {
        const r = new FileReader();
        r.onload = () => finish(resolve, r.result);
        r.onerror = () => finish(reject, new Error('filereader: ' + ((r.error && r.error.name) || 'error')));
        r.readAsArrayBuffer(f);
      } catch (e) { finish(reject, e); }
    };
    const slow = setTimeout(() => viaReader('slow'), 6000);
    const limit = setTimeout(() => finish(reject, new Error('read_timeout')), 20000);
    try { primary().then(buf => finish(resolve, buf), e => viaReader('rejected: ' + ((e && e.name) || e))); }
    catch (e) { viaReader('threw: ' + ((e && e.name) || e)); }
  }).then(buf => { stage('bytes_read', { bytes: buf.byteLength }); return buf; },
          e => { const out = e instanceof Error ? e : new Error(String(e)); out.slReadFailed = true; throw out; });
  /* ARTWORK QUALITY CONFIRMATION (D-099).
     `qualityAckFor` holds the artwork the customer has already answered for —
     not a boolean. Keyed that way so a REPLACEMENT file is judged afresh while
     the current one never re-interrupts: the modal is a question about one
     file, so the answer belongs to that file. */
  const [qualityOpen, setQualityOpen] = useState(false);
  const [qualityAckFor, setQualityAckFor] = useState(null);
  /* A one-shot highlight on the inline card, set when Add to Cart is pressed
     before the question has been answered. It replaces what would otherwise be
     an error: nothing is wrong, there is simply one thing left to decide. */
  const [qualityNudge, setQualityNudge] = useState(false);
  /* MOBILE DISCLOSURE. Two things were burying the product on a phone: the QA
     readout, which is a developer instrument sitting on top of the sticker,
     and the source advisory, which said everything it had to say at once. Both
     keep all their content; both start closed on a phone and open on a tap.
     Neither exists for a customer who has not asked for qadebug, and the
     advisory's ACTIONS are never behind a disclosure — only its explanation. */
  const [qaOpen, setQaOpen] = useState(false);
  /* THE LAST ADD-TO-CART DECISION, kept so the phone can show it.
     A refusal that only exists in a console line cannot be read by the person
     holding the device, and this failure is intermittent on real hardware -
     which means the ONE run that fails is the only run whose state matters.
     Captured before the guard, updated by it, rendered in the QA panel. */
  const [qaCart, setQaCart] = useState(null);
  const [advWhy, setAdvWhy] = useState(false);
  /* The quality card's long copy, folded away by default. Same pattern as
     `advWhy` above: an interface fact, not a production one. */
  const [qMore, setQMore] = useState(false);
  /* THE SOURCE-FILE ADVISORY (D-103) — a different question, kept apart.
     "Is this the kind of file a logo prints cleanest from?" is not "does this
     raster have enough pixels for the ordered size?", so it gets its own
     acknowledgement. Answering one must never answer the other: a customer who
     accepts a JPG at 2in must still be warned when they enlarge it to 10in. */
  const [sourceAckFor, setSourceAckFor] = useState(null);
  const [sourceOpen, setSourceOpen] = useState(false);
  const artBytesRef = useRef(null); // original file bytes → SLUpload (parsing lives in the engine)
  /* The customer's UNTOUCHED file, kept only when the builder had to work on a
     reduced copy (an image over the pixel cap, see reduceOversize). The order's
     _artwork_original / _artwork_master and the hand-over to another builder
     use this; everything that decodes or measures uses artBytesRef. */
  const artOriginalRef = useRef(null);
  /* That original's pixel size ({ w, h }), set and cleared with artOriginalRef,
     so a print file built from the reduced copy can say so instead of calling
     the copy the original. */
  const artOriginalPxRef = useRef(null);
  /* P-50 PROTOTYPE: AN EXTERIOR GREY HALO, CLEANED. AI sticker art often has a
     thin grey halo just outside its white border (assets/sl-halo-clean.js). When
     it is confidently found, the builder works on the cleaned PNG - preview,
     MOTO and the production file all read it - and the customer's own file is
     kept byte-exact in artOriginalRef for the order, as for an oversized one.
     { on, changed, cleaned: File, original: File, cleanedBytes, originalBytes, px, review }
     or null; `on` false means the customer chose Restore original. */
  const [halo, setHalo] = useState(null);
  /* CLEANUP REVIEW (Ryan, 25 Oct 2026). When the cleaner was sure of most of a grey rim but not of a
     seam running on into the design, it cleans only what it was sure of and flags the rest for a
     person ({ reason, at, box, px }, sl-halo-clean.js reviewOf). The flag goes on the cart line as
     `_art_review` and in the design config as `artReview`, and HQ holds Approve cut for that design
     until someone reviews it. It belongs to the CLEANED picture: Restore original drops it, Clean
     edge brings it back. A reopened saved design reads it from its own cleaned PNG (resumedReview),
     because the saved-design service keeps no internal fields. */
  const [resumedReview, setResumedReview] = useState(null);
  const artReview = halo ? (halo.on ? (halo.review || null) : null) : resumedReview;
  const artReviewRef = useRef(null);
  artReviewRef.current = artReview;
  /* While the upload is checked for a halo: 'check', then 'clean' once one is
     found and the cleaned file is being written; false otherwise. The work
     runs in a worker, so the processing panel keeps moving while it does. */
  const [haloBusy, setHaloBusy] = useState(false);
  /* The working image (the <=1400 px picture MOTO traces and the production
     file embeds) for a file reduceOversize handled, drawn ONCE straight from
     the decoded ORIGINAL with the same steps beginRasterDecode uses, so the
     cut line is what it would be if the original could be decoded here.
     Read and cleared by beginRasterDecode for that one file. */
  const oneStepRef = useRef(null);
  /* THE DESIGN ID THIS BUILDER SESSION SAVES UNDER.
     Minted once, on the first save, and kept for the life of the session — so
     a customer who saves, tweaks the border and saves again UPDATES their
     design instead of collecting three near-identical rows. A design reopened
     from My Designs adopts its own id here for the same reason. */
  const saveIdRef = useRef(null);
  /* The name a reopened design was SAVED under. Separate from the Design ID
     and from the filename: identity never derives from either, and a name the
     customer chose must survive a reopen. */
  const savedNameRef = useRef(null);
  /* THE LATEST ARTWORK, READABLE WITHOUT A RENDER.
     The order payload is built inside an async click handler, so reading `art`
     there reads whatever the closure captured when that handler was created.
     This ref is written on every render and is therefore always the committed
     value at the moment the customer taps Add to Cart — which is what makes the
     submit boundary a reconciliation point rather than a snapshot. */
  const artRef = useRef(null);
  artRef.current = art;
  /* THE SAME BYTES, AS STATE (D-101).
     A ref write triggers no render, and a `useMemo` only re-evaluates DURING a
     render — so listing `artBytesRef.current` as a dependency made the quality
     analysis depend on some OTHER state change happening to follow the async
     `arrayBuffer()` that fills it. When nothing did, the memo kept the value it
     computed while the bytes were still null: no measurement, no warning, and
     no later recovery.

     The ref is untouched, because the durable-artwork uploader reads it. This
     mirror exists solely so the measurement is driven by a real render. */
  const [artBytes, setArtBytes] = useState(null);
  const [drag, setDrag] = useState(false);
  const [shape, setShape] = useState('custom');
  const [corners, setCorners] = useState(CG ? 'sharp' : 'round');
  const [size, setSize] = useState(3);
  const [sizeMode, setSizeMode] = useState(CG ? 'custom' : 'preset');
  const [customW, setCustomW] = useState(CG ? String(CG_ENTRY.w) : '3');
  const [customH, setCustomH] = useState(CG ? String(CG_ENTRY.h) : '2');
  const [lockAR, setLockAR] = useState(!CG); // custom size: keep die-cut proportional to the art
  const [artAR, setArtAR] = useState(1);
  /* The artwork's OPAQUE bounding box, in baseArt pixels — the sticker's real
     extent, which the canvas is not. Measured once per upload; `null` until it
     is, and every consumer falls back to today's behaviour meanwhile. */
  const [artSil, setArtSil] = useState(null);
  /* WHICH PICTURE `artSil` WAS MEASURED FROM.
     The silhouette decides the ordered size, and it is re-measured whenever the
     effective artwork changes — an image decode and a full-resolution alpha
     scan, both asynchronous. Until that finishes, `artSil` describes the
     PREVIOUS picture, and the ordered size is a value for artwork that is no
     longer the one that would ship. Recording the source it belongs to is what
     lets the order gate tell "measured" from "not measured yet"; `artSil`
     alone cannot, because a legitimate result is `null`. */
  const [silFor, setSilFor] = useState(null);
  const [qty, setQty] = useState(CG ? CG_ENTRY.qty : PROMO ? PROMO.packQty : 100);
  const [qtyMode, setQtyMode] = useState('preset');
  const [customQty, setCustomQty] = useState('150');
  const [material, setMaterial] = useState('vinyl');
  const [laminate, setLaminate] = useState(CG ? CG_ENTRY.finish : 'matte');
  const [border, setBorder] = useState('none');
  const [borderColor, setBorderColor] = useState('#2E86E3');
  const [bsize, setBsize] = useState('standard');
  const [surface, setSurface] = useState(CG && CG_ENTRY.landing ? 'cart' : 'light');
  /* CART GRAPHICS ONLY. The front panel is a PREVIEW measurement and the
     placement is where the customer dragged the graphic on that panel. Neither
     is an input to the ordered size, the price, the artwork transform or the
     production file — they are recorded on the order as reference and nothing
     reads them back. Text is kept as typed; the last VALID panel is what draws. */
  const [cgPanelW, setCgPanelW] = useState(CG ? String(CG.PANEL.defaultWidthIn) : '');
  const [cgPanelH, setCgPanelH] = useState(CG ? String(CG.PANEL.defaultHeightIn) : '');
  const [cgPlace, setCgPlace] = useState(null);       // null = centred
  const [cgShapeNote, setCgShapeNote] = useState('');
  const cgPanelRef = useRef(CG ? { w: CG.PANEL.defaultWidthIn, h: CG.PANEL.defaultHeightIn } : { w: 0, h: 0 });
  const cgPanelWv = CG ? CG.validatePanelDimension('width', cgPanelW) : { ok: true };
  const cgPanelHv = CG ? CG.validatePanelDimension('height', cgPanelH) : { ok: true };
  if (CG && cgPanelWv.ok) cgPanelRef.current.w = cgPanelWv.value;
  if (CG && cgPanelHv.ok) cgPanelRef.current.h = cgPanelHv.value;
  const cgPanel = cgPanelRef.current;
  const [openOpt, setOpenOpt] = useState(CG ? 'size' : null);
  const [workflow, setWorkflow] = useState(null); // null | 'photo' | 'ready' | 'shaped' | 'production' — chosen after upload
  const [saved, setSaved] = useState(false);
  const [brandMenu, setBrandMenu] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  /* Saving now crosses the network, so it has the three states a network call
     has. `saveBusy` also guards the button: a double-click must not become two
     uploads of the same artwork. */
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [tourI, setTourI] = useState(-1); // guided mini tour step (-1 = off)
  const [tourSteps, setTourSteps] = useState([]);
  const [tourMode, setTourMode] = useState('builder'); // 'builder' | 'choose'
  // Context-aware guide: the steps depend on which screen you're on when you launch it.
  const startTour = () => {
    let steps, mode;
    if (document.querySelector('.pb-choose')) {
      mode = 'choose';
      // Post-upload workflow chooser is showing
      steps = [{
        sel: '.pb-choose-thumb',
        title: 'Your upload',
        body: 'This is the artwork you just added. Looks good? Let’s decide how to make it.',
        place: 'auto'
      }, {
        sel: '.pb-choose-grid',
        title: 'Choose how to make it',
        body: 'Cut it out for a clean die-cut, or set it inside a shape. We’ll set up the rest for you.',
        place: 'auto'
      }];
    } else {
      mode = 'builder';
      steps = [{
        sel: '.pb-stage',
        title: 'Upload your artwork',
        body: 'Drag & drop a file or tap browse: PNG, JPG, SVG or PDF. Go ahead and add yours now; the guide picks back up after you choose.',
        place: 'auto'
      }, {
        sel: '.pb-col-left .pb-optlist',
        title: 'Set it up',
        body: 'Pick your size, shape, border, laminate and quantity here. Each choice snaps closed once you tap it.',
        place: 'right'
      }, {
        sel: '.pb-col-right',
        title: 'Smart tools',
        body: 'Remove the background for a clean die-cut, and check the quality and finished-size cards before you order.',
        place: 'left'
      }, {
        sel: '.pb-bar',
        title: 'Price & cart',
        body: 'Your live total updates as you go. When it looks right, add it to your cart. A free digital proof is one tick away.',
        place: 'top'
      }];
    }
    setTourMode(mode);
    setTourSteps(steps);
    setTourI(0);
  };
  const [pendingNav, setPendingNav] = useState(null); // destination URL when guarding an unsaved design
  const [saveName, setSaveName] = useState('');
  const [saveFolder, setSaveFolder] = useState(null);
  /* THE SAVING STATE. Set on the TAP, before any expensive work begins.
     ---------------------------------------------------------------------
     Add to Cart is not a cheap button: it renders a proof to a canvas, builds
     the production SVG, and POSTs ~560 KB of durable assets in one multipart
     request. Measured from real order #1017 — original 66 KB, preview 89 KB,
     config 95 KB, production artwork 312 KB — which is roughly three seconds
     of upload alone on weak LTE, before any of the main-thread work.

     For all of that the button previously did nothing visible, so a customer
     could reasonably believe the tap missed. `saveStage` is a plain string:
     null when idle, the truthful current stage otherwise. It is deliberately
     NOT a percentage — we have no measurable progress to report, and a fake
     bar is a lie that gets found out on a slow connection. */
  /* THE CUSTOMER NEVER READS OUR PLUMBING.
     ---------------------------------------------------------------------
     "Uploading artwork", "rendering the proof", "generating the SVG" are our
     words for our problems. A customer buying a sticker should be told what is
     happening to their STICKER, in a voice that sounds like the shop. The
     stage names in the console keep the technical detail — that is where it is
     useful — and the two vocabularies are deliberately kept apart so neither
     drifts into the other. */
  const SAVE_STAGE = {
    start:   { title: CG ? 'Saving your graphic…' : 'Saving your sticker…',    sub: 'Locking in your design' },
    prepare: { title: 'Getting it print-ready…', sub: 'Just a moment' },
    upload:  { title: 'Getting it print-ready…', sub: 'Just a moment' },
    done:    { title: 'Added!',                  sub: 'Opening your cart…' }
  };
  const [saveStage, setSaveStage] = useState(null);
  const [ordered, setOrdered] = useState(false);
  const [decision, setDecision] = useState(false); // → "your sticker is ready" decision screen
  const [sheetOpen, setSheetOpen] = useState(false); // → sticker sheet workspace
  const [sheetIntent, setSheetIntent] = useState(false); // arrived wanting a sticker sheet (Home card / My Lab)
  const [sheetArt, setSheetArt] = useState(null); // flattened WYSIWYG sticker handed to the sheet
  const sheetPickRef = useRef(false); // sheetIntent was raised by the chooser's Sticker Sheet card
  // Sticker Sheets — ONE shared state for desktop overlay AND mobile Sheet panel (D-050). Sheet size
  // comes from the shared SLSheets catalog; number of sheets defaults to 10 (clears the 10-sticker
  // pricing minimum for any config). Both platforms read/write these same two values + the shared engines.
  const [sheetSizeId, setSheetSizeId] = useState((typeof window !== 'undefined' && window.SLSheets && window.SLSheets.DEFAULT_SHEET_SIZE_ID) || '8.5x10');
  const [numSheets, setNumSheets] = useState(10);
  const [sheetCutTrue, setSheetCutTrue] = useState(null);   // { key, wIn, hIn }: the measured kiss cut (D-199)
  /* ONE way to change the sheet size (D-198), for desktop and mobile alike. "Single sticker"
     sells individually backed stickers, priced from 10 (SLSheets.SINGLE_MIN_QTY), so choosing it
     lifts a smaller count to that floor instead of leaving an unpriceable order. */
  const slSingleSheet = id => !!(typeof window !== 'undefined' && window.SLSheets && window.SLSheets.isSingle && window.SLSheets.isSingle(id));
  /* The fewest sheets for a size (SLSheets.minSheets: 10 for 4 x 6, Ryan 24 Oct 2026), and,
     once the layout is known, enough sheets for the 10-sticker pricing minimum. */
  const slSheetMin = (id, perSheet) => {
    const S = typeof window !== 'undefined' && window.SLSheets;
    if (S && S.minSheets) return S.minSheets(id, perSheet);
    return slSingleSheet(id) ? ((S && S.SINGLE_MIN_QTY) || 10) : 1;
  };
  const chooseSheetSize = id => {
    setSheetSizeId(id);
    const min = slSheetMin(id);
    setNumSheets(n => Math.max(min, parseInt(n, 10) || min));
  };
  // ---- live cart item count for the header badge ----
  const [cartN, setCartN] = useState(0);
  useEffect(() => {
    const read = () => {
      let n = 0;
      try {
        const c = JSON.parse(localStorage.getItem('stickr_cart') || '[]');
        n = Array.isArray(c) ? c.length : 0;
      } catch (e) {}
      setCartN(n);
    };
    read();
    window.addEventListener('storage', read);
    window.addEventListener('focus', read);
    return () => {
      window.removeEventListener('storage', read);
      window.removeEventListener('focus', read);
    };
  }, []);
  // ---- art transform ----
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({
    x: 0,
    y: 0
  });
  const [rot, setRot] = useState(0);
  const [flipX, setFlipX] = useState(false);
  const [faceColor, setFaceColor] = useState('white');
  const [faceCustom, setFaceCustom] = useState('#2E86E3');
  const [colorOpen, setColorOpen] = useState(false);
  // Die-cut border outline (the baked cut line) — this renders the sticker's border,
  // NOT background removal (customer-facing background removal was removed for Launch V1, D-034).
  const [outlinedUrl, setOutlinedUrl] = useState(null);
  /* THE OUTLINE REBUILD, AS A STATE THE INTERFACE CAN SEE.
     This is the slow stage -- measured on the live builder it holds the main
     thread for seconds -- and it was the one stage with no flag, so the
     processing panel went dark for exactly the work worth reporting. It is set
     before the bake yields its paint and cleared wherever the bake ends. */
  const [bakeBusy, setBakeBusy] = useState(false);
  /* D-176: a bake that has published (so a production cut can follow it), the
     arrival of that cut, and whether the customer must be told it is drawn by
     hand. The bake's own layers are kept so the arriving cut can be painted
     without baking again. */
  const [bakeStamp, setBakeStamp] = useState(0);
  const [prodTick, setProdTick] = useState(0);
  const [cutReview, setCutReview] = useState(null);
  const bakeLayersRef = useRef(null);
  /* ── REMOVE BACKGROUND — A DERIVED VIEW, NEVER A REPLACEMENT ───────────
     The upload is never altered. `art` keeps the customer's file exactly as it
     arrived — it is what `artBytes`, the DPI/quality engine and the order's
     original asset all read — and background removal produces a SEPARATE
     picture that the builder may show INSTEAD. Turning the toggle off is
     therefore not an undo of anything; it is pointing at the original again,
     which is why it is instant and lossless however many times it is flipped.

     `bgUrl` is computed once per upload and cached. Re-deriving it on every
     toggle would put the artwork through another encode each time, and an
     image that degrades a little every time the customer changes their mind is
     the kind of defect nobody reports and everybody notices. */
  const [bgOn, setBgOn] = useState(false);
  const [bgUrl, setBgUrl] = useState(null);        // the derived PNG (blob URL), cached
  const [bgInfo, setBgInfo] = useState(null);      // what the engine said about the upload
  const [bgMeta, setBgMeta] = useState(null);      // method + how much came away
  /* idle · checking · working · ready · failed · none ('none' = not offered) */
  const [bgState, setBgState] = useState('idle');
  const [bgError, setBgError] = useState('');
  /* The padding the current `outlinedUrl` was built with. Set and cleared with
     it, so the scale and the picture can never describe different frames. */
  const [outlineFrame, setOutlineFrame] = useState(null);
  /* The authoritative pre-border silhouette, published by the outline baker
     and consumed once at production time. See the baker for its lifetime. */
  const silhouetteRef = useRef(null);
  /* every published silhouette gets a number, so "the cut was built from THIS
     silhouette" is an identity test rather than a guess (D-176) */
  const silSeqRef = useRef(0);
  const fileRef = useRef(null);
  const dropRef = useRef(null);
  const faceRef = useRef(null);
  const canvasRef = useRef(null);
  /* THE STAGE, MEASURED. The preview box used to be derived from the VIEWPORT
     WIDTH alone (`min(440, innerWidth - 24)`), which knows nothing about how
     much height the stage actually has. On a phone the stage came out 390x205
     and a square sticker was sized 179 wide by a rule that had never seen the
     205. It fitted by luck of aspect; a taller sticker would not have.
     Measured instead, and re-measured when the layout moves. */
  const [stageBox, setStageBox] = useState(null);
  /* The contour-fit diagnostics from the last production render: which pass
     landed it, what the correction was, and the residual on every side. Held
     in a ref rather than state because reading it must never re-render the
     builder mid-export. */
  const cfitReportRef = useRef(null);
  /* WHICH GEOMETRY DREW THE VISIBLE PERIMETER, for the QA readout. Written by
     the outline bake, read only by diagnostics. */
  const contourSrcRef = useRef(null);
  const colorRef = useRef(null);
  const dragging = useRef(false);
  const panStopRef = useRef(null); // teardown for an in-flight one-finger pan, so a starting pinch can end it
  const movedRef = useRef(false); // did the current stage interaction drag/pinch? (so a real gesture doesn't close the panel)
  const lockRatioRef = useRef(1); // frozen width/height ratio used while proportions are locked (keeps stepping drift-free)

  // ---- pricing ----
  /* A cart-graphic quantity is a positive whole number, validated rather than
     clamped. The sticker expression below is untouched: Cart Graphics keeps
     `qtyMode` on 'preset' and holds its last VALID quantity in `qty`, so
     `effQty` is simply that. What the customer is typing lives in
     `cgQtyText`; while it is invalid the field says what is wrong, the last
     valid quantity keeps pricing the screen, and the order is refused. */
  const [cgQtyText, setCgQtyText] = useState(CG && CG.QUANTITY.presets.indexOf(CG_ENTRY.qty) < 0 ? String(CG_ENTRY.qty) : null);   // the custom field's text; null while a preset is chosen
  const cgQty = !CG ? null : CG.validateQuantity(cgQtyText == null ? qty : cgQtyText);
  const effQty = qtyMode === 'custom' ? Math.max(10, Math.min(1000, Math.round(parseFloat(customQty) || 0))) : qty;
  const shp = SHAPES.find(s => s.id === shape) || SHAPES[0];
  /* `shpR` is resolved further down, once `w`/`h` exist — the radius is a
     physical proportion of the sticker expressed in the preview's pixels, so
     it cannot be computed before the preview box is known (D-092). */
  const bsz = BSIZES.find(s => s.id === bsize) || BSIZES[1];
  const contour = shape === 'custom';
  /* THE UPLOAD, AND THE PICTURE THE STICKER IS MADE FROM.
     They are usually the same thing. When background removal is on they are
     not, and the difference is confined to this one line: everything
     downstream — the silhouette, the contour fit, MOTO, the white border, the
     CutContour and the production export — reads `shownArt`/`baseArt` and so
     follows the customer's choice without knowing there was one. `art` itself
     is untouched, so the original file still reaches the order. */
  const originalArt = art ? art.url : null;
  const shownArt = (bgOn && bgUrl) ? bgUrl : originalArt;
  const baseArt = shownArt;
  /* Nothing may be ordered while the derivative is in an indeterminate state:
     what would ship is not yet decided. */
  const bgBusy = bgState === 'checking' || bgState === 'working';
  /* ── AND THE PICTURE IS NOT THE ONLY THING THAT HAS TO CATCH UP ────────
     `bgBusy` covers the DERIVATION. It goes false the moment the derived PNG
     exists — `setBgUrl`, `setBgOn` and `setBgState('ready')` are one batch —
     but the ORDERED SIZE does not come from the PNG. It comes from `artSil`,
     the alpha bounding box, which a separate effect re-measures with an image
     decode and a full-resolution scan. Those start only once `shownArt` has
     already changed, so for their duration the button is enabled and
     `widthIn`/`heightIn` still describe the PREVIOUS picture.

     Measured on the DEV theme, 05-script-lettering, desktop: the order became
     committable at 357ms carrying 3 x 2in, and the size it would actually
     ship settled at 497ms to 3 x 2.4in — a 140ms window in which a customer
     can buy a size that is not the one they are about to see.

     That window is what 10H recorded as a layout-dependent default. It is not
     layout-dependent: the desktop reaches Add to Cart in fewer steps than the
     mobile reaches it through the Review sheet, so the two flows landed on
     opposite sides of the same race. With a Thick border on a 3in order the
     stale full-canvas silhouette gives 2 + (2/3)(0.105) = 2.07 -> 2.1in, which
     is exactly the value 10H saw on the desktop, against 2.4in settled.

     Only in contour mode: there `arForSize` reads `cfit.aspect`, which is
     solved from the silhouette. Every other shape sizes from `shp.ar`, a fixed
     number the silhouette cannot move, so nothing is gated that a silhouette
     does not decide. */
  const silPending = contour && !!shownArt && silFor !== shownArt;
  /* THE ONE PLACE "what would ship is not yet decided" IS DECIDED. */
  const orderBusy = bgBusy || silPending;

  /* ── WHAT THE LAB IS DOING, IF IT IS DOING ANYTHING ────────────────────
     One label, derived from the SAME state the rest of the builder already
     runs on -- no timers, no invented stages, and nothing that can say
     "Removing background" while the engine is idle. If a stage is not
     happening, it is not offered as a message. */
  const procLabel =
      haloBusy === 'clean'   ? 'Cleaning the edge'
    : haloBusy               ? 'Checking the edge'
    : bgState === 'checking' ? 'Checking artwork'
    : bgState === 'working'  ? 'Removing background'
    : bakeBusy               ? 'Building sticker'
    : silPending             ? 'Updating sticker'
    : null;

  /* ── SHOWN ONLY IF IT IS ACTUALLY SLOW ─────────────────────────────────
     Most of these finish in well under a tenth of a second, and a panel that
     appears and vanishes that fast reads as a glitch rather than as progress.
     So the work starts immediately and the PANEL waits: if the operation is
     still running after the threshold, it is genuinely slow enough to deserve
     saying so. The clicked control still acknowledges on the next paint -- its
     own busy state is not delayed by this and never was. */
  const [procShown, setProcShown] = useState(false);
  /* THE TIMER FOLLOWS *WHETHER* WORK IS HAPPENING, NEVER *WHICH* WORK.
     Keyed on the label, this never painted once. Remove Background moves
     through two stages -- the removal itself, then the sticker rebuild -- and
     the first finishes in about a tenth of a second, so the label changed, the
     effect re-ran, and the 130ms wait started again. That second wait expired
     inside the synchronous rebuild, with the main thread held: the panel was
     mounted and never got a frame. Recorded frame by frame on the live site,
     it appeared in 0 of 677 frames while the gap between frames reached
     4723ms.

     Keyed on the boolean, the wait starts once when work starts and is not
     restarted by the text changing underneath it. */
  const procBusy = !!procLabel;
  useEffect(() => {
    if (!procBusy) { setProcShown(false); return undefined; }
    const t = setTimeout(() => setProcShown(true), 100);
    return () => clearTimeout(t);
  }, [procBusy]);
  /* The panel is the customer's, so it goes the moment the customer-visible
     result is there. Whatever bookkeeping continues afterwards is ours. */
  const processing = procShown && !!procLabel
    ? /*#__PURE__*/React.createElement("div", {
        className: "pb-proc", role: "status", "aria-live": "polite"
      },
        /* THE MOTIF: a cut line going round a sticker, which is what the lab
           is actually doing. Two strokes on the same rounded-square path --
           one faint, the whole outline; one a short dash travelling along it.
           Inline SVG so it scales with the type and costs no request, and the
           geometry is small enough that the motion is one compositor property
           on a 34px box rather than anything the main thread has to think
           about while it is already busy. */
        /*#__PURE__*/React.createElement("span", { className: "pb-proc-mark", "aria-hidden": "true" },
          /*#__PURE__*/React.createElement("svg", { viewBox: "0 0 44 44", width: "34", height: "34" },
            /*#__PURE__*/React.createElement("path", {
              className: "pb-proc-sheet",
              d: "M9 5h20l10 10v24a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4V9a4 4 0 0 1 4-4z"
            }),
            /*#__PURE__*/React.createElement("path", {
              className: "pb-proc-peel", d: "M29 5l10 10h-8a2 2 0 0 1-2-2z"
            }),
            /*#__PURE__*/React.createElement("path", {
              className: "pb-proc-cut",
              d: "M9 5h20l10 10v24a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4V9a4 4 0 0 1 4-4z"
            }))),
        /*#__PURE__*/React.createElement("span", { className: "pb-proc-tx" },
          /*#__PURE__*/React.createElement("span", { className: "pb-proc-k" }, "Processing artwork"),
          /*#__PURE__*/React.createElement("span", { className: "pb-proc-s" }, procLabel + '…')))
    : null;

  /* ── WHAT THE BACKGROUND CONTROL SAYS, DERIVED ONCE ────────────────────
     Desktop and mobile render their own trees on purpose, and that is exactly
     how they drift: a label fixed in one is still wrong in the other, which
     has happened here before. So the WORDS are computed once, from the state
     the engine already publishes, and both trees read this.

     It derives nothing new. `bgOn`, `bgBusy`, `bgState` and `bgInfo` are the
     existing canonical state; this only chooses which of them to say out loud.

     "Original" is the existing inverse — it is not an undo. Removal produced a
     SEPARATE picture and switching back just points at the upload again, which
     is why the word is Original rather than Restore. */
  const bgLabel = (() => {
    if (!art) return { text: 'Remove background', title: 'Upload artwork first', can: false };
    if (bgState === 'checking') return {
      text: 'Checking background…',
      title: 'Looking at the edges of your artwork', can: false };
    if (bgBusy) return {
      text: 'Working…', title: 'Removing the background', can: false };
    if (bgOn) return {
      text: 'Original',
      title: 'Show the original image again', can: true };
    if (bgState === 'none') {
      /* REFUSED, AND IT SAYS WHICH. Already-cut-out artwork and a photograph
         are different answers and the customer is owed the difference — a
         greyed control with no words is the thing that reads as broken. */
      const already = !!(bgInfo && bgInfo.alreadyTransparent);
      return {
        text: already ? 'No background to remove' : 'Background can’t be removed',
        title: (bgInfo && bgInfo.reason) ||
               'This artwork has no plain background around it',
        can: false };
    }
    return {
      text: 'Remove background',
      title: 'Remove the plain background from around your artwork', can: true };
  })();

  /* ── IS THERE A BACKGROUND WORTH OFFERING TO REMOVE? ───────────────────
     Asked once per upload, of the engine, before any control is shown. Three
     answers matter and only one of them puts a button on the screen:
     already-transparent artwork is left alone (a control that can only
     disappoint), a photograph is refused with a reason, and a plain surround
     is offered. Deriving nothing here is deliberate: detection is cheap, the
     removal is not, and most customers will not ask for it. */
  useEffect(() => {
    setBgOn(false);
    setBgError('');
    setBgInfo(null);
    setBgMeta(null);
    setBgUrl(prev => { if (prev) { try { URL.revokeObjectURL(prev); } catch (e) {} } return null; });
    if (!originalArt) { setBgState('idle'); return; }
    const eng = window.SLBgRemove;
    if (!eng) { setBgState('none'); return; }
    let cancelled = false;
    setBgState('checking');
    eng.analyzeUrl(originalArt).then(a => {
      if (cancelled) return;
      setBgInfo(a);
      setBgState(a.removable ? 'idle' : 'none');
    }).catch(e => {
      if (cancelled) return;
      /* A FAILED ANALYSIS IS NOT AN INELIGIBLE IMAGE, and the customer sees the
         same thing either way — no control. Record which it was, so "Remove
         Background did not appear" can be answered instead of investigated.
         A tainted canvas and a photograph both end up here quietly; only one
         of them is working as intended. */
      setBgInfo({ verdict: 'analysis-failed', removable: false, alreadyTransparent: false,
                  reason: String((e && e.message) || e) });
      setBgState('none');
    });
    return () => { cancelled = true; };
  }, [originalArt]);

  /* The derived picture belongs to this builder; when the tab goes, so does it. */
  useEffect(() => () => { if (bgUrl) { try { URL.revokeObjectURL(bgUrl); } catch (e) {} } }, [bgUrl]);

  /* OFF is instant and free. ON is instant too once the derivative exists —
     it is computed exactly once per upload and then reused. */
  const toggleBg = () => {
    if (bgBusy) return;
    if (bgOn) { setBgOn(false); return; }
    if (bgUrl) { setBgOn(true); return; }
    const eng = window.SLBgRemove;
    if (!eng || !originalArt) return;
    setBgError('');
    setBgState('working');
    eng.removeFromUrl(originalArt).then(r => {
      setBgUrl(r.url);
      setBgMeta({ method: r.method, removedFrac: r.removedFrac, width: r.width, height: r.height });
      setBgOn(true);
      setBgState('ready');
    }).catch(e => {
      /* A refusal is not a crash, and the customer keeps their artwork either
         way. Say what happened and leave the original showing. */
      setBgOn(false);
      setBgError((e && e.message) ? String(e.message) : 'the background could not be removed');
      setBgState('failed');
    });
  };

  // custom size math
  /* CART GRAPHICS: what the customer types lives in `cgWText`/`cgHText` and is
     validated by the product engine. Only a VALID entry is written through to
     `customW`/`customH`, so the sticker expressions below are untouched and
     the preview and price run on the last valid size — an out-of-range or
     half-typed value never silently becomes a different one. */
  const [cgWText, setCgWText] = useState(CG ? String(CG_ENTRY.w) : '');
  /* What the last print-file build actually produced (pixels, density,
     format), and — when a device or the file-size limit forced the print image
     below the source's own density — by how much, for the design it was
     measured on. The resolution warning is then asked about the image that
     will really print, not the one the source could have given. */
  const cgPrintInfoRef = useRef(null);
  /* The border widths in force for THIS design: the product's current ones
     for a new design, the saved ones for a reopened design. They live in the
     shared border rule (one place decides the number); this counter only makes
     the screen redraw when they are swapped. */
  const [, setCgBorderRev] = useState(0);
  const cgUseBorderTable = (saved) => {
    try {
      const C = _szCore();
      if (CG && C && C.setBorderWidthTable && C.setBorderWidthTable(CG.borderTableFor(saved))) setCgBorderRev(r => r + 1);
    } catch (e) {}
  };
  const cgPrintBudgetRef = useRef(0);          // bytes the print file may weigh in the add-to-cart request
  const cgMasterRef = useRef(false);           // this cart graphic's request also carries _artwork_master
  const cgDieCutRefusalRef = useRef(null);     // why a die-cut graphic's cut line was refused, if it was (D-183)
  const [cgProdCap, setCgProdCap] = useState(null);
  const [cgHText, setCgHText] = useState(CG ? String(CG_ENTRY.h) : '');
  const cgSize = CG ? CG.validateSize(cgWText, cgHText) : null;
  /* A size that changes from anywhere but the field — a shape choice, a
     restored design, Reset — is shown in the field. A field holding an invalid
     entry is left alone: `customW` did not change, so this does not run. */
  useEffect(() => {
    if (!CG) return;
    const p = CG.parseInches(cgWText);
    if (!p.ok || Math.abs(p.value - parseFloat(customW)) > 1e-9) setCgWText(CG.fmtIn(parseFloat(customW)) || String(customW));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customW]);
  /* The moment a file is in, the preview shows it on the cart's front panel.
     The customer can still switch to White or Dark. */
  useEffect(() => {
    if (CG && art) setSurface('cart');
  }, [art]);
  useEffect(() => {
    if (!CG) return;
    const p = CG.parseInches(cgHText);
    if (!p.ok || Math.abs(p.value - parseFloat(customH)) > 1e-9) setCgHText(CG.fmtIn(parseFloat(customH)) || String(customH));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customH]);
  const cW = Math.max(0.5, parseFloat(customW) || 0);
  const cH = Math.max(0.5, parseFloat(customH) || 0);
  /* THE ORDERED SIZE IS THE FINISHED STICKER (contour-fit-v1).
     `size` is the long edge the customer chose, and for a contour sticker that
     is the long edge of the CUT SHAPE — artwork plus border — not of the
     artwork canvas. The border width is the canonical physical one and depends
     only on that same long edge, so there is no circularity here.

     Falls back to the artwork aspect while the silhouette is still being
     measured, or if it cannot be, so a slow decode never blocks a price. */
  /* THE ORDERED LONG EDGE, WHICHEVER WAY IT WAS CHOSEN.
     ---------------------------------------------------------------------
     `size` is the PRESET long edge, and the presets are only 2/3/4/5. In
     custom mode the customer's real size lives in `cW`/`cH` and `size` keeps
     whatever it last held — its initial value is 3 — so every custom order was
     fitted against a 3in sticker.

     Real order SL-1M1TJ-YQ3Y9-2KEXR (custom 1.25 x 1.25, White Standard)
     recorded it in its own metadata: `border 0.066in` where 0.022 x 1.25 is
     0.0275, and `finished 2.9409x3in`. 0.066 is 0.022 x 3.

     The damage is downstream of the fit rather than in it. `thick` (below)
     already resolves from `longestIn`, so the border DISTANCE was right; what
     went wrong is that `uX`/`uY` are derived from `cfit.finishedW/H`, so the
     unit scale came out 102.35 units/in instead of 240.80 and the physical
     tolerance converted 2.353x too tight - 1.535 units instead of 3.612. The
     measured short-axis excess was 1.986, which falls exactly between the two,
     so the short-axis guard refused a sticker it should have passed:
     `contour_fit_short_axis_will_not_fit`.

     `slContourCorrect` is NOT changed. Its formula was never wrong; it was
     being handed a tolerance from an obsolete basis.

     No circularity. `cfit.aspect` does depend on L, and in PRESET mode that
     aspect feeds `widthIn`/`heightIn` - but there `size` IS the ordered long
     edge, so that path is unchanged. In CUSTOM mode `widthIn`/`heightIn` come
     straight from `cW`/`cH` and never consult the aspect, so the ordered long
     edge is already known here. */
  const cfitLongIn = sizeMode === 'custom'
    ? Math.max(cW, cH)
    : size;
  const cfitBorderIn = contour && _szCore() && _szCore().borderInchesForLevel
    ? (border === 'none' ? 0 : _szCore().borderInchesForLevel(bsize, cfitLongIn))
    : 0;
  /* A NO-BORDER DIE-CUT CART GRAPHIC IS FITTED BY THE EDGE IT IS CUT ON.
     With no border the cut is traced straight from the artwork at alpha 128,
     but `artSil` measures down to alpha 8. On a soft (anti-aliased) edge the
     two differ: at 20in a bicubic-upscaled logo measured 0.06in larger than
     its traced outline, so the cut came out short and the placement check
     refused every such order (cut_long_edge_off_target). It is fitted by the
     alpha-128 box instead, so the ordered size is the size that is cut; the
     faint fringe outside it is under half opacity and is trimmed with the
     edge. A bordered graphic is cut from its baked border silhouette.

     A DIE-CUT STICKER WITH A FRINGE THAT MATTERS IS FITTED THE SAME WAY
     (P-45, 20 Oct 2026). A sticker's cut, with or without a border, grows from
     the same alpha-128 trace. Order #1068 carried a 17px haze of alpha 8-63
     down one side: `artSil` measured 1232px wide, the trace 1214px, the fit
     promised 3 x 2.9in for a cut that is 3 x 2.94in, and the short-axis guard
     refused it. A sticker switches to the alpha-128 box only when the fringe
     it would ignore is more than the size tolerance at the ordered size, so
     every sticker that fits by `artSil` today (#1064: 0.010in) is fitted
     exactly as before. */
  const fitFringeIn = (!CG && contour && artSil && artSil.cut && cfitLongIn > 0)
    ? Math.max(artSil.w - artSil.cut.w, artSil.h - artSil.cut.h) *
      ((cfitLongIn - 2 * cfitBorderIn) / Math.max(artSil.w, artSil.h))
    : 0;
  const fitByCut = !!(artSil && artSil.cut && contour &&
    (CG ? border === 'none' : fitFringeIn > SL_SIZE_TOL_IN));
  const fitSil = fitByCut ? Object.assign({}, artSil, artSil.cut) : artSil;
  const cfit = contour && fitSil && artRes
    ? slContourFit(fitSil, fitSil.iw, fitSil.ih, cfitBorderIn, cfitLongIn)
    : null;
  /* THE PUBLISHED RECT, AS A VALUE THE EFFECT CAN WATCH.
     `artworkRectIn` is where HQ places the customer's ORIGINAL on the left of
     the working document, and the production SVG is built from the ordered
     size. If those two disagree the two halves of the operator's document are
     at different scales and a CutContour cannot be carried across by
     translation — which is exactly what order #1033 shipped: a rect of 2.916in
     (a 3.000in order) published against a 3.250in production file, a ratio of
     1.1145.

     The cause was a missing dependency. The effect that publishes the
     transform watched shape, zoom, offset, rotation, flip, border colour,
     border SIZE and the preview box — but never the ordered SIZE. `cfit`
     depends on `size`, so changing the size changed the rect and nothing
     republished it. `w`/`h` do not stand in for it either: on a square the
     aspect is unchanged, so the preview box does not move.

     Depending on `cfit` itself would re-run on every render, because it is a
     fresh object each time. This is the VALUE, so the effect fires exactly
     when the rect actually changes and never otherwise. */
  const cfitRectKey = (cfit && cfit.artworkRectIn)
    ? [cfit.artworkRectIn.x, cfit.artworkRectIn.y,
       cfit.artworkRectIn.width, cfit.artworkRectIn.height].join(',')
    : 'none';
  const arForSize = contour ? ((cfit && cfit.aspect) || artAR || 1) : shp.ar;
  const widthIn = sizeMode === 'custom' ? +cW.toFixed(2) : arForSize >= 1 ? size : +(size * arForSize).toFixed(1);
  const heightIn = sizeMode === 'custom' ? +cH.toFixed(2) : arForSize >= 1 ? +(size / arForSize).toFixed(1) : size;
  const longestIn = Math.max(widthIn, heightIn);
  /* INDIVIDUAL STICKERS ARE AT LEAST 1 INCH (Ryan, 24 Oct 2026). A die cut
     (proportions locked to the art) needs 1in on its LONGEST side; a circle or
     square, or its unlocked oval/rectangle, needs 1in on BOTH sides. A sticker
     on a sheet keeps the 0.5in limit, and a cart graphic has its own rules.
     The same rule travels to SLBuilderCore (sizeRule), which refuses the line. */
  /* NEVER ON THE PRODUCTION HOST: HQ and MOTO reopen orders that are already
     paid, and an order is built at the size it was bought at (Ryan: honour
     paid orders), even one below today's minimum. */
  const indSizeRule = (CG || sheetIntent || !ORDER_RULES_APPLY) ? null : (contour ? 'diecut' : 'shape');
  /* THE STICKER'S OUTLINE, UNROUNDED, FOR THE SHEET LAYOUT ONLY (D-179).
     `widthIn` / `heightIn` are the ORDERED size: a preset die cut's short
     side is shown, priced and ordered to one decimal (3 x 1.4 in). The cut is
     the artwork's real proportion (3 x 1.426). A sheet has to be laid out
     for the outline that is cut, or neighbouring kiss cuts end up closer
     than the gap says. Nothing else reads these two: the ordered size, the
     price and the production file are exactly what they were. */
  const cutWIn = sizeMode === 'custom' ? widthIn : (arForSize >= 1 ? size : size * arForSize);
  const cutHIn = sizeMode === 'custom' ? heightIn : (arForSize >= 1 ? size / arForSize : size);
  /* THE BORDER IS A PROPORTION OF THE STICKER, RESOLVED IN THE CORE.
     ---------------------------------------------------------------------
     `bsz.in` — a fixed 0.03/0.05/0.10 inches — is deliberately no longer read
     here. It made Standard 10% of a 0.5" sticker and 0.56% of a 9" one, so
     resizing a design appeared to dissolve the border the customer had chosen.

     `borderInchesForLevel` is the ONE place the number is decided, and it is
     decided in physical inches. Everything downstream already consumes `thick`
     — the on-screen ring through `borderPxFor`, the canonical `padPx`, the
     proof, and the production SVG's outer geometry and CutContour — so display,
     proof and print cannot disagree. This is not a display-only correction. */
  const thick = (_szCore() && _szCore().borderInchesForLevel)
    ? _szCore().borderInchesForLevel(bsize, longestIn)
    : bsz.in;
  /* CART GRAPHICS is priced by its own engine: area × the approved anchor,
     times the quantity, with no tiers. `cgTotal` is what every Cart Graphics
     surface shows; the sticker `total` below it is the original line and is
     simply not displayed on this product. */
  const cgPrice = CG ? CG.price(widthIn, heightIn, effQty) : null;
  const cgAvail = CG ? CG.availability(widthIn, heightIn, effQty, { shape: shape, borderIn: border === 'none' ? 0 : +thick }) : null;
  const cgInputOk = !CG || (cgSize.ok && cgQty.ok);
  const cgCanOrder = !CG || (cgInputOk && cgAvail.orderable && CG_CHECKOUT_OPEN);
  const cgTotal = (CG && cgPrice && !cgPrice.error) ? cgPrice.price : 0;
  /* THE PROMO PACK'S PRICE IS THE VARIANT'S, not the formula's. `promoRegular`
     is what these exact proportional dimensions cost at 50 on the normal
     price list, so the struck-through price and "You save" are the
     customer's own numbers, never a square-sticker headline. */
  const total = PROMO ? PROMO.price50 : slPrice(widthIn, heightIn, effQty);
  /* Exactly SLPricing.diecut(W, H, 50).price; 0 when pricing is unavailable,
     and then the strike and the saving are hidden rather than guessed. */
  const promoRegular = PROMO ? slPrice(widthIn, heightIn, PROMO.packQty) : 0;
  const promoRule = PROMO ? promoPriceRule(promoRegular, PROMO.price50) : null;
  const promoSave = promoRule ? promoRule.saveCents / 100 : 0;
  /* The window (dates, wiring) is said first; then whether this SIZE can be
     sold as the pack at all (promoPriceRule). Either one disables the add. */
  const promoWindowBlock = PROMO ? promoBlockText(Date.now()) : '';
  const promoBlock = PROMO ? (promoWindowBlock
    || (promoRule.cheaper ? 'At this size our regular price is lower than the pack.'
      : promoRule.unpriced ? 'Checking the price for this size…' : '')) : '';
  /* THE PACK IS FIXED. A saved design, a reorder, the Home-page handoff or a
     sheet hint can each arrive carrying a custom size, another quantity, a
     sheet or another material; on the promo page every one of them is put
     back to what the pack is. Nearest of 2 or 3 in by the longest side. */
  useEffect(() => {
    if (!PROMO) return;
    if (sizeMode !== 'preset' || !promoIsSize(size)) {
      const longest = sizeMode === 'custom' ? Math.max(widthIn, heightIn) : size;
      setSizeMode('preset');
      setSize(promoSnapSize(longest));   // nearest 2.0–3.0 in, 0.1 in steps
    }
    if (qtyMode !== 'preset' || qty !== PROMO.packQty) { setQtyMode('preset'); setQty(PROMO.packQty); }
    if (sheetIntent) setSheetIntent(false);
    if (material !== 'vinyl') setMaterial('vinyl');
    if (laminate !== 'matte' && laminate !== 'gloss') setLaminate('matte');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizeMode, size, qtyMode, qty, sheetIntent, material, laminate]);
  /* Measured so the rule's effect on conversion can be read: one event per
     distinct ordered size that turned out cheaper at regular price. */
  const promoIneligibleSeenRef = useRef({});
  useEffect(() => {
    if (!PROMO || !art || !promoRule || !promoRule.cheaper) return;
    const k = widthIn + 'x' + heightIn;
    if (promoIneligibleSeenRef.current[k]) return;
    promoIneligibleSeenRef.current[k] = true;
    promoTrack('promo_ineligible_size', { widthIn: widthIn, heightIn: heightIn, regular: promoRegular, pack: PROMO.price50 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [art, widthIn, heightIn, promoRule && promoRule.cheaper]);
  const promoStartedRef = useRef(false);
  useEffect(() => {
    if (!PROMO || !art || promoStartedRef.current) return;
    promoStartedRef.current = true;
    promoTrack('promo_build_start', {});
  }, [art]);
  const perUnit10 = slPrice(widthIn, heightIn, 10) / 10;
  const savePct = Math.max(0, Math.round((1 - total / effQty / perUnit10) * 100));

  // ---- unified proportional sizing (Lock Proportions; reuses the shared custom-size pipeline) ----
  // One model for every shape. Lock ON links Width & Height into a single "Size" control; unlocking reveals
  // independent Width & Height. The locked ratio is 1:1 for Circle/Square (perfect circle / square) and the
  // art's ratio for Die Cut (stays proportional to the art). Unlocking naturally turns a Circle into an ellipse,
  // a Square into a rectangle, and lets a Die Cut take independent W×H with its outline following the new dims —
  // so separate Rectangle/Oval choices are no longer needed.
  /* THE LOCKED RATIO OF A DIE CUT IS THE FINISHED STICKER'S, NOT THE UPLOAD'S.
     The preset path already sizes a die cut from `cfit.aspect` - artwork PLUS
     border - and the custom fields used the bare upload ratio. On a wide logo
     the border is a large share of the short side, so the ordered height came
     out shorter than the finished sticker and contour-fit refused it:
     Defuse (SL-1M4B8-H4SWZ-V035T, 750x162) was ordered 2.5 x 0.54 with a
     finished sticker of 2.5 x 0.626, and shipped with no cut line at all
     (`contour_fit_short_axis_will_not_fit`). Duckhorn only built because its
     customer typed 0.9 over the 0.81 the lock offered. */
  const dieLockAR = (cfit && cfit.aspect) || artAR || 1;
  const shapeLockRatio = () => shape === 'circle' || shape === 'square' ? 1 : dieLockAR;
  const sizeLocked = lockAR;                              // effective Lock Proportions state (every shape toggles)
  // Every shape starts with Lock Proportions ON, snapped to its natural ratio. Freeze the locked ratio only at
  // discrete moments (shape change, and lock-on in toggleSizeLock) — NEVER on every step — so locked stepping
  // derives the partner from a stable ratio and can't drift.
  useEffect(() => {
    /* Cart Graphics chooses its shapes through `cgPickShape`, which owns the
       lock and the dimensions together. The snap below is the sticker grid
       (0.25in, capped at 9in) and must never touch a 34in graphic. */
    if (CG) return;
    const r = shapeLockRatio();
    lockRatioRef.current = r > 0 ? r : 1;
    setLockAR(true);
    if (sizeMode === 'custom') {
      const wv = snapSz(widthIn);
      setCustomW(String(wv));
      setCustomH(String(lockH(wv, lockRatioRef.current)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shape]);
  /* A DIE CUT'S LOCKED RATIO MOVES WITH ITS BORDER. The finished sticker is
     artwork plus border, so choosing Thick after typing a width makes the
     sticker squarer - and the height typed under Standard is then too short
     for it. Measured through the builder's own controls: Duckhorn at 2in,
     width typed, then Thick, kept 0.87in for a 0.896in sticker and was
     refused. So when the finished ratio itself changes (border level, or the
     silhouette being measured) the frozen ratio is refreshed and the SHORT
     side re-derived from the long one. Stepping the size never changes the
     ratio, so the no-drift rule above still holds; keying on the rounded
     value means this fires on a real change and not on every render. */
  const dieLockKey = contour ? dieLockAR.toFixed(4) : null;
  useEffect(() => {
    if (!contour || !lockAR || !(dieLockAR > 0)) return;
    /* THE STEPPER'S RATIO TOO, IN EVERY SIZE MODE. The phone's Size stepper
       reads `lockRatioRef`, which the shape effect froze before the artwork
       was measured - 1:1 - so stepping a die cut from the 3in preset to 2in
       ordered a 2 x 2in square for a 2 x 0.87in sticker (and priced it so),
       on live as well. It follows the finished ratio from now on. */
    lockRatioRef.current = dieLockAR;
    if (sizeMode !== 'custom') return;
    /* exact to the hundredth, as the Width/Height fields write it - not the
       stepper's quarter-inch snap, which would turn 0.626 into 0.75 */
    if (dieLockAR >= 1) setCustomH(String(+(widthIn / dieLockAR).toFixed(2)));
    else setCustomW(String(+(heightIn * dieLockAR).toFixed(2)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dieLockKey]);
  // Apply an edit to one dimension through the shared custom-size state. Switching to custom mode preserves the
  // other dimension (free) or recomputes it from the frozen ratio (locked); every value is snapped + clamped.
  const stableRatio = () => lockRatioRef.current > 0 ? lockRatioRef.current : (heightIn > 0 ? widthIn / heightIn : 1);
  const applyWidth = nextW => {
    const wv = snapSz(nextW);
    setSizeMode('custom');
    setCustomW(String(wv));
    /* a die cut's partner is its finished proportion, exact to the hundredth:
       snapping it to a quarter inch could order a sticker shorter than its own
       border allows (0.87 -> 0.75) and contour-fit would refuse it */
    setCustomH(String(sizeLocked ? (contour ? +(wv / stableRatio()).toFixed(2) : lockH(wv, stableRatio())) : snapSz(heightIn)));
  };
  const applyHeight = nextH => {
    const hv = snapSz(nextH);
    setSizeMode('custom');
    setCustomH(String(hv));
    setCustomW(String(sizeLocked ? (contour ? +(hv * stableRatio()).toFixed(2) : lockW(hv, stableRatio())) : snapSz(widthIn)));
  };
  // Unified "Size" control (shown while locked): drive the longer side; the partner follows via the locked
  // ratio, so a single value scales Width & Height together.
  const applySize = next => stableRatio() >= 1 ? applyWidth(next) : applyHeight(next);
  const toggleSizeLock = () => {
    if (lockAR) { setLockAR(false); return; }             // unlock → reveal independent Width & Height
    const r = shapeLockRatio();                            // lock on → snap back to the shape's natural ratio
    lockRatioRef.current = r > 0 ? r : 1;                  // (perfect circle / square; art ratio for Die Cut)
    setLockAR(true);
    setSizeMode('custom');
    const wv = snapSz(widthIn);
    setCustomW(String(wv));
    setCustomH(String(lockH(wv, lockRatioRef.current)));
  };

  /* THE MINIMUM IS APPLIED TO THE SIZE ITSELF, wherever it came from: a step,
     a typed side, a shape change, a saved design or a Home-page handoff. A
     size below it is raised to it - proportionally while the lock is on - and
     the customer is told once. Never while a side is still being typed (the
     field would fight the keyboard); the blur re-runs it via sizeFixTick. */
  const [sizeFixTick, setSizeFixTick] = useState(0);
  const [sizeMinNote, setSizeMinNote] = useState('');
  const indMinFix = (w, h) => {
    if (!(w > 0) || !(h > 0)) return null;
    const r2 = v => +(+v).toFixed(2);
    if (indSizeRule === 'shape') {
      if (Math.min(w, h) >= 1 - 1e-9) return null;
      if (lockAR) { const k = 1 / Math.min(w, h); return { w: r2(w * k), h: r2(h * k) }; }
      return { w: Math.max(w, 1), h: Math.max(h, 1) };
    }
    if (indSizeRule !== 'diecut' || Math.max(w, h) >= 1 - 1e-9) return null;
    /* locked: the long side becomes 1in and the short side follows the frozen
       ratio exactly as applyWidth/applyHeight derive it, so a raised size and a
       typed 1in land on the same dimensions (Shop check, 24 Oct: 1 x 0.69 vs 0.70) */
    if (lockAR) { const r = stableRatio(); return r >= 1 ? { w: 1, h: r2(1 / r) } : { w: r2(r), h: 1 }; }
    return w >= h ? { w: 1, h: h } : { w: w, h: 1 };
  };
  useEffect(() => {
    if (!indSizeRule || sizeMode !== 'custom') return;
    const a = typeof document !== 'undefined' ? document.activeElement : null;
    if (a && a.tagName === 'INPUT' && a.type === 'number') return;
    const fix = indMinFix(widthIn, heightIn);
    if (!fix) return;
    setCustomW(String(fix.w));
    setCustomH(String(fix.h));
    setSizeMinNote(indSizeRule === 'diecut' ? 'Raised to our 1″ minimum on the longest side.' : 'Raised to our 1″ minimum on each side.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indSizeRule, sizeMode, widthIn, heightIn, lockAR, sizeFixTick]);
  useEffect(() => {
    if (!sizeMinNote) return undefined;
    const t = setTimeout(() => setSizeMinNote(''), 6000);
    return () => clearTimeout(t);
  }, [sizeMinNote]);
  const indMinText = indSizeRule === 'diecut' ? 'Minimum 1″ on the longest side' : indSizeRule === 'shape' ? 'Minimum 1″ on each side' : '';
  /* The phone stepper's floor for one control: the unified Size (the longer
     side) and every side of a shape stop at 1in; an unlocked die cut's side
     may go to 0.5in while its other side is already 1in or more. */
  const mDimMin = label => {
    if (!indSizeRule) return SZ_MIN;
    if (indSizeRule === 'shape' || label === 'Size') return 1;
    const other = label === 'Width' ? heightIn : widthIn;
    return other >= 1 - 1e-9 ? SZ_MIN : 1;
  };

  // Presentation selector (single source of truth for the breakpoint). Declared here — above the DPI
  // work — so that work can be skipped on mobile, where the DPI/quality UI is never rendered.
  /* A PRODUCTION HOST IS ALWAYS THE DESKTOP PRESENTATION.
     Not a preference — the mobile shell is a second storefront tree, with its
     own header (brand link to `/`, cart link to `/cart`), its own Review Order
     stage and its own Add to Cart. Gating those one by one would leave HQ one
     narrow window away from a shop, and MOTO drives the DESKTOP controls, so a
     bench panel under 820px would also stop being drivable. One selector,
     decided by the host, keeps both facts true. */
  const [isMobile, setIsMobile] = useState(!PRODUCTION_HOST && typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width:820px)').matches : false);
  /* THE STAGE, MEASURED — and declared AFTER what it depends on.
     This effect's dependency array reads `isMobile`, and a dependency array is
     evaluated DURING RENDER. Sitting above the `const isMobile` declaration it
     was a temporal dead zone: it threw on the first render, the boundary caught
     it, and every customer got "That file could not be processed". Exactly the
     defect that white-screened the live theme, reintroduced 200 lines away.

     One observer, on whichever stage this tree rendered. Falls back to a single
     read where ResizeObserver is missing, so the box is never null on a browser
     that simply lacks the API. */
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      if (!(r.width > 0) || !(r.height > 0)) return;
      setStageBox(prev => (prev && Math.abs(prev.w - r.width) < 1 && Math.abs(prev.h - r.height) < 1)
        ? prev : { w: r.width, h: r.height });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => { try { ro.disconnect(); } catch (e) {} };
  }, [isMobile, art]);
  useEffect(() => {
    if (PRODUCTION_HOST) return undefined; // the host, not the viewport, chooses the shell
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width:820px)');
    const on = () => setIsMobile(mq.matches);
    on();
    if (mq.addEventListener) { mq.addEventListener('change', on); return () => mq.removeEventListener('change', on); }
    mq.addListener(on); return () => mq.removeListener(on);
  }, []);

  /* ── IS THE CUSTOMER STILL BEING ASKED? ──────────────────────────────────
     One name for "the Die Cut / Shape chooser is on screen right now", read
     by the chooser itself AND by the expensive work that must not block it,
     so the two cannot drift apart.

     Every term is a SHELL fact, which is why this is not a geometry fork:
     * PRODUCTION_HOST  HQ shows no chooser; a job arrives already decided.
     * !art             nothing uploaded, so there is nothing to choose about.
     * workflow         already answered, or restored with a saved design.

     THE PHONE SHELL ASKS TOO. It used to be excluded here with an
     `isMobile` term, because `renderMobileShell()` did not contain the
     chooser: with nobody to answer, `workflow` stayed null, this stayed
     true, and the outline bake waited forever (measured on the live site as
     a 0px ring at every level). That term may only go together with the
     chooser being rendered in the phone shell, which it now is. Remove one
     without the other and the phone border is gone again.

     Declared HERE, below `isMobile`, rather than beside `workflow`: above it
     the reference is a temporal dead zone — the same defect that once
     white-screened the live theme. */
  const choosingWorkflow = !(PRODUCTION_HOST || !art || workflow);

  /* ---- BUSINESS-OUTCOME TELEMETRY ------------------------------------------
     Two events, and only two, emitted from this bundle: the customer's artwork
     became available, and Shopify accepted the cart add. Everything else the
     builder might report is diagnostics and stays in the console.

     Both go through NAMED seams on `window.slTelemetry`, exactly as
     `uploadOutcome` already does. Not `slTelemetry.track`, which has never
     existed, and not the client's general-purpose emitter either — the name is
     left unwritten here on purpose, because a test asserts this bundle does not
     contain it. This bundle is deliberately barred
     from holding a general-purpose emitter, because its `.pb-*` class names are
     the QA screenshot contract and a generic call would let any future edit
     invent an event with no vocabulary and no reviewer. A seam per fact can
     only ever say the one thing it is named for.

     Sixteen guarded calls to the non-existent `slTelemetry.track` are still
     scattered through this file doing nothing. They are a separate cleanup and
     are deliberately untouched here: activating them without first correcting
     their names and payloads would put unvalidated events on the wire.

     `entryPath` is a ref, not state: it is provenance about the artwork the
     builder is holding, it must be readable synchronously from inside an
     effect, and changing it must never cause a render. */
  const artEntryRef = useRef('direct_builder');
  /* Read from the SAME breakpoint that selects the tree, so the dimension can
     never disagree with the UI it describes. The telemetry envelope's `device`
     is a viewport bucket with different boundaries and cannot answer this. */
  const slSurface = () => (isMobile ? 'mobile_builder' : 'desktop_builder');
  const slOutcome = (name) => {
    try {
      const t = window.slTelemetry;
      if (!t) return;
      const seam = name === 'artwork_available' ? t.artworkAvailable : t.cartAddCompleted;
      if (typeof seam === 'function') seam(artEntryRef.current, slSurface());
    } catch (e) {}
  };

  /* THE ARTWORK OUTCOME.
     Keyed on the `art` OBJECT, not on a token derived from its contents. Every
     acquisition path ends in exactly one `setArt({...})` with a fresh object,
     so this fires once per artwork and cannot double-count a journey that
     passes through several technical steps — homepage store, IndexedDB read,
     builder decode and byte-caching are four steps and one outcome.
     A content-derived token would fire twice: `artToken` includes the cached
     byte length, which lands after the picture does.
     Observing the OUTCOME rather than the transport also means a future
     acquisition path is instrumented the day it is written. */
  useEffect(() => {
    if (!art) return;                       // clearing artwork is not an outcome
    slOutcome('artwork_available');
  }, [art]);
  /* effective print DPI from original resolution
     DPI + quality come from the Upload engine (single source of truth; recomputes as size changes).
     Memoized so it does NOT re-run on every render (e.g. per drag/pinch frame); recomputes only when the
     bytes/artwork/target size change.

     THIS USED TO BE SKIPPED ON MOBILE (D-099). `if (isMobile) return null` was
     added because the DPI *panel* is desktop-only — but it also switched off
     the quality DECISION, and `hiRes` defaults to true when `dpi` is null. So a
     phone had no quality signal at all and silently reported "high res" for
     every file. Anything built on this would have been desktop-only by
     accident. The measurement is device-independent; only its PRESENTATION is
     responsive, and that is decided in the render. */
  /* CART GRAPHICS: the resolution question is asked about the size the
     ARTWORK prints at, not the size of the graphic it sits in. A shaped
     graphic letterboxes the art and then applies the customer's zoom, so art
     zoomed to 2x prints at half the resolution the ordered size implies; a
     die cut prints its whole canvas across the published artwork rect. The
     upload engine still does the measuring and still owns the thresholds. */
  const cgDpiTarget = !CG ? null
    : contour ? ((cfit && cfit.artworkRectIn) ? { widthIn: cfit.artworkRectIn.width, heightIn: cfit.artworkRectIn.height } : null)
    : (artRes ? CG.placedArtworkInches({ natW: artRes.w, natH: artRes.h, widthIn: widthIn, heightIn: heightIn, zoom: zoom }) : null);
  const cgCapKey = !CG ? '' : [(artBytes ? artBytes.length : 0), widthIn, heightIn, zoom, rot, pos.x, pos.y].join(':');
  /* Fewer print pixels over the same inches is the same as the source's
     pixels over more inches, which is the form the upload engine measures. */
  const cgCapDiv = (cgProdCap && cgProdCap.key === cgCapKey && cgProdCap.ratio > 0 && cgProdCap.ratio < 1) ? cgProdCap.ratio : 1;
  /* An upload over the pixel cap is measured here on its reduced copy, but
     its print file embeds the ORIGINAL whenever it can carry it whole (no
     background removal, room in the request). Then the original's pixels are
     what print: k times the copy's, over the same inches. */
  const cgOrigPx = artOriginalPxRef.current, cgOrigBytes = artOriginalRef.current;
  const cgOrigK = (CG && cgOrigPx && cgOrigPx.k > 1 && cgOrigBytes && !(bgOn && bgUrl) &&
                   /\.(png|jpe?g)$/i.test((art && art.name) || '') &&
                   CG.printRequestPlan(cgOrigBytes.length, 1048576, true).maxSvgBytes >= CG.printWholeNeed(cgOrigBytes.length)) ? cgOrigPx.k : 1;
  const cgDpiW = cgDpiTarget ? +(cgDpiTarget.widthIn / cgCapDiv / cgOrigK).toFixed(3) : 0;
  const cgDpiH = cgDpiTarget ? +(cgDpiTarget.heightIn / cgCapDiv / cgOrigK).toFixed(3) : 0;
  const dpiInfo = React.useMemo(() => {
    if (!artBytes || typeof window === 'undefined' || !window.SLUpload) return null;
    return window.SLUpload.analyzeBytes(artBytes, art ? art.name : '', {
      targetWidthIn: cgDpiW || widthIn,
      targetHeightIn: cgDpiH || heightIn
    });
  }, [artBytes, art, widthIn, heightIn, cgDpiW, cgDpiH]);

  /* THE QUALITY VERDICT (D-099).
     ---------------------------------------------------------------------
     Asked of the Upload engine, which owns the approved thresholds (300 ideal
     / 150 floor) and already computes effective print DPI as the WORST axis.
     Nothing is recomputed here — a second DPI calculator is precisely what
     must not exist.

     It re-derives whenever the ordered size changes, so enlarging a sticker
     can turn a good file into a warned one and shrinking it back clears the
     warning. Border, shape, colour and the rest cannot reach it: they are not
     inputs to `analyzeBytes`. */
  const quality = React.useMemo(() => {
    const SLU = (typeof window !== 'undefined') ? window.SLUpload : null;
    if (!SLU || !SLU.qualityOf) return { level: 'good', dpi: null, vector: false, code: null };
    return SLU.qualityOf(dpiInfo);
  }, [dpiInfo]);

  /* Identifies the FILE, not the design. Byte length plus name is enough to
     tell one upload from another and costs nothing; it never leaves the
     browser. */
  const artToken = (artBytes ? artBytes.length : 0) + ':' + (art ? art.name : '');
  /* A raster whose bytes have not arrived yet is UNKNOWN, not good — the
     distinction the old code lost. Nothing is warned on an unknown, but nothing
     is declared fine either, and the measurement reruns when the bytes land. */
  const qualityMeasured = quality.level !== 'unknown';
  const qualityLow = quality.level === 'low';
  /* DECLARED BESIDE THE VALUE IT PAIRS WITH, AND BEFORE ANY READER.
     This sat BELOW `wantQuality`, which reads it. `const` is in the temporal
     dead zone until its own line runs, so the read threw
     "Cannot access 'qualityAcked' before initialization" and took the whole
     builder down with it.
     It only ever fired on LOW-RESOLUTION artwork: `qualityLow && !qualityAcked`
     short-circuits, so a high-DPI upload never evaluated the dead binding and
     never crashed — which is why every fixture used during the MOTO work was
     fine and a customer's 640x432 photo was not.
     Its sibling `sourceAcked` was always declared next to `sourceAdvise`; this
     is the same shape, restored. */
  const qualityAcked = qualityAckFor === artToken;

  /* Classified from the customer's own filename by the Upload engine, which
     owns the format tables. Device is never consulted — the same file decides
     the same way on a phone and a desktop. */
  const sourceAdvice = React.useMemo(() => {
    const SLU = (typeof window !== 'undefined') ? window.SLUpload : null;
    if (!SLU || !SLU.sourceAdvisoryFor || !art) return { advise: false, ext: '', category: 'unknown' };
    return SLU.sourceAdvisoryFor(art.name, (dpiInfo && dpiInfo.metadata && dpiInfo.metadata.mimeType) || '');
  }, [art, dpiInfo]);
  const sourceAdvise = !!sourceAdvice.advise;
  const sourceAcked = sourceAckFor === artToken;

  /* ONE DIALOG AT A TIME, AND THE SOURCE QUESTION GOES FIRST.
     Two modals stacked on a phone is a trap; and the source advisory is about
     the file the customer just chose, so it is the more immediate question.
     The DPI warning waits behind it and opens on its own once the source
     question has been answered — it is not cancelled, only queued. */
  const wantSource = sourceAdvise && !sourceAcked && !!art;
  const wantQuality = qualityLow && !qualityAcked && !!art;
  const activeDialog = wantSource ? 'source' : (wantQuality ? 'quality' : null);

  /* EVERY VERDICT IS REPORTED (D-101).
     The warning did not appear on a real phone and there was no way to see why:
     no dimensions, no DPI, no verdict, no token. One line now states the whole
     decision for whichever route the file arrived by, so direct upload and the
     home-page restore can be compared on the SAME file. Numbers and MIME only;
     never bytes. */
  useEffect(() => {
    if (!art) return;
    var m = (dpiInfo && dpiInfo.metadata) || {};
    try {
      console.info('[sl-quality]', {
        name: art.name, mime: m.mimeType || m.format || null, kind: m.kind || null,
        srcW: m.widthPx || null, srcH: m.heightPx || null,
        orderedIn: widthIn + 'x' + heightIn,
        effectiveDpi: m.effectivePrintDpi != null ? m.effectivePrintDpi : null,
        level: quality.level, measured: qualityMeasured, vector: quality.vector,
        token: artToken, ackedFor: qualityAckFor, acked: qualityAcked,
        willWarn: !!(qualityLow && !qualityAcked)
      });
    } catch (e) {}
  }, [dpiInfo, quality, artToken, qualityAcked, art, widthIn, heightIn]);

  /* Ask ONCE per file, and only when it is actually a problem. A slider drag
     that crosses the threshold opens it once; every later frame sees the ack
     and stays quiet. */
  useEffect(() => {
    if (!qualityLow || qualityAcked || !art) return;
    if (wantSource) return;            // queued behind the source question
    setQualityOpen(true);
    try {
      if (window.slTelemetry && window.slTelemetry.track) {
        window.slTelemetry.track('artwork_quality_warning_shown', {
          dpiBucket: window.SLUpload && window.SLUpload.qualityBucket
            ? window.SLUpload.qualityBucket(quality.dpi) : 'unknown',
          widthIn: widthIn, heightIn: heightIn,
          fileCategory: (dpiInfo && dpiInfo.metadata && dpiInfo.metadata.format) || 'unknown'
        });
      }
    } catch (e) {}
  }, [qualityLow, qualityAcked, art]);

  useEffect(() => {
    if (activeDialog !== 'source') return;
    setSourceOpen(true);
    try {
      if (window.slTelemetry && window.slTelemetry.track) {
        window.slTelemetry.track('source_advisory_shown', {
          ext: sourceAdvice.ext, category: sourceAdvice.category,
          widthIn: widthIn, heightIn: heightIn
        });
      }
    } catch (e) {}
  }, [activeDialog, sourceAdvice.ext]);

  /* Recovered — the sticker shrank, or a better file arrived. Close the
     question rather than leaving it hanging over a design that is now fine. */
  useEffect(() => {
    if (qualityLow || !qualityOpen) return;
    setQualityOpen(false);
    try {
      if (window.slTelemetry && window.slTelemetry.track) {
        window.slTelemetry.track('artwork_quality_recovered', { widthIn: widthIn, heightIn: heightIn });
      }
    } catch (e) {}
  }, [qualityLow, qualityOpen]);

  const qualityTrack = (name) => {
    try {
      if (window.slTelemetry && window.slTelemetry.track) {
        window.slTelemetry.track(name, {
          dpiBucket: window.SLUpload && window.SLUpload.qualityBucket
            ? window.SLUpload.qualityBucket(quality.dpi) : 'unknown',
          widthIn: widthIn, heightIn: heightIn
        });
      }
    } catch (e) {}
  };
  /* "Use this file anyway" — nothing about the order changes. No resize, no
     re-encode, no price change, no block on Add to Cart. The customer was
     told; the decision is theirs. */
  const qualityProceed = () => {
    setQualityAckFor(artToken); setQualityOpen(false); setQualityNudge(false);
    qualityTrack('artwork_quality_proceed');
  };
  /* "Choose a better file" — the SAME picker the drop zone uses, so the
     replacement lands in `readFile` like every other upload. Cancelling the
     picker does nothing at all: the current design is untouched. */
  /* The source advisory's two answers. Deliberately the same shapes as the
     resolution warning's: one canonical picker, one acknowledgement, nothing
     touched. */
  const sourceProceed = () => {
    setSourceAckFor(artToken);
    setSourceOpen(false);
    try {
      if (window.slTelemetry && window.slTelemetry.track) {
        window.slTelemetry.track('source_advisory_use_anyway', { ext: sourceAdvice.ext, category: sourceAdvice.category });
      }
    } catch (e) {}
  };
  const sourceChooseBetter = () => {
    setSourceOpen(false);
    try {
      if (window.slTelemetry && window.slTelemetry.track) {
        window.slTelemetry.track('source_advisory_choose_better', { ext: sourceAdvice.ext, category: sourceAdvice.category });
      }
    } catch (e) {}
    /* The SAME hidden input the drop zone uses — no second uploader. Cancelling
       it does nothing at all: the current artwork and its advisory state stand. */
    try { if (fileRef.current) fileRef.current.click(); } catch (e) {}
  };

  const qualityChooseBetter = () => {
    setQualityOpen(false);
    qualityTrack('artwork_quality_choose_better_file');
    try { if (fileRef.current) fileRef.current.click(); } catch (e) {}
  };

  const dpi = dpiInfo && dpiInfo.metadata ? dpiInfo.metadata.effectivePrintDpi : null;
  const hiRes = dpi == null ? true : !dpiInfo.warnings.some(w => w.code === 'very-low-dpi');
  /* Read once and named, because it is asked twice and the two questions are
     different: whether to SHOW the background row, and whether the folded
     copy has a background paragraph in it. Same predicate as before. */
  const hasBgWarn = !!(dpiInfo && dpiInfo.warnings &&
                       dpiInfo.warnings.some(w => w.code === 'no-transparency'));

  // ---- sticker preview geometry (fixed, canvas-relative) ----
  /* THE SAME AUTHORITY AS `arForSize`, AND FOR THE SAME REASON.
     ---------------------------------------------------------------------
     These two constants describe one shape and must not be allowed to
     disagree. `arForSize` decides the ORDERED dimensions; `effAR` decides the
     preview box, which becomes `plan.canvas`, which becomes the production
     SVG's viewBox. When contour-fit moved the ordered size onto the finished
     sticker and this line was left on the artwork canvas, the order described a
     1.667 shape while the file it was rendered into was a 1.502 one.

     Measured on the real bear at 3in Standard before this was fixed:

         root      width="3in" height="1.8in"     aspect 1.667
         viewBox   0 0 398 265                    aspect 1.502
         CutContour  2.9589 x 1.6088 in  in a 3.000 x 1.800 order
         residuals   left -0.122   top 14.280   right 5.573   bottom 13.876

     The fit solves against the FINISHED dimensions, so drawing that solution
     into a canvas of a different aspect leaves the long edge unspent and the
     path hanging over one edge. Same expression, same fallbacks, one shape. */
  const effAR = CG ? (cW / cH) : sizeMode === 'custom' ? Math.max(0.2, Math.min(6, cW / cH))
    : contour ? ((cfit && cfit.aspect) || artAR || 1)
    : shp.ar;
  // Preview base size. Desktop: grows with product size (unchanged). Mobile: FROZEN and sized to the VIEWPORT
  // (never wider than the screen) so the preview's layout box can't overflow the stage — that overflow is what
  // made the centered sticker resolve off-centre to the right. Depends only on the viewport, NOT product size,
  // so changing size never resizes/re-fits/re-centers it (only widthIn/heightIn → price/payload). Aspect ratio
  // still comes from effAR (shape). Capped at 440 so it isn't huge on large phones/tablets.
  const mobilePreviewBase = Math.min(440, (typeof window !== 'undefined' && window.innerWidth ? window.innerWidth : 400) - 24);
  const szBase = isMobile ? mobilePreviewBase : 320 + Math.min(6, longestIn) * 26;
  /* ── ONE DISPLAY CONTRACT ──────────────────────────────────────────────
     THE FINISHED STICKER'S OUTER BOUNDS FIT THE SAFE FRAME. In every state.

     `w`/`h` are the finished sticker: `effAR` is the FINISHED aspect (for a
     die-cut it comes from `cfit.aspect`, artwork plus border), and
     `cfit.fitFactor` exists precisely so that after it the finished sticker —
     not the artwork canvas — spans this box. So fitting THIS box inside the
     stage is fitting the whole sticker, border and cut line included.

     What this replaces: a width-only heuristic that never saw the stage. With
     the background present the sticker is the whole rectangular photo and with
     it removed the sticker is the subject, so the two states have different
     SHAPES and different aspects — both legitimate, and both were being sized
     by a rule indifferent to the space available. That is how one state came
     out postage-stamp small and the other overflowed the stage by 26px top and
     bottom on a 205px-tall phone stage.

     SAFE_FRAME is a fraction of the measured stage, not a device constant:
     the sticker occupies the same proportion of whatever room it is given, and
     the remainder is deliberate breathing room for the border, the cut line
     and the drop shadow. */
  /* ── ONE FRACTION PER PRESENTATION, NOT ONE NUMBER ────────────────────
     0.86 was chosen on a PHONE, where the stage is about 205px tall and the
     sticker genuinely needs most of it to be legible at all. The desktop stage
     is several times that, and the same fraction made a desktop upload fill
     almost the whole checkerboard: correct by the contract, and too big to
     read as a sticker sitting on a surface.

     So the contract is unchanged -- the finished sticker still fits a fraction
     of the MEASURED stage, and the fraction is still proportional rather than
     a device constant. Only the fraction differs, and it differs on the same
     `isMobile` selector that already chooses the whole tree, so it can never
     disagree with the layout it is sizing.

     NOTHING PHYSICAL MOVES. `pxPerInch` is `max(w,h) / longestIn`, so a smaller
     box is a smaller number of pixels per inch and the ordered size is
     untouched; `borderPxFor` and `cornerPxFor` are fractions of the sticker, so
     the ring and the radius shrink with it and stay the same physical widths.
     Production geometry does not read this at all: since 10K the frame comes
     from `blueprintLongEdge(orderedIn)` and `effAR`, neither of which knows the
     window exists. That separation is what makes this a display change.

     THE PRODUCTION HOST KEEPS THE OLD FRACTION:
     * PRODUCTION_HOST  HQ renders the desktop tree, and MOTO's bench is a
       working surface where the operator is inspecting a cut line rather than
       admiring a sticker. Shrinking it there would be collateral damage to a
       system this change has no business touching.

     (The flag is named on a `*` line above because tests/builder-production-
     shell.test.js scans every line mentioning it and skips only comment lines
     in that form -- the same shape the other prose mentions of it use.) */
  const SAFE_FRAME_COMPACT = 0.86;   // phones, and the HQ/MOTO bench
  const SAFE_FRAME_DESKTOP = 0.62;   // the customer's desktop builder
  const SAFE_FRAME = (isMobile || PRODUCTION_HOST) ? SAFE_FRAME_COMPACT : SAFE_FRAME_DESKTOP;
  /* THE CONTRACT HOLDS EVEN WHEN THE STAGE IS SMALL.
     A first attempt ignored the measurement below 120px tall and fell back to
     the width-based box. That looked sensible and was not: at 320px the stage
     really is about 44px tall, so the fallback drew a 296px sticker into it
     and clipped the border by 8px top and bottom — re-creating the exact
     defect this contract exists to prevent, in the one place it was most
     needed.

     So the measurement is always honoured: the sticker fits whatever frame it
     is given. A stage collapsed to 44px is a LAYOUT defect, not a sizing one,
     and it is flagged in the QA readout rather than compensated for here. A
     small sticker is a legible bug; a clipped one is a shipped one. */
  const STAGE_SHORT_H = 120;      // below this the stage is reported as squeezed
  let w, h;
  const _stW = stageBox && stageBox.w > 0 ? stageBox.w : 0;
  const _stH = stageBox && stageBox.h > 0 ? stageBox.h : 0;
  if (_stW > 0 && _stH > 0) {
    const availW = _stW * SAFE_FRAME, availH = _stH * SAFE_FRAME;
    /* Fit the finished aspect into the safe frame, tightest axis wins. */
    const fitH = Math.min(availH, availW / Math.max(0.0001, effAR));
    h = Math.max(24, Math.round(fitH));
    w = Math.max(24, Math.round(fitH * effAR));
    if (CG && !contour) {
      /* A SHAPED GRAPHIC'S PRODUCTION FILE IS DRAWN IN THIS BOX (the shaped
         lane's viewBox is still the preview box), and the box is two
         independently rounded integers. On a 3 in sticker the resulting
         mismatch between the box's aspect and the ordered aspect is a few
         thousandths of an inch. On a 40 in graphic the same half pixel is a
         tenth of an inch. So for a cart graphic the box is the one, within 15%
         of the fitted height, whose integer sides match the ordered aspect
         most closely — often exactly. Display only gets marginally smaller;
         nothing physical is derived from how big the box is. */
      let bestH = h, bestErr = Math.abs(w / h - effAR) / effAR;
      for (let hh = h; hh >= Math.max(24, Math.floor(h * 0.85)) && bestErr > 0; hh--) {
        const ww = Math.max(24, Math.round(hh * effAR));
        const err = Math.abs(ww / hh - effAR) / effAR;
        if (err < bestErr - 1e-12) { bestErr = err; bestH = hh; }
      }
      h = bestH;
      w = Math.max(24, Math.round(bestH * effAR));
    }
  } else if (effAR >= 1) {
    /* Before the stage has been measured — the very first paint — keep the
       old box so nothing renders at zero. */
    w = Math.round(szBase);
    h = Math.round(szBase / effAR);
  } else {
    h = Math.round(szBase);
    w = Math.round(szBase * effAR);
  }
  const pxPerInch = Math.max(w, h) / longestIn;
  /* Resolved HERE, not at the top: a corner radius is a proportion of the
     sticker rendered into this box, so it needs both (D-092). */
  const shpR = shapeRadius(shape, corners, longestIn, w, h);
  /* ONE BORDER RULE, AND IT IS THE PHYSICAL ONE (SLBuilderCore.borderPxFor).
     ---------------------------------------------------------------------
     Mobile used to resolve the ring as `vfrac * max(w,h)` — a fraction of the
     PHONE'S SCREEN, with no term for the sticker's physical size. So the same
     order rendered a different border on a phone than on a desktop, and the
     mobile one was simply wrong: a Standard 0.05" ring on a 1" sticker came
     out near 9.7px where physics wanted 22px.

     That mattered far beyond the preview. `borderPxView` becomes `padPx` in
     the canonical proof geometry, so the wrong number reached the proof, the
     canonical config, and anything generated from them. A wrong-width ring is
     not a display quirk — it prints.

     The rule now lives in the core and both platforms call it. They still get
     different preview boxes (mobile's is frozen to the viewport), but the ring
     occupies the same fraction of the sticker in both, which is what makes
     them the same design.

     The old 2px floor and 10%-of-short-side cap went with it. Both were
     functions of the BOX rather than of the sticker, so they bound at
     different physical thicknesses on different screens and reinstated the
     disagreement; the cap could also silently halve a border the customer paid
     for. See SLBuilderCore.borderPxFor for the full reasoning. */
  const borderPxView = border === 'none' ? 0
    : (_szCore() && _szCore().borderPxFor
        ? _szCore().borderPxFor(thick, longestIn, w, h)
        : thick * pxPerInch);
  // Kept for the swatch-chip padding below; the RING geometry is borderPxView above.
  const borderPx = border === 'none' ? 0 : Math.max(1, Math.round(thick * pxPerInch));
  /* parseFLOAT, not parseInt: the outer radius is fractional now, and
     truncating it here would put the face ring back on the integer grid the
     outer edge just left (D-092). */
  const innerR = shpR === '50%' ? '50%' : shpR === '999px' ? '999px' : Math.max(2, parseFloat(shpR) - borderPxView).toFixed(3) + 'px';
  const borderCss = border === 'none' ? 'transparent' : border === 'custom' ? borderColor : (BORDERS.find(b => b.id === border) || {}).sw;
  const faceColorCss = faceColor === 'black' ? '#0a0c10' : faceColor === 'custom' ? faceCustom : '#ffffff';
  const contourFilter = contour ? 'drop-shadow(0 16px 24px rgba(15,23,42,0.28)) drop-shadow(0 3px 6px rgba(15,23,42,0.16))' : 'none';
  // A die-cut sticker is any custom-contour shape; its border cut line is baked into outlinedUrl.
  const dieCut = contour;

  /* ── CAN THE CUSTOMER ACTUALLY MOVE THE ARTWORK? ──────────────────────
     One predicate, because the cursor and the handlers have to agree and
     there is no way to notice when they stop.

     They had already stopped. `onFaceDown` and `onFaceTouch` both refuse a
     die cut -- there is no frame to pan inside, so panning means nothing --
     while the `movable` class was applied on `art` alone. The face therefore
     offered a grab cursor on every die-cut sticker and did nothing when you
     pulled on it: the UI advertised a capability the code had deliberately
     withheld.

     Panning is a CROP, which is why the die cut has none. `spin` refuses
     rotation for the same reason and says so in its own comment. */
  const canPanArt = !!art && !dieCut;

  const usingOutline = dieCut && border !== 'none' && !!outlinedUrl;
  const displayArt = usingOutline ? outlinedUrl : baseArt;
  /* PUBLISHED BY THE BAKE, NOT RE-DERIVED.
     `usingOutline` already requires `outlinedUrl`, and the frame is set in the
     same commit as that URL — so this can only ever read the padding that
     belongs to the picture on screen. Re-deriving it here from `artRes` would
     be wrong: the preview is downscaled to 1400px and `pad`'s absolute `+8`
     term does not survive a rescale, so the two would disagree. */
  const contourScale = (usingOutline && outlineFrame) ? outlineFrame.contourScale : 1;
  /* ONE SCALE, NAMED ONCE. The die-cut composite is drawn at `w` and then
     scaled by this; `proofGeometry` and the on-screen fit must agree about it
     or the preview fits a size the picture does not use. */
  const dieScale = dieCut ? contourScale * ((cfit && cfit.fitFactor) || 1) : 1;
  /* The painted sticker, in CSS pixels — the box times the scale times the
     fraction of the composite that carries ink. This is what has to fit. */
  const paintedExtent = (dieCut && usingOutline && outlineFrame && outlineFrame.extent)
    ? outlineFrame.extent : null;
  // geometry (die-cut sticker vs shaped)
  const shapeRad = dieCut ? '0' : contour ? Math.max(10, Math.round(Math.min(w, h) * 0.13)) + 'px' : shpR;
  const padPx = borderPxView;
  const faceRad = dieCut ? '0' : contour ? Math.max(6, Math.round(Math.min(w, h) * 0.13) - padPx) + 'px' : innerR;
  const faceBg = dieCut ? 'transparent' : faceColorCss; // shaped stickers always get a solid Sticker-color face (white by default)
  const stickerBg = border === 'none' ? faceBg : borderCss;

  /* ── THE CANONICAL PROOF GEOMETRY ──────────────────────────────────────
     ONE description of what the customer is looking at, read by BOTH the
     on-screen preview and the exported proof.

     It exists because they had drifted apart in the worst possible way: the
     screen composited border, face fill, zoom, offset, rotation and flip,
     while `_artwork_preview` on the ORDER was the raw upload URL passed
     straight through. Order #1014 shipped an "approved proof" whose bytes
     were sha1-identical to the customer's source file — same 1920x1080, same
     486,372 bytes. Nothing the customer did in the builder was in it.

     Two geometries exist and only two, because `dieCut === contour`:

       die-cut   the art IS the sticker. No border ring, no face fill, the
                 cut line is already baked into `outlinedUrl`, and the art is
                 laid out at the box WIDTH with a free height. `contourScale`
                 enlarges it to sit under the keyline.
       shaped    a border ring around a filled face, with the art letterboxed
                 (object-fit: contain) into the box.

     Anything reading this must not re-derive scale, offset or fit. That
     second approximation is exactly what produced a proof of the wrong
     picture. */
  /* `adjust` is the SECOND-PASS correction: a uniform multiplier on the
     die-cut scale and an absolute replacement for the derived centring, both
     solved from the CutContour that was actually built. Production geometry,
     never a customer value — the canonical transform still records scale 1,
     offset {0,0}, rotation 0. */
  const proofGeometry = (adjust) => ({
    boxW: Math.max(1, Math.round(w)),
    boxH: Math.max(1, Math.round(h)),
    dieCut: dieCut,
    /* Die-cut draws unclipped and unfilled; shaped clips to the ring and the
       face, and paints both. */
    padPx: dieCut ? 0 : padPx,
    shapeRad: dieCut ? '0' : shapeRad,
    faceRad: dieCut ? '0' : faceRad,
    stickerBg: dieCut ? 'transparent' : stickerBg,
    faceBg: dieCut ? 'transparent' : faceBg,
    /* How the art is sized into the box BEFORE the transform. */
    fit: dieCut ? 'width' : 'contain',
    /* The live transform, exactly as the screen applies it. `contourScale` is
       part of the die-cut scale on screen and must be part of it here.

       ── A DIE-CUT STICKER HAS NO CROP ────────────────────────────────
       The artwork IS the silhouette, so there is no frame to position it
       inside: zoom, offset and rotation do not exist for this model and are
       pinned here rather than read from state. The controls are gone too, but
       state outlives controls — `pos`, `zoom` and `rot` survive a switch from
       a circle to a die-cut — and the one thing this system must not be able
       to do is emit a cropped contour order. Pinning at the geometry means
       the preview, the proof and the production SVG all take the same
       guarantee from one place.

       `contourScale` is NOT customer zoom. It is the exact cancellation of
       the border baker's padding (slOutlineFrame), and without it the
       artwork renders small by iw/cw. It stays. */
    /* `cfit.fitFactor` shrinks the artwork so the FINISHED sticker — artwork
       plus generated border — spans the ordered size, instead of the artwork
       spanning it and the border hanging over the canvas edge. It MULTIPLIES
       contourScale rather than replacing it: the two corrections answer
       different questions (padding cancellation vs finished-size fit) and
       folding them together is how the first one was lost once already. */
    scale: (dieCut ? dieScale * ((adjust && adjust.scaleMul) || 1) : zoom),
    /* DERIVED CENTRING, NOT A CUSTOMER OFFSET. The silhouette is off-centre
       inside its own canvas, so centring the artwork would leave the STICKER
       off-centre by that asymmetry. The canonical record still carries
       offset {0,0}: the customer moved nothing. */
    x: dieCut ? ((adjust && adjust.gx != null) ? adjust.gx
        : ((cfit && w) ? w * ((cfit.artworkRectIn.x + cfit.artworkRectIn.width / 2) / cfit.finishedW - 0.5) : 0)) : pos.x,
    y: dieCut ? ((adjust && adjust.gy != null) ? adjust.gy
        : ((cfit && h) ? h * ((cfit.artworkRectIn.y + cfit.artworkRectIn.height / 2) / cfit.finishedH - 0.5) : 0)) : pos.y,
    rotDeg: dieCut ? 0 : (rot || 0),
    flipX: !!flipX,
    src: displayArt
  });

  /* ── THE PRODUCTION FRAME — ONE DERIVATION, EVERY CONSUMER (10K) ───────
     The coordinate system production geometry is BUILT in, as opposed to the
     one it is displayed in. Both axes come from the ORDER: the long edge is
     `blueprintLongEdge(orderedIn)` and the short edge is that long edge put
     through `effAR`, the FINISHED aspect. Neither term knows the window
     exists, so the frame is the same on a phone, on a desktop, and in HQ,
     which has no preview box to measure at all.

     WHY IT HAD TO STOP BEING THE PREVIEW BOX. `plan.canvas` was `round(w) x
     round(h)`, and `w`/`h` are the measured DOM stage times SAFE_FRAME. That
     box is what `buildDoubleDip` reconstructs in, what `doubleDipGeometry`
     turns into `unitsPerInch`, what the physical tolerance is converted
     through, and what the exported viewBox becomes. Measured on DEV,
     07-wave-organic, same order and BIT-IDENTICAL evidence:

         mobile   viewBox 654x454   218 units/in    30 anchors
         desktop  viewBox 703x489   234.33 units/in 33 anchors

     10C normalised the CANONICAL frame and 10D normalised the EVIDENCE
     raster, but the frame the candidates are constructed in was left on the
     preview. The canonical scale survives that - `k = D_CANON / distance` and
     `distance` is proportional to `cw`, so the canonical WIDTH collapses to an
     order-derived quantity whatever the box - but the canonical ASPECT does
     not: `ch/cw` is the ratio of two independently rounded box edges, 1.440529
     against 1.437628 here. So MOTO reconstructed identical evidence inside
     frames of different shape, and the candidate SET changed with it.

     This is deliberately the SAME expression the analysis raster already uses,
     lifted here so there is one of it rather than two that agree today. When
     the frame and the raster are the same size the mask maps to the frame
     1:1 and 10I's `kFit` is exactly 1 - its correction stays in place and
     simply has nothing left to correct, which is what it was compensating for.

     `boxW`/`boxH` are the fallback ONLY: `effAR` is unusable in no real order,
     and falling back to the box reproduces exactly the old behaviour. */
  /* A DIE-CUT CART GRAPHIC IS TRACED FROM A FINER SILHOUETTE (D-183).
     The blueprint long edge is capped at 1024 px, which on a 40 in graphic is
     26 px per inch: one pixel is wider than the whole 0.015 in size
     tolerance. The Cart Graphics engine states the long edge for its die cut
     (75 px per inch); every other order keeps the blueprint's. The frame and
     the analysis raster both read this, so they stay the same size. */
  const cgEvidenceEdge = (blueprintPx) => (CG && contour && CG.dieCutMaskLongEdge)
    ? (CG.dieCutMaskLongEdge(Math.max(widthIn, heightIn)) || blueprintPx)
    : blueprintPx;
  const productionFrame = (boxW, boxH) => {
    const PA = (typeof window !== 'undefined') && window.SLProductionArtwork;
    if (!PA || !PA.blueprintLongEdge) return null;
    const want = PA.blueprintLongEdge(Math.max(widthIn, heightIn));
    const edge = cgEvidenceEdge(want);
    if (!(edge > 0)) return null;
    const ar = (isFinite(effAR) && effAR > 0)
      ? effAR
      : ((boxW > 0 && boxH > 0) ? boxW / boxH : 1);
    return ar >= 1
      ? { w: edge, h: Math.max(1, Math.round(edge / ar)), longEdgePx: edge }
      : { w: Math.max(1, Math.round(edge * ar)), h: edge, longEdgePx: edge };
  };

  /* The CSS transform the on-screen art carries. Built from the same model the
     exporter draws from, so the two cannot describe different pictures. */
  const proofTransformCss = (g) =>
    `translate(calc(-50% + ${g.x}px), calc(-50% + ${g.y}px)) scale(${g.scale}) ` +
    `rotate(${g.rotDeg}deg) scaleX(${g.flipX ? -1 : 1})`;

  // ---- file read (captures original resolution before downscale) ----
  // PDFs can't render in an <img>; rasterize page 1 with pdf.js into a PNG we can preview + position.
  const renderPdfBytes = (bytes, name) => {
    const lib = window.pdfjsLib;
    if (!lib) return Promise.resolve();
    return lib.getDocument({
      data: bytes,
      /* the vendored pdf.js is 3.11.174: a crafted font can run script
         (CVE-2024-4367) unless eval-based font compilation is off */
      isEvalSupported: false
    }).promise.then(pdf => pdf.getPage(1)).then(page => {
      const base = page.getViewport({
        scale: 1
      });
      const longest = Math.max(base.width || 0, base.height || 0) || 1;
      let s = Math.min(6, 1400 / longest);
      if (!(s > 0)) s = 1;
      let vp = page.getViewport({
        scale: s
      });
      let cw = Math.max(1, Math.ceil(vp.width)),
        ch = Math.max(1, Math.ceil(vp.height));
      const MAXAREA = 4200000; // keep toDataURL safe on large sheets
      if (cw * ch > MAXAREA) {
        const r = Math.sqrt(MAXAREA / (cw * ch));
        s *= r;
        vp = page.getViewport({
          scale: s
        });
        cw = Math.max(1, Math.ceil(vp.width));
        ch = Math.max(1, Math.ceil(vp.height));
      }
      const cv = document.createElement('canvas');
      cv.width = cw;
      cv.height = ch;
      return page.render({
        canvasContext: cv.getContext('2d'),
        viewport: vp,
        background: 'rgba(0,0,0,0)'
      }).promise.then(() => {
        let url = '';
        try {
          url = cv.toDataURL('image/png');
        } catch (e) {
          url = '';
        }
        if (!url || url.length < 64 || url === 'data:,') {
          try {
            console.error('Stickr: PDF rasterized to an empty image', {
              cw: cw,
              ch: ch
            });
          } catch (e) {}
          return;
        }
        setArtRes({
          w: cw,
          h: ch
        });
        setArt({
          url: url,
          name: name || 'artwork.pdf'
        });
        resetArtState();
      });
    }).catch(err => {
      try {
        console.error('Stickr: PDF render failed —', err && (err.message || err.name) || err);
      } catch (e) {}
    });
  };
  const renderPdf = file => {
    file.arrayBuffer().then(buf => renderPdfBytes(new Uint8Array(buf), file.name)).catch(() => {});
  };
  /* `entryPath` records HOW this file reached the builder. It defaults to the
     direct path because three of the four callers are a file input or a drop
     zone; only the two handoff callers say otherwise. Recorded here, at the
     one place every acquisition passes through, rather than guessed later. */
  /* WHERE DID IT GET TO? (upload stage trace)
     A white screen tells you the builder died and nothing about where. These
     marks are cheap, they are the same names the reliability review asked for,
     and they turn "it crashed on upload" into a line number. They never throw:
     a diagnostic that can break the thing it is diagnosing is worse than none. */
  const slStage = (name, extra) => {
    try {
      console.info('[sl-upload] ' + name, extra || '');
      if (window.slTelemetry && window.slTelemetry.track) {
        window.slTelemetry.track('upload_stage', Object.assign({ stage: name }, extra || {}));
      }
    } catch (e) {}
  };

  const readFile = (file, entryPath) => {
    if (!file) return;
    if (!file.__slPrepared) { setUploadBusy(true); setUploadFail(null); }
    /* A phone photo (HEIC/HEIF/WEBP/AVIF) is converted to PNG in the browser first where it can
       be (SLUpload.prepareFile, WG2 24 Oct 2026); it then takes this same path as any PNG. A file
       that cannot be converted goes on unchanged, and validation names the format and the fix. */
    if (!file.__slPrepared && window.SLUpload && window.SLUpload.prepareFile && entryPath !== 'saved_design') {
      window.SLUpload.prepareFile(file).then(r => {
        const f = r.file;
        try { f.__slPrepared = true; } catch (e) {}
        if (r.converted) slStage('file_converted', { from: r.from, scaled: !!r.scaled });
        readFile(f, entryPath);
      });
      return;
    }
    slStage('file_selected', { name: file.name, type: file.type, size: file.size });
    artEntryRef.current = entryPath || 'direct_builder';
    // A freshly uploaded design starts with the standard white die-cut border (the classic sticker keyline).
    // The customer can change the colour/thickness or remove it in Style. Shared by mobile + desktop; on
    // desktop the workflow chooser may still refine this afterward (e.g. print-ready keeps no border).
    /* A cart graphic starts as a plain rectangle with no border, and a
       replacement file keeps whatever border the customer already chose. */
    if (!CG) {
      setBorder('white');
      setBsize('standard');
    }
    /* A first upload is a new design, on the product's current border widths.
       A reopened design passes through here too and keeps its own, and so
       does a replacement image: it is the same design with a new picture. */
    if (CG && entryPath !== 'saved_design' && !art) cgUseBorderTable(null);
    // The Upload engine (SLUpload) owns parsing / validation / metadata; this component keeps
    // only the visual preview pipeline below. Cache original bytes and analyze off the file.
    artBytesRef.current = null;
    artOriginalRef.current = null;
    artOriginalPxRef.current = null;
    setHalo(null);
    setResumedReview(null);
    setArtBytes(null);
    setUploadMeta(null);
    /* A NEW FILE IS A NEW DESIGN. Whatever name the previous one was saved
       under does not belong to this one, and keeping it would put somebody
       else's title on a fresh upload.

       EXCEPT WHEN THE FILE *IS* THE REOPENED DESIGN. A resume arrives through
       this same function -- that is the whole point of routing it here -- and
       the resume has already recorded the name it was saved under. Clearing
       unconditionally would wipe it microseconds after it was set, and the
       customer's chosen title would silently revert to the filename on their
       next save. */
    if (entryPath !== 'saved_design') savedNameRef.current = null;
    /* THE UPLOAD OUTCOME (see sl-builder-telemetry.js). `upload_started` fires
       from the file-input listener; nothing has ever reported what happened
       next, so "the upload failed" and "no upload was ever attempted" looked
       identical in the data — which is precisely the ambiguity that made order
       #1012 so slow to diagnose.

       Caching the bytes IS the success condition: `artBytesRef.current` is
       what the durable-asset uploader attaches at checkout, so if it is set,
       the original can be persisted. Never carries the filename. */
    const slUploadOutcome = (ok, reason) => {
      try { if (window.slTelemetry) window.slTelemetry.uploadOutcome(ok, reason); } catch (e) {}
    };
    /* Any successful selection clears a previous handoff failure — the message
       must never outlive the problem it describes. */
    setHandoffError(null);
    /* A new file is a new question: any previous "use it anyway" belonged to the
       file it was asked about (D-099). */
    setQualityAckFor(null);
    setQualityOpen(false);
    /* A new file is a new question for BOTH systems (D-103). The source
       advisory and the resolution warning acknowledge separately, and neither
       may be inherited by the file that replaces this one. */
    setSourceAckFor(null);
    setSourceOpen(false);
    /* ── DECODE IS SEQUENCED BEHIND VALIDATION ───────────────────────────
       These used to be two independent chains off the same file: validation on
       `arrayBuffer()`, and the decode below on a FileReader started immediately
       after. Nothing ordered them, so the decode raced the verdict and usually
       won — the console shows `image_decoded` before `[sl-quality]` every time.

       For an oversized raster that is not a race worth losing. A 10000x8000 PNG
       is 0.32 MB on disk and clears the byte check sixty times over, then asks
       the browser for 320 MB of RGBA plus a canvas copy before the rejection
       exists. On a phone the tab dies, and the customer is told nothing.

       `analyzeBytes` is safe to call first because it never decodes: `parsePNG`
       reads IHDR at fixed offsets and walks chunk headers without inflating
       IDAT, and `parseJPEG` scans markers to SOF and stops at SOS. A few hundred
       bytes buys the verdict. So the verdict is taken FIRST, and the decode
       happens only if the file survives it. */
    const startDecode = (f = file) => {
      const isPdf = /\.pdf$/i.test(f.name || '') || f.type === 'application/pdf';
      if (isPdf && window.pdfjsLib) { renderPdf(f); return; }
      beginRasterDecode(f);
    };
    /* OVER THE PIXEL CAP IS NOT A DEAD END (Ryan, 21 Oct 2026).
       Logo exports are routinely 80-110 megapixels while under 1 MB on disk:
       a 14646 x 7400 PNG is 108 MP. The 30 MP cap exists so the builder never
       decodes an image that size into a canvas, and it still never does: the
       image is decoded ONCE here into a bitmap, drawn into a canvas no larger
       than the cap, and from then on the builder works on that copy exactly
       as if the customer had uploaded it. The customer's own file is kept
       untouched in artOriginalRef and is what the order attaches as the
       original. A device that cannot decode the image (a phone short of
       memory) still gets the validator's message, never a silent empty
       builder. PNG keeps its transparency; JPEG stays JPEG. */
    const reduceOversize = (meta, origBytes) => {
      const md = (meta && meta.metadata) || {};
      const cap = (window.SLUpload && window.SLUpload.limits) ? window.SLUpload.limits().maxRasterPixels : 30e6;
      const isPng = md.format === 'png';
      const type = isPng ? 'image/png' : 'image/jpeg';
      const toBlob = (c, q) => new Promise(res => { try { c.toBlob(b => res(b), type, q); } catch (e) { res(null); } });
      const draw = (bmp, target) => {
        const bw = bmp.naturalWidth || bmp.width, bh = bmp.naturalHeight || bmp.height;
        const k = Math.sqrt(target / (bw * bh));
        const w = Math.max(1, Math.floor(bw * k)), h = Math.max(1, Math.floor(bh * k));
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const ctx = c.getContext('2d');
        if (!ctx) return null;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bmp, 0, 0, w, h);
        return c;
      };
      /* DECODED BY AN <img>, THE WAY beginRasterDecode DECODES. Measured: an
         ImageBitmap of the same PNG gave different alpha on 4.8% of the
         working image's pixels (transparent edges), and MOTO's silhouette is
         read from that alpha; through an <img> the result is identical to the
         builder's own path. */
      const decodeOriginal = () => new Promise((res, rej) => {
        const u = URL.createObjectURL(file);
        const im = new Image();
        im.onload = () => res({ im: im, u: u });
        im.onerror = () => { URL.revokeObjectURL(u); rej(new Error('decode_failed')); };
        im.src = u;
      });
      return decodeOriginal().then(async ({ im: bmp, u }) => {
        /* MOTO'S PICTURE COMES FROM THE ORIGINAL. The same arithmetic and the
           same draw as beginRasterDecode (MAX 1400, Math.round, default
           smoothing, PNG data URL), only from this original bitmap instead of
           the reduced copy, so no second resample sits between the
           customer's pixels and the traced cut line. */
        let oneStep = null;
        try {
          const MAX = 1400;
          const sc = Math.min(1, MAX / Math.max(bmp.naturalWidth, bmp.naturalHeight));
          const ow = Math.round(bmp.naturalWidth * sc), oh = Math.round(bmp.naturalHeight * sc);
          const oc = document.createElement('canvas');
          oc.width = ow; oc.height = oh;
          const ox = oc.getContext('2d');
          if (ox) { ox.drawImage(bmp, 0, 0, ow, oh); oneStep = { url: oc.toDataURL('image/png'), w: ow, h: oh }; }
          oc.width = 0; oc.height = 0;
        } catch (e) { oneStep = null; }
        /* the cap, less a margin; a device whose canvas refuses that tries
           the iOS canvas ceiling before giving up */
        const origPx = { w: bmp.naturalWidth, h: bmp.naturalHeight };
        let blob = null;
        for (const target of [cap * 0.98, 16e6 * 0.98]) {
          const c = draw(bmp, target);
          blob = c ? await toBlob(c, 0.95) : null;
          if (c) { c.width = 0; c.height = 0; }
          if (blob) break;
        }
        try { URL.revokeObjectURL(u); bmp.src = ''; } catch (e) {}
        if (!blob) throw new Error('reduce_failed');
        const work = new File([blob], file.name, { type: type, lastModified: file.lastModified || Date.now() });
        const workBytes = new Uint8Array(await work.arrayBuffer());
        const meta2 = window.SLUpload.analyzeBytes(workBytes, work.name, { fileSize: work.size, targetWidthIn: widthIn, targetHeightIn: heightIn });
        if (!meta2 || meta2.status === 'rejected') throw new Error('reduced_still_rejected');
        try { console.info('[sl-upload] oversize_reduced', { from: (md.widthPx || '?') + 'x' + (md.heightPx || '?'), to: (meta2.metadata ? meta2.metadata.widthPx + 'x' + meta2.metadata.heightPx : '?'), originalBytes: origBytes.length, workingBytes: workBytes.length }); } catch (e) {}
        setUploadMeta(meta2);
        artOriginalRef.current = origBytes;
        artOriginalPxRef.current = Object.assign(origPx, {
          k: (meta2.metadata && meta2.metadata.widthPx > 0) ? origPx.w / meta2.metadata.widthPx : 1 });
        artBytesRef.current = workBytes;
        setArtBytes(workBytes);
        slUploadOutcome(true);
        oneStepRef.current = oneStep ? Object.assign({ file: work }, oneStep) : null;
        startDecode(work);
      });
    };
    if (file.arrayBuffer) {
      slReadUploadBytes(file, () => file.arrayBuffer(), slStage).then(buf => {
        const bytes = new Uint8Array(buf);
        let meta = null;
        try {
          if (window.SLUpload) meta = window.SLUpload.analyzeBytes(bytes, file.name, {
            fileSize: file.size,
            targetWidthIn: widthIn,
            targetHeightIn: heightIn
          });
        } catch (e) { meta = null; }
        if (meta) setUploadMeta(meta);
        slStage('validated', { status: (meta && meta.status) || null, format: (meta && meta.metadata && meta.metadata.format) || null });
        /* REJECTED MEANS NOT DECODED. Over the byte cap, over the pixel cap,
           unsupported or corrupt — none of them reach an Image, a canvas, the
           1400px downscale or MOTO. The artwork state is left untouched, so the
           customer keeps whatever they had before this file. */
        if (meta && meta.status === 'rejected') {
          const errs = (meta.warnings || []).filter(w => w.level === 'error');
          const onlyPixels = errs.length > 0 && errs.every(w => w.code === 'image-too-many-pixels');
          const raster = meta.metadata && (meta.metadata.format === 'png' || meta.metadata.format === 'jpg' || meta.metadata.format === 'jpeg');
          if (onlyPixels && raster && typeof Image === 'function') {
            reduceOversize(meta, bytes).catch(err => {
              try { console.warn('[sl-upload] oversize_reduce_failed', err && err.message); } catch (e) {}
              slUploadOutcome(false, 'rejected');
              uploadFailed(UPLOAD_FAIL.oversize);
            });
            return;
          }
          uploadFailed((errs[0] && errs[0].message) || UPLOAD_FAIL.rejected);
          slUploadOutcome(false, 'rejected');
          return;
        }
        const accept = () => {
          artOriginalRef.current = null;
          artOriginalPxRef.current = null;
          artBytesRef.current = bytes;
          setArtBytes(bytes);            // drives the render the measurement needs
          slUploadOutcome(true);
          startDecode();
        };
        /* P-50 PROTOTYPE: a confidently found exterior grey halo is cleaned
           before anything decodes the file, so preview, MOTO and production
           all read the same cleaned pixels. Stickers only; a reopened design
           is already what it was saved as. Anything not confident is left
           exactly as uploaded. */
        const md0 = meta && meta.metadata;
        /* a reopened design brings its cleanup review back from its own file */
        if (entryPath === 'saved_design' && !CG && window.SLHaloClean && window.SLHaloClean.readReview) {
          try { setResumedReview(window.SLHaloClean.readReview(bytes)); } catch (e) {}
        }
        if (!CG && md0 && md0.format === 'png' && entryPath !== 'saved_design' && window.SLHaloClean) {
          /* Anything unexpected - a throw, a rejection, a cleaner still busy
             after 15 s - leaves the upload exactly as uploaded. `settled` makes
             sure only one outcome ever lands. */
          let settled = false;
          const asUploaded = why => { if (settled) return; settled = true; setHaloBusy(false); try { console.warn('[sl-upload] halo skipped', why); } catch (e) {} accept(); };
          const giveUp = setTimeout(() => asUploaded('timeout'), 15000);
          setHaloBusy('check');
          window.SLHaloClean.cleanPngBytes(bytes, { onStage: s => { if (!settled && s === 'clean') setHaloBusy('clean'); } }).then(r => {
            if (settled) return;
            clearTimeout(giveUp);
            setHaloBusy(false);
            try { console.info('[sl-upload] halo', { changed: r.changed, reason: r.analysis && r.analysis.reason, review: r.review || null, ms: r.ms, where: r.where, workerFailed: r.workerFailed }); } catch (e) {}
            if (!r || !r.changed) { settled = true; accept(); return; }
            try {
              const work = new File([r.bytes], file.name, { type: 'image/png', lastModified: file.lastModified || Date.now() });
              artOriginalRef.current = bytes;
              artOriginalPxRef.current = { w: md0.widthPx, h: md0.heightPx, k: 1 };
              artBytesRef.current = r.bytes;
              setArtBytes(r.bytes);
              setHalo({ on: true, changed: r.changed, cleaned: work, original: file, cleanedBytes: r.bytes, originalBytes: bytes, px: { w: md0.widthPx, h: md0.heightPx }, review: r.review || null });
              slStage('halo_cleaned', { px: r.changed, ms: r.ms, review: r.review ? r.review.reason : null });
              slUploadOutcome(true);
              startDecode(work);
              settled = true;
            } catch (eApply) {
              /* applying the cleaned file failed part-way: undo it and take the upload as it came */
              setHalo(null);
              asUploaded('apply_failed: ' + String((eApply && eApply.message) || eApply));
            }
          }).catch(err => { clearTimeout(giveUp); asUploaded(String((err && err.message) || err)); });
          return;
        }
        accept();
      }).catch(e => {
        /* only a failed READ gets the read message; anything else keeps the old outcome */
        if (e && e.slReadFailed) {
          slStage('bytes_read_failed', { reason: String(e.message || e) });
          uploadFailed(UPLOAD_FAIL.unreadable);
        }
        slUploadOutcome(false, 'unknown');
      });
    } else {
      /* No `arrayBuffer` means the original bytes are unreachable on this
         browser, so nothing durable can ever be attached for this file. That
         is a failed upload, not a silent one — and with no bytes there is
         nothing to validate, so nothing is decoded either. */
      slUploadOutcome(false, 'unsupported');
    }
    return;
  };

  /* The raster decode + 1400px working downscale, unchanged except that it is
     now REACHED only after validation has accepted the file. */
  const beginRasterDecode = (file, opts) => {
    if (!file) return;
    /* `keep`: the same design with its picture swapped (P-50 Restore original /
       clean again), so zoom, position and the chosen workflow stay as they are. */
    const keep = !!(opts && opts.keep);
    const r = new FileReader();
    r.onload = e => {
      slStage('file_read', { bytes: (e.target.result || '').length });
      const img = new Image();
      img.onload = () => {
        slStage('image_decoded', { w: img.naturalWidth, h: img.naturalHeight });
        setArtRes({
          w: img.naturalWidth,
          h: img.naturalHeight
        });
        const MAX = 1400;
        const scale = Math.min(1, MAX / Math.max(img.naturalWidth, img.naturalHeight));
        let url = e.target.result;
        const fromOriginal = (oneStepRef.current && oneStepRef.current.file === file) ? oneStepRef.current : null;
        if (fromOriginal) {
          oneStepRef.current = null;
          url = fromOriginal.url;
          slStage('image_downscaled', { w: fromOriginal.w, h: fromOriginal.h, from: 'original' });
        } else if (scale < 1) {
          /* THE DOWNSCALE IS OPTIONAL, SO IT MAY NOT BE FATAL.
             `getContext` returns null when the browser refuses another canvas
             (memory pressure, too many contexts), and `toDataURL` throws on an
             oversized surface. Both were unguarded, so a large upload on a
             tight device lost the whole file rather than merely its
             optimisation. The original data URL is a perfectly good preview —
             bigger, but correct — so a failure here degrades instead of
             failing. */
          try {
            const cw = Math.round(img.naturalWidth * scale),
              ch = Math.round(img.naturalHeight * scale);
            const cv = document.createElement('canvas');
            cv.width = cw;
            cv.height = ch;
            const cx = cv.getContext('2d');
            if (!cx) throw new Error('no 2d context');
            cx.drawImage(img, 0, 0, cw, ch);
            url = cv.toDataURL('image/png');
            slStage('image_downscaled', { w: cw, h: ch });
          } catch (dsErr) {
            slStage('image_downscale_failed', { reason: String(dsErr && dsErr.message || dsErr) });
          }
        }
        /* A NAME THE PLATFORM MAY NOT HAVE GIVEN US.
           The PDF and resumed-design paths already default this; the main image
           path passed `file.name` straight through, and an iPhone can hand back
           a File with an empty name (camera capture, a pasted image, some
           share-sheet and Files-provider paths). That empty string used to
           travel all the way to the order guard as "no artwork". The cart
           boundary no longer depends on it, but the label should still be
           right, so it is filled in at the point of acquisition too. */
        const artName = (file && file.name ? String(file.name).trim() : '') || 'artwork.png';
        setArt({
          url,
          name: artName
        });
        if (!keep) resetArtState();
        slStage('artwork_state_created', { name: artName });
      };
      /* THE BROWSER COULD NOT DECODE IT (D-100).
         An iPhone photo can arrive as HEIC/HEIF, which most browsers will not
         render. Showing an undecodable image as if it loaded produced a blank
         sticker with no explanation, so the reason is named — and named
         ACCURATELY: an unsupported container is a format problem, not a
         transfer problem and not a quality problem. */
      img.onerror = () => {
        const ext = (String(file.name || '').split('.').pop() || '').toLowerCase();
        const known = ['png', 'jpg', 'jpeg', 'svg', 'pdf'];
        setHandoffError(known.indexOf(ext) === -1 ? 'format' : 'decode');
        setUploadBusy(false);
        setUploadFail(known.indexOf(ext) === -1
          ? 'This photo format can’t be opened here. Save it as a PNG or JPG and upload that.'
          : 'We received the file but couldn’t open it. Try saving it again as a PNG or JPG.');
        try { console.warn('[sl-handoff] decode failed', { name: file.name, type: file.type, ext: ext }); } catch (x) {}
        setArt(null);
        setArtRes(null);
        resetArtState();
      };
      img.src = e.target.result;
    };
    r.readAsDataURL(file);
  };
  /* P-50: Restore original / clean again. No confirmation: one tap swaps the
     working picture between the cleaned and the untouched file, and back. The
     order always carries the untouched file as the original; when the original
     itself is the working picture there is no separate original to keep. */
  const toggleHalo = () => {
    const h = halo;
    if (!h) return;
    const on = !h.on;
    const b = on ? h.cleanedBytes : h.originalBytes;
    artBytesRef.current = b;
    setArtBytes(b);
    artOriginalRef.current = on ? h.originalBytes : null;
    artOriginalPxRef.current = on ? { w: h.px.w, h: h.px.h, k: 1 } : null;
    setHalo(Object.assign({}, h, { on }));
    slStage(on ? 'halo_reapplied' : 'halo_restored', { px: h.changed });
    beginRasterDecode(on ? h.cleaned : h.original, { keep: true });
  };
  const haloNote = (cls) => halo ? /*#__PURE__*/React.createElement("span", { className: cls, "data-sl-halo": halo.on ? 'cleaned' : 'original' },
    halo.on ? 'Grey edge cleaned' : 'Original edge', ' · ',
    /*#__PURE__*/React.createElement("button", { type: "button", className: "pb-halo-link", onClick: toggleHalo,
      style: { background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' } },
      halo.on ? 'Restore original' : 'Clean edge')) : null;
  const resetArtState = () => {
    setZoom(1);
    setPos({
      x: 0,
      y: 0
    });
    setRot(0);
    setFlipX(false);
    setWorkflow(null);
  };
  const onDrop = e => {
    e.preventDefault();
    setDrag(false);
    readFile(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  useEffect(() => { if (art) { setUploadBusy(false); setUploadFail(null); } }, [art]);
  useEffect(() => {
    if (!uploadBusy) return undefined;
    const t = setTimeout(() => {
      setUploadBusy(false);
      setUploadFail(f => f || 'We couldn’t open this file. Try again, or save it as a PNG or JPG first.');
      try { console.warn('[sl-upload] upload stalled: no preview after 25 s'); } catch (e) {}
    }, 25000);
    return () => clearTimeout(t);
  }, [uploadBusy]);
  // ---- product hint from Home page / My Sticker Lab: 'sheet' routes Continue straight to the sheet workspace ----
  /* THE CATEGORY ALREADY CHOSEN IS KEPT (WG2, Ryan 24 Oct 2026). A customer who picked
     "Individual Sticker" or "Sticker Sheet" on the Home page has answered "How should we make
     it?" already, so their upload goes straight into that category instead of showing the
     chooser again. The generic hero upload carries no hint and still meets the chooser. */
  const [hintWf, setHintWf] = useState(null);
  useEffect(() => {
    try {
      const h = sessionStorage.getItem('stickr_product_hint');
      if (h === 'sheet' && !CG && !PROMO) setSheetIntent(true);
      if (!CG && !PROMO && (h === 'sheet' || h === 'single')) setHintWf(h === 'sheet' ? 'sheet' : 'photo');
      if (h) sessionStorage.removeItem('stickr_product_hint');
    } catch (e) {}
  }, []);

  /* ---- artwork handed in from the Home page (D-098) --------------------
     Home parks the customer's actual FILE in IndexedDB and navigates; this
     picks it up and feeds it to `readFile` - the SAME function the in-builder
     file input calls. Convergence is the point: this block used to be a second
     upload implementation, decoding base64, caching bytes, branching on PDF and
     loading the image itself, all in parallel with `readFile`. Two copies of
     one path is how they drift.

     `take()` deletes as it reads, so a refresh, a back button or a second
     design cannot pick the same file up twice.

     The legacy sessionStorage record is still honoured for one release: a
     customer mid-flow when this deployed has one parked, and it is small by
     definition - anything large is exactly what used to fail. */
  useEffect(() => {
    let cancelled = false;

    const fromLegacySession = () => {
      let raw = null;
      try { raw = sessionStorage.getItem('stickr_incoming_art'); } catch (e) {}
      if (!raw) return false;
      try { sessionStorage.removeItem('stickr_incoming_art'); } catch (e) {}
      let rec = null;
      try { rec = JSON.parse(raw); } catch (e) { return false; }
      if (!rec || !rec.url) return false;
      try {
        const i = rec.url.indexOf(',');
        const meta = rec.url.slice(0, i);
        const bin = atob(rec.url.slice(i + 1));
        const arr = new Uint8Array(bin.length);
        for (let k = 0; k < bin.length; k++) arr[k] = bin.charCodeAt(k);
        const mime = (meta.match(/data:([^;]+)/) || [])[1] || '';
        readFile(new File([arr], rec.name || 'artwork', { type: mime }), 'homepage_handoff');
        return true;
      } catch (e) { return false; }
    };

    const HO = window.SLUploadHandoff;
    if (!HO || !HO.available()) { fromLegacySession(); return; }

    /* PEEK, RESTORE, AND ONLY THEN CONSUME (D-100).
       This used to  — read and delete in one transaction. If anything
       downstream then failed, the pending upload was already destroyed and
       there was nothing to retry with. Ownership now transfers only after the
       canonical path has actually accepted the file. */
    HO.peek().then(function (rec) {
      if (cancelled) return;
      if (!rec) { fromLegacySession(); return; }
      const file = HO.toFile(rec);
      if (!file) {
        try { console.warn('[sl-handoff] restore: could not rebuild a file from the stored record'); } catch (e) {}
        setHandoffError('rebuild');
        return;                       // the record survives, so a retry is possible
      }
      try { console.info('[sl-handoff] restored', { name: rec.name, type: rec.type, size: rec.size, stored: rec.stored || null }); } catch (e) {}
      try {
        readFile(file, 'homepage_handoff');
      } catch (e) {
        setHandoffError('decode');
        return;                       // keep the record; the customer can retry
      }
      /* Accepted. Now it is the builder's. */
      HO.consume().catch(function () {});
    }).catch(function (e) {
      if (cancelled) return;
      try { console.warn('[sl-handoff] restore failed', e && e.stage, e && e.message); } catch (x) {}
      if (!fromLegacySession()) setHandoffError('transfer');
    });
    return () => { cancelled = true; };
  }, []);

  /* ---- a design prepared in ChatGPT -----------------------------------
     The customer described their sticker to ChatGPT, our app parked it behind
     a single-use token, and they arrived at ?sl_handoff=<token>.

     THE SAME TWO SEAMS AS EVERY OTHER ARRIVAL. Artwork goes through
     `readFile`, which is what the file input calls, so a ChatGPT design gets
     the identical validation, DPI analysis and byte-caching a walk-in design
     gets. Options go through the ordinary setters, which is what the Edit
     Design path below does. This effect owns no state of its own and is not a
     third implementation of either.

     ORDER MATTERS, ONCE. `readFile` resets the border to white/standard for
     every fresh upload, so the customer's border has to be applied AFTER it or
     the default would silently win over what they actually asked for.

     EVERY FAILURE LANDS IN THE ORDINARY BUILDER. `restore()` never rejects; an
     expired or spent token sets a message and changes nothing else, so the
     customer designs their sticker from scratch instead of meeting an error. */

  /* The path a redeemed handoff already answered, waiting for the artwork it
     belongs to. A REF, not state: it is a message between two effects, and
     nothing renders from it -- so it must not cause a render of its own, and
     it must survive the re-render the artwork triggers. Null means either no
     handoff or one that did not resolve a path, and both mean the gateway
     asks its question normally. */
  const handoffPathRef = useRef(null);
  /* The same message for the other arrival that knows its answer: a saved
     design being reopened. See the resume effect below. */
  const resumePathRef = useRef(null);

  useEffect(() => {
    const HO = window.SLChatGPTHandoff;
    if (!HO || !HO.available()) return;
    let cancelled = false;

    HO.restore().then(r => {
      if (cancelled || !r || r.state === 'none') return;

      if (r.state === 'failed') {
        /* 'empty' means the handoff arrived carrying nothing usable. There is
           no prepared design to mourn and no action for the customer to take,
           so it is not worth a red message on a builder that works. */
        if (r.reason !== 'empty') setHandoffError(r.reason === 'expired' ? 'chatgpt_expired' : 'chatgpt');
        return;
      }

      if (r.file) {
        try { readFile(r.file, 'chatgpt_handoff'); }
        catch (e) { setHandoffError('decode'); }
      }

      const o = r.options || {};

      /* ── THE QUESTION THEY HAVE ALREADY ANSWERED ──────────────────────
         `choosingWorkflow` is true whenever there is artwork and `workflow`
         is still null, so artwork arriving from ChatGPT raised the "How
         should we make it?" gateway on top of a design that was already
         fully described. The rocket was visible IN the gateway cards --
         they render `art.url` -- while the die-cut choice it had already
         been given sat unread. Nothing was broken; the handoff simply never
         set the one piece of state that says the question is answered.

         THE SAME DOOR THE CARDS USE. `chooseWorkflow` is what `onPick`
         calls, so the path is entered exactly as a click enters it -- same
         state, same defaults, same tour handling. Reproducing its effects
         here would be a second implementation of the transition, and the
         two would drift.

         WHAT COUNTS AS RESOLVED. A stated `shape` decides it outright:
         circle and square are the shaped path, and anything else is the
         die cut this builder already defaults to. When no shape was stated
         the answer is the default product, which is where the builder opens
         anyway -- so this picks nothing the customer did not already have,
         and the Shape control beside them still changes it in one click.

         AN ARTWORK-ONLY HANDOFF IS NOT RESOLVED. Someone who sent a picture
         and said nothing about how to make it genuinely has not answered
         the question, so they still get asked it. That is the one case this
         must not swallow.

         BEFORE THE OPTIONS, DELIBERATELY. `chooseWorkflow` applies each
         path's own defaults -- the die cut sets a white standard border --
         so the customer's stated options have to land after it, exactly as
         they already land after `readFile` for the same reason. */
      const resolvedPath = Object.keys(o).length
        ? (o.shape === 'circle' || o.shape === 'square' ? 'shaped' : 'photo')
        : null;

      /* ── RECORDED NOW, APPLIED WHEN THE ARTWORK LANDS ─────────────────
         Calling `chooseWorkflow` here does not survive. `readFile` is
         asynchronous -- FileReader, then an Image decode -- and when the
         picture finally arrives its handler runs `setArt(...)` followed by
         `resetArtState()`, which ends with `setWorkflow(null)`.

         So the sequence was: choose the path, then have the artwork wipe it
         milliseconds later. Both landed in the same commit: `art` truthy and
         `workflow` null, which is precisely the gateway's open condition.
         The customer saw "How should we make it?" over a design that had
         already answered it -- and the production logs showed the handoff
         redeeming with all six options, because nothing had gone wrong with
         the handoff at all.

         The path is therefore parked here and applied by the layout effect
         beside `chooseWorkflow`, which waits for the artwork to settle. That
         is the same shape the production-host effect already uses, and for
         the same reason. */
      handoffPathRef.current = resolvedPath;

      if (o.shape) setShape(o.shape);
      if (o.corners) setCorners(o.corners);
      if (o.material) setMaterial(o.material);
      if (o.finish) setLaminate(o.finish);
      if (o.sizeMode === 'preset' && o.sizePreset != null) {
        setSizeMode('preset');
        setSize(o.sizePreset);
      } else if (o.sizeMode === 'custom') {
        setSizeMode('custom');
        if (o.widthIn != null) setCustomW(String(o.widthIn));
        if (o.heightIn != null) setCustomH(String(o.heightIn));
        /* Two dimensions is an explicit aspect. Leaving the proportion lock on
           would re-derive the second from the first and quietly discard one of
           the two numbers the customer gave us. */
        if (o.widthIn != null && o.heightIn != null) setLockAR(false);
      }
      if (o.quantity != null) {
        if (o.qtyMode === 'preset') { setQtyMode('preset'); setQty(o.quantity); }
        else { setQtyMode('custom'); setCustomQty(String(o.quantity)); }
      }
      /* After readFile, deliberately. */
      if (o.border) setBorder(o.border);
      if (o.borderSize) setBsize(o.borderSize);
      if (o.borderColor) setBorderColor(o.borderColor);

      /* Options arrived, artwork did not: the builder is configured and the
         upload panel is already what the customer sees, so this only has to
         explain why. */
      if (r.artworkMissing) setHandoffError('chatgpt_artwork');
    }).catch(() => {});

    return () => { cancelled = true; };
  }, []);

  // ---- Edit Design → restore a saved design exactly where they left off ----
  useEffect(() => {
    let raw = null;
    try {
      raw = localStorage.getItem('stickr_resume_design');
    } catch (e) {}
    if (!raw) return;
    /* A SAVED DESIGN REOPENS IN THE BUILDER FOR ITS OWN PRODUCT (D-177).
       Read before the record is consumed: a cart graphic that lands on the
       sticker builder (or the reverse) is sent to the right route with the
       record still waiting for it, rather than being forced through size and
       price rules that are not its own. A record with no product type is a
       sticker design from before this existed, and it is treated as one. */
    {
      let peek = null;
      try { peek = JSON.parse(raw); } catch (e) {}
      const recIsCart = !!(peek && peek.state && peek.state.productType === 'cart_graphics');
      if (recIsCart !== !!CG) {
        try { window.location.replace(recIsCart ? SL_ROUTE_CART_GRAPHICS : SL_ROUTE_STICKERS); } catch (e) {}
        return;
      }
    }
    try {
      localStorage.removeItem('stickr_resume_design');
    } catch (e) {}
    let rec;
    try {
      rec = JSON.parse(raw);
    } catch (e) {
      return;
    }
    const st = rec && rec.state;
    if (!st || !st.artUrl) return;
    /* THE DESIGN KEEPS ITS IDENTITY. Saving again updates this row instead of
       making a second one, which is the whole difference between "Use Design"
       and "start over with the same picture". */
    if (rec.designId) saveIdRef.current = rec.designId;
    if (rec.name) savedNameRef.current = rec.name;
    /* A DESIGN SAVED AS A SHEET REOPENS AS A SHEET, with the sheet size and
       the number of sheets it was saved with. 'sheet' is how the record says
       so; it is not a workflow the builder runs, so it is turned back into
       the sheet intent over the sticker's own path. */
    const sheetSaved = st.workflow === 'sheet';
    const savedPath = (sheetSaved || !st.workflow)
      ? ((st.shape && st.shape !== 'custom') ? 'shaped' : 'photo') : st.workflow;
    /* A REOPENED DESIGN HAS ALREADY ANSWERED "How should we make it?".
       `readFile` clears `workflow` when the artwork lands, so the answer is
       parked here and put back by the layout effect beside `chooseWorkflow`.
       It is put back as-is, NOT through `chooseWorkflow`, whose defaults
       would overwrite the border and shape the customer saved. */
    resumePathRef.current = savedPath;
    // apply saved settings
    const applySaved = () => {
    setWorkflow(savedPath);
    if (sheetSaved) {
      setSheetIntent(true);
      const offered = !!(st.sheetSize && window.SLSheets && window.SLSheets.isOffered && window.SLSheets.isOffered(st.sheetSize));
      const restoreSize = offered ? st.sheetSize : ((window.SLSheets && window.SLSheets.DEFAULT_SHEET_SIZE_ID) || '8.5x10');
      setSheetSizeId(restoreSize);
      if (st.numSheets > 0) setNumSheets(Math.max(slSheetMin(restoreSize), st.numSheets));
    }
    if (st.shape) setShape(st.shape);
    if (st.corners) setCorners(st.corners);
    if (st.size) setSize(st.size);
    if (st.sizeMode) setSizeMode(st.sizeMode);
    if (st.customW != null) setCustomW(st.customW);
    if (st.customH != null) setCustomH(st.customH);
    if (st.material) setMaterial(st.material);
    if (st.laminate) setLaminate(st.laminate);
    if (st.border) setBorder(st.border);
    if (st.borderColor) setBorderColor(st.borderColor);
    if (st.bsize) setBsize(st.bsize);
    if (st.qty) setQty(st.qty);
    if (st.qtyMode) setQtyMode(st.qtyMode);
    if (st.customQty != null) setCustomQty(st.customQty);
    if (st.faceColor) setFaceColor(st.faceColor);
    if (st.faceCustom) setFaceCustom(st.faceCustom);
    if (CG) {
      /* The cart-graphic half of the record. Sizes are restored as text, the
         way the fields hold them; the lock decides Rectangle vs Square and
         Oval vs Circle; the preview panel and placement come back as saved. */
      setSizeMode('custom');
      if (st.customW != null) setCustomW(CG.fmtIn(st.customW) || String(st.customW));
      if (st.customH != null) setCustomH(CG.fmtIn(st.customH) || String(st.customH));
      if (st.lockAR != null) setLockAR(!!st.lockAR);
      else if (st.customW != null && st.customH != null) setLockAR(st.shape === 'custom' || Math.abs(st.customW - st.customH) < 1e-6);
      if (st.qty) cgSetQty(+st.qty);
      else setQtyMode('preset');
      if (st.cartPanelW != null) setCgPanelW(CG.fmtIn(st.cartPanelW));
      if (st.cartPanelH != null) setCgPanelH(CG.fmtIn(st.cartPanelH));
      setCgPlace(st.cartPlace && isFinite(+st.cartPlace.x) && isFinite(+st.cartPlace.y) ? { x: +st.cartPlace.x, y: +st.cartPlace.y } : null);
      setLaminate(CG.resolveFinish(st.laminate));
      /* the border keeps the physical width it was designed with */
      cgUseBorderTable(st);
    }
    };
    applySaved();

    /* ── THE ARTWORK COMES BACK AS BYTES, NOT AS A PICTURE ────────────────
       A resumed design used to arrive as a URL in an <img>. It looked right
       and could not be bought: `artBytesRef` stayed empty, and the original
       file is a HARD requirement at add-to-cart — the same requirement that
       orders #1012 and #1013 failed. It could not be re-saved either.

       So the bytes are fetched and pushed through `readFile`, the exact path
       an upload takes. Validation, the decode, the working downscale, the
       measurement and the byte cache all happen once, in one place, and a
       resumed design is indistinguishable from an uploaded one downstream.

       `readFile` resets the border to the new-upload default, so the saved
       settings are applied again after it. */
    const resumeFromBytes = () => {
      if (typeof fetch !== 'function') return Promise.reject();
      /* SAME-ORIGIN AND CREDENTIALED. The artwork route is served through the
         Shopify App Proxy, which is what proves whose artwork this is; the
         browser carries no key of its own. */
      return fetch(st.artUrl, { credentials: 'same-origin', cache: 'no-store' })
        .then(res => { if (!res.ok) throw new Error('artwork ' + res.status); return res.blob(); })
        .then(blob => {
          if (!blob || !blob.size) throw new Error('empty artwork');
          const name = st.artName || 'artwork.png';
          let file;
          try { file = new File([blob], name, { type: blob.type || 'application/octet-stream' }); }
          catch (e) { file = blob; file.name = name; }
          readFile(file, 'saved_design');
          applySaved();          // the saved look wins over the upload defaults
        });
    };

    // load the artwork
    const showFromUrl = () => {
    const img = new Image();
    const done = () => {
      /* A RESUMED design, not a new acquisition. It still makes artwork
         available to the builder — so it still reports the outcome, and a
         session that resumes and buys stays nested under the stage above it —
         but it is named apart so it can never be counted as an upload. */
      artEntryRef.current = 'saved_design';
      /* Nothing cleared `workflow` on this route, so the parked answer is
         not needed, and must not wait around for the NEXT upload. */
      resumePathRef.current = null;
      setArt({
        url: st.artUrl,
        name: st.artName || 'artwork.png'
      });
      setZoom(1);
      setPos({
        x: 0,
        y: 0
      });
      setRot(0);
      setFlipX(false);
    };
    img.onload = () => {
      setArtRes({
        w: img.naturalWidth,
        h: img.naturalHeight
      });
      done();
    };
    img.onerror = () => {
      setArtRes(null);
      done();
    };
    img.src = st.artUrl;
    };

    /* IF THE BYTES CANNOT BE HAD, THE DESIGN STILL OPENS — visibly, and
       honestly. The customer sees their design and can change everything
       about it; what they cannot do is order it without re-uploading, which
       is exactly what the add-to-cart guard already tells them. Showing
       nothing would be worse, and pretending would be worse still. */
    resumeFromBytes().catch(() => { showFromUrl(); });
  }, []);

  // ---- pan / zoom / rotate / flip ----
  const startPan = (cx, cy) => {
    dragging.current = true;
    const o = {
      ...pos
    };
    const move = (mx, my) => {
      if (!movedRef.current && Math.abs(mx - cx) + Math.abs(my - cy) > 8) movedRef.current = true; // real drag, not a tap
      setPos({ x: o.x + (mx - cx), y: o.y + (my - cy) });
    };
    const mm = ev => move(ev.clientX, ev.clientY),
      tm = ev => {
        // lock page scroll while the customer is actively dragging their artwork
        if (ev.touches[0]) { if (ev.cancelable) ev.preventDefault(); move(ev.touches[0].clientX, ev.touches[0].clientY); }
      };
    const stop = () => {
      dragging.current = false;
      panStopRef.current = null;
      window.removeEventListener('mousemove', mm);
      window.removeEventListener('mouseup', stop);
      window.removeEventListener('touchmove', tm);
      window.removeEventListener('touchend', stop);
    };
    panStopRef.current = stop;
    window.addEventListener('mousemove', mm);
    window.addEventListener('mouseup', stop);
    window.addEventListener('touchmove', tm, {
      passive: false
    });
    window.addEventListener('touchend', stop);
  };
  const onFaceDown = e => {
    /* No artwork, or a die-cut sticker, which cannot be panned: there is no
       frame to pan it inside. Guarded here as well as at the binding site,
       because a handler that is merely unbound is one refactor away from
       being bound again -- and read from `canPanArt`, which is also what
       draws the cursor, so the affordance cannot outlive the capability. */
    if (!canPanArt) return;
    e.preventDefault();
    startPan(e.clientX, e.clientY);
  };
  // Touch on the artwork: 1 finger = drag (existing), 2 fingers = pinch-zoom + rotate. Touch-only — the
  // desktop mouse path (onFaceDown / wheel) is untouched. Gesture math lives in SLBuilderCore (D-043);
  // updates flow through setZoom/setPos/setRot → the canonical transform (normalized) via the D-042 sync.
  const onFaceTouch = e => {
    /* Same rule as the mouse, from the same predicate, and it covers BOTH
       touch gestures: the one-finger pan below and the two-finger
       pinch-zoom/rotate. All three are crop operations, and a die-cut
       sticker has nothing to crop. */
    if (!canPanArt) return;
    const CORE = window.slBuilder || {};
    const clampZ = CORE.clampZoom || (z => Math.min(4, Math.max(0.4, z)));
    const pinchZ = CORE.pinchZoom || ((b, s, d) => clampZ(b * (d / s)));
    const spinR = CORE.spinRotate || ((b, dd) => ((b + dd) % 360 + 360) % 360);
    if (e.touches.length >= 2) {
      if (e.cancelable) e.preventDefault();
      if (panStopRef.current) panStopRef.current();   // a 2nd finger ends the one-finger pan: only one gesture at a time
      const a0 = e.touches[0], b0 = e.touches[1];
      const d0 = Math.hypot(b0.clientX - a0.clientX, b0.clientY - a0.clientY) || 1;
      const ang0 = Math.atan2(b0.clientY - a0.clientY, b0.clientX - a0.clientX);
      const z0 = zoom, r0 = rot;
      dragging.current = true;
      const tm = ev => {
        if (!ev.touches || ev.touches.length < 2) return;
        if (ev.cancelable) ev.preventDefault();
        movedRef.current = true; // a pinch/rotate is a real gesture, never a tap-to-close
        const a = ev.touches[0], b = ev.touches[1];
        const d = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY) || 1;
        const ang = Math.atan2(b.clientY - a.clientY, b.clientX - a.clientX);
        setZoom(pinchZ(z0, d0, d));
        setRot(spinR(r0, (ang - ang0) * 180 / Math.PI));
      };
      const stop = ev => {
        if (ev.touches && ev.touches.length >= 1) return;   // fingers still down
        dragging.current = false;
        window.removeEventListener('touchmove', tm);
        window.removeEventListener('touchend', stop);
        window.removeEventListener('touchcancel', stop);
      };
      window.addEventListener('touchmove', tm, { passive: false });
      window.addEventListener('touchend', stop);
      window.addEventListener('touchcancel', stop);
      return;
    }
    if (!e.touches[0]) return;
    if (e.cancelable) e.preventDefault();
    startPan(e.touches[0].clientX, e.touches[0].clientY);
  };
  const bumpZoom = d => { if (dieCut) return; setZoom(z => Math.min(4, Math.max(0.4, +(z + d).toFixed(2)))); };
  const fitArt = () => {
    setZoom(1);
    setPos({
      x: 0,
      y: 0
    });
    setRot(0);
  };
  /* Rotation is a crop operation as this builder implements it: the preview
     box aspect comes from the UNROTATED artwork (`effAR`) and the proof canvas
     never grows, so rotating a contour design pushes it outside both. Rather
     than allow that, contour rotation is refused until it can rotate the
     sticker, the border and the frame as one unit — which changes the ordered
     size and is therefore a separate, deliberate decision. */
  const spin = d => { if (dieCut) return; setRot(r => ((r + d) % 360 + 360) % 360); };

  // Keep the whole sticker visible & centered in the canvas on any screen size.
  // Desktop canvas is large enough → scale stays 1; small phones scale it down to fit.
  const [fitScale, setFitScale] = useState(1);
  React.useLayoutEffect(() => {
    const cv = canvasRef.current;
    if (!cv || !art) {
      setFitScale(1);
      return;
    }
    const compute = () => {
      const cr = cv.getBoundingClientRect();
      const availW = cr.width - 28,
        availH = cr.height - 28;
      if (availW <= 0 || availH <= 0 || !w || !h) return;
      /* FIT THE PAINTED STICKER, NOT ITS BOX. The box is the artboard; the
         sticker is what has ink on it, and for a die-cut those differ by the
         composite's own margin times `dieScale`. Fitting the box let a thick
         border reach the edge while the box still looked comfortable. */
      const pw = paintedExtent ? w * dieScale * paintedExtent.wFrac : w;
      const ph = paintedExtent ? h * dieScale * paintedExtent.hFrac : h;
      setFitScale(Math.max(0.2, Math.min(1, availW / Math.max(1, pw), availH / Math.max(1, ph))));
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [w, h, art, surface, dieScale, paintedExtent]);
  useEffect(() => {
    const el = faceRef.current;
    if (!el) return;
    const onWheel = e => {
      if (!art) return;
      /* Not for die-cut. Wheel zoom means "zoom the artwork inside the frame",
         and the frame does not exist here. Returning WITHOUT preventDefault so
         the page scrolls normally over a contour preview, which is what a
         customer expects from a control that is not there. */
      if (dieCut) return;
      e.preventDefault();
      setZoom(z => Math.min(4, Math.max(0.4, +(z - e.deltaY * 0.0016).toFixed(3))));
    };
    el.addEventListener('wheel', onWheel, {
      passive: false
    });
    return () => el.removeEventListener('wheel', onWheel);
  }, [art, dieCut]);
  useEffect(() => {
    if (!shownArt) return;
    const im = new Image();
    im.onload = () => {
      if (im.naturalHeight > 0) setArtAR(im.naturalWidth / im.naturalHeight);
    };
    im.src = shownArt;
  }, [shownArt]);
  /* ── THE ARTWORK'S REAL EXTENT ─────────────────────────────────────────
     The opaque bounding box, which is the sticker; the canvas, which is what
     everything used to measure, is the file. On the bear those differ by 20px
     on one side and 27 on the other, and the ordered size inherited the whole
     difference.

     Measured ONCE PER UPLOAD and cached in state, not recomputed per render:
     it depends on the pixels alone, so border, size and shape changes must not
     re-scan a 1.4-megapixel image. Failure leaves it null and every consumer
     falls back to the artwork canvas, which is exactly today's behaviour. */
  useEffect(() => {
    if (!shownArt) { setArtSil(null); setSilFor(null); return; }
    let cancelled = false;
    /* EVERY WAY OUT OF THIS EFFECT IS A RESULT, including the ones that give
       up. A refusal still ANSWERS the question "has this picture been
       measured?" — so it must publish its source too, or the order gate would
       wait forever on artwork whose silhouette can never be read. */
    const settle = (v) => { if (cancelled) return; setArtSil(v); setSilFor(shownArt); };
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => {
      if (cancelled) return;
      const iw = im.naturalWidth, ih = im.naturalHeight;
      if (!(iw > 0) || !(ih > 0)) { settle(null); return; }

      /* ── ONE READ, AND WHY IT STAYED ONE READ ────────────────────────────
         This is a single getImageData over the whole upload, and on a
         5040x3360 phone photo that is a 68MB copy. It was split into strips
         with a yield between them, on the theory that the main thread would
         get a turn. Measured against the live site that was WORSE, not better:
         choice-to-builder went from 7100ms to 11683ms. The yield never
         happened -- the reconstruction that follows owns the thread
         synchronously, so the frames never arrived -- and seventeen canvas
         readbacks cost materially more than one. It was reverted on the
         measurement.

         Downsampling is the other tempting fix and is the wrong one here:
         `artSil` is where the ORDERED SIZE comes from, so a bounding box one
         resampled pixel out is a finished sticker out by a fraction of a
         millimetre. This read stays exact and whole; the cost is bounded
         where it can be bounded without touching geometry, in the bake. */
      let d;
      try {
        const cv = document.createElement('canvas');
        cv.width = iw; cv.height = ih;
        const cx = cv.getContext('2d', { willReadFrequently: true });
        if (!cx) { settle(null); return; }
        cx.drawImage(im, 0, 0);
        d = cx.getImageData(0, 0, iw, ih).data;
      } catch (e) { settle(null); return; }   // tainted or refused: fall back
      let x0 = iw, y0 = ih, x1 = -1, y1 = -1;
      /* the same box at the cut's own edge (alpha >= 128, the threshold the
         trace and the trace check use), measured in the same pass */
      let cx0 = iw, cy0 = ih, cx1 = -1, cy1 = -1;
      for (let y = 0; y < ih; y++) {
        const row = y * iw * 4;
        for (let x = 0; x < iw; x++) {
          /* The same low threshold the thumbnail framing uses: an antialiased
             edge is part of the sticker, and shaving it would under-measure
             the very silhouette the border is offset from. */
          const a = d[row + x * 4 + 3];
          if (a > 8) {
            if (x < x0) x0 = x;
            if (x > x1) x1 = x;
            if (y < y0) y0 = y;
            if (y > y1) y1 = y;
            if (a >= 128) {
              if (x < cx0) cx0 = x;
              if (x > cx1) cx1 = x;
              if (y < cy0) cy0 = y;
              if (y > cy1) cy1 = y;
            }
          }
        }
      }
      /* A fully transparent upload has no silhouette to size to. Null, not a
         degenerate box — the caller then sizes the way it always did. */
      if (x1 < x0 || y1 < y0) { settle(null); return; }
      settle({ x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1, iw: iw, ih: ih,
               cut: (cx1 >= cx0 && cy1 >= cy0) ? { x: cx0, y: cy0, w: cx1 - cx0 + 1, h: cy1 - cy0 + 1 } : null });
    };
    im.onerror = () => { settle(null); };
    im.src = shownArt;
    return () => { cancelled = true; };
  }, [shownArt]);
  const pickBorderColor = async () => {
    setBorder('custom');
    if (window.EyeDropper) {
      try {
        const {
          sRGBHex
        } = await new window.EyeDropper().open();
        setBorderColor(sRGBHex);
      } catch (e) {}
    } else if (colorRef.current) {
      colorRef.current.click();
    }
  };

  // bake contour outline PNG — smooth, clean sticker cut line.
  // bake contour outline PNG — a uniform-offset sticker cut line (like Illustrator's
  // "offset path"). It follows the artwork's REAL edges at a constant gap, dipping into
  // concavities, instead of ballooning into a blob.
  // Technique: (1) lightly de-jag the subject alpha so single-pixel stair-steps go away
  // WITHOUT changing the shape; (2) dilate by stamping the silhouette around a full circle
  // of radius = border width — true disk dilation, so the offset is uniform and hugs the
  // contour; (3) fill enclosed holes so the white backing is solid; (4) anti-alias; (5)
  // colorize and lay the art back on top.
  useEffect(() => {
    /* ── NOT WHILE THE CUSTOMER IS STILL CHOOSING ──────────────────────────
       This bake runs the cut-path engine, and profiled against the live site
       it is a SINGLE 1774ms block on the main thread: arcDistance 366ms,
       pointSegDist 185ms, cornerResidual 166ms, fitFromMoments 164ms,
       getImageData 455ms. It began the moment artwork arrived.

       The chooser is on screen by 117ms. So for the next second and three
       quarters the customer saw two cards that could not answer the mouse --
       and if they then chose Shape, every millisecond of it was thrown away,
       because a shaped sticker has no contour to bake.

       Nothing about the Die Cut / Shape decision needs this result, so it
       waits for the decision -- but ONLY on the shell that actually asks.
       `choosingWorkflow` is false wherever no chooser is ever shown (the
       production host, and the mobile tree), false once a card is clicked,
       and false for a restored design that arrives with its answer. So the
       one case that defers is the one where the answer is genuinely not
       known yet, and no shell can be left waiting for a question it never
       asks. */
    if (choosingWorkflow) {
      return undefined;
    }
    if (!contour || border === 'none' || !shownArt) {
      setOutlinedUrl(null);
      setOutlineFrame(null);
      setBakeBusy(false);
      /* No bake, no silhouette. Border=None reaches production with nothing
         here and derives its geometry from the artwork itself (section 6). */
      silhouetteRef.current = null;
      return;
    }
    let cancelled = false;
    /* ── ONE BAKE PER DECISION, NOT ONE PER TAP ──────────────────────────
       The bake is synchronous once it starts, so a second tap arriving during
       it cannot interrupt it: the work runs to completion and its result is
       then thrown away by `cancelled`. Measured, three quick thickness taps
       cost three full reconstructions — 8.2s of blocked main thread for two
       answers nobody would ever see.

       So the start is deferred by a frame or two. A settled choice is delayed
       imperceptibly; a flurry of them collapses to the last one, which is the
       only one the customer is waiting on. `cancelled` still guards the result,
       so a bake that does start can never overwrite newer state. */
    let started = false;
    let startTimer = null;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      /* ── MEASURED, AND NOT BOUNDED ───────────────────────────────────────
         This reconstruction runs several full getImageData passes at the
         upload's own size, and it looked like the obvious cost: profiled with
         a large upload, choosing Die Cut froze the main thread for seconds
         with getImageData dominating. It was bounded to a 1400px working edge
         on that reading.

         A/B on the live site said otherwise. Capped and uncapped, measured
         back to back in the same machine state, were indistinguishable --
         14721/13501ms uncapped against 15010/13951/13765ms capped -- so a 3.5x
         reduction in this canvas bought nothing, and whatever owns that time
         is not this. The cap was reverted rather than kept as decoration: it
         sits on the path that feeds the production proof raster, and an
         unproven change there is a risk with no return.

         The cost is real and still unattributed; see the milestone report. */
      const iw = img.naturalWidth,
        ih = img.naturalHeight;
      const col = border === 'custom' ? borderColor : border === 'black' ? '#0b0e13' : '#ffffff';
      const vfrac = bsz.vfrac || 0.06;
      /* THE SAME ARITHMETIC THE SCALE IS DERIVED FROM (slOutlineFrame). Two
         copies of this is how the drawn frame and the compensating scale came
         to disagree by 3.6%. */
      /* The silhouette is measured on the SAME artwork this bake is dilating,
         but possibly at a different decode size, so it travels as a RATIO of
         the long edge rather than as pixels. A ratio cannot be stale against a
         rescale the way a pixel count can. */
      const silRatio = (artSil && artSil.iw > 0 && artSil.ih > 0)
        ? (Math.max(artSil.w, artSil.h) / Math.max(artSil.iw, artSil.ih))
        : null;
      const frame = slOutlineFrame(iw, ih, vfrac,
                                   silRatio ? silRatio * Math.max(iw, ih) : null);
      if (!frame) return;
      const rad = frame.rad;
      const closeR = frame.closeR;
      const pad = frame.pad;
      const cw = iw + pad * 2,
        ch = ih + pad * 2;
      // 1. subject
      const base = document.createElement('canvas');
      base.width = cw;
      base.height = ch;
      base.getContext('2d').drawImage(img, pad, pad, iw, ih);
      // 1b. de-jag: a tiny blur+threshold at 50% smooths pixel stair-steps but keeps the true shape
      const clean = document.createElement('canvas');
      clean.width = cw;
      clean.height = ch;
      const cctx = clean.getContext('2d', {
        willReadFrequently: true
      });
      cctx.filter = 'blur(1.1px)';
      cctx.drawImage(base, 0, 0);
      cctx.filter = 'none';
      let cid;
      try {
        cid = cctx.getImageData(0, 0, cw, ch);
      } catch (e) {
        return;
      }
      const cd = cid.data;
      for (let i = 3; i < cd.length; i += 4) {
        cd[i] = cd[i] > 128 ? 255 : 0;
      }
      cctx.putImageData(cid, 0, 0);
      /* 1c-5. THE PIXEL WORK, OFF THE PAGE'S THREAD (D-176 performance).
         Closing, growing, hole filling, depth and the backing are one pure
         function, slBakeMasks; slBakeRun runs it in a worker (or, with no
         worker, here). A newer bake stops this one, and `cancelled` still
         guards every result, so a design changed mid-bake can never paint. */
      const cleanMask = new Uint8Array(cw * ch);
      for (let p = 0; p < cw * ch; p++) cleanMask[p] = cd[p * 4 + 3];
      const hex = (col || '#ffffff').replace('#', '');
      const br = parseInt(hex.slice(0, 2), 16) || 255,
        bgc = parseInt(hex.slice(2, 4), 16) || 255,
        bbl = parseInt(hex.slice(4, 6), 16) || 255;
      // the whole sticker body (behind the art) takes the chosen Sticker color
      const fhex = (faceColor === 'black' ? '#0a0c10' : faceColor === 'custom' ? faceCustom : '#ffffff').replace('#', '');
      const fr = parseInt(fhex.slice(0, 2), 16) || 255,
        fg = parseInt(fhex.slice(2, 4), 16) || 255,
        fb = parseInt(fhex.slice(4, 6), 16) || 255;
      // A thin WHITE kiss-line separates the artwork from a coloured border, so a dark
      // (e.g. black) border reads crisp instead of merging into dark art edges — the classic
      // premium die-cut look. The halo is the innermost slice of the ring (nearest the art).
      const borderIsWhite = br > 244 && bgc > 244 && bbl > 244;
      const sep = borderIsWhite ? 0 : Math.max(2, Math.round(rad * 0.3)); // no halo needed for a white border
      slBakeRun(cleanMask, cw, ch, closeR, rad, [br, bgc, bbl], [fr, fg, fb], sep).then(function (masks) {
      if (cancelled) return;
      const sil = document.createElement('canvas');
      sil.width = cw;
      sil.height = ch;
      const silx = sil.getContext('2d', {
        willReadFrequently: true
      });
      const sId = silx.createImageData(cw, ch);
      const sD = sId.data;
      for (let p = 0; p < cw * ch; p++) sD[p * 4 + 3] = masks.sil[p];
      silx.putImageData(sId, 0, 0);
      /* THE AUTHORITATIVE PRE-BORDER SILHOUETTE (double-dip, section 1/2).
         -------------------------------------------------------------------
         `sil` is the closed, hole-filled, UN-DILATED artwork silhouette, and
         everything below this line grows it into pixels. Production wants the
         thing BEFORE that growth: reconstructing a border that has already
         been dilated means reconstructing the damage dilation caused, which is
         the architecture this replaces.

         It is published rather than recomputed, so there is exactly ONE
         silhouette in the system. `base` travels with it because the Created
         SVG needs the artwork WITHOUT the baked border — the border is drawn
         as vector underneath — and `base` is the upload in this very frame, so
         one draw plan places both.

         LIFETIME: written on every bake, i.e. whenever contour, border,
         bsize, baseArt, faceColor or faceCustom change; read only at
         production time. `key` records what it was built from so a stale
         silhouette can be detected rather than trusted. */
      silhouetteRef.current = {
        sil: sil, base: base, w: cw, h: ch, pad: pad, rad: rad, closeR: closeR,
        iw: iw, ih: ih,
        key: [baseArt, border, bsize, contour].join('|'),
        id: ++silSeqRef.current
      };
      // 2-5. the backing the worker painted, as the bake's `bk` canvas
      const bk = document.createElement('canvas');
      bk.width = cw;
      bk.height = ch;
      const bkc = bk.getContext('2d');
      const bimg = bkc.createImageData(cw, ch);
      bimg.data.set(masks.bd);
      bkc.putImageData(bimg, 0, 0);

      /* ── 5b. THE PERIMETER, RECONSTRUCTED RATHER THAN GROWN ─────────────
         Everything above grew the silhouette in PIXELS: blur, threshold at
         128, dilate and erode by a disk. That is a binary mask, so its edge
         steps — visibly on a phone, and worse on a background-removed JPEG
         where the flood boundary follows compression noise. Meanwhile
         production reconstructed the SAME perimeter as vector geometry through
         MOTO and nobody could see it, because MOTO only ran at export.

         So the preview now asks the same engine the same question, on the same
         authoritative pre-border silhouette production uses, and draws the
         answer. B is the outer band and P is the silhouette: filling B in the
         border colour and P in the face colour reproduces the ring exactly,
         and the band drawn here IS the path production emits as CutContour —
         the same object, not a second one that has to be kept in step.

         The raster backing above is not deleted. It stays as the fallback for
         a refusal, which is a real outcome and must not be a blank sticker. */
      let motoBacking = null;
      let contourSrc = 'raster-bake';
      let motoNote = null;
      try {
        const CP = (typeof window !== 'undefined') && window.SLCutPath;
        if (SL_PREVIEW_INTERIM_MOTO && CP && CP.doubleDip && rad > 0) {
          /* ── THE PREVIEW MAY BE CHEAPER; IT MAY NOT BE DIFFERENT ─────────
             `silMask` and `viewBox` are independent in the engine — production
             already hands it a 589x600 mask against a 379x386 viewBox — so a
             coarser mask still returns paths in FULL composite coordinates.
             Same engine, same Double-Dip contract, same distance, same
             everything the geometry is derived from: only the number of
             samples the silhouette is read at changes.

             It has to change. Measured on a 390px phone, one border tap cost a
             single 2.9-second block, essentially all of it inside `doubleDip`
             on a ~1400px mask, and three quick taps serialised into 8.2s of
             blocked main thread. The screen shows this at about 335px.

             PRODUCTION IS NOT TOUCHED. `silhouetteRef` keeps the full-
             resolution silhouette, and `buildDoubleDip` re-rasterises it at the
             production plan's own resolution for the exported file. This is the
             preview's copy and only the preview's. */
          const PREVIEW_MOTO_MAX = 720;
          let mSrc = sil, mW = cw, mH = ch;
          const longEdge = Math.max(cw, ch);
          if (longEdge > PREVIEW_MOTO_MAX) {
            const k = PREVIEW_MOTO_MAX / longEdge;
            mW = Math.max(8, Math.round(cw * k));
            mH = Math.max(8, Math.round(ch * k));
            const small = document.createElement('canvas');
            small.width = mW; small.height = mH;
            const sx = small.getContext('2d');
            sx.imageSmoothingEnabled = true;
            sx.imageSmoothingQuality = 'high';
            sx.drawImage(sil, 0, 0, mW, mH);
            mSrc = small;
          }
          /* The OFFSET stage is where the time actually goes, and its grid is
             sized from the VIEWBOX, not the mask — so shrinking the mask alone
             barely moved it (1650ms to 1500ms measured). The whole
             reconstruction therefore runs in the small frame, distance scaled
             with it, and the finished paths are scaled back up. Same engine,
             same contract, same shape: `transformPath` is the engine's own
             transport for exactly this, and production still reconstructs at
             its own full resolution for the exported file. */
          const kBack = mW > 0 ? cw / mW : 1;
          const mid = (mSrc === sil ? silx : mSrc.getContext('2d')).getImageData(0, 0, mW, mH);
          const geo = CP.doubleDip({
            silMask: { data: mid.data, width: mW, height: mH },
            viewBox: { w: mW, h: mH },
            distance: rad / kBack,
            borderFill: col,
            faceFill: '#' + fhex,
            haloPx: sep / kBack
          });
          /* Back into composite coordinates before anything is drawn with it. */
          if (kBack !== 1 && geo && geo.accepted && geo.B && geo.P && CP.transformPath) {
            const tUp = { sx: kBack, sy: kBack, tx: 0, ty: 0 };
            const upB = CP.transformPath(geo.B, tUp), upP = CP.transformPath(geo.P, tUp);
            /* A refusal to transport is a refusal to use it: the raster
               fallback below is a real outcome and is better than a path in
               the wrong coordinate system. */
            if (upB && upP) { geo.B = upB; geo.P = upP; }
            else { geo.accepted = false; geo.reason = 'preview_transport_failed'; }
          }
          motoNote = { accepted: !!geo.accepted, reason: geo.reason || null,
                       pLane: geo.pLane || null, pFamily: geo.pFamily || null,
                       pConfidence: geo.pConfidence || null,
                       pathB: geo.B || null, pathP: geo.P || null };
          if (geo.accepted && geo.B && geo.P && typeof Path2D !== 'undefined') {
            const mb = document.createElement('canvas');
            mb.width = cw; mb.height = ch;
            const mx = mb.getContext('2d');
            const PB = new Path2D(geo.B), PP = new Path2D(geo.P);
            /* the die-cut band, in the border colour */
            mx.fillStyle = col;
            mx.fill(PB, 'nonzero');
            /* the white kiss-line, derived from P and nothing else: a stroke
               centred on the silhouette whose OUTER half stays visible inside
               the ring. Skipped for a white border, which needs no separation
               from itself. */
            if (sep > 0) {
              mx.strokeStyle = '#ffffff';
              mx.lineWidth = sep * 2;
              mx.lineJoin = 'round';
              mx.stroke(PP);
            }
            /* the sticker face, behind the artwork */
            mx.fillStyle = '#' + fhex;
            mx.fill(PP, 'nonzero');
            motoBacking = mb;
            contourSrc = 'moto-' + (geo.pLane || geo.pFamily || 'accepted');
          }
        } else if (!CP || !CP.doubleDip) {
          motoNote = { accepted: false, reason: 'engine_unavailable' };
        }
      } catch (e) {
        motoNote = { accepted: false, reason: 'threw:' + ((e && e.message) || e) };
      }
      contourSrcRef.current = {
        source: contourSrc,
        moto: motoNote,
        key: [baseArt, border, bsize, contour].join('|')
      };

      /* the layers the production cut is painted with when it arrives (D-176) */
      bakeLayersRef.current = { silObj: silhouetteRef.current, base: base, bk: bk, cw: cw, ch: ch,
                                col: col, fhex: fhex, sep: sep, frame: Object.assign({}, frame) };

      // 6. anti-alias the backing, then lay the upload on top (upload ALWAYS front layer)
      const out = document.createElement('canvas');
      out.width = cw;
      out.height = ch;
      const octx = out.getContext('2d');
      /* A vector backing is already smooth; blurring it would only soften a
         clean edge back towards the one being replaced. The raster fallback
         still gets its anti-aliasing pass. */
      if (motoBacking) {
        octx.drawImage(motoBacking, 0, 0);
      } else {
        octx.filter = 'blur(0.6px)';
        octx.drawImage(bk, 0, 0);
        octx.filter = 'none';
      }
      octx.drawImage(base, 0, 0);
      /* ── THE FIT AUTHORITY IS WHAT THIS CANVAS ACTUALLY PAINTS ──────────
         The display box is chosen from the FINISHED aspect, but the pixels
         that reach the screen are this composite, and the two were disagreeing
         by up to 2.7%. `.pb-face` clips, so that difference was taken off the
         OUTER edge — the border and the cut line — and it grew with border
         thickness: measured 2.5px at Thin and 4.56px at Thick on a 390px
         phone. That is the "sticker touches the preview edge" defect.

         So the composite reports its own painted extent and the layout fits
         THAT. Not the artwork's bounds, not the box, not a CSS border width:
         the outermost pixels this canvas actually has, which are the outer
         edge of B.

         Strided, because this runs on every bake — at stride 3 an 1100px
         composite costs ~130k samples — and then widened by one stride so the
         estimate can only ever be generous. */
      let extent = null;
      try {
        const ST = 3;
        const eid = octx.getImageData(0, 0, cw, ch).data;
        let ex0 = cw, ey0 = ch, ex1 = -1, ey1 = -1;
        for (let py = 0; py < ch; py += ST) {
          const row = py * cw;
          for (let px = 0; px < cw; px += ST) {
            if (eid[(row + px) * 4 + 3] > 8) {
              if (px < ex0) ex0 = px;
              if (px > ex1) ex1 = px;
              if (py < ey0) ey0 = py;
              if (py > ey1) ey1 = py;
            }
          }
        }
        if (ex1 >= 0) {
          ex0 = Math.max(0, ex0 - ST); ey0 = Math.max(0, ey0 - ST);
          ex1 = Math.min(cw - 1, ex1 + ST); ey1 = Math.min(ch - 1, ey1 + ST);
          extent = { wFrac: (ex1 - ex0 + 1) / cw, hFrac: (ey1 - ey0 + 1) / ch };
        }
      } catch (e) { extent = null; }
      frame.extent = extent;
      if (!cancelled) {
        /* Frame FIRST, so no render can ever see the new URL beside the old
           padding. React batches both into one commit either way; the order
           makes the intent explicit rather than relying on that. */
        slPngDataUrl(out, 'bake').then(function (url) {
          if (cancelled) return;
          setOutlineFrame(frame);
          setOutlinedUrl(url);
          setBakeBusy(false);
          setBakeStamp(n => n + 1);
        }, function () {
          if (!cancelled) { setOutlinedUrl(null); setOutlineFrame(null); setBakeBusy(false); }
        });
      }
      }, function () { /* superseded by a newer bake: it owns the screen */ });
    };
    img.onerror = () => {
      if (!cancelled) { setOutlinedUrl(null); setOutlineFrame(null); setBakeBusy(false); }
    };
    /* Started here, after `onload`/`onerror` are attached, so the decode also
       waits for the choice to settle rather than racing it. */
    /* ── LET THE BROWSER PAINT BEFORE TAKING THE THREAD ──────────────────
       Everything below this line is synchronous and, on real artwork, holds
       the main thread for seconds. Anything the interface wanted to say about
       that has to be ON SCREEN first, and setting state is not painting: React
       can commit a frame that never renders because this started 4ms later.
       Measured, the processing panel appeared in 0 of 677 frames for exactly
       that reason.

       Two frames, because one only guarantees the callback ran BEFORE a paint;
       the second guarantees a paint happened in between. Not a sleep -- it is
       the browser telling us it has drawn, and on an idle thread it costs
       about a frame. */
    const twoFrames = (fn) => {
      if (typeof requestAnimationFrame !== 'function') { fn(); return; }
      requestAnimationFrame(() => { if (!cancelled) requestAnimationFrame(() => { if (!cancelled) fn(); }); });
    };
    setBakeBusy(true);
    startTimer = setTimeout(() => { twoFrames(() => { started = true; img.src = baseArt; }); }, 140);
    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      if (!started) img.src = '';
    };
    /* `artSil` is a DEPENDENCY now, not just a reader: the border radius is
       derived from it, so a bake that kept an older silhouette would paint the
       previous artwork's border thickness onto this one. This is the same
       class of staleness that once let `artworkRectIn` go out of date. */
  /* `workflow` and `art` join the list because the gate above reads them:
     the bake must start the moment the customer answers the chooser. */
  }, [contour, border, borderColor, bsize, baseArt, faceColor, faceCustom, artSil, choosingWorkflow]);

  // Mirror the visual transform into the canonical shared Builder state (SLBuilderCore, D-041) so the
  // payload carries it and the future mobile UI reads/writes the SAME model. `offset` is normalized by
  // the face size (w×h) → screen-independent. This ONLY writes to the core; desktop rendering is unchanged.
  /* THE COLOURS ARE THE RESOLVED ONES, NOT THE PICKER STATE.
     ---------------------------------------------------------------------
     This used to record `borderColor` and the raw `faceColor` token. Both are
     UI state, and neither is what gets painted:

       borderColor  is only consulted when the border is `custom`. Its default
                    is '#2E86E3', so a customer choosing the BLACK preset had
                    '#2E86E3' written to the canonical config — live order
                    #1014 records exactly that beside `options.border: "black"`.
       faceColor    is a token ('white' | 'black' | 'custom'). On 'custom' the
                    chosen colour lives in `faceCustom` and never reached the
                    config at all, so the design could not be reproduced.

     `borderCss` and `faceColorCss` are the values the screen and the proof
     actually paint, so recording them makes the config agree with the picture
     by construction. The border/face IDENTITY is not lost: `options.border`
     already carries the preset id, and 'transparent' is the honest colour for
     a sticker with no border ring.

     `|| null` rather than a bare pass-through: `setTransform` ignores a null,
     and an `undefined` from an unknown id would silently keep a STALE colour —
     which is the same class of bug being fixed here. */
  useEffect(() => {
    const B = window.slBuilder;
    if (!B || !B.setTransform) return;
    const W = w || 1, H = h || 1;
    /* Die-cut carries no crop — see `proofGeometry`. Mirrored here as well as
       there because these are two different consumers of the same state and a
       guarantee that holds in only one of them is not a guarantee. The core
       asserts it a third time when it builds the payload, where the crop model
       is named `contour-fit-v1`. */
    B.setTransform({
      shape,
      scale: dieCut ? 1 : zoom,
      offset: dieCut ? { x: 0, y: 0 }
                     : { x: (pos.x || 0) / W, y: (pos.y || 0) / H },
      rotation: dieCut ? 0 : rot,
      flip: { x: !!flipX },
      /* DERIVED PRODUCTION GEOMETRY, published so Illustrator never has to
         re-derive it. Where the artwork sits inside the ordered sticker, in
         inches, x right and y down from the production canvas top-left — the
         SVG viewBox convention, so a reader needs no second frame of
         reference. It is NOT a customer offset: the transform above still
         records scale 1, offset {0,0}, rotation 0. */
      artworkRectIn: (dieCut && cfit) ? cfit.artworkRectIn : null,
      border: { color: borderCss || null, thickness: bsize },
      faceColor: faceColorCss || null,
      /* WHICH ARTWORK THE CUSTOMER ACTUALLY APPROVED. The production export
         already carries the pixels, but a file alone cannot say whether the
         customer chose this or whether something upstream mangled their
         upload. Recorded so HQ can tell the two apart without guessing. */
      backgroundRemoved: !!(bgOn && bgUrl),
      backgroundRemovalMethod: (bgOn && bgUrl && bgMeta) ? bgMeta.method : null
    });
  }, [shape, zoom, pos, rot, flipX, borderCss, bsize, faceColorCss, w, h, bgOn, bgUrl, bgMeta,
      /* THE ORDERED SIZE, AND THE RECT IT DETERMINES. Without these the
         published artworkRectIn goes stale the moment the customer changes
         size, and HQ places the original at a scale the production file does
         not share. See the note beside `cfitRectKey`. */
      cfitRectKey, widthIn, heightIn]);

  // ---- order ----
  // The canonical, Shopify-INDEPENDENT payload is built by the orchestrator (SLBuilderCore) from
  // the engines — no pricing/upload/layout logic is assembled here. Shopify is reached only via
  // the SLCartAdapter. (Cart Integration v1.)
  /* THE RECONCILIATION POINT.
     ---------------------------------------------------------------------
     Every payload resolves the customer's artwork HERE, immediately before
     validation, from the authoritative refs rather than from whatever a render
     closure happened to capture. One function, so "has artwork" cannot come to
     mean different things to the preview, Review Order, the production export
     and the Add-to-Cart guard.

     `name` falls back because a name is a label, not evidence: an iPhone can
     hand back a File with an empty `.name`, and refusing a paid order over a
     missing string is the bug this replaces. Bytes with no name are artwork.
     NO bytes and no name resolve to nothing, and the guard still refuses. */
  const resolveOrderArtwork = () => {
    const bytes = artBytesRef.current;
    const a = artRef.current;
    const rawName = a && a.name ? String(a.name).trim() : '';
    const has = !!bytes || !!rawName;
    return {
      has: has,
      bytes: bytes || null,
      name: rawName || (bytes ? 'artwork' : null),
      size: bytes ? bytes.length : null
    };
  };

  const buildOrderPayload = () => {
    try {
      if (!window.slBuilder) return null;
      const A0 = resolveOrderArtwork();
      return window.slBuilder.buildPayload({
        productType: CG ? CG.PRODUCT_TYPE : 'diecut',
        sizeRule: CG ? undefined : !ORDER_RULES_APPLY ? 'none' : (contour ? 'diecut' : 'shape'),
        bytes: A0.bytes,
        fileName: A0.name,
        fileSize: A0.size,
        width: widthIn,
        height: heightIn,
        quantity: effQty,
        material,
        finish: laminate,
        border,
        /* ONE acknowledgement, one source. This is the same `qualityAckFor`
           the inline card sets and the Add-to-Cart gate reads — passed here so
           the order carries it, rather than a second flag invented for the
           purpose. Null unless the customer answered for THIS artwork. */
        qualityAck: qualityAcked ? artToken : null,
        /* Cart Graphics only; absent from every sticker payload. */
        cartGraphics: CG ? cgOrderRecord() : undefined,
        dpiWidthIn: CG ? (cgDpiW || undefined) : undefined,
        dpiHeightIn: CG ? (cgDpiH || undefined) : undefined
      }, {
        artworkRef: durableArtworkRef()
      });
    } catch (e) {
      return null;
    }
  };
  /* ADD TO CART ADDS TO THE CART.
     ---------------------------------------------------------------------
     It did not. It built a payload, assigned it to `window.__stickrLastOrder`
     — a global nothing has ever read — and set `ordered`, which renders a
     panel telling the customer the sticker is in their cart. The only code
     that reached Shopify was `goCheckout`, wired to "Go to cart".

     So a customer who clicked Add to cart and then "Build another sticker"
     lost the design: `startOver` resets React state, the pending payload went
     with it, and Shopify had never been told. It looked exactly like the cart
     had been emptied. Nothing was deleted — the line was never created.

     The fix is to use the ONE real submit path, with `navigate: false` so the
     customer stays in the builder. `ordered` is now gated on Shopify actually
     accepting the line, so the confirmation panel can no longer describe a
     sale that did not happen.

     The proof is rendered first, exactly as `goCheckout` does — same canonical
     geometry, same no-substitution rule. Sharing the path is the point: two
     add-to-cart implementations is what produced this defect. */
  /* ONE SUBMIT, ONE SAVING STATE, THREE ENTRY POINTS.
     ---------------------------------------------------------------------
     Desktop's Add to cart, mobile's Add to Cart and the sheet path all run
     through here so the feedback cannot drift between them, and so there is
     still exactly one canonical submitter underneath.

     The stage is set BEFORE the first await. That ordering is the whole point:
     `rasterizeFace` is a canvas render and a PNG encode, which on a phone is
     hundreds of milliseconds of blocked main thread. Setting the state after
     it would show the spinner only once the slow part was already over. */
  const runAddToCart = async (buildPayload, opts) => {
    if (window.__slCartAddInFlight || saveStage) return false;   // a second tap does nothing
    /* A cart graphic whose size or quantity is invalid, or which is not sold
       online at this size yet, is not submitted. The reason is already on
       screen beside the field; this is the guard behind the disabled button. */
    if (CG && !cgCanOrder) return false;
    /* ── THE RESOLUTION QUESTION IS STILL REQUIRED ───────────────────────
       The warning stopped being a modal; it did not stop being a decision. A
       low-resolution order still needs the customer to have said yes to it, and
       that answer is now recorded on the order itself.

       But a refusal here must not read as a failure. There is no error, no red,
       and nothing is lost: the card is brought into view, focused so a keyboard
       or screen-reader user lands on it too, and highlighted once. Every builder
       selection is untouched — the customer is one decision from where they
       were going, not sent back. */
    if (qualityLow && !qualityAcked && art) {
      setQualityOpen(true);
      setQualityNudge(true);
      try {
        var el = qualityDialogRef.current;
        if (el) {
          if (el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (el.focus) el.focus({ preventScroll: true });
        }
      } catch (e) {}
      qualityTrack('artwork_quality_nudge');
      return false;
    }
    setSaveStage(SAVE_STAGE.start);
    const T = stageTimer();
    /* Yield one frame so React paints the saving state before the main thread
       is taken by the rasterizer. Without this the spinner and the freeze
       arrive together, which is the same as no spinner at all. */
    /* A HIDDEN TAB NEVER PAINTS, so it never delivers a frame.
       requestAnimationFrame is throttled to nothing while a page is
       backgrounded, and on a phone that is one app-switch, one lock-screen or
       one incoming call away. This yield exists only so the spinner paints
       before the rasteriser takes the main thread; waiting FOREVER for a frame
       that is never coming turns "Saving your sticker" into a dead button with
       no error, no timeout and no way back. Measured on the dev theme: with
       the tab hidden the add never advanced past this line.
       The frame is still preferred when it comes. The timeout is the floor. */
    await new Promise(function (r) {
      var settled = false;
      var go = function () { if (!settled) { settled = true; r(); } };
      if (typeof requestAnimationFrame === 'function') {
        try { requestAnimationFrame(function () { setTimeout(go, 0); }); } catch (e) {}
      }
      setTimeout(go, 250);
    });
    T.mark('paint_saving_state');
    let ok = false;
    try {
      let proof = null;
      /* `frame: 'content'` — THE ONLY CALLER THAT ASKS FOR IT.
         This proof becomes `_artwork_preview`, which is what the cart tile and
         HQ show, and both of them contain-fit it into a small square. Framing
         it to the sticker rather than to the builder box is what makes those
         tiles show the sticker instead of a stamp in a field of empty canvas.
         The production exporter calls `rasterizeFace` separately and WITHOUT
         this option, so print geometry is not involved. */
      try { proof = await ((opts && opts.proof) ? opts.proof() : rasterizeFace({ frame: 'content' })); }
      catch (e) { proof = null; }
      if (!proof) {
        try { console.warn('[sl-cart] proof render unavailable — the order will carry no preview'); } catch (x) {}
      }
      T.mark('proof_render');
      const payload = buildPayload();
      /* THE STATE THE DECISION WAS MADE ON, recorded BEFORE the guard sees it.
         This refusal is intermittent on real hardware and identical from the
         outside whatever caused it, so the only useful evidence is the state
         of the one run that failed. React's own size/quantity and the store's
         are both recorded because a stale React value against a fresh store
         value (or the reverse) is exactly the class of bug this is hunting. */
      let qaSnap = null;
      try {
        const st = (window.slBuilder && window.slBuilder.getState) ? window.slBuilder.getState() : null;
        const A2 = window.SLCartAdapter;
        const vId = window.SL_ONE_CENT_VARIANT_ID || null;
        const li2 = (A2 && payload) ? A2.toLineItem(payload, { variantId: vId }) : null;
        qaSnap = {
          at: new Date().toISOString().slice(11, 19),
          reactWidth: widthIn, reactHeight: heightIn, reactQty: effQty,
          storeWidth: st ? st.width : null, storeHeight: st ? st.height : null,
          storeQty: st ? st.quantity : null,
          price: payload && payload.pricing ? payload.pricing.price : null,
          priceError: payload && payload.pricing ? (payload.pricing.error || null) : null,
          payloadWidth: payload && payload.dimensions ? payload.dimensions.widthIn : null,
          payloadHeight: payload && payload.dimensions ? payload.dimensions.heightIn : null,
          payloadQty: payload ? payload.quantity : null,
          uploadStatus: st ? st.uploadStatus : null,
          warnings: st ? (st.warnings || []).map(function (w) { return w.level + ':' + w.code; }).join(',') : null,
          canOrder: payload && payload.readiness ? payload.readiness.canOrder : null,
          readinessReasons: payload && payload.readiness ? (payload.readiness.reasons || []).join(',') : null,
          variantId: vId,
          payloadPresent: !!payload,
          lineItemPresent: !!li2,
          lineItemId: li2 ? (li2.id || null) : null,
          lineItemQty: li2 ? (li2.quantity || null) : null,
          bytes: artBytesRef.current ? artBytesRef.current.length : 0,
          bgOn: !!bgOn, proof: !!proof,
          refusalReason: null
        };
        setQaCart(qaSnap);
        window.__slQACart = qaSnap;
      } catch (e) {
        qaSnap = { at: 'snapshot-failed', refusalReason: String(e && e.message || e).slice(0, 90) };
        setQaCart(qaSnap); window.__slQACart = qaSnap;
      }
      ok = await submitPayloadToCart(payload, proof, {
        navigate: !!(opts && opts.navigate),
        onStage: setSaveStage,
        timer: T,
        /* The guard names its refusal; this carries that name back to the
           screen the customer is actually holding. */
        onRefusal: function (why) {
          try {
            if (qaSnap) { qaSnap.refusalReason = why; setQaCart(Object.assign({}, qaSnap)); window.__slQACart = qaSnap; }
          } catch (e2) {}
        }
      });
      if (ok && opts && opts.onDone) opts.onDone(payload);
    } finally {
      /* On the navigating path the page is being replaced, so the state is
         left up on purpose (see the submit). Everywhere else — success or
         failure — the button comes back so the customer can act again. */
      if (!(ok && opts && opts.navigate)) setSaveStage(null);
    }
    return ok;
  };

  /* The button's contents while saving: a spinner, the truthful stage, and a
     quiet second line so the customer knows what the wait is FOR. `role=status`
     lets a screen reader announce the stage changes without stealing focus. */
  /* THE CUSTOMER IS TOLD, NOT SURPRISED (D-176). When no automatic cut line
     follows this design faithfully, the order still goes through - a person
     draws the cut in HQ - and the customer hears that before checkout, in the
     same place they press Add to Cart. Nothing is blocked and nothing is
     promised beyond what happens. */
  const cutReviewNote = () => (dieCut && cutReview)
    ? /*#__PURE__*/React.createElement("div", {
        className: "sl-cutreview", role: "status",
        style: { margin: "10px 0", padding: "10px 12px", borderRadius: "10px", background: "#fff7e6",
                 border: "1px solid #f3d08a", color: "#5c3d00", fontSize: "13px", lineHeight: 1.45, textAlign: "left" }
      },
      /*#__PURE__*/React.createElement("strong", { style: { display: "block", marginBottom: "2px" } }, "We’ll draw your cut line by hand"),
      "Our automatic cut line couldn’t follow every detail of this design closely enough, so our team will draw it by hand before printing. You can check out as normal.")
    : null;
  const saveBtnContent = (idleLabel) => saveStage
    ? /*#__PURE__*/React.createElement("span", { className: "pb-saving", role: "status", "aria-live": "polite" },
        /* The tick replaces the spinner on the last stage: the work is done and
           only the page change is left, so a still-spinning indicator would be
           saying "wait" when the honest word is "added". */
        /*#__PURE__*/React.createElement("span", {
          className: saveStage.title === SAVE_STAGE.done.title ? "pb-savedtick" : "pb-spin",
          "aria-hidden": "true"
        }),
        /*#__PURE__*/React.createElement("span", { className: "pb-saving-txt" },
          /*#__PURE__*/React.createElement("b", null, saveStage.title),
          /*#__PURE__*/React.createElement("small", null, saveStage.sub)))
    : idleLabel;

  /* THE CONFIG RECORDS STATE, NOT PIXELS.
     ---------------------------------------------------------------------
     `artwork.reference` used to receive `shownArt`, which for an upload is a
     data URL — so the canonical config became a base64 copy of the customer's
     file. On real order #1020 that was 3.23 MB of a 3.38 MB config: 100% of it,
     against 1,666 bytes of actual configuration.

     Nothing consumed it. `_artwork_ref` is DELETED from the line properties a
     few lines below whenever it exceeds 200 characters, which a data URL always
     does, so it never reached an order, HQ or the cart — it was only ever
     uploaded and stored.

     A short, real URL still travels: that is the case the field was for, and it
     costs nothing. Only the megabytes are refused. The customer's bytes remain
     durable exactly where they belong, as `_artwork_original`.

     `payload-v3` is unchanged — the field is still present and still means what
     it meant. Only the value stops being a copy of an asset we already store. */
  const durableArtworkRef = () => {
    var u = shownArt;
    if (typeof u !== 'string' || !u) return null;
    return /^(https?:)?\/\//.test(u) ? u : null;      // a real URL, never inline bytes
  };

  /* W-PROMO: a pack skips the generic "Added to your cart" modal and goes to
     the cart, which opens the upgrade offer on arrival (?promo_offer=1). */
  const addToCart = () => runAddToCart(buildOrderPayload, PROMO ? { navigate: true } : {
    navigate: false,
    onDone: function (payload) {
      try { window.__stickrLastOrder = payload; } catch (e) {}
      setOrdered(true);
    }
  });
  /* The localStorage preview stash (`stashPreview` / `_preview_id`) was REMOVED here
     (D-056). It kept the cart thumbnail in the shopper's own browser, so the image
     vanished when they cleared site data, switched device, or checked out on a phone
     after designing on desktop — and production never received anything at all. The
     preview is now uploaded to Shopify with the order as `_artwork_preview`, and the
     cart reads that CDN URL. Nothing about the design lives in localStorage any more. */
  // Shared cart submit — turns ANY canonical payload (die-cut OR sheet, D-050) into a Shopify line item
  // via SLCartAdapter (quantity-as-price transport, D-038) and POSTs it to the REAL cart. One code path
  // for both products; Shopify is the source of truth (no localStorage cart). `previewUrl` is the builder's
  // existing WYSIWYG preview (data URL) — stashed for the cart thumbnail, never regenerated.
  /* @param {Object} opts  { navigate }. `navigate: false` adds the line and
     STAYS in the builder, which is what "Build another sticker" needs; the
     default keeps the existing go-to-cart behaviour. Resolves TRUE only when
     Shopify accepted the line, so a caller can never announce a sale that did
     not happen. */
  /* STAGE TIMING, MEASURED ON THE DEVICE THAT ACTUALLY MATTERS.
     ---------------------------------------------------------------------
     Guessing at mobile performance from a desktop is how people optimise the
     wrong thing. This records `performance.now()` at each boundary, prints one
     table to the console at the end, and hands the same numbers to telemetry —
     so a real phone on a real connection produces the real profile.

     It costs a handful of floating-point reads and cannot fail the add: every
     call is wrapped, and `mark` returning early is harmless. */
  const stageTimer = () => {
    var t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    var last = t0, marks = [];
    return {
      mark: function (name) {
        try {
          var now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
          marks.push([name, now - last, now - t0]);
          last = now;
        } catch (e) {}
      },
      report: function (ok, extra) {
        try {
          var total = marks.length ? marks[marks.length - 1][2] : 0;
          console.info('%c[sl-cart] add-to-cart ' + (ok ? 'OK' : 'FAILED') + ' in ' +
                       total.toFixed(0) + ' ms', 'font-weight:bold');
          console.table(marks.map(function (m) {
            return { stage: m[0], ms: +m[1].toFixed(1), cumulative_ms: +m[2].toFixed(1) };
          }));
          if (window.slTelemetry && window.slTelemetry.track) {
            var payload = { ok: !!ok, totalMs: Math.round(total) };
            marks.forEach(function (m) { payload[m[0]] = Math.round(m[1]); });
            for (var k in (extra || {})) payload[k] = extra[k];
            window.slTelemetry.track('add_to_cart_timing', payload);
          }
        } catch (e) {}
      }
    };
  };

  const submitPayloadToCart = async (payload, previewUrl, opts) => {
    if (window.__slCartAddInFlight) return false;           // duplicate-add protection
    /* THE PACK IS REFUSED, not sent at the wrong price, when it cannot be
       sold: outside the promo window, with no variant wired, or at any
       quantity but the pack's. */
    if (PROMO) {
      /* The size rule is re-checked HERE against the payload that is about to
         be ordered, not against what the screen last showed. */
      const rule = promoPriceRule(payload && payload.pricing ? payload.pricing.price : 0, PROMO.price50);
      const why = promoBlockText(Date.now()) || ((payload && payload.quantity !== PROMO.packQty) ? 'The pack is ' + PROMO.packQty + ' stickers.' : '')
        || (rule.cheaper ? 'At this size, 50 stickers are $' + (+payload.pricing.price).toFixed(2) + ' at our regular price, less than this pack. Please order it from the regular builder.'
          : rule.unpriced ? 'We couldn’t check the price for this size. Please try again.' : '');
      if (why) {
        try { console.warn('[sl-cart] REFUSED promo add -', why); } catch (e) {}
        try { if (opts && typeof opts.onRefusal === 'function') opts.onRefusal('promo: ' + why); } catch (e) {}
        try { alert(why); } catch (e) {}
        return false;
      }
    }
    var stage = (opts && opts.onStage) || function () {};
    var T = (opts && opts.timer) || stageTimer();
    const A = window.SLCartAdapter;
    const variantId = window.SL_ONE_CENT_VARIANT_ID || null;
    /* Mint the durable design id HERE — once, at the moment of purchase (D-056). It is
       deliberately not derived inside the payload: the payload is deterministic by
       contract, and an id that is recomputed can change, while an id that is stored
       cannot. One id per line item. A saved design or reorder would instead pass its
       existing id in, which is why the payload accepts one rather than inventing one. */
    if (payload && !payload.designId && window.slBuilder && window.slBuilder.newDesignId) {
      payload.designId = window.slBuilder.newDesignId();
    }
    const li = A && payload ? A.toLineItem(payload, { variantId: variantId }) : null;
    // Unpriced / not-ready / missing variant → NOT orderable. Never fake a cart.
    if (!li || li.orderable === false || !li.id) {
      /* WHICH of the three refusals this was. They have very different causes
         - a payload that never built, a payload the engines refused, and a
         store whose transport variant did not reach the page - and the
         customer-facing sentence is the same for all three, which is why a
         real-device failure could not be told apart from a console line that
         only said "undefined". Named here so the next one answers itself. */
      var why = !li ? 'no-line-item (payload missing or adapter unavailable)'
              : (li.orderable === false ? ('not-orderable: ' + (li.reason || 'unknown'))
                                        : 'no-variant-id (SL_ONE_CENT_VARIANT_ID missing on this page)');
      try {
        console.warn('[sl-cart] REFUSED add-to-cart -', why, {
          hasAdapter: !!A, hasPayload: !!payload, variantId: variantId,
          price: payload && payload.pricing && payload.pricing.price,
          canOrder: payload && payload.readiness && payload.readiness.canOrder,
          reasons: payload && payload.readiness && payload.readiness.reasons
        });
      } catch (e) {}
      try { if (window.slTelemetry && window.slTelemetry.track) window.slTelemetry.track('add_to_cart_refused', { why: why }); } catch (e) {}
      try { if (opts && typeof opts.onRefusal === 'function') opts.onRefusal(why); } catch (e) {}
      var msg = 'This item can’t be ordered online yet. Please try again or contact us.';
      /* On a QA build the reason travels WITH the message, so a real-device
         reproduction does not depend on someone having a console attached. */
      try { if (/[?&]qadebug=1/.test(location.search)) msg += ' [qa] ' + why; } catch (e) {}
      try { alert(msg); } catch (e) {}
      return false;
    }
    const props = li.properties || {};
    /* THE CLEANUP REVIEW rides on the line (hidden: underscore) and in the design config, so HQ holds
       Approve cut for this design, and only this design, until a person has looked (see artReview). */
    {
      const rvw = !CG ? artReviewRef.current : null;
      const rvLabel = rvw && window.SLHaloClean && window.SLHaloClean.reviewLabel ? window.SLHaloClean.reviewLabel(rvw) : null;
      if (rvLabel) {
        props._art_review = rvLabel;
        if (payload) payload.artReview = { reason: rvw.reason, at: rvw.at || null, box: rvw.box || null, px: rvw.px || null };
      }
    }
    /* THE PACK RIDES ON ITS OWN VARIANT. The adapter built the ordinary line
       (every artwork, design and production property, untouched); only the
       transport is swapped: the $22 variant once, instead of the $0.01
       variant x price-in-cents. `Quantity` stays 50, the stickers to make.
       The price fields say what the customer pays; the regular price is kept
       beside them for reporting. No shipping instruction is written: the
       service is chosen at fulfilment (Ryan, 18 Oct 2026). */
    if (PROMO) {
      const reg = payload && payload.pricing ? +payload.pricing.price : 0;
      const attr = promoAttribution();
      li.id = PROMO.variant50;
      li.quantity = 1;
      props['Promotion'] = PROMO.packQty + ' stickers for ' + promoMoney(PROMO.price50);
      props._promo = 'promo50';
      props._promo_pack = String(PROMO.packQty);
      /* The cart blocks checkout for a pack whose window has closed, reading
         it from the line itself, so it needs no settings of its own. */
      if (PROMO.end) props._promo_end = String(PROMO.end);
      if (reg > 0) props._promo_regular_price = reg.toFixed(2);
      props._line_price = (+PROMO.price50).toFixed(2);
      props._unit_price = (PROMO.price50 / PROMO.packQty).toFixed(2);
      props._price_source = 'promo50';
      if (attr.q) props._utm = attr.q;
      if (attr.landed) props._promo_landed = attr.landed;
      /* ONE ADD, ONE LINE. Packs are unlimited and each is its own design, so
         two adds must never merge into one line of quantity 2, not even for
         the same artwork added twice from a saved design. Shopify merges lines
         whose variant and properties are identical; this token never is. */
      props._promo_line = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    }
    // A data-URL artwork ref would bloat every cart/checkout request and is redundant now
    // that the real file is uploaded below. Keep the small production props.
    if (props._artwork_ref && String(props._artwork_ref).length > 200) delete props._artwork_ref;
    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    window.__slCartAddInFlight = true;

    /* THE PRODUCTION FILE IS BUILT HERE, inside the in-flight guard.
       ---------------------------------------------------------------------
       It has to be after the designId is minted (it travels in the SVG) and
       before the form is built, and it is asynchronous — it loads the image.
       Generating it after `__slCartAddInFlight` is set means a second click
       during that window is refused rather than starting a parallel add.

       ADDITIVE FOR NOW. A failure is logged and reported to telemetry, and the
       sale continues on the existing durable path: nothing downstream requires
       `_production_artwork` yet, so refusing checkout over it would cost real
       orders to protect a contract that does not exist. Making it mandatory is
       the NEXT commit, after real orders have shown it building reliably.

       What a failure must never do is substitute. No upload, no preview, no
       placeholder goes into this slot — a production file containing something
       other than the customer's design is worse than an absent one, because it
       looks like a real one. */
    T.mark('prepare');
    stage(SAVE_STAGE.prepare);
    let productionSvg = null;
    /* A cart graphic's print file shares the add-to-cart request with the
       original file and the cart thumbnail, and Shopify refuses the request
       above about 20 MB in total. So the print file is told how much room it
       has. The room is weighed against the byte-exact master copy (the
       original in a ZIP, which Shopify does not re-encode) by
       CG.printRequestPlan: the master rides whenever it can without costing
       the print file the original embedded whole, or most of its room.
       The original counted is the file the order attaches, which for an
       upload over the pixel cap is not the reduced working copy.
       A die-cut graphic is printed from the same kind of file (D-183). */
    {
      const ob = artOriginalRef.current || artBytesRef.current;
      const rp = (CG && CG.printRequestPlan)
        ? CG.printRequestPlan(ob ? ob.length : 0,
            Math.ceil((previewUrl ? String(previewUrl).length : 0) * 0.75) + 262144,
            /\.(png|jpe?g)$/i.test((art && art.name) || '') && !(bgOn && bgUrl))
        : null;
      cgPrintBudgetRef.current = rp ? rp.maxSvgBytes : 0;
      cgMasterRef.current = !!(rp && rp.master);
    }
    try { productionSvg = await renderProductionSvg(payload && payload.designId); }
    catch (e) { productionSvg = null; }
    T.mark('production_artwork');
    /* A CART GRAPHIC IS PRINTED FROM THIS FILE, so for this product it is not
       optional: no print file, no order. And the order records what the file's
       image really is, measured after it was built.
       A die-cut graphic is held to the same rule, and its file also needs a
       cut line that passed every check (D-183). */
    if (CG) {
      const pinfo = cgPrintInfoRef.current;
      if (!productionSvg || !pinfo) {
        window.__slCartAddInFlight = false;
        const cutRefused = dieCut ? cgDieCutRefusalRef.current : null;
        try { console.error('[sl-cart] cart graphic print file could not be built — refusing to add', cutRefused || ''); } catch (x) {}
        try {
          alert(cutRefused
            ? 'We couldn’t make an accurate cut line for this artwork at this size, so this die-cut graphic can’t be ordered online. Choose Rectangle, Square, Circle or Oval, or contact us for a die-cut quote.'
            : cgPrintBudgetRef.current < 1048576
            ? 'This artwork file is too large to order online as a cart graphic. Please upload a file under 15 MB, or contact us.'
            : 'Sorry — we couldn’t prepare the print file for this graphic. Please try again, or contact us.');
        } catch (x) {}
        return false;
      }
      props._production_image = CG.printImageSummary(pinfo);
      if (pinfo.kind === 'raster' && pinfo.source === 'raster') {
        props._effective_dpi = String(Math.round(pinfo.dpi));
        if (pinfo.reduced && pinfo.nativeDpi > 0) {
          /* The image came out below the source's density. If that moves it
             into the low-resolution band the customer has not been asked
             about, stop and ask, with the real figure. */
          const ratio = pinfo.dpi / pinfo.nativeDpi;
          let lvl = null;
          try {
            const SLU = window.SLUpload;
            lvl = SLU.qualityOf(SLU.analyzeBytes(artBytesRef.current, art ? art.name : '', {
              targetWidthIn: (cgDpiW || widthIn) / ratio * cgCapDiv, targetHeightIn: (cgDpiH || heightIn) / ratio * cgCapDiv })).level;
          } catch (x) { lvl = null; }
          if (lvl === 'low' && !qualityLow) {
            window.__slCartAddInFlight = false;
            setCgProdCap({ ratio: ratio, key: cgCapKey });
            return false;
          }
        }
      }
    }
    /* A die cut that left without a CutContour is drawn by hand in HQ; if the
       builder had not already said so, the customer hears it here, on the way
       to the cart, rather than never (D-176). The sale continues either way. */
    if (dieCut && productionSvg && !/id="CutContour"/.test(productionSvg)) {
      stage({ title: 'We’ll draw your cut line by hand', sub: 'Our team checks it before printing. You can check out as normal.' });
      await new Promise(function (r) { setTimeout(r, 2600); });
    }
    /* MOTO GVE: a shape held for review is flagged on the line (hidden: underscore), so HQ
       knows from the order list without opening the file (the `_art_review` pattern) */
    if (dieCut && productionSvg) {
      const gvWhy = slGeometryReviewOf(productionSvg);
      if (gvWhy) props._geometry_review = gvWhy;
    }
    /* ── THE RECT ON THE ORDER IS THE RECT PRODUCTION WAS BUILT FROM ──────
       `buildPayload()` ran before the blueprint did, so its `artworkRectIn` is
       the CLOSED-FORM estimate. When the contour fit needed its one correction
       pass, that estimate is not the placement any file actually used — and
       `_design_config` is what HQ hands Illustrator to reproduce production.
       Shipping the estimate would send the operator to a position nothing was
       ever drawn at, which is a subtler version of the bug this whole effort
       started from.

       Patched here rather than by rebuilding the payload: the design id, the
       pricing and the durable-artwork references are already settled, and
       re-deriving them to carry one corrected rect would put four other things
       at risk to fix one. */
    try {
      const cfr = cfitReportRef.current;
      if (cfr && cfr.contourFitPass === 2 && cfr.correctedRectIn &&
          payload && payload.transform && payload.transform.crop) {
        payload.transform.artworkRectIn = cfr.correctedRectIn;
        payload.transform.crop.artworkRectIn = cfr.correctedRectIn;
        payload.transform.crop.contourFit = {
          pass: cfr.contourFitPass,
          correctionScale: cfr.correctionScale,
          finalLongEdgeIn: cfr.finalLongEdgeIn,
          requestedLongEdgeIn: cfr.requestedLongEdgeIn
        };
      }
    } catch (x) {}
    if (!productionSvg) {
      try { console.error('[sl-cart] production artwork could not be built — ' +
                          'the order will carry no _production_artwork'); } catch (x) {}
      try { if (window.slTelemetry && window.slTelemetry.track) {
        window.slTelemetry.track('production_artwork_failed', {
          designId: (payload && payload.designId) || null,
          dieCut: !!dieCut
        });
      } } catch (x) {}
    }

    /* THE SHEET, LAID OUT (D-179). A sticker sheet order used to carry one
       sticker's production file and a count. This builds the finished sheet
       from that same file and the layout the customer was shown: every
       sticker in place with its kiss cut, and the trimmed sheet's own outer
       cut. Additive, exactly as the production file itself is: a failure
       costs this one file and is logged, and never substitutes anything. */
    let sheetLayoutSvg = null;
    try {
      const fin = payload && payload.productType === 'sheet' && payload.sheet && payload.sheet.finished;
      if (fin && productionSvg && window.SLSheetProduction) {
        const built0 = window.SLSheetProduction.build(productionSvg, fin, {
          designId: payload.designId,
          nominal: payload.sheet.sheetSize || null,
          sheets: payload.sheet.sheetQuantity
        });
        sheetLayoutSvg = built0 ? built0.svg : null;
        if (!sheetLayoutSvg) { try { console.error('[sl-cart] sheet layout could not be built — the order will carry no _sheet_layout'); } catch (x) {} }
      }
    } catch (e) { sheetLayoutSvg = null; }

    /* DURABLE ARTWORK (D-056). The three production assets — original file, flattened
       preview, canonical config — are attached as line-item FILE properties. Shopify
       uploads each to its CDN and puts the URL on the order, so the artwork survives the
       browser, the device and the session. That is the whole point: the previous
       localStorage preview stash died the moment a customer cleared site data or checked
       out on their phone after designing on desktop, and production never received the
       original at all.

       This REQUIRES multipart FormData. The old JSON body could not carry a file — the
       same limitation Shopify hit in Dawn (Shopify/dawn#731). Note the flat form shape
       (id / quantity / properties[...]), not the {items:[…]} JSON shape, and note that
       no Content-Type header is set: the browser must add the multipart boundary
       itself. */
    const DA = window.SLDesignAssets;
    const built = DA && DA.buildAddToCartForm ? DA.buildAddToCartForm({
      lineItem: { id: li.id, quantity: li.quantity, properties: props },
      payload: payload,
      designId: payload && payload.designId,
      createdAtMs: Date.now(),
      originalBytes: artOriginalRef.current || artBytesRef.current,
      originalName: art ? art.name : null,
      previewDataUrl: previewUrl,
      productionSvg: productionSvg,
      sheetLayoutSvg: sheetLayoutSvg,
      /* a cart graphic sends its master when printRequestPlan made room */
      omitMaster: !!CG && !cgMasterRef.current
    }) : null;

    if (!built) {                                            // module missing → never fake success
      window.__slCartAddInFlight = false;
      try { console.error('[sl-cart] SLDesignAssets unavailable — refusing to add without durable artwork'); } catch (x) {}
      try { alert('Sorry — we couldn’t add this to your cart. Please refresh and try again.'); } catch (x) {}
      return false;
    }

    /* THE ORIGINAL IS NOT OPTIONAL.
       ---------------------------------------------------------------------
       `buildAddToCartForm` reports what it managed to attach and what it did
       not. Having the module present is NOT the same as having the file: if
       the original bytes were never cached — an unreadable file, a browser
       with no `arrayBuffer`, a race on a slow phone — the form is still
       perfectly valid and the order still checks out, carrying a filename and
       no artwork. That is exactly the shape of orders #1012 and #1013.

       So the ORIGINAL is a hard requirement and nothing else is. A missing
       preview costs a thumbnail; a missing config costs a reprint's
       convenience; a missing original means nobody can make the sticker the
       customer just paid for. Refuse, keep the cart untouched, and say so. */
    if (built.skipped.indexOf('_artwork_original') !== -1) {
      window.__slCartAddInFlight = false;
      try {
        if (window.slTelemetry) window.slTelemetry.uploadOutcome(false, 'unknown');
      } catch (x) {}
      try { console.error('[sl-cart] original artwork was not attached — refusing to add. ' +
                          'skipped: ' + built.skipped.join(', ')); } catch (x) {}
      try { alert('Sorry — your artwork didn’t finish uploading, so we can’t add this to ' +
                  'your cart yet. Please re-select your file and try again.'); } catch (x) {}
      return false;
    }

    T.mark('build_form');
    try { console.info('[sl-cart] durable assets attached:', built.attached.join(', ') || 'none',
                       built.skipped.length ? '| skipped: ' + built.skipped.join(', ') : ''); } catch (x) {}
    stage(SAVE_STAGE.upload);

    /* THE NAVIGATION IS THE CALLER'S DECISION, NOT THIS FUNCTION'S.
       ---------------------------------------------------------------------
       This used to end by navigating to the cart page unconditionally, which
       is why "Add to cart" could not use it: the only way to reach the real
       cart was to leave the builder. Desktop therefore kept a SECOND,
       fake add — one that parked the payload in a global and rendered a
       confirmation panel — and "Build another sticker" discarded it. Nothing
       ever deleted a cart line; the line was never created.

       When we stay, the in-flight lock MUST be released. On the navigating
       path the page is about to be replaced so the flag dies with it, but a
       caller that stays would be permanently unable to add a second design —
       which is precisely the flow this change exists to enable. */
    var navigate = !(opts && opts.navigate === false);
    /* What we are actually asking a phone to send. Recorded so the timing
       report can be read against the payload that produced it — a slow add on
       a 900 KB upload is a different problem from a slow add on 60 KB. */
    var uploadBytes = 0;
    try {
      built.formData.forEach(function (v) {
        uploadBytes += (v && typeof v.size === 'number') ? v.size : String(v).length;
      });
    } catch (e) { uploadBytes = -1; }
    try {
      const r = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },          // NO Content-Type — the browser sets the boundary
        body: built.formData
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error('add ' + r.status + ' ' + t);
      }
      T.mark('upload');
      T.report(true, { bytes: uploadBytes, navigate: !!navigate });
      if (PROMO) promoTrack('promo_add', { pack: PROMO.packQty, widthIn: widthIn, heightIn: heightIn, finish: laminate });
      /* THE CART OUTCOME. Below the r.ok check, so it cannot fire for a
         refused add; above the navigate branch, so the cart-and-leave path
         reports before the page is replaced (the telemetry client flushes its
         queue on pagehide, by a transport that survives teardown — this bundle
         deliberately names none of that machinery, and a test enforces it).
         This is the shared submit for every path and both trees, which is
         exactly why it is here and not on a button. */
      slOutcome('cart_add_completed');
      if (navigate) {
        /* The saving state deliberately stays UP through the navigation. The
           page is about to be replaced; clearing it first would flash the idle
           button for the last few hundred milliseconds and read as "it did
           nothing" at the exact moment it worked. */
        stage(SAVE_STAGE.done);
        window.location.href = PROMO                         // Shopify cart is now authoritative
          ? cartUrl + (cartUrl.indexOf('?') < 0 ? '?' : '&') + 'promo_offer=1'   // W-PROMO: cart opens its upgrade offer
          : cartUrl;
        return true;
      }
      window.__slCartAddInFlight = false;                    // stay, and allow the NEXT design
      return true;
    } catch (e) {
      window.__slCartAddInFlight = false;                   // allow retry; never fake success
      T.mark('upload_failed');
      T.report(false, { bytes: uploadBytes });
      try { console.error('[sl-cart] add error', e); } catch (x) {}
      try { alert('Sorry — we couldn’t add this to your cart. Please try again.'); } catch (x) {}
      return false;
    }
  };
  // Go to cart → add the die-cut design to the REAL Shopify cart, then open the cart page. The preview is
  // the outlined/finished face the builder already shows (displayArt), falling back to the raw upload.
  /* Add to cart with a RENDERED PROOF, not the upload.
     ---------------------------------------------------------------------
     This used to pass `displayArt || shownArt` — a URL to the customer's own
     file — as the preview. `_artwork_preview` on order #1014 was therefore
     sha1-identical to `_artwork_original`: same bytes, same 1920x1080. The
     order carried two copies of the upload and no record of what the
     customer actually designed.

     The proof is rendered from the canonical geometry instead. If the render
     fails, the upload is NOT substituted: a file that is not a proof must
     never be stored under the proof's name, and the durable-artwork guard in
     sl-design-assets.js already treats a missing preview as a missing
     preview rather than a reason to refuse the sale. The ORIGINAL is
     untouched and is what production prints. */
  /* Mobile's Add to Cart. Same submitter, same saving state, and it navigates
     to the cart on success — so the state is held up through the transition
     rather than flashing back to idle as the page leaves. */
  const goCheckout = () => runAddToCart(buildOrderPayload, { navigate: true });
  /* The confirmation panel's "Go to cart" NAVIGATES. It must not call
     `goCheckout`: since `addToCart` now performs the real add, adding again
     here would put a SECOND line on the order for one design — the mirror of
     the bug this change fixes. The line is already in Shopify; this only
     opens it. */
  const goToCart = () => {
    try {
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    } catch (e) {}
  };
  // Flatten the live sticker (border ring + shaped face + background fill + the art
  // with its exact crop/zoom/rotate) into a single PNG so the sheet shows the real
  // finished sticker, not the raw upload. Die-cut art is already its own shape → keep as-is.
  /* THE PROOF. Draws the canonical geometry to a canvas — the same model the
     screen renders, so the exported image is the picture the customer
     approved rather than a second approximation of it.

     It used to bail out for die-cut and hand back `displayArt` — the upload
     URL — which is how an "approved proof" came to be byte-identical to the
     customer's source file. Die-cut is now composited like everything else:
     it has no ring and no face fill, but it very much has zoom, offset,
     rotation and flip, and those are the whole point of a proof. */
  /* @param {Object} [opts] { longEdgePx } — bound the output for the blueprint.
     Omitted, the behaviour is exactly what it always was (3x supersample), so
     every existing caller is unchanged. */
  /* @param {Object} [opts]
       longEdgePx      bound the output for the blueprint
       onComposite     see below
       sourceOverride  a canvas in the SAME FRAME as `g.src` to draw INSTEAD of
                       it. The double dip needs three pictures of one sticker —
                       the silhouette, the artwork without its baked border,
                       and the border composite — and they must be registered
                       to the last pixel. Drawing them through this one
                       function guarantees that: same plan, same transform
                       chain, same rounding. A second copy of the chain here is
                       exactly how two renderers come to disagree.
       skipEncode      resolve as soon as the canvas exists; the caller only
                       wanted the pixels, and toDataURL is the expensive part. */
  const rasterizeFace = (opts) => new Promise(resolve => {
    const g = proofGeometry(opts && opts.fitAdjust);
    if (!g.src) {
      resolve(null);
      return;
    }
    const want = opts && +opts.longEdgePx > 0 ? +opts.longEdgePx : 0;
    const Sw = g.boxW, Sh = g.boxH;
    /* ── THE ANALYSIS RASTER IS SIZED BY THE ORDER, NOT BY THE WINDOW ─────
       `want` is `blueprintLongEdge(orderedIn)` and is a property of the ORDER.
       `Sw`/`Sh` are the on-screen preview box, which moves with the browser.
       The old form computed the canvas as `Sw * (want / max(Sw, Sh))`, which
       is exactly `want` on the long axis in real arithmetic — and is not what
       a float gives you. Measured on the real builder: a 417px preview box
       produced 417 * (480/417) = 479.99999999999994, and assigning that to
       `canvas.width` truncates to 479, while a 413px box landed on 480.

       One pixel of evidence is not a rounding detail. With the canonical frame
       held identical, a 480 vs 479 mask moved the reconstruction by 0.125 of a
       border width at the offset, 0.321 at pathclean and 0.189 at lockrefit —
       which is the whole of the residual drift Milestone 10C could not explain,
       and it is the reason two customers on different screens got different cut
       geometry from the same order.

       So the dimensions are ROUNDED INTEGERS DERIVED FROM `want`: the long axis
       is exactly `want`, and the short axis is `want` scaled by the box's own
       aspect and rounded once. Nothing multiplies the window by a float any
       more, and the same order produces the same raster on every device.

       PREVIEW IS UNAFFECTED. Preview renders call this with no `longEdgePx`,
       take the `want === 0` branch and keep their responsive 3x supersample of
       whatever box the layout gave them. The separation between the preview
       raster and the production analysis raster is exactly this branch. */
    const cv = document.createElement('canvas');
    if (want > 0) {
      /* THE ASPECT COMES FROM `effAR`, NOT FROM THE ROUNDED BOX.
         `boxW`/`boxH` are `round(w)`/`round(h)`, and that rounding is its own
         source of window dependence: deriving the short edge from their ratio
         still produced 480x319, 480x320 and 480x321 for the same 1.5-aspect
         order at different window sizes. `effAR` is the FINISHED aspect — the
         customer's typed dimensions, the contour fit, or the chosen shape —
         and none of those know the window exists. */
      /* 10K: this expression now lives in `productionFrame`, because the
         geometry frame has to be the same number and two copies that agree
         today are how one of them drifts. The fallback below is only for a
         caller that asked for a long edge this component cannot resolve. */
      const pf = productionFrame(Sw, Sh);
      if (pf && pf.longEdgePx === want) {
        cv.width = pf.w;
        cv.height = pf.h;
      } else {
        const ar = (isFinite(effAR) && effAR > 0)
          ? effAR
          : ((Sw > 0 && Sh > 0) ? Sw / Sh : 1);
        if (ar >= 1) {
          cv.width = want;
          cv.height = Math.max(1, Math.round(want / ar));
        } else {
          cv.height = want;
          cv.width = Math.max(1, Math.round(want * ar));
        }
      }
    } else {
      cv.width = Sw * 3;
      cv.height = Sh * 3;
    }
    const ctx = cv.getContext('2d');
    /* ── ONE UNIFORM SCALE, CENTRED ON THE CANVAS ───────────────────────
       This used to be `ctx.scale(cv.width / Sw, cv.height / Sh)`: one factor
       per axis, each the ratio of two INDEPENDENTLY ROUNDED numbers. The canvas
       is `want x round(want/ar)` and the box is `round(w) x round(h)`, and both
       are honest roundings of the same finished aspect - but they are not the
       same rounding, and the discrepancy is different on every layout.

       Measured on the real builder, same order, 08-monstera-leaf:

           box 558x518 (mobile)   box 672x624 (desktop)   canvas 600x557 both
           a/d  0.99998070992572985   vs   1.0002564801811133
           net drawn height 601.696   vs   601.530     (0.166 px apart)
           net y            -17.9789  vs   -17.8960    (0.083 px apart)

       against a rasteriser that resolves about 4e-4 px (Milestone 10F). So the
       artwork landed at a different height on a phone than on a desktop, the
       pass-3 evidence differed, and every valid non-square cross-layout pair in
       Milestone 10H failed before MOTO ever ran.

       Ordinary artwork fit is not a distortion, so the scale is UNIFORM: one k,
       `a == d` exactly. And the centring is taken from the CANVAS rather than
       from the box, because `k * (Sh / 2)` carries the box's rounded aspect and
       is precisely the 0.083 px above - a uniform scale alone would not have
       been enough.

       Both layouts now reconstruct the same transform from the same order:
       `k * Sw` is `want` by construction, and the art is fitted to the box
       WIDTH, so its height in box units is proportional to `Sw` too and scales
       to the same place. The residual `k * Sh` difference lands only in the
       canvas padding, which nothing is drawn into.

       The preview branch is unchanged: its canvas is exactly `Sw*3 x Sh*3`, so
       k is 3 and both offsets are zero - the same transform it always had. */
    const kFit = (want > 0) ? (cv.width / Sw) : 3;
    ctx.setTransform(kFit, 0, 0, kFit,
                     (cv.width - Sw * kFit) / 2,
                     (cv.height - Sh * kFit) / 2);
    /* ── THE PRODUCTION RASTER TRANSFORM, PUBLISHED ──────────────────────
       The plan is expressed in box units and then scaled onto the analysis
       canvas, so what actually decides the evidence pixels is the COMPOSED
       transform, not either half of it. Two layouts can agree on the canvas
       size and still disagree here, and there was no way to see that from
       outside — Milestone 10D could only observe the resulting pixels and had
       to reason backwards about why they differed.
       Diagnostic only: nothing reads it, and it is the same handful of numbers
       HQ will need to prove it draws the same evidence the browser does. */
    if (want > 0) {
      try {
        const kx = kFit, ky = kFit;
        const rf = {
          canvas: { w: cv.width, h: cv.height },
          box: { w: Sw, h: Sh },
          scale: { x: kx, y: ky, uniform: kx === ky },
          plan: { scale: g.scale, x: g.x, y: g.y, fit: g.fit, dieCut: !!g.dieCut },
          /* the plan as the canvas actually receives it */
          composed: { scale: g.scale * kx, x: (g.x || 0) * kx, y: (g.y || 0) * ky }
        };
        window.__slRasterFit = rf;
        /* the mobile route navigates after add-to-cart and takes page globals
           with it, so the last production render is kept where a reader can
           still find it afterwards */
        try { sessionStorage.setItem('__slRasterFit', JSON.stringify(rf)); } catch (eS) {}
      } catch (eDbg) { /* diagnostics never cost an order */ }
    }
    /* The corner-radius rule is the CORE'S (SLBuilderCore.resolveRadius), so the
       canvas proof and the production SVG round the same corners. It used to be
       a private lambda here; the SVG exporter would have needed a second copy,
       and two copies of a shape rule is how two renderers come to disagree. */
    const radOf = (val, box) => {
      const C = _szCore();
      if (C && C.resolveRadius) return C.resolveRadius(val, box);
      if (val === '50%' || val === '999px') return box / 2;
      const n = parseFloat(val);
      return isNaN(n) ? 0 : Math.min(n, box / 2);
    };
    const rr = (x, y, ww, hh, r) => {
      r = Math.min(r, ww / 2, hh / 2);
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + ww, y, x + ww, y + hh, r);
      ctx.arcTo(x + ww, y + hh, x, y + hh, r);
      ctx.arcTo(x, y + hh, x, y, r);
      ctx.arcTo(x, y, x + ww, y, r);
      ctx.closePath();
    };
    const img = new Image();
    /* The art may be a same-origin data URL (fresh upload) or a Shopify CDN
       URL (an outlined die-cut face). Without CORS the canvas is tainted and
       `toDataURL` throws, which previously would have surfaced as a silent
       catch and another raw-upload proof. */
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      /* THE PLAN IS COMPUTED IN THE CORE (SLBuilderCore.proofDrawPlan) — pure,
         shared and unit-tested. This function only executes it. Re-deriving
         any of it here would recreate the second approximation that put the
         customer's raw upload on the order under the proof's name. */
      const core = window.slBuilder;
      if (!core || !core.proofDrawPlan) { resolve(null); return; }
      /* ── THE CENTRING OFFSETS ARE NORMALISED TO THE CANVAS ──────────────
         The scale above is deliberately uniform, which fixes the drawn SIZE.
         It does not fix the drawn POSITION, because the composed translation
         works out as

             f = cv.height / 2 + kFit * g.y

         and `kFit` is derived from the WIDTH while `g.y` is derived from the
         HEIGHT. Their product therefore carries `Sh / Sw` - the rounded box
         aspect - which is the same quantity the uniform scale just removed.
         Measured on 07-wave-organic after the scale fix: size agreed to 2e-7
         but vertical position still differed by 3.456e-3 px, and that flipped
         four boundary pixels.

         Pre-scaling each offset by its own axis ratio makes the composition
         `cv.width * (g.x / Sw)` and `cv.height * (g.y / Sh)` - each a FRACTION
         of the box applied to the canvas, which is what the offsets always
         meant. The transform stays uniform; only the offsets are normalised.

         In the preview branch the canvas is exactly `Sw*3 x Sh*3`, so both
         ratios are 1 and the plan is untouched. */
      let gDraw = g;
      if (want > 0 && Sw > 0 && Sh > 0 && kFit > 0) {
        const nx = (cv.width / Sw) / kFit, ny = (cv.height / Sh) / kFit;
        if (isFinite(nx) && isFinite(ny)) {
          gDraw = Object.assign({}, g, { x: (g.x || 0) * nx, y: (g.y || 0) * ny });
        }
      }
      const plan = core.proofDrawPlan(gDraw, img.naturalWidth, img.naturalHeight);
      /* WHERE THE SOURCE LANDED (D-176). The exact affine this canvas draws
         the source with - the box-to-canvas scale and centring set above, then
         the plan's translate, scale and flip - so a caller can carry geometry
         from this canvas back into the source's own pixels without
         re-deriving any of it. Reported, never acted on here. */
      if (opts && typeof opts.onPlan === 'function') {
        try {
          const srcP = (opts && opts.sourceOverride) || null;
          opts.onPlan({
            art: plan.art, kFit: kFit,
            offX: (cv.width - Sw * kFit) / 2, offY: (cv.height - Sh * kFit) / 2,
            cvW: cv.width, cvH: cv.height,
            srcW: srcP ? srcP.width : img.naturalWidth, srcH: srcP ? srcP.height : img.naturalHeight
          });
        } catch (eP) {}
      }

      const paint = (r) => {
        if (!r) return;
        ctx.save();
        rr(r.x, r.y, r.w, r.h, radOf(r.radius, Math.min(r.w, r.h)));
        /* A cart graphic's Oval has unequal sides, and the screen draws it as
           an ellipse. A sticker circle always has equal sides, where the two
           are the same shape, so this is taken on Cart Graphics only. */
        if (CG && r.radius === '50%' && Math.abs(r.w - r.h) > 1e-6) {
          ctx.beginPath();
          ctx.ellipse(r.x + r.w / 2, r.y + r.h / 2, r.w / 2, r.h / 2, 0, 0, Math.PI * 2);
          ctx.closePath();
        }
        if (r.clip) ctx.clip();
        if (r.fill) { ctx.fillStyle = r.fill; ctx.fillRect(r.x, r.y, r.w, r.h); }
      };
      const depth = (plan.ring ? 1 : 0) + (plan.face ? 1 : 0);
      paint(plan.ring);
      paint(plan.face);

      const a = plan.art;
      ctx.save();
      /* translate → scale → rotate → flip, the order the CSS applies. */
      ctx.translate(a.translate.x, a.translate.y);
      ctx.scale(a.scale, a.scale);
      ctx.rotate(a.rotateRad);
      ctx.scale(a.flipX ? -1 : 1, 1);
      /* THE OVERRIDE MUST DESCRIBE THE SAME FRAME, or the plan that was
         computed from `img` would be placing something else. Checked rather
         than assumed: a mismatch means no picture, never a misregistered one. */
      const srcDraw = (opts && opts.sourceOverride) || img;
      if (srcDraw !== img &&
          (srcDraw.width !== img.naturalWidth || srcDraw.height !== img.naturalHeight)) {
        resolve(null);
        return;
      }
      ctx.drawImage(srcDraw, -a.w / 2, -a.h / 2, a.w, a.h);
      ctx.restore();
      for (let i = 0; i < depth; i++) ctx.restore();
      /* THE FINISHED CANVAS, offered to the caller before it is encoded.
         `onComposite` exists so the cut-path reconstruction can read the ALPHA
         of the very pixels that become the blueprint, rather than rendering a
         second composite that could differ from this one. It is wrapped
         separately: a failure to read the mask must never cost the proof. */
      if (opts && typeof opts.onComposite === 'function') {
        /* The third argument is the artwork's placement IN CANVAS PIXELS, the
           transform this draw actually used. A die-cut cart graphic places its
           full-resolution artwork in the print file with it, so the picture
           sits exactly where the silhouette its cut line was traced from sat. */
        const artPx = (want > 0) ? {
          w: a.w * kFit, h: a.h * kFit,
          translate: { x: (cv.width - Sw * kFit) / 2 + kFit * a.translate.x,
                       y: (cv.height - Sh * kFit) / 2 + kFit * a.translate.y },
          scale: a.scale, rotateRad: a.rotateRad, flipX: !!a.flipX
        } : null;
        try { opts.onComposite(cv, ctx, artPx); } catch (e) {}
      }
      if (opts && opts.skipEncode) { resolve(cv); return; }
      /* PRESENTATION FRAMING, AND ONLY THAT.
         `frame: 'content'` re-crops the finished composite to the sticker it
         actually contains, plus even breathing room. It is asked for by the
         THUMBNAIL path and by nothing else: the production exporter takes this
         same canvas at its full size, so print geometry never sees this.

         WHY THE THUMBNAIL NEEDED IT. The canvas is the builder box, and for a
         transformed or tall design most of that box is empty — one real order
         put the sticker in 40% of the width, hard against one edge. Cart and
         HQ both do object-fit:contain on this asset, which faithfully fits all
         that emptiness into a 116px tile and leaves the sticker small and off
         to one side. The CSS was never wrong; it was being handed the wrong
         frame. */
      var out = cv;
      if (opts && opts.frame === 'content') {
        try { out = slContentFrame(cv) || cv; } catch (e) { out = cv; }
      }
      /* A tainted canvas throws here. Returning the upload would put a
         non-proof on the order wearing the proof's name, so this reports
         failure and lets the caller decide. */
      /* NOT asynchronous: this PNG is embedded in the production file, and toBlob
         is not byte-identical to toDataURL for every canvas (measured, D-176) */
      try { resolve(out.toDataURL('image/png')); }
      catch (e) { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = g.src;
  });

  /* THE PRODUCTION FILE. Same plan, same picture, different medium.
     ---------------------------------------------------------------------
     `rasterizeFace` above flattens the canonical plan to a PNG so the customer
     can see what they approved. This renders the SAME plan as an SVG so the
     operator can OPEN it: physical inches, the raster embedded, ring and face
     as editable vectors, and a named cut path on shaped stickers.

     It re-reads `proofGeometry()` rather than receiving anything from the
     rasterizer, so both executors start from one description of the design and
     neither can drift. All the layout arithmetic lives in
     SLProductionArtwork/SLBuilderCore; this function only fetches pixels and
     hands them over.

     A failure resolves null. There is deliberately no fallback: the upload is
     not a production file, and filing it as one is the #1014 defect wearing a
     new property name. */

  /* The operator summary that travels in the document. Every value is read
     from the canonical payload the order already carries — this adds no schema
     and invents nothing. */
  const productionMeta = (designId) => ({
    designId: designId || null,
    sizeText: widthIn + ' × ' + heightIn + ' in',
    quantity: effQty,
    material: matLabel || material,
    finish: CG ? (CG.LAUNCH.finishConfirmed ? CG.finishById(CG.resolveFinish(laminate)).label + ' vinyl, no laminate' : 'White vinyl, no laminate (finish not confirmed)') : (lamLabel || laminate),
    shape: dieCut ? 'Die Cut' : (shapeLabel || shape),
    /* Built from component-scope state, NOT from the cart's `borderVal` — that
       one is a React element declared inside the mobile review renderer, so it
       is neither in scope here nor a string. The thickness matters to the
       operator as much as the colour, so both travel. */
    border: border === 'none' ? 'None'
      : [(BORDERS.find(b => b.id === border) || {}).label || (border === 'custom' ? borderColor : border),
         (BSIZES.find(b => b.id === bsize) || {}).label || bsize,
         CG ? (+(+thick).toFixed(4)) + ' in' : null].filter(Boolean).join(' · '),
    fileName: art ? art.name : null,
    /* THE FIT CHAIN, RECORDED WITH THE FILE THAT IT PRODUCED.
       ------------------------------------------------------------------
       A production file that comes out wrong cannot be diagnosed from the
       file alone: every number that decided its scale lives in React state
       that dies with the tab. Order SL-1M1T3-85QBP-M94S4 shipped a die-cut
       squared off on four sides, and answering "by how much, and from which
       term" needed values nobody had kept. These are those values. They
       change no geometry - they are written into the Metadata text block
       beside the design id, and nothing reads them back. */
    fitDiag: (function () {
      try {
        var e = paintedExtent;
        return {
          fitFactor: (cfit && cfit.fitFactor != null) ? +cfit.fitFactor.toFixed(6) : null,
          contourScale: +(+contourScale).toFixed(6),
          dieScale: +(+dieScale).toFixed(6),
          borderIn: (cfit && cfit.borderIn != null) ? +cfit.borderIn.toFixed(4) : null,
          inchesPerPx: (cfit && cfit.inchesPerPx != null) ? +cfit.inchesPerPx.toFixed(6) : null,
          finishedIn: cfit ? (+cfit.finishedW.toFixed(4) + 'x' + (+cfit.finishedH.toFixed(4))) : null,
          artworkRectIn: (cfit && cfit.artworkRectIn)
            ? ([+cfit.artworkRectIn.x.toFixed(4), +cfit.artworkRectIn.y.toFixed(4),
                +cfit.artworkRectIn.width.toFixed(4), +cfit.artworkRectIn.height.toFixed(4)].join(','))
            : null,
          /* the upload, the silhouette inside it, and the padded frame the
             baker actually drew - the three bases the scale is built from */
          uploadPx: (artRes && artRes.w) ? (artRes.w + 'x' + artRes.h) : null,
          silPx: artSil ? ([artSil.x, artSil.y, artSil.w, artSil.h].join(',')) : null,
          framePx: outlineFrame ? (outlineFrame.cw + 'x' + outlineFrame.ch) : null,
          framePad: outlineFrame ? outlineFrame.pad : null,
          frameRad: outlineFrame ? outlineFrame.rad : null,
          radBasis: outlineFrame ? (outlineFrame.basis || 'upload') : null,
          silLongPx: outlineFrame ? (outlineFrame.silLongPx || null) : null,
          /* the fraction of the padded frame that carries ink, measured by the
             baker itself - this is the painted sticker */
          extent: e ? (+e.wFrac.toFixed(6) + 'x' + (+e.hFrac.toFixed(6))) : null,
          /* the box the composite is drawn into, and the painted sticker's
             size in that box after every scale above has been applied */
          boxPx: Math.round(w) + 'x' + Math.round(h),
          paintedPx: e ? (Math.round(w * dieScale * e.wFrac) + 'x' + Math.round(h * dieScale * e.hFrac)) : null
        };
      } catch (e2) { return null; }
    })()
  });

  /* `canvasHasAlpha` lived here to choose PNG vs JPEG for the blueprint
     raster. The blueprint now embeds the COMPOSITED proof from
     `rasterizeFace`, which is PNG because a shaped sticker is transparent
     outside its own outline — so there is no format question left to ask,
     and an unused helper is a suggestion to call it again (D-105). */
  /* ==================== DIE-CUT CUT PATH (milestone 2) ====================
     Reconstruct a vector contour for the sticker the customer approved, and
     hand it to the production exporter in the exporter's own coordinates.

     ── WHAT IS ANALYSED, AND WHY IT IS THE RIGHT THING ────────────────────
     The COMPOSITED PROOF — the same canvas that becomes the blueprint raster.
     It already carries the crop, zoom, offset, rotation, flip and, crucially,
     the BORDER/KEYLINE: for a die-cut sticker `proofGeometry().src` is
     `displayArt`, which is `outlinedUrl` whenever a border is selected, so its
     alpha is the OUTER edge of the sticker the customer bought. Reconstructing
     the artwork's own edge instead would cut the purchased keyline away.

     ── THE COORDINATE TRANSFORM, IN FULL ──────────────────────────────────
     This is the highest-risk part of the integration, and it is deliberately
     the simplest thing available rather than the cleverest.

     `rasterizeFace` draws the plan into an analysis canvas whose long edge is
     EXACTLY `blueprintLongEdge(orderedIn)` and whose short edge is that scaled
     by the box's aspect and rounded once (Milestone 10D — it used to be
     `boxW * K`, which put a float through an integer truncation and made the
     raster one pixel smaller on some window sizes). boxW/boxH are
     `plan.canvas.w/h`. The exporter then places that image with

         <image x="0" y="0" width="cw" height="ch">      cw,ch = plan.canvas

     so the composite is stretched onto the viewBox EXACTLY. A mask pixel and a
     viewBox point are therefore related by one axis-aligned scale:

         viewBoxX = (maskX + 0.5) * cw / maskW
         viewBoxY = (maskY + 0.5) * ch / maskH

     and nothing else. No rotation term, no crop term, no border term — every
     one of those is already inside the pixels being measured, which is the
     entire reason this source was chosen over the upload. The half-pixel is
     the sample centre: a traced contour point is a pixel INDEX, and its centre
     is where that sample was taken.

     `sx` and `sy` are computed from the ACTUAL canvas dimensions, because the
     two axes are rounded independently and no single factor fills both exactly.
     Using the real dimensions makes the mapping agree with the `<image>`
     placement by construction, whatever those dimensions turn out to be. */
  /* THE DOUBLE DIP.
     ---------------------------------------------------------------------
     Milestone 2 reconstructed a cut line from the FINISHED composite — the
     one with the raster border already dilated into it — so it was
     reconstructing the damage that dilation had caused. A 22px stair under a
     28px disk leaves ~2.2px of scalloping, and the cut inherited every bit of
     it.

     The order is now inverted. The un-dilated silhouette is reconstructed
     FIRST, and the border is grown from the clean path as geometry:

         sil  ->  analyzeMask  ->  P     the artwork's true outline
         P    ->  offsetPath   ->  B     the sticker's true outer edge

     B is then used twice — as the printed border and as the CutContour —
     from one string, so the printed edge and the cut edge cannot drift.

     Nothing here can cost the order. Every failure returns `accepted:false`
     and the caller falls back to exactly today's behaviour. */
  const buildDoubleDip = (plan, silComposite, geom, opts) => {
    const CPX = window.SLCutPath;
    const out = { ran: false, accepted: false, reason: null, evidence: null,
                  bands: null, cutPath: null, P: null, B: null };
    if (!CPX || !CPX.doubleDip) { out.reason = 'engine_unavailable'; return out; }
    if (!silComposite) { out.reason = 'no_silhouette_composite'; return out; }
    const cw = +plan.canvas.w, ch = +plan.canvas.h;

    /* ── THE ONE TRANSPORT OUT OF CANONICAL SPACE ──────────────────────────
       Carries a whole doubleDip result from the canonical frame back into
       production coordinates, so that everything downstream — the size gates,
       the guard, the physical size authority, the exported file — keeps working
       in exactly the units it always has.

       Every path goes through `CPX.transformPath`, the engine's own vector
       transport, rather than being re-derived: a second implementation of
       "scale a path" is how two coordinate authorities start disagreeing, and
       this one has to be exactly the inverse of the frame or the sticker changes
       size. The transform is a uniform scale about the origin, so it is exactly
       invertible and cannot introduce an aspect or centring error.

       The MASK is not resampled. Its pixels are the evidence and they are the
       same pixels either way; only `sx`/`sy`, the scale that maps a mask pixel
       to a coordinate, are restated.

       It lives INSIDE buildDoubleDip so that it uses the same `CPX` the caller
       already resolved. `sl-cutpath` is loaded as a separate asset and can be
       absent, and a second reference to it reached from an outer scope is how a
       stale or undefined engine gets used by one path and not another;
       tests/contour-export-integrity holds that line.

       Returns null if ANY path fails to transport. A partial transport would
       leave some geometry in canonical units and some in production units, which
       is far worse than falling back to the production viewBox and behaving
       exactly as before. */
    const slCanonToProduction = (gc, fr) => {
      if (!gc || !fr) return null;
      /* a refused reconstruction has no geometry to move, and its refusal is
         just as true in either frame */
      if (!gc.accepted) return gc;
      const T = { sx: 1 / fr.k, sy: 1 / fr.k, tx: 0, ty: 0 };
      const move = (d) => {
        if (!d) return { ok: true, d: d };
        const t = CPX.transformPath(d, T);
        return t ? { ok: true, d: t } : { ok: false, d: null };
      };
      const cut = move(gc.cutPath), P = move(gc.P), B = move(gc.B);
      if (!cut.ok || !P.ok || !B.ok) return null;

      let bands = gc.bands;
      if (gc.bands && gc.bands.length) {
        bands = [];
        for (let i = 0; i < gc.bands.length; i++) {
          const m = move(gc.bands[i].d);
          if (!m.ok) return null;
          bands.push({ d: m.d, fill: gc.bands[i].fill });
        }
      }
      /* THE IDENTITY SURVIVES THE CROSSING. Band 0 and the cut line are one
         string used twice; transporting them separately would produce two equal
         strings by luck rather than one by construction, so the cut line's
         transported form is reused directly. */
      if (bands && bands.length) bands[0] = { d: cut.d, fill: gc.bands[0].fill };

      const moved = Object.assign({}, gc, { cutPath: cut.d, P: P.d, B: B.d, bands: bands });
      if (gc.inkMask) {
        moved.inkMask = Object.assign({}, gc.inkMask, {
          sx: ((+gc.inkMask.sx > 0 ? +gc.inkMask.sx : 1) / fr.k),
          sy: ((+gc.inkMask.sy > 0 ? +gc.inkMask.sy : 1) / fr.k)
        });
      }
      return moved;
    };

    const mw = silComposite.width | 0, mh = silComposite.height | 0;
    if (!(cw > 0) || !(ch > 0) || !(mw > 0) || !(mh > 0)) { out.reason = 'degenerate_dimensions'; return out; }

    let id;
    try { id = silComposite.ctx.getImageData(0, 0, mw, mh); }
    catch (e) { out.reason = 'mask_unreadable_tainted_canvas'; return out; }

    const t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    /* ── MOTO REASONS IN THE CANONICAL FRAME, NOT IN THE BROWSER'S ───────
       `cw`/`ch` come from `plan.canvas`, which is the on-screen preview box.
       That is a presentation quantity — it moves with the device, the window
       and the stage measurement — and until Milestone 10C it was also the unit
       every geometric tolerance was written in. Measured on the real customer
       G with the evidence held constant and ONLY the viewBox varied, the
       reconstruction stages drifted by up to 0.90 of a border width and the
       guard's chosen candidate changed three ways across the range.

       So the engine is asked for the same geometry in a frame chosen from the
       ORDER instead: `SLCanonical` scales until the white border is always the
       same number of units, which fixes every tolerance in every module at
       once — including the ones still buried as absolute hard floors, which is
       what 10B could only fix one constant at a time.

       THE MASK IS NOT RESAMPLED. `silMask` and `viewBox` are independent in
       the engine, so the evidence handed in here is bit-identical to what the
       production viewBox would have received; only the coordinates the answer
       comes back in have changed.

       Everything below this block still sees PRODUCTION coordinates. The
       canonical result is transported back immediately, once, through the
       engine's own `transformPath`, so the size gates, the guard, the physical
       size authority and the exported file are all untouched by this and keep
       their existing meaning. `geoCanon` is kept alongside purely so the
       reconstruction stages can reason in the canonical frame. */
    const CNx = (typeof window !== 'undefined') && window.SLCanonical;
    let frC = null;
    if (CNx && CNx.frameFromDistance) {
      frC = CNx.frameFromDistance({ distance: geom.distance, viewBoxW: cw, viewBoxH: ch });
    }
    /* THE GUARD'S OWN TOLERANCE, HANDED TO THE ENGINE (D-175). A fitted job is
       judged by contour-fit to `slContourTolUnits` on the long-axis scale; the
       engine is told that same number so it never offers a primitive whose
       size already disagrees with the silhouette by more. Same expression as
       the size gates below; an unfitted job states none and is unchanged. */
    const extentUL = (opts && +opts.uLong > 0) ? +opts.uLong
                   : ((geom && +geom.unitsPerInch > 0) ? +geom.unitsPerInch : 0);
    const extentTolU = (opts && opts.fitted) ? slContourTolUnits(cw, ch, extentUL) : 0;
    /* TRACE FIRST ... VALIDATE LAST (D-176). Every job, fitted or not: the
       engine judges its own cut against the artwork's pixels and climbs to a
       more faithful reconstruction rather than ship one that strays. */
    /* a die-cut cart graphic's floor (SL_CG_TRACE_NOISE_PX) arrives as
       `traceNoisePx`, in evidence pixels; every other job states none */
    const traceTolU = (extentUL > 0)
      ? Math.max(SL_TRACE_TOL_IN * extentUL,
                 (opts && +opts.traceNoisePx > 0 && mw > 0) ? +opts.traceNoisePx * (cw / mw) : 0)
      : 0;
    const silForTrace = { data: id.data, width: mw, height: mh };
    /* MOTO GVE (Phase B, -49 27 Oct): the inch scale is the one the contour fit pins (the LONG axis,
       as the size gates above use), and the cut box an exact primitive is built at is the fit's
       UNROUNDED finished box (border included) - never the 0.1in-rounded label. The engine reads
       only its long axis; the short axis comes from the artwork. Both in production units here. */
    const gveUnitsPerInch = extentUL;
    const gveOrdered = (opts && opts.orderedIn && +opts.orderedIn.w > 0 && +opts.orderedIn.h > 0 && extentUL > 0)
      ? { w: +opts.orderedIn.w * extentUL, h: +opts.orderedIn.h * extentUL } : null;
    let geo, geoCanon = null;
    try {
      if (frC && frC.ok) {
        geoCanon = CPX.doubleDip({
          silMask: { data: id.data, width: mw, height: mh },
          viewBox: { w: frC.w, h: frC.h },
          distance: frC.d,
          borderFill: geom.borderFill,
          faceFill: geom.faceFill,
          /* the halo is a distance too, so it travels with the frame */
          haloPx: (+geom.haloPx > 0) ? geom.haloPx * frC.k : geom.haloPx,
          /* the outline fit stated in inches, die-cut cart graphics only
             (D-183); undefined everywhere else, which is the engine default */
          engineOpts: (opts && opts.engineOpts) || undefined,
          offsetOpts: (opts && opts.offsetOpts) || undefined,
          /* ...and so is the tolerance */
          extentTolUnits: extentTolU * frC.k,
          validateTolUnits: traceTolU * frC.k,
          /* MOTO GVE, in this call's units (an engine without Phase B reads neither) */
          unitsPerInch: gveUnitsPerInch > 0 ? gveUnitsPerInch * frC.k : undefined,
          orderedUnits: gveOrdered ? { w: gveOrdered.w * frC.k, h: gveOrdered.h * frC.k } : undefined
        });
        geo = slCanonToProduction(geoCanon, frC);
        /* A refusal to transport is a refusal to use the frame: falling back to
           the production viewBox is exactly today's behaviour and is far better
           than geometry in the wrong coordinate system reaching the size gates. */
        if (!geo) { geoCanon = null; frC = null; }
      }
      if (!geo) {
        geo = CPX.doubleDip({
          silMask: { data: id.data, width: mw, height: mh },
          viewBox: { w: cw, h: ch },
          distance: geom.distance,
          borderFill: geom.borderFill,
          faceFill: geom.faceFill,
          haloPx: geom.haloPx,
          engineOpts: (opts && opts.engineOpts) || undefined,
          offsetOpts: (opts && opts.offsetOpts) || undefined,
          extentTolUnits: extentTolU,
          validateTolUnits: traceTolU,
          unitsPerInch: gveUnitsPerInch > 0 ? gveUnitsPerInch : undefined,
          orderedUnits: gveOrdered || undefined
        });
      }
    } catch (e) { out.reason = 'engine_threw:' + (e && e.message); return out; }
    out.ran = true;
    out.canonicalFrame = frC ? { d: frC.d, k: +frC.k.toFixed(6),
                                 w: +frC.w.toFixed(3), h: +frC.h.toFixed(3) } : null;
    const ms = ((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - t0;

    out.evidence = {
      engine: geo.version, pFamily: geo.pFamily, pConfidence: geo.pConfidence,
      pLane: geo.pLane, bFamily: geo.bFamily, samplePx: geo.samplePx,
      gridPxAcrossBorder: geo.gridPxAcrossBorder ? +geo.gridPxAcrossBorder.toFixed(2) : 0,
      borderNone: !!geo.borderNone,
      widthMedian: geo.widthMedian, widthMax: geo.widthMax, widthRms: geo.widthRms,
      selfIntersections: geo.selfIntersections,
      maskW: mw, maskH: mh, viewBoxW: cw, viewBoxH: ch,
      reconstructMs: geo.tReconstruct, offsetMs: geo.tOffset, totalMs: Math.round(ms),
      /* which faithful rung the cut came from, and every rung's verdict */
      traceRung: geo.rung || null,
      traceAttempts: (geo.attempts || []).map(function (t) {
        return t.rung + ':' + (t.valid ? 'ok' : (t.accepted ? (t.failures || []).join('+') : ('refused ' + t.reason)));
      }),
      traceTolIn: extentUL > 0 ? +(traceTolU / extentUL).toFixed(4) : SL_TRACE_TOL_IN
    };
    if (!geo.accepted) {
      out.reason = geo.reason;
      /* MOTO GVE: the refusal carries the shape for HQ to review. A refusal
         crosses the canonical frame untouched (slCanonToProduction), so the
         review is moved here, by the same 1/k the cut would have been. */
      if (geo.reason === 'geometry_review_required' && geo.review) {
        const RT = frC ? { sx: 1 / frC.k, sy: 1 / frC.k, tx: 0, ty: 0 } : { sx: 1, sy: 1, tx: 0, ty: 0 };
        out.review = slTransformGeometryReview(geo.review, RT, CPX.transformPath) || null;
      }
      return out;
    }

    /* ── A BORDERLESS CUT MAY NOT TRIM THE INK (P-45) ────────────────────
       With no border the cut IS the fitted outline, and the geometric lane
       draws it as a rounded polygon: straight sides joined by tangent arcs.
       On #1067 design 1 (20 Oct 2026) two corner arcs were rounder than the
       artwork and one side ran straight across an edge that bows ~1 px
       outward, so the cut trimmed solid ink by up to 0.009in - accepted,
       inside the 0.020in trace tolerance, and twice the worst any shipped
       order has. Ryan's rule: the complete artwork is kept.

       So the polygon is kept and only moved where it cuts the ink:
         - a SIDE that cuts more than one evidence pixel into the ink moves
           outward, parallel to itself, just far enough to bring the ink to
           within half a pixel (where every other straight side already sits);
         - its two corners are rebuilt tangent to the moved side at their own
           radius, and any CORNER arc that still cuts more than one pixel into
           the ink gets the largest radius that brings it to within half a pixel.
       Same sides, same directions, same corners: the shape is the same shape,
       moved outward where the ink needs it and nowhere else.

       Every move is bounded by INK_PROTECT_MAX_IN, so it cannot become a
       visible white edge. If the ink cannot be protected inside that bound,
       or the protected cut fails validateCut or any gate below, the job is
       REFUSED (`ink_protection_conflict`): no CutContour, so HQ flags it for a
       hand-drawn cut, rather than shipping a cut that trims the artwork or one
       that bypassed a check. A cut that trims nothing by more than one pixel
       is never touched, so a sticker that passes today is the cut it was. */
    const INK_PROTECT_MAX_IN = 0.010;
    /* How far the ink reaches outside a cut made only of arcs (Option A). The
       engine writes a circle or an oval as two arcs between opposite points
       (M p0 A .. p1 A .. p0 Z), so the centre is the midpoint of p0 and p1.
       Each solid pixel (alpha >= 128) is taken at its centre plus half a pixel,
       the same half pixel the engine adds, and measured along its ray from the
       centre: exact for a circle, never less than the true gap for an oval.
       Null: not an arc-only path (slProtectInk's business). measured:false: arcs
       that are not one circle or one oval. */
    const slArcOnlyInkOut = (d0, px, cw0, ch0) => {
      if (/[^MAZ0-9\s.,eE+\-]/.test(d0) || !/A/.test(d0)) return null;
      const tk = d0.match(/[MAZ]|[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g);
      if (!tk || tk[0] !== 'M') return null;
      const p0 = { x: +tk[1], y: +tk[2] }, arcs = [];
      let i = 3;
      while (i < tk.length && tk[i] === 'A') { arcs.push(tk.slice(i + 1, i + 8).map(Number)); i += 8; }
      const unmeasured = { family: 'arcs', measured: false };
      if (arcs.length !== 2 || tk[i] !== 'Z' || i + 1 !== tk.length) return unmeasured;
      const a0 = arcs[0], a1 = arcs[1], rx = a0[0], ry = a0[1], rot = a0[2];
      if (!(rx > 0 && ry > 0) || Math.abs(a1[0] - rx) > 1e-6 * rx || Math.abs(a1[1] - ry) > 1e-6 * ry || Math.abs(a1[2] - rot) > 1e-6) return unmeasured;
      const p1 = { x: a0[5], y: a0[6] };
      if (Math.hypot(a1[5] - p0.x, a1[6] - p0.y) > 1e-3) return unmeasured;
      const cx = (p0.x + p1.x) / 2, cy = (p0.y + p1.y) / 2, half = Math.hypot(p1.x - p0.x, p1.y - p0.y) / 2;
      const family = Math.abs(rx - ry) <= 1e-6 * Math.max(rx, ry) ? 'circle' : 'ellipse';
      const phi = rot * Math.PI / 180, cph = Math.cos(phi), sph = Math.sin(phi);
      /* the shape's radius along a direction */
      const rho = (dx, dy) => {
        const u = dx * cph + dy * sph, v = -dx * sph + dy * cph, L = Math.hypot(u, v) || 1;
        return 1 / Math.sqrt((u / L) * (u / L) / (rx * rx) + (v / L) * (v / L) / (ry * ry));
      };
      /* p0 and p1 must be the two ends of a diameter of THIS shape */
      if (Math.abs(half - rho(p1.x - cx, p1.y - cy)) > 1e-3 * Math.max(rx, ry) + 1e-3) return unmeasured;
      const kx = cw0 / mw, ky = ch0 / mh, halfPx = 0.25 * (kx + ky);
      let outU = 0;
      for (let yy = 0; yy < mh; yy++) for (let xx = 0; xx < mw; xx++) {
        if (px[(yy * mw + xx) * 4 + 3] < 128) continue;
        const dx = (xx + 0.5) * kx - cx, dy = (yy + 0.5) * ky - cy;
        const o = Math.hypot(dx, dy) + halfPx - rho(dx, dy);
        if (o > outU) outU = o;
      }
      return { family: family, measured: true, outU: outU };
    };
    const slProtectInk = (d0, px, cw0, ch0, capU) => {
      if (/[^MLAZ0-9\s.,eE+\-]/.test(d0)) return null;           /* absolute M/L/A/Z only */
      const tk = d0.match(/[MLAZ]|[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g);
      if (!tk || tk[0] !== 'M') return null;
      let i = 1;
      const num = () => +tk[i++];
      const m0 = { x: num(), y: num() };
      const seg = [];
      while (i < tk.length) {
        const c = tk[i++];
        if (c === 'L') seg.push({ t: 'L', x: num(), y: num() });
        else if (c === 'A') {
          const s = { t: 'A', r: num(), ry: num(), rot: num(), la: num(), sw: num(), x: num(), y: num() };
          if (!(s.r > 0) || Math.abs(s.r - s.ry) > 1e-6 * s.r) return null;
          seg.push(s);
        } else if (c === 'Z') { if (i !== tk.length) return null; }
        else return null;
      }
      const n = seg.length;
      if (n < 3 || !seg.every(s => isFinite(s.x) && isFinite(s.y)) || !isFinite(m0.x) || !isFinite(m0.y)) return null;
      if (Math.hypot(seg[n - 1].x - m0.x, seg[n - 1].y - m0.y) > 1e-3) return null;
      /* sides and the corners between them; two arcs in a row is not this shape */
      const sides = [], sideOfSeg = new Array(n).fill(-1);
      for (let q = 0; q < n; q++) {
        if (seg[q].t === 'A' && seg[(q + 1) % n].t === 'A') return null;
        if (seg[q].t === 'L') { sideOfSeg[q] = sides.length; sides.push({ seg: q }); }
      }
      const K = sides.length;
      if (K < 3) return null;
      let area = 0;
      for (let q = 0; q < n; q++) { const a = seg[(q - 1 + n) % n], b = seg[q]; area += a.x * b.y - b.x * a.y; }
      const sgn = area > 0 ? 1 : -1;
      for (let j = 0; j < K; j++) {
        const q = sides[j].seg, A0 = seg[(q - 1 + n) % n], B0 = seg[q], L = Math.hypot(B0.x - A0.x, B0.y - A0.y);
        if (!(L > 1e-6)) return null;
        const u = { x: (B0.x - A0.x) / L, y: (B0.y - A0.y) / L };
        sides[j].p = { x: A0.x, y: A0.y }; sides[j].u = u;
        sides[j].nOut = sgn > 0 ? { x: u.y, y: -u.x } : { x: -u.y, y: u.x };
        sides[j].off = 0;
      }
      /* corner j joins side j to side j+1: an arc segment between them, or sharp */
      const corners = [];
      for (let j = 0; j < K; j++) {
        const q = sides[j].seg, nx = (q + 1) % n, s1 = sides[j].u, s2 = sides[(j + 1) % K].u;
        const cr = s1.x * s2.y - s1.y * s2.x;
        if (!(Math.abs(cr) > 1e-6) || (cr > 0 ? 1 : -1) !== sgn) return null;     /* convex corners only */
        const cnr = { arc: seg[nx].t === 'A' ? nx : -1, r: 0, r0: 0,
                      tanH: Math.tan(0.5 * Math.acos(Math.max(-1, Math.min(1, s1.x * s2.x + s1.y * s2.y)))),
                      nIn: sgn > 0 ? { x: -s1.y, y: s1.x } : { x: s1.y, y: -s1.x } };
        if (cnr.arc >= 0) cnr.r = cnr.r0 = seg[nx].r;
        corners.push(cnr);
      }
      const meet = (j) => {                                    /* where sides j and j+1 meet, as moved */
        const A1 = sides[j], B1 = sides[(j + 1) % K];
        const pa = { x: A1.p.x + A1.nOut.x * A1.off, y: A1.p.y + A1.nOut.y * A1.off };
        const pb = { x: B1.p.x + B1.nOut.x * B1.off, y: B1.p.y + B1.nOut.y * B1.off };
        const cr = A1.u.x * B1.u.y - A1.u.y * B1.u.x, dx = pb.x - pa.x, dy = pb.y - pa.y;
        const s = (dx * B1.u.y - dy * B1.u.x) / cr;
        return { x: pa.x + A1.u.x * s, y: pa.y + A1.u.y * s };
      };
      const geomNow = () => {
        const V = [], tin = [], tout = [];
        for (let j = 0; j < K; j++) {
          const v = meet(j), t = corners[j].r * corners[j].tanH, u1 = sides[j].u, u2 = sides[(j + 1) % K].u;
          V.push(v); tin.push({ x: v.x - u1.x * t, y: v.y - u1.y * t }); tout.push({ x: v.x + u2.x * t, y: v.y + u2.y * t });
        }
        return { V: V, tin: tin, tout: tout };
      };
      /* the engine's own arcs must be true tangent fillets of its sides */
      const g0 = geomNow();
      for (let j = 0; j < K; j++) {
        if (corners[j].arc < 0) continue;
        const A = seg[(corners[j].arc - 1 + n) % n], E = seg[corners[j].arc], V = g0.V[j], t0 = corners[j].r * corners[j].tanH;
        if (Math.abs(Math.hypot(A.x - V.x, A.y - V.y) - t0) > 0.02 * t0 + 0.05 ||
            Math.abs(Math.hypot(E.x - V.x, E.y - V.y) - t0) > 0.02 * t0 + 0.05) return null;
      }
      const kx = cw0 / mw, ky = ch0 / mh, kpx = 0.5 * (kx + ky);
      /* nothing happens unless something trims more than a pixel; once it does,
         every side and corner is brought to within half a pixel (FIX allows the
         solver's own rounding) */
      const TRIGGER = 1.0 * kpx, GOAL = 0.5 * kpx, FIX = GOAL + 0.1 * kpx;
      /* the solid ink each side and each corner answers for, gathered once */
      const inkIn = (x0u, y0u, x1u, y1u, keep) => {
        const pts = [];
        const xa = Math.max(0, Math.floor(x0u / kx) - 1), xb = Math.min(mw - 1, Math.ceil(x1u / kx) + 1);
        const ya = Math.max(0, Math.floor(y0u / ky) - 1), yb = Math.min(mh - 1, Math.ceil(y1u / ky) + 1);
        for (let yy = ya; yy <= yb; yy++) for (let xx = xa; xx <= xb; xx++) {
          if (px[(yy * mw + xx) * 4 + 3] < 128) continue;
          const qx = (xx + 0.5) * kx, qy = (yy + 0.5) * ky;
          if (keep(qx, qy)) pts.push(qx, qy);
        }
        return pts;
      };
      const reach = capU + 2 * kpx;
      sides.forEach((S, j) => {
        const a = g0.tout[(j - 1 + K) % K], b = g0.tin[j];
        const xs = [a.x, b.x, a.x + S.nOut.x * reach, b.x + S.nOut.x * reach], ys = [a.y, b.y, a.y + S.nOut.y * reach, b.y + S.nOut.y * reach];
        S.ink = inkIn(Math.min.apply(null, xs), Math.min.apply(null, ys), Math.max.apply(null, xs), Math.max.apply(null, ys), (qx, qy) => {
          const d = (qx - S.p.x) * S.nOut.x + (qy - S.p.y) * S.nOut.y;
          return d > -2 * kpx && d < reach;
        });
      });
      corners.forEach((C, j) => {
        if (C.arc < 0) return;
        const a = g0.tin[j], b = g0.tout[j], v = g0.V[j];
        C.ink = inkIn(Math.min(a.x, b.x, v.x) - reach, Math.min(a.y, b.y, v.y) - reach,
                      Math.max(a.x, b.x, v.x) + reach, Math.max(a.y, b.y, v.y) + reach, () => true);
      });
      /* how far the ink lies beyond a side's straight run, and beyond a corner's arc */
      const sideExcess = (j, G) => {
        const S = sides[j], a = G.tout[(j - 1 + K) % K], b = G.tin[j];
        const L = (b.x - a.x) * S.u.x + (b.y - a.y) * S.u.y;
        const px0 = S.p.x + S.nOut.x * S.off, py0 = S.p.y + S.nOut.y * S.off;
        let worst = -Infinity;
        for (let q = 0; q < S.ink.length; q += 2) {
          const qx = S.ink[q], qy = S.ink[q + 1], along = (qx - a.x) * S.u.x + (qy - a.y) * S.u.y;
          if (along < 0 || along > L) continue;
          const d = (qx - px0) * S.nOut.x + (qy - py0) * S.nOut.y;
          if (d > worst) worst = d;
        }
        return worst === -Infinity ? 0 : worst;
      };
      const cornerExcess = (j, G, r) => {
        const C = corners[j];
        if (C.arc < 0 || !C.ink) return 0;
        const u1 = sides[j].u, u2 = sides[(j + 1) % K].u, V = G.V[j], t = r * C.tanH;
        const sx = V.x - u1.x * t, sy = V.y - u1.y * t, ex = V.x + u2.x * t, ey = V.y + u2.y * t;
        const n2 = sgn > 0 ? { x: -u2.y, y: u2.x } : { x: u2.y, y: -u2.x };
        const cx = sx + C.nIn.x * r, cy = sy + C.nIn.y * r;
        let worst = 0;
        for (let q = 0; q < C.ink.length; q += 2) {
          const qx = C.ink[q], qy = C.ink[q + 1];
          if ((qx - V.x) * C.nIn.x + (qy - V.y) * C.nIn.y < 0 || (qx - V.x) * n2.x + (qy - V.y) * n2.y < 0) continue;
          if ((qx - sx) * u1.x + (qy - sy) * u1.y < 0 || (qx - ex) * u2.x + (qy - ey) * u2.y > 0) continue;
          const over = Math.hypot(qx - cx, qy - cy) - r;
          if (over > worst) worst = over;
        }
        return worst;
      };
      const worstNow = () => {
        const G = geomNow(); let w = 0;
        for (let j = 0; j < K; j++) w = Math.max(w, sideExcess(j, G), cornerExcess(j, G, corners[j].r));
        return w;
      };
      const before = worstNow();
      const note = { sides: K, arcs: corners.filter(c => c.arc >= 0).length, inkOutPx0: +(before / kpx).toFixed(2),
                     maxMoveIn: INK_PROTECT_MAX_IN, moved: [], tightened: [] };
      if (!(before > TRIGGER)) return { d: null, note: note };
      /* sides first: parallel, outward, only as far as the ink needs */
      for (let pass = 0; pass < 3; pass++) {
        const G = geomNow();
        for (let j = 0; j < K; j++) {
          const ex = sideExcess(j, G);
          if (ex > (pass ? GOAL + 0.01 * kpx : FIX)) sides[j].off += ex - GOAL;
        }
      }
      for (let j = 0; j < K; j++) {
        if (sides[j].off > capU) return { d: null, note: note, conflict: 'side_needs_more_than_' + INK_PROTECT_MAX_IN + 'in' };
        if (sides[j].off > 0) note.moved.push({ side: j, outPx: +(sides[j].off / kpx).toFixed(2) });
      }
      /* then corners: rebuilt on the moved sides at their own radius, tightened only if they still cut the ink */
      const G1 = geomNow();
      for (let j = 0; j < K; j++) {
        const C = corners[j];
        if (C.arc < 0) continue;
        const ex = cornerExcess(j, G1, C.r);
        if (!(ex > FIX)) continue;
        let lo = 0, hi = C.r;
        for (let it = 0; it < 30; it++) { const mid = 0.5 * (lo + hi); if (cornerExcess(j, G1, mid) <= GOAL) lo = mid; else hi = mid; }
        if (!(lo > 0.25 * kpx)) return { d: null, note: note, conflict: 'corner_unresolved' };
        note.tightened.push({ corner: j, r0: +C.r.toFixed(3), r1: +lo.toFixed(3), inkOutPx0: +(ex / kpx).toFixed(2) });
        C.r = lo;
      }
      /* A corner moves only with its sides (bounded above) or toward the ink it
         was cutting (tightened to half a pixel from it), so it cannot open a
         white edge of its own; the sides carry the bound. */
      const G2 = geomNow();
      /* every straight run must still be a run */
      for (let j = 0; j < K; j++) {
        const a = G2.tout[(j - 1 + K) % K], b = G2.tin[j];
        if ((b.x - a.x) * sides[j].u.x + (b.y - a.y) * sides[j].u.y < 0.5 * kpx) return { d: null, note: note, conflict: 'side_vanished' };
      }
      const after = worstNow();
      note.inkOutPx1 = +(after / kpx).toFixed(2);
      if (after > FIX) return { d: null, note: note, conflict: 'ink_still_outside' };
      const f = (v) => String(+v.toFixed(4));
      const endOf = (q) => {
        if (seg[q].t === 'L') { const j = sideOfSeg[q]; return corners[j].arc >= 0 ? G2.tin[j] : G2.V[j]; }
        return G2.tout[sideOfSeg[(q - 1 + n) % n]];
      };
      const last = endOf(n - 1);
      let d1 = 'M ' + f(last.x) + ' ' + f(last.y);
      for (let q = 0; q < n; q++) {
        const pt = endOf(q), s = seg[q];
        if (s.t === 'L') d1 += ' L ' + f(pt.x) + ' ' + f(pt.y);
        else { const r = corners[sideOfSeg[(q - 1 + n) % n]].r; d1 += ' A ' + f(r) + ' ' + f(r) + ' ' + f(s.rot) + ' ' + s.la + ' ' + s.sw + ' ' + f(pt.x) + ' ' + f(pt.y); }
      }
      return { d: d1 + ' Z', note: note };
    };
    if (opts && opts.inkProtect && geo.borderNone && !(+geom.distance > 0) &&
        typeof geo.cutPath === 'string' && CPX.validateCut && traceTolU > 0 && extentUL > 0) {
      const capU = INK_PROTECT_MAX_IN * extentUL;
      let ip = null;
      try { ip = slProtectInk(geo.cutPath, id.data, cw, ch, capU); } catch (eIp) { ip = { d: null, note: {}, conflict: 'threw:' + (eIp && eIp.message) }; }
      out.inkProtect = ip ? ip.note : { applicable: false };
      const refuse = (why) => {
        out.inkProtect.applied = false;
        out.inkProtect.conflict = why;
        out.reason = 'ink_protection_conflict';
        return out;
      };
      if (ip && ip.conflict) return refuse(ip.conflict);
      /* ── A CUT MADE ONLY OF ARCS IS MEASURED TOO (Option A, Ryan 27 Oct 2026) ──
         slProtectInk adjusts polygons (sides + corner arcs) and returns null for a
         path of arcs alone, so a circle or an oval was never held to the 0.010 in
         rule: #1086's exact circle trimmed 0.015 in of its black ring. The engine
         now refuses such a primitive for review (circle_ink_outside_tolerance);
         this is the builder's own second check, with the engine's numbers. The
         cut is never moved or resized here: it is measured, and refused when the
         ink lies more than the cap outside it. A shape of arcs it cannot measure
         is refused as well, never assumed fine. */
      if (!ip) {
        const m = slArcOnlyInkOut(geo.cutPath, id.data, cw, ch);
        if (m) {
          out.inkProtect = { applicable: true, family: m.family, arcOnly: true };
          if (!m.measured) return refuse('arc_only_unmeasured');
          out.inkProtect.inkOutsideIn = +(m.outU / extentUL).toFixed(4);
          if (m.outU > capU) return refuse(m.family + '_ink_outside_tolerance');
        }
      }
      if (ip && ip.d) {
        let vr = null, canonD = null;
        try {
          vr = CPX.validateCut({ silMask: silForTrace, viewBox: { w: cw, h: ch }, cutPath: ip.d,
                                 distance: 0, tolUnits: traceTolU });
        } catch (eCv) { vr = null; }
        if (!vr) return refuse('validator_threw');
        if (!vr.ok) return refuse('validateCut:' + (vr.reasons || []).join('+'));
        if (geoCanon && frC && frC.ok) {
          try { canonD = CPX.transformPath(ip.d, { sx: frC.k, sy: frC.k, tx: 0, ty: 0 }); } catch (eCx) { canonD = null; }
          if (!canonD) return refuse('canonical_transport');
        }
        const swap = (g, from, to) => {
          if (g.P === from) g.P = to;
          if (g.B === from) g.B = to;
          if (g.bands && g.bands.length) g.bands = g.bands.map(function (bd) { return bd.d === from ? { d: to, fill: bd.fill } : bd; });
          g.cutPath = to;
        };
        if (geoCanon && canonD) swap(geoCanon, geoCanon.cutPath, canonD);
        swap(geo, geo.cutPath, ip.d);
        out.inkProtect.applied = true;
        out.inkProtect.maxOutsetIn = +(vr.metrics.maxOutsetUnits / extentUL).toFixed(4);
      }
    }

    /* ---- validation, before anything is embedded ---------------------- */
    /* ── TWO BOXES, TWO JOBS ─────────────────────────────────────────────
       `bb` is the STRUCTURAL box: `pathBBox` bounds cubics by their control
       hull and arcs by endpoint-plus-radius, so it can over-report and never
       under-report. That is the right shape of error for "is this parseable,
       finite, non-degenerate" and it stays here for exactly that.

       `mbb` is the MEASURED box: the path flattened to a polyline, so the
       extremes are points actually on the curve. That is the only one of the
       two that answers "how big is the sticker".

       They were the same variable, and the size gates read the structural one.
       On the real bear that box over-reported the long edge by 8.894 units
       against a 1.592-unit tolerance, so the correction pass saw an error that
       did not exist, shrank the artwork 2.3%, and shipped 2.93in for a 3.00in
       order — the correction pass was CAUSING the undershoot it existed to
       prevent. Measured truly, the same closed form reports no correction is
       needed and the sticker lands at 3.0018in. */
    /* `let`, because the manufacturing cleanup below may replace this with a
       rebuilt path — always together with the border band, never alone. */
    let d = geo.cutPath;
    const bb = CPX.pathBBox(d);
    const mbb = CPX.measuredBBox(d);
    const cmds = CPX.countCommands(d);
    if (!bb || !cmds) { out.reason = 'path_unparseable'; return out; }
    if (!mbb) { out.reason = 'path_unmeasurable'; return out; }
    if (!isFinite(bb.minX) || !isFinite(bb.minY) || !isFinite(bb.maxX) || !isFinite(bb.maxY)) {
      out.reason = 'non_finite_bbox'; return out;
    }
    if (!d.trim()) { out.reason = 'empty_path'; return out; }
    if (cmds.Z < 1) { out.reason = 'path_not_closed'; return out; }
    if (cmds.M !== 1) { out.reason = 'not_a_single_subpath'; return out; }
    if (!(bb.w > cw * 0.05) || !(bb.h > ch * 0.05)) { out.reason = 'degenerate_area'; return out; }
    /* ── THE FINISHED STICKER MUST FIT THE CANVAS ────────────────────────
       This used to allow the cut line to run 8% of the long edge OUTSIDE the
       artboard, which is not a sanity check — it is a licence. The canvas is
       the production viewBox, so anything past it is a STRAIGHT EDGE SLICED
       THROUGH THE WHITE BORDER. On the real bear order at Standard the band
       ran 10.07 units past the right edge, 8.80 past the top and 10.01 past
       the bottom of a 372x248 viewBox, was accepted by this very check, and
       printed flat-cut on two sides.

       Under contour-fit-v1 the artwork is scaled so the CutContour spans the
       ordered size exactly, so the correct tolerance is numerical, not
       generous: rounding of the ordered inches to one decimal, of the box to
       whole pixels, and of the reconstructed path. A failure here REFUSES the
       double dip — the file then ships with the baked raster border and no
       CutContour, which is a worse-looking sticker and an honest one, rather
       than a cut line that slices the border.

       Orders without the fit (border=none has no band to overflow; a
       silhouette we could not measure falls back to the old sizing) keep the
       old allowance, because tightening it there would refuse files that are
       correct under the rules they were built by. */
    const fitted = !!(opts && opts.fitted);
    /* MEASURED, AND KEPT WHETHER OR NOT IT PASSES. The correction pass solves
       from this box, so a rejection has to carry it — a reason string is not
       something you can do arithmetic on. */
    out.cutBBox = { minX: mbb.minX, minY: mbb.minY, maxX: mbb.maxX, maxY: mbb.maxY };
    /* ── THE SAME BOX, MEASURED WHERE IT DOES NOT MOVE ───────────────────
       `measuredBBox` flattens the path at MEASURE_FLATTEN_TOL, which is an
       ABSOLUTE tolerance. The production path is in viewBox units, so the same
       0.01 is 0.0058 canonical units on one layout and 0.0073 on another: the
       two are sampled at different granularity and their measured extremes
       disagree. Measured on the real builder with a BIT-IDENTICAL silhouette
       behind them, the canonical maxX still came out 3.627e-3 apart and the
       centre 1.824e-3 apart.

       That is harmless for the size gates below - three thousandths of a unit
       against a tolerance of about 1.6 - and it is not harmless for the contour
       fit, which subtracts this centre from the canvas centre to make a
       quantity a thousand times smaller. There a 5.6e-6 error in the normalised
       centre becomes 1.03% on the answer, and 10F proved that lands as 2.7e-3
       of a canvas pixel in the pass-4 translation, six times the ~5e-4 the
       rasteriser can resolve.

       So the box is measured a second time in the canonical frame, where the
       path is the SAME path at the SAME scale on every device and the flattening
       therefore samples the same points. `out.cutBBox` is untouched and remains
       the production-unit size authority; this is only for the fit's centring. */
    if (geoCanon && frC && frC.ok && geoCanon.cutPath) {
      try {
        const mbc = CPX.measuredBBox(geoCanon.cutPath);
        if (mbc && isFinite(mbc.minX) && isFinite(mbc.maxX)) {
          out.cutBBoxCanon = { minX: mbc.minX, minY: mbc.minY,
                               maxX: mbc.maxX, maxY: mbc.maxY,
                               boxW: frC.w, boxH: frC.h };
        }
      } catch (eCB) { /* the fit falls back to the production box */ }
    }
    /* Kept so a reader can see the gap that caused the undershoot rather than
       having to rediscover it. Nothing decides anything from this. */
    out.cutBBoxConservative = { minX: bb.minX, minY: bb.minY, maxX: bb.maxX, maxY: bb.maxY };
    out.overReportUnits = +(Math.max(bb.w, bb.h) - Math.max(mbb.w, mbb.h)).toFixed(3);
    /* The long-axis scale, supplied by the caller which knows the finished
       inches; `geom.unitsPerInch` is the X axis and is the fallback. */
    const uLong = (opts && +opts.uLong > 0) ? +opts.uLong
                : ((geom && +geom.unitsPerInch > 0) ? +geom.unitsPerInch : 0);
    const pad = fitted ? slContourTolUnits(cw, ch, uLong) : Math.max(cw, ch) * 0.08;
    /* ── THE MARGIN THAT REPLACES AN UNBOUNDED ONE ───────────────────────
       The flattened polyline is an INNER bound: it can sit at most one
       flattening sagitta inside the true curve. Two sagittas is a deliberate
       factor of safety over a bound measured at well under one (0.003 units on
       a real bear path at this tolerance).

       This ADDS to the existing tolerance, it does not replace it. That 1.592
       covers ordered inches rounded to one decimal, the preview box rounded to
       whole pixels, and reconstruction slop — none of which are measurement
       artefacts. What is being removed is the IMPLICIT, unbounded control-hull
       margin that used to ride along inside the number itself. */
    const containPad = fitted ? pad + 2 * CPX.MEASURE_FLATTEN_TOL : pad;
    /* ── AN UNDER-SIZED CONTOUR IS NOT A PASS ────────────────────────────
       Containment was the only test here, and containment has nothing to say
       about a sticker that came out SMALL. The real 3in bear cleared this gate
       at 2.9837in — comfortably inside the canvas, 0.0163in short of the size
       the customer bought, and silent about it. Being inside the box and being
       the ordered size are two different claims and both have to hold.
       A die-cut cart graphic is held to these same two gates AFTER its final
       placement (`cgNorm`, at the end of this function), not before it. */
    const cgNorm = !!(fitted && opts && opts.cgNormalize);
    if (fitted && !cgNorm) {
      var longActual = Math.max(mbb.w, mbb.h);
      var canvasLong = Math.max(cw, ch);
      out.longEdge = { actual: +longActual.toFixed(3), target: +canvasLong.toFixed(3),
                       error: +(longActual - canvasLong).toFixed(3) };
      if (Math.abs(longActual - canvasLong) > pad) {
        out.reason = 'cut_long_edge_off_target';
        return out;
      }
    }
    if (!cgNorm && (mbb.minX < -containPad || mbb.minY < -containPad ||
        mbb.maxX > cw + containPad || mbb.maxY > ch + containPad)) {
      out.reason = fitted ? 'cut_bbox_outside_canvas' : 'bbox_outside_artboard';
      out.overflow = {
        left: +(0 - mbb.minX).toFixed(3), top: +(0 - mbb.minY).toFixed(3),
        right: +(mbb.maxX - cw).toFixed(3), bottom: +(mbb.maxY - ch).toFixed(3),
        tolerance: +containPad.toFixed(3)
      };
      return out;
    }
    /* THE IDENTITY THAT MAKES THIS ARCHITECTURE WORTH HAVING. The printed
       border's outer edge and the cut line are not two paths that agree; they
       are one string used twice. Asserted here so a future edit that quietly
       re-derives one of them fails closed instead of shipping a mismatch. */
    if (!geo.borderNone) {
      if (!geo.bands || !geo.bands.length) { out.reason = 'no_border_bands'; return out; }
      if (geo.bands[0].d !== d) { out.reason = 'cut_and_border_diverged'; return out; }
    }

    /* ── MANUFACTURING GEOMETRY, REBUILT FROM ITS OWN EVIDENCE ───────────
       The cut line is correct at this point but it is TRACED, not drawn: the
       offset re-fits the whole outline as cubics, so a straight edge comes back
       as several little curves that bow a few ten-thousandths of an inch and a
       long sweep comes back covered in anchors. Measured on the real G it is
       perfectly smooth — no curvature sign reversals at quarter-unit sampling —
       and it still reads as machine output rather than as something a
       production artist drew.

       `SLPathClean` rebuilds it: persistent straight regions become ONE true
       line (snapped to exact horizontal or vertical when the snap moves the
       geometry less than a third of the deviation budget), coherent curvature
       becomes the fewest cubics that hold the shape, and real corners survive.
       On a perfect circle it returns four anchors; on a rounded rectangle,
       eight with four exact axis-aligned sides.

       THE BUDGET IS PHYSICAL. A quarter of the finished-size tolerance, so the
       rebuild can never spend more than a fraction of the promise the cut line
       already has to keep.

       IT IS ADDITIVE AND IT FAILS SAFE. `clean` measures its own result —
       deviation, bbox movement, self-intersection, node gain — and returns
       nothing when the rebuild is not demonstrably better. Then this block does
       nothing and production keeps the geometry it already had. The cut line
       and the border band are replaced TOGETHER by the same string, so the
       identity checked just above cannot be broken here. */
    /* THE OFFSET, KEPT BEFORE ANYTHING SMOOTHS IT.
       The offset is the set of points exactly the border distance from the
       artwork, so it is correct about border thickness by construction. Every
       later stage is an improvement in appearance bought at some cost in
       fidelity, and without keeping the original there is nothing to measure
       that cost against — which is how a border 19% under-width reached
       Illustrator with every gate reporting success. */
    /* ── THE FRAME EVERY RECONSTRUCTION STAGE REASONS IN ─────────────────
       One object, read by every stage below, so there is exactly one answer to
       "what units am I in and how wide is the border". When the canonical
       frame is available this is the canonical one; when it is not — an
       engine without transport, a degenerate order, anything that made the
       frame refuse — it is the production frame, which is precisely today's
       behaviour. There is no third case and no per-stage decision.

       `uLong` travels with the frame because the deviation budgets are
       PHYSICAL: 0.015in of tolerance is the same promise in either frame, and
       it is only the number of units expressing it that changes. */
    var RF = (geoCanon && frC && frC.ok)
      ? { on: true, geo: geoCanon, d: frC.d, k: frC.k, uLong: uLong * frC.k }
      : { on: false, geo: geo, d: geom.distance, k: 1, uLong: uLong };
    /* the working path, in the reconstruction frame, advanced by each stage
       that improves on the last */
    var wd = RF.geo.cutPath;
    var offsetPath = wd;
    /* EVERY CANDIDATE IS KEPT, NOT JUST THE LATEST.
       Each stage below used to overwrite `d` and the one that happened to run
       last shipped. `sl-cutguard` chooses instead, from all of them, against
       the ink and the raw offset — so they are collected rather than
       consumed. The offset goes in first because it is the least-processed
       thing there is and is correct about border width by construction. */
    var candidates = [{ stage: 'offset', d: offsetPath }];
    var artworkRing = null;
    try {
      var pLoops = RF.geo.P ? CPX._flattenPath(RF.geo.P, 0.02) : null;
      if (pLoops && pLoops.length) {
        artworkRing = pLoops[0].map(function (q) { return [q.x, q.y]; });
      }
    } catch (ePr) { artworkRing = null; }

    var cleanNote = null;
    try {
      var PCx = window.SLPathClean;
      if (PCx && PCx.clean && !geo.borderNone) {
        /* 0.4 of the finished-size tolerance = 0.006in. Measured across the
           real G and the golden bear: at 0.25 the bear could not be improved
           at all and was declined, at 0.4 both rebuild cleanly (46->36 and
           57->53) with worst deviations of 2.6 and 3.6 thou — inside a
           cutter's own mechanical accuracy and well under half the promise the
           finished size already has to keep. */
        var budget = (RF.uLong > 0) ? (0.015 * RF.uLong * 0.4) : 0;
        if (budget > 0) {
          var cleaned = PCx.clean(wd, {
            /* THREE LIMITS, EACH ANSWERING A DIFFERENT QUESTION.
               `maxDeviation` is an RMS budget: is the rebuild CENTRED on the
               evidence. `maxExcursion` is physical: never wander far enough to
               cut into the printed border, so it is a fraction of the border
               width. `maxBBoxShift` protects the FINISHED SIZE, so it comes
               from the size tolerance. Judging all three against one number is
               what previously threw away every rebuild that averaged the noise
               instead of tracking it. */
            maxDeviation: budget,
            maxExcursion: RF.d * 0.3,
            maxBBoxShift: 0.015 * RF.uLong * 0.6,
            /* pathclean's own feature scales (its straightness tolerance, its
               minimum straight run, its corner window) are physical lengths,
               and it has no way to know what a viewBox unit is worth without
               this. Without it they stayed absolute and the cleaner behaved
               differently on different screen sizes — see Milestone 10B. */
            borderWidth: RF.d,
            flatten: CPX._flattenPath, parsePath: CPX.parsePath
          });
          if (cleaned && cleaned.d) {
            /* the working path advances IN THE RECONSTRUCTION FRAME. `geo`,
               `geo.B` and the bands are production-side and are updated once,
               after the guard has chosen, from the transported winner. */
            wd = cleaned.d;
            cleanNote = cleaned.stats;
            candidates.push({ stage: 'pathclean', d: cleaned.d });
          } else if (cleaned) {
            cleanNote = cleaned.stats;                 /* why it declined */
          }
        }
      }
    } catch (e) {
      /* A cleanup that throws must cost nothing: the un-rebuilt cut line is a
         correct cut line. Recorded, never fatal. */
      try { console.warn('[sl-pathclean] declined:', e && e.message); } catch (x) {}
    }
    out.cleanup = cleanNote;

    /* ---- STRUCTURAL RECONSTRUCTION -------------------------------------
       The cleaner above rebuilds the whole cut line from local evidence. This
       stage asks a different question of the SAME path: which parts of it can
       be PROVEN to be a straight edge, an arc, an ellipse or a tangent fillet,
       and rebuilds only those as exact geometry. Everything the detector cannot
       prove is carried through from the cleaner's output untouched — an UNKNOWN
       region is not an invitation to try harder, it is a region whose evidence
       was too thin to justify redrawing a customer's artwork.

       IT RUNS AFTER THE CLEANER, NOT INSTEAD OF IT, and that ordering is the
       safety property: the fallback for every unproven region is literally the
       geometry production already accepts today, so the worst case is the
       current behaviour. The module returns null whenever its own gates fail —
       a crossing, too much deviation, a moved bounding box, or no real
       simplification of the regions it claimed — and `d` is then left exactly
       as the cleaner produced it.

       The border band and the cut line are replaced TOGETHER by the same
       string, exactly as above, so the two cannot drift apart. */
    var structNote = null;
    try {
      var SBx = window.SLStructBuild, STx = window.SLStructure;
      if (SBx && SBx.build && STx && !geo.borderNone && uLong > 0) {
        var sb = SBx.build(wd, {
          parsePath: CPX.parsePath,
          flatten: CPX._flattenPath,
          arcToCentre: CPX._arcToCentre,
          structure: STx,
          pathclean: PCx,
          /* STRUCTURE IS FOUND ON THE TIDIED CURVE AND MEASURED ON THE OFFSET.
             Both halves matter. The cleaner is what makes the G's inner sweep
             detectable at all — on the raw offset that arc is too noisy to
             prove, and reconstructing from the raw path alone loses it. But the
             cleaner has also already rounded off every convex corner, so
             fitting to it inherits that damage: the G's bar edge came back
             sloped, carrying 8.7 units of border where 10.54 were ordered.
             So spans are FOUND here and MEASURED there, and unproven regions
             take the offset's geometry rather than the smoothed version. */
          fitFrom: offsetPath,
          fallback: offsetPath,
          reference: offsetPath,
          artwork: artworkRing,
          /* the scale over which offsetting rounds a vertex, which is what
             junction reasoning has to look past */
          borderWidth: RF.d,
          /* the same three limits the cleaner answers to, for the same three
             reasons: centred on the evidence, never into the printed border,
             and never moving the finished size */
          maxDeviation: 0.015 * RF.uLong * 0.4,
          maxExcursion: RF.d * 0.3,
          /* A TIGHTER SIZE LIMIT THAN THE CLEANER ANSWERS TO, deliberately.
             The cleaner rebuilds locally and its worst case is a small local
             wander. This stage replaces a whole sweep with one arc, so when it
             is wrong it is wrong along the entire length of that sweep and the
             finished size moves with it. The ordered size is a promise to the
             customer measured on the long edge, and a stage capable of moving
             it globally should be allowed less of that budget than one that
             cannot. */
          maxBBoxShift: 0.015 * RF.uLong * 0.4
        });
        if (sb && sb.d) {
          wd = sb.d;
          structNote = sb.stats;
          candidates.push({ stage: 'structbuild', d: sb.d });
        } else if (sb) {
          structNote = { declined: sb.declined, gate: sb.stats && sb.stats.gate };
        }
      }
    } catch (e) {
      /* Same contract as the cleaner: a reconstruction that throws must cost
         nothing. The un-rebuilt cut line is a correct cut line. */
      try { console.warn('[sl-structbuild] declined:', e && e.message); } catch (x) {}
    }
    out.structure = structNote;

    /* ---- LOCK AND REFIT ------------------------------------------------
       A SIBLING OF THE STAGE ABOVE, NOT A REPLACEMENT FOR IT. `sl-structbuild`
       rebuilds what the detector proved and hands UNKNOWN spans back exactly as
       they arrived, which is right when the incoming path is already good. When
       it declines — and on the hardest real uploads it does — what ships is the
       raw offset, carrying every oscillation of the raster it was thresholded
       from. This module locks the same proven geometry and REFITS the uncertain
       spans instead: minimum-complexity, tangent-continuous, bounded by
       whatever was proven either side.

       It is offered as another candidate and wins nothing by existing. Both
       variants are submitted — with and without the convex-corner fillet —
       because which is right depends on whether the corner turns outward, and
       the guard is already the thing that decides between candidates. If both
       fail, the pipeline is exactly Milestone 7B. */
    var lockNote = null;
    try {
      var LRx = window.SLLockRefit;
      if (LRx && LRx.build && SBx && SBx._rasterize && STx && !geo.borderNone && uLong > 0) {
        var lrSource = (cleanNote && cleanNote.anchorsAfter) ? wd : offsetPath;
        var lrOpts = {
          parsePath: CPX.parsePath, flatten: CPX._flattenPath,
          arcToCentre: CPX._arcToCentre, structure: STx, rasterize: SBx._rasterize,
          /* found on the tidied curve, measured on the offset */
          fitFrom: offsetPath, borderWidth: RF.d
        };
        [true, false].forEach(function (useFillets) {
          var lr = LRx.build(lrSource, Object.assign({}, lrOpts, { fillets: useFillets }));
          if (lr && lr.d) {
            candidates.push({ stage: 'lockrefit' + (useFillets ? '' : '-nofillet'), d: lr.d });
            if (!lockNote) lockNote = lr.stats;
          } else if (!lockNote) lockNote = { declined: lr && lr.declined };
        });
      }
    } catch (eL) {
      try { console.warn('[sl-lockrefit] declined:', eL && eL.message); } catch (x) {}
      lockNote = { error: (eL && eL.message) || String(eL) };
    }
    out.lockRefit = lockNote;

    /* ---- THE STRUCTURAL CANDIDATE (8D + 8E + 8F + 8G + 8H) ---------------
       One more candidate, built from what the ARTWORK says rather than from
       what the offset looks like. Three stages feed it and each owns exactly
       one question:

         sl-junctions   what KIND is each junction - sharp, round, tangent, or
                        not known. Read on the raw silhouette, because an
                        outward offset is not intent-preserving: a sharp CONVEX
                        corner and a genuinely round one of radius d are the
                        same curve on the cut path, and only the artwork
                        separates them.
         sl-relations   what the primitives are to EACH OTHER - parallel,
                        perpendicular, concentric - judged against each line's
                        own measured angular uncertainty rather than a degree
                        threshold, so a real 87-degree corner survives.
         sl-lockrefit   construction, told what to build rather than deciding.

       It wins nothing by existing. Like every other stage it is offered to the
       guard, and on the thirteen-file batch it is REFUSED on five of them -
       bear, wave, monstera, snake and motocross - where an earlier refinement
       or the raw offset ships instead. That is the design working, not a gap:
       use the best safe geometry we can prove, and fall back when we cannot. */
    var structuralNote = null;
    try {
      var JXx = window.SLJunctions, RXx = window.SLRelations;
      var im = RF.geo.inkMask;
      if (JXx && RXx && LRx && LRx.build && lrOpts && lrSource && SBx && SBx._rasterize &&
          STx && !geo.borderNone && uLong > 0 && im && im.data && CPX._traceOuterContour) {
        /* the RAW silhouette, in viewBox units. `geo.P` is already simplified
           and the fine-scale curvature these stages read has been averaged out
           of it before they would ever see it. */
        var msx = im.sx || 1, msy = im.sy || 1;
        var traced = CPX._traceOuterContour(im.data, im.w, im.h);
        var artRing = [];
        for (var ti = 0; ti < traced.length; ti++) {
          artRing.push({ x: (traced[ti].x + 0.5) * msx, y: (traced[ti].y + 0.5) * msy });
        }
        var offLoops = CPX._flattenPath(offsetPath, 0.02);
        var offRing = (offLoops && offLoops.length) ? offLoops[0] : null;
        /* the mask may not be at viewBox resolution, and "one pixel" is the
           floor every scale-dependent test in these stages is expressed in */
        var pxUnits = Math.max(msx, msy);

        if (artRing.length > 8 && offRing && offRing.length > 8) {
          var jctx = JXx.prepare({ artwork: artRing, offset: offRing,
                                   borderWidth: RF.d, pixelSize: pxUnits });
          var lockedJ = [];
          if (jctx && jctx.ok) {
            var jmap = jctx.map();
            for (var ji = 0; ji < jmap.junctions.length; ji++) {
              var J = jmap.junctions[ji];
              if (J.kind === 'UNKNOWN' || J.confidence < 0.6) continue;
              lockedJ.push({
                at: (J.observedOffset && J.observedOffset.at) ? J.observedOffset.at : J.at,
                kind: J.kind, convex: J.convex, radius: J.radius, id: J.id });
            }
          }
          var rel = RXx.analyse({ artwork: artRing, offset: offRing,
                                  borderWidth: RF.d, pixelSize: pxUnits });
          var sr = LRx.build(lrSource, Object.assign({}, lrOpts, {
            straightFirst: true, dropSlivers: true, snapFreeAxis: true,
            filletJunctions: true, filletSeam: true,
            classifiedJunctions: lockedJ,
            relationConstraints: (rel && rel.ok) ? rel.constraints : null,
            breakSharpCorners: true
          }));
          if (sr && sr.d) candidates.push({ stage: 'structural', d: sr.d });
          structuralNote = {
            junctions: lockedJ.length,
            relations: (rel && rel.ok && rel.counts) ? rel.counts : null,
            constraints: (rel && rel.ok) ? rel.constraints.length : 0,
            built: !!(sr && sr.d), declined: sr && sr.declined
          };
        }
      }
    } catch (eS) {
      /* the same contract as every other stage: a stage that throws costs
         nothing, and what ships is exactly what the earlier ones produced */
      try { console.warn('[sl-structural] declined:', eS && eS.message); } catch (x) {}
      structuralNote = { error: (eS && eS.message) || String(eS) };
    }
    out.structural = structuralNote;

    /* ---- THE FINAL-CANDIDATE CONTRACT ----------------------------------
       Nothing above this line decides what the customer gets. Each stage
       proposes; this chooses, and it chooses against fixed authorities — the
       INK the customer uploaded and the RAW OFFSET of the border they ordered
       — rather than against whichever stage ran before it.

       That inversion is the whole point. The old arrangement checked each
       stage against its own input, so `sl-structbuild` measured its candidate
       against the offset and declined when it lost border width, and the
       moment it declined the cleaner's output shipped WITHOUT BEING MEASURED
       AT ALL. Across the thirteen-file torture batch that was seven files, and
       every one of the seven was deformed: borders up to 24 points narrower
       and 24 points wider than the offset achieved in the same place, on files
       with nothing in common but which stage drew them.

       Now a later candidate that erodes the border, leaves ink outside the
       blade, changes the topology, crosses itself, falls outside the size
       tolerance or creases a join the offset owes as an arc simply does not
       ship, and the last one that was actually safe does. Failing that, the
       offset ships and says so. No stage is trusted for having run last. */
    /* ── ONE TRANSPORT OUT, AT THE BOUNDARY ──────────────────────────────
       Everything above reasoned in `RF`. The guard is the safety authority and
       it is deliberately NOT moved into the canonical frame: it judges physical
       containment, erosion, widening and the finished size, so it stays exactly
       where it has always been, in production coordinates, judging the geometry
       that will actually be printed. Transporting the candidates to meet it is
       the smaller and safer of the two boundaries — proving a canonical
       comparison equivalent would mean re-deriving every one of those physical
       tests, to reach the same verdicts on the same shapes.

       So the crossing happens once, here, for every candidate at the same time.
       A candidate that will not transport is DROPPED rather than passed along
       in the wrong units; if that leaves nothing, the offset is rebuilt in
       production coordinates so the guard always has the least-processed
       candidate to fall back to. */
    if (RF.on && RF.k !== 1) {
      var Tout = { sx: 1 / RF.k, sy: 1 / RF.k, tx: 0, ty: 0 };
      var moved = [], dropped = [];
      for (var ci = 0; ci < candidates.length; ci++) {
        var td = null;
        try { td = CPX.transformPath(candidates[ci].d, Tout); } catch (eT) { td = null; }
        if (td) moved.push({ stage: candidates[ci].stage, d: td });
        else dropped.push(candidates[ci].stage);
      }
      if (!moved.length) moved.push({ stage: 'offset', d: geo.cutPath });
      candidates = moved;
      /* THE GUARD'S FIDELITY REFERENCE, TAKEN FROM `geo` AND NOT FROM THE
         ARRAY. `geo.cutPath` is the transported raw offset by construction, so
         this cannot be wrong; reading `candidates[0]` instead would silently
         hand the guard a PROCESSED path to measure fidelity against the moment
         anything changed the order of the array or dropped the first entry. */
      offsetPath = geo.cutPath;
      if (dropped.length) out.canonicalDropped = dropped;
    }

    /* ── EVERY CANDIDATE AGAINST THE ARTWORK, BEFORE 7B CHOOSES (D-176) ──
       The offset was judged inside the engine. Each refinement is judged here,
       in production units against the same silhouette, by the same rule: a
       candidate that strays from the artwork is not offered to the selector at
       all. It is not forgotten - shadow learning still sees it, marked failed
       with the trace reason - but no quality score can choose it. The offset
       stays as the guard's fidelity reference whatever happens. */
    var traceRejected = [];
    if (CPX.validateCut && traceTolU > 0 && candidates.length) {
      var offered = [];
      for (var vi = 0; vi < candidates.length; vi++) {
        var cv = candidates[vi], vr = null;
        try {
          vr = CPX.validateCut({ silMask: silForTrace, viewBox: { w: cw, h: ch }, cutPath: cv.d,
                                 distance: geom.distance, tolUnits: traceTolU });
        } catch (eV) { vr = { ok: false, reasons: ['validator_threw'] }; }
        if (vr.ok || cv.stage === 'offset') offered.push(cv);
        else traceRejected.push({ stage: String(cv.stage), d: String(cv.d), ok: false,
                                  failures: vr.reasons.map(function (r) { return 'trace:' + r; }) });
      }
      candidates = offered;
      out.evidence.traceRejected = traceRejected.map(function (c) { return c.stage + ':' + c.failures.join('+'); });
    }

    var guardNote = null;
    try {
      var GDx = window.SLCutGuard;
      if (GDx && GDx.choose && !geo.borderNone && candidates.length) {
        var gctx = GDx.context({
          flatten: CPX._flattenPath, parsePath: CPX.parsePath,
          countSelfIntersections: CPX._countSelfIntersections,
          /* `uLong` IS units-per-inch on the long axis, and contour-fit scales
             the artwork so the CutContour spans the ordered size exactly — so
             the canvas's long edge in units, divided by it, is the ordered
             size in inches. Both numbers come from the same place the size
             gates above use, so the guard cannot disagree with them. */
          unitsPerInch: uLong,
          orderedIn: uLong > 0 ? (Math.max(cw, ch) / uLong) : 0,
          borderWidth: geom.distance,
          ink: geo.inkMask || null,
          offset: offsetPath
        });
        /* MILESTONE 8H: SAFETY IS A HARD GATE, THEN QUALITY CHOOSES.
           The old rule was "the last candidate that passes ships", which made
           the order stages happen to run in the tiebreaker - measured in 8C,
           where a candidate that ADDED anchors on three files won all three by
           arriving later. The quality policy eliminates every unsafe candidate
           first and then chooses on the geometry: pathological defects, then
           slivers, then anchor economy LAST, then a total order on the path so
           enumeration order cannot decide even a perfect tie.

           D-144: NOW THE `accepted` POLICY, AND A HUMAN CHOSE IT. Ryan opened
           its output in Illustrator beside the cut line he drew by hand and
           said it looked the same as his. `quality` ranked on anchor ECONOMY,
           which is why it shipped a refinement carrying three severe segments
           and 12.7% of its perimeter materially away from his cut, where the
           offset it refined had one and 5.2%. `accepted` keeps the same hard
           safety gate, then refuses any candidate that changed how many
           concavities the artwork has, then ranks what is left by how smoothly
           it actually cuts. If its two measures are not loaded it falls back to
           the old order policy and says so on the result rather than pretending
           it chose. */
        var pick = GDx.choose(candidates, gctx, { selectionPolicy: 'accepted' });
        if (pick && pick.d && pick.d !== d) {
          d = pick.d;
          geo.bands = geo.bands.slice();
          geo.bands[0] = { d: d, fill: geo.bands[0].fill };
          geo.B = d;
        }
        /* ══════════════════════════════════════════════════════════════
           READ-ONLY EVIDENCE FOR SHADOW LEARNING  (D-155B)

           The candidate set and 7B's verdicts exist here and nowhere else, and
           HQ could not see either - so shadow mode was architecturally complete
           and observing nothing. This is the whole of the fix: a copy of what
           was ALREADY COMPUTED, taken after the decision has been made.

           IT CHANGES NOTHING. It is assembled after `pick`, reads only values
           that already exist, and is never consulted by anything in this file.
           Deleting these lines would leave every production output identical,
           which is exactly the property the corpus comparison proves.

           The strings are copied rather than referenced: `d` is an immutable
           primitive in JavaScript, so a reader downstream cannot reach back
           into a candidate through it. */
        try {
          out.shadowEvidence = {
            /* what the frozen selector actually chose */
            winner: (pick && pick.stage) || null,
            /* every candidate, with the guard's OWN verdict - not a second
               opinion about safety, the same `ok` the selector gated on */
            candidates: candidates.map(function (c, ci) {
              var q = (pick && pick.reports && pick.reports[ci]) || null;
              return { stage: String(c.stage), d: String(c.d),
                       ok: !!(q && q.ok),
                       failures: (q && q.failures) ? q.failures.slice() : [] };
            }).concat(traceRejected),
            /* the frame the candidates are expressed in, so a reader does not
               have to re-derive it and cannot get it wrong */
            unitsPerInch: gctx.unitsPerInch,
            borderWidthU: gctx.borderWidth,
            borderIn: (gctx.unitsPerInch > 0)
              ? (gctx.borderWidth / gctx.unitsPerInch) : null,
            orderedIn: gctx.orderedIn,
            policy: (pick && pick.policy) || null,
            policyUnavailable: !!(pick && pick.policyUnavailable)
          };
        } catch (eS) { out.shadowEvidence = null; }

        guardNote = {
          chose: pick && pick.stage,
          accepted: !!(pick && pick.accepted),
          policy: (pick && pick.policy) || null,
          /* set only when the accepted policy could not run - a missing script
             tag must not be able to look like a deliberate choice */
          policyUnavailable: (pick && pick.policyUnavailable) || undefined,
          selection: (pick && pick.selection) || null,
          refusedBySelector: (pick && pick.refusedBySelector) || null,
          quality: (pick && pick.quality) || null,
          /* how far down the ladder the shipped geometry came from, so a
             fallback is visible in QA rather than inferred */
          fallbackLevel: (pick && pick.stage === 'offset') ? 'raw-offset'
                       : (pick && pick.stage === 'structural') ? 'newest'
                       : 'earlier-refinement',
          offered: candidates.map(function (c) { return c.stage; }),
          rejected: (pick && pick.rejected) || [],
          reports: (pick && pick.reports || []).map(function (q) {
            return { stage: q.stage, ok: q.ok, failures: q.failures,
                     borderMinFrac: q.m.borderMinFrac, borderMaxFrac: q.m.borderMaxFrac,
                     sizeErrIn: q.m.sizeErrIn, inkOutsidePx: q.m.inkOutsidePx,
                     selfX: q.m.selfX, loops: q.m.loops };
          })
        };
      }
    } catch (eG) {
      /* Same contract as every other stage: a guard that throws must cost
         nothing. What ships is then whatever the stages produced, which is
         exactly today's behaviour. */
      try { console.warn('[sl-cutguard] declined:', eG && eG.message); } catch (x) {}
      guardNote = { error: (eG && eG.message) || String(eG) };
    }
    out.guard = guardNote;

    /* ── DIE-CUT CART GRAPHICS: ONE EXACT FINAL PLACEMENT (D-183) ─────────
       At 40-72 in the trace lands within about 0.01-0.02 in of the ideal on
       each side, which is the size gates' own tolerance, so a second trace
       to correct it is a coin flip: it moves the result by as much as it
       corrects. Instead the FINISHED geometry is placed once, exactly: one
       uniform scale and one shift that centre the cut in the canvas and put
       its long edge on the ordered size. The cut, the border and the
       artwork's placement (applied by the caller from `out.normalized`) move
       together, so how the cut sits on the artwork is unchanged.

       IT IS BOUNDED, SO IT CANNOT HIDE A BAD TRACE. The scale may change the
       long edge, and the shift may move the cut, by no more than three times
       the size tolerance (0.045 in): the trace noise measured at this scale is
       up to about 0.03 in, and with Border = None the cut is the outline
       itself, which runs half a silhouette pixel inside the ink on each side.
       The errors this product was refused for were 0.08-0.12 in. Anything
       larger than the budget is a trace that is wrong, not noisy, and is
       refused under the gate it would have failed.

       THEN THE SAME TWO GATES, on what will actually be cut. */
    /* the cut in the silhouette's own frame, for the final trace check below */
    var dTraced = d, nTraced = 1;
    if (cgNorm) {
      var nb = CPX.measuredBBox(d);
      if (!nb) { out.reason = 'path_unmeasurable'; return out; }
      var nLong = Math.max(nb.w, nb.h), cLong = Math.max(cw, ch);
      var nm = cLong / nLong;
      var ncx = (nb.minX + nb.maxX) / 2, ncy = (nb.minY + nb.maxY) / 2;
      var budgetU = 3 * pad;
      out.normalized = { scale: +nm.toFixed(6), shiftX: +(cw / 2 - ncx).toFixed(3), shiftY: +(ch / 2 - ncy).toFixed(3),
                         budgetUnits: +budgetU.toFixed(3) };
      if (Math.abs(nLong - cLong) > budgetU) { out.reason = 'cut_long_edge_off_target'; return out; }
      if (Math.abs(cw / 2 - ncx) > budgetU || Math.abs(ch / 2 - ncy) > budgetU) {
        out.reason = 'cut_bbox_outside_canvas'; return out;
      }
      var NT = { sx: nm, sy: nm, tx: cw / 2 - nm * ncx, ty: ch / 2 - nm * ncy };
      var nd = CPX.transformPath(d, NT);
      var nP = geo.P ? CPX.transformPath(geo.P, NT) : geo.P;
      if (!nd || (geo.P && !nP)) { out.reason = 'normalize_untransportable'; return out; }
      var nBands = [];
      for (var nbi = 0; nbi < (geo.bands || []).length; nbi++) {
        var nbd = (nbi === 0) ? nd : CPX.transformPath(geo.bands[nbi].d, NT);
        if (!nbd) { out.reason = 'normalize_untransportable'; return out; }
        nBands.push({ d: nbd, fill: geo.bands[nbi].fill });
      }
      nTraced = nm;
      d = nd;
      geo.bands = nBands;
      geo.B = nd;
      geo.P = nP;
      out.normalized.transform = NT;
      var fb = CPX.measuredBBox(d);
      if (!fb) { out.reason = 'path_unmeasurable'; return out; }
      out.cutBBox = { minX: fb.minX, minY: fb.minY, maxX: fb.maxX, maxY: fb.maxY };
      out.cutBBoxCanon = null;
      var fLong = Math.max(fb.w, fb.h);
      out.longEdge = { actual: +fLong.toFixed(3), target: +cLong.toFixed(3), error: +(fLong - cLong).toFixed(3) };
      if (Math.abs(fLong - cLong) > pad) { out.reason = 'cut_long_edge_off_target'; return out; }
      if (fb.minX < -containPad || fb.minY < -containPad ||
          fb.maxX > cw + containPad || fb.maxY > ch + containPad) {
        out.reason = 'cut_bbox_outside_canvas';
        out.overflow = {
          left: +(0 - fb.minX).toFixed(3), top: +(0 - fb.minY).toFixed(3),
          right: +(fb.maxX - cw).toFixed(3), bottom: +(fb.maxY - ch).toFixed(3),
          tolerance: +containPad.toFixed(3)
        };
        return out;
      }
      if (!geo.borderNone && (!geo.bands.length || geo.bands[0].d !== d)) { out.reason = 'cut_and_border_diverged'; return out; }
    }

    out.evidence.bbox = { x: +bb.minX.toFixed(2), y: +bb.minY.toFixed(2),
                          w: +bb.w.toFixed(2), h: +bb.h.toFixed(2) };
    out.evidence.commands = cmds;
    out.evidence.bandCount = geo.bands.length;

    /* ── VALIDATE LAST (D-176) ──────────────────────────────────────────
       The cut that would ship, after every stage and the selector, judged one
       final time against the artwork it was built from. Nothing after this
       line changes the path. A failure here is a refusal like any other: the
       file carries no CutContour, says why, and HQ flags it for a hand-drawn
       cut - never a cut that only resembles the artwork.
       A die-cut cart graphic's cut was placed above (`cgNorm`) together with
       its artwork, so the two still sit as traced; the silhouette here was
       not moved, so the check reads the cut as it was before that placement,
       with the tolerance in the same units. */
    if (CPX.validateCut && traceTolU > 0) {
      var fin = null;
      try {
        fin = CPX.validateCut({ silMask: silForTrace, viewBox: { w: cw, h: ch }, cutPath: dTraced,
                                distance: geom.distance, tolUnits: traceTolU / nTraced });
      } catch (eF) { fin = { ok: false, reasons: ['validator_threw'], metrics: {} }; }
      out.validation = fin;
      out.evidence.finalTrace = fin.ok ? 'ok' : fin.reasons.join('+');
      if (!fin.ok) { out.reason = 'final_trace_validation_failed:' + fin.reasons.join('+'); return out; }
    }
    out.accepted = true;
    out.bands = geo.bands;
    out.cutPath = d;
    out.P = geo.P;
    out.B = geo.B;
    out.note = geo.note;
    return out;
  };

  /* The border the double dip must reproduce, in the production document's
     units. The THICKNESS is the canonical physical one (`thick`, resolved by
     SLBuilderCore.borderInchesForLevel — D-085), not the baker's frame
     fraction, so print and cut agree with the product rule rather than with a
     rasterisation detail. */
  const doubleDipGeometry = (plan) => {
    const cw = +plan.canvas.w;
    const unitsPerInch = cw / Math.max(1e-6, widthIn);
    /* The baker's own test, not a new one: it treats anything above 244 in
       all three channels as white and skips the kiss-line there. Two different
       definitions of "white" would put a halo in the print that the preview
       never showed. */
    const hx = String(borderCss || '#ffffff').replace('#', '');
    const bR = parseInt(hx.slice(0, 2), 16), bG = parseInt(hx.slice(2, 4), 16), bB = parseInt(hx.slice(4, 6), 16);
    const borderIsWhite = !(bR <= 244 || bG <= 244 || bB <= 244);
    const distance = border === 'none' ? 0 : Math.max(0, thick * unitsPerInch);
    /* The baker draws a white kiss-line inside a COLOURED border so it reads
       crisp against dark artwork; a white border has none. Same rule, same
       proportion, expressed as geometry instead of as BFS depth. */
    const haloPx = (border === 'none' || borderIsWhite) ? 0 : Math.max(2, distance * 0.3);
    return {
      distance: distance,
      unitsPerInch: unitsPerInch,
      borderFill: border === 'none' ? null : (borderCss || '#ffffff'),
      faceFill: faceColorCss || '#ffffff',
      haloPx: haloPx
    };
  };

  /* ── THE LAST BUILD'S SHADOW EVIDENCE ──────────────────────────────────
     Held so `SLProductionGateway` can hand it to HQ after the fact. It is set
     on the way past, never awaited, and nothing in the build reads it back. A
     build that produced none leaves it null and the gateway says so. */
  let lastShadowEvidence = null;

  /* CART GRAPHICS PRINTS FROM ITS PRODUCTION FILE, so it is not a blueprint.
     A shaped cart graphic is built by SLCartGraphicsExport from the customer's
     ORIGINAL BYTES at their own resolution, with the border and the cut line
     as vectors. `renderProductionSvg` hands a shaped cart graphic here and
     does nothing else for it; stickers, sheets and die cuts never reach this.
     What the export produced is kept for the add-to-cart step, which records
     it on the order. */
  const cgRenderPrintFile = (designId, g, resolve, dieCut) => {
    cgPrintInfoRef.current = null;
    const CGX = window.SLCartGraphicsExport;
    const bytes = artBytesRef.current;
    if (!CGX || !CGX.build || !bytes) { resolve(null); return; }
    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onerror = () => resolve(null);
    probe.onload = () => {
      const meta = productionMeta(designId);
      const rows = [];
      if (meta.designId) rows.push('Design: ' + meta.designId);
      rows.push('Size: ' + meta.sizeText, 'Qty: ' + meta.quantity, 'Finish: ' + meta.finish, 'Shape: ' + meta.shape, 'Border: ' + meta.border);
      if (meta.fileName) rows.push('Original: ' + meta.fileName);
      if (dieCut) rows.push('Cut line: traced from the artwork, border included');
      CGX.build({
        dieCut: dieCut || null,
        g: g, workW: probe.naturalWidth, workH: probe.naturalHeight,
        bytes: bytes, fileName: art ? art.name : '',
        /* THE ORIGINAL, for an upload over the pixel cap: `bytes` is then the
           reduced working copy, and the print file embeds this instead
           whenever it can carry it whole (its pixel size is known, so it is
           not decoded again). When it cannot, the copy is used and the
           print file's note says so. */
        original: (artOriginalRef.current && artOriginalPxRef.current)
          ? { bytes: artOriginalRef.current, w: artOriginalPxRef.current.w, h: artOriginalPxRef.current.h } : null,
        maskUrl: (bgOn && bgUrl) ? bgUrl : null,
        widthIn: widthIn, heightIn: heightIn, borderIn: border === 'none' ? 0 : +thick,
        /* the corner radius in inches, from the one rule that decides it */
        cornerIn: (shape === 'square' && _szCore() && _szCore().cornerInchesForLevel)
          ? _szCore().cornerInchesForLevel(corners === 'sharp' ? 'sharp' : 'rounded', Math.max(widthIn, heightIn)) : null,
        designId: designId || null, title: art ? art.name : null, rows: rows,
        maxSvgBytes: cgPrintBudgetRef.current || 0
      }).then(r => {
        cgPrintInfoRef.current = r ? r.info : null;
        try { window.__slCgPrint = r ? r.info : null; } catch (e) {}
        resolve(r ? r.svg : null);
      }).catch(() => resolve(null));
    };
    probe.src = g.src;
  };

  /* A DIE-CUT CART GRAPHIC'S PRINT FILE (D-183).
     The cut line was traced and passed every gate a die-cut sticker's must
     pass; this hands it, with the placement of the silhouette it was traced
     from, to the Cart Graphics print file, which draws the ORIGINAL artwork
     there at full resolution. A refused cut is not substituted: no cut line,
     no print file, and the add-to-cart step says why. */
  const cgFinishDieCut = (designId, g, cut, resolve) => {
    cgDieCutRefusalRef.current = null;
    if (!cut || !cut.accepted || !cut.cutPath || !cut.tracedArt) {
      cgPrintInfoRef.current = null;
      cgDieCutRefusalRef.current = (cut && cut.reason) || 'not-run';
      resolve(null);
      return;
    }
    /* THE SILHOUETTE WAS DRAWN FROM THE PADDED FRAME. With a border, the
       picture on screen is the artwork centred in `outlineFrame` — `pad`
       transparent pixels added on every side for the baked border — and the
       placement above is that whole frame. The original carries no padding,
       so it fills only the frame's inner `iw x ih` region, about the same
       centre. */
    const a0 = cut.tracedArt.art;
    const of = (usingOutline && outlineFrame && outlineFrame.cw > 0 && outlineFrame.ch > 0) ? outlineFrame : null;
    const artIn = of ? Object.assign({}, a0, { w: a0.w * (of.iw / of.cw), h: a0.h * (of.ih / of.ch) }) : a0;
    cgRenderPrintFile(designId, g, resolve, {
      canvas: cut.tracedArt.canvas, art: artIn,
      bands: cut.bands || [], cutPath: cut.cutPath
    });
  };

  /* ── ONE CUT, SEEN AND SHIPPED (D-176) ────────────────────────────────
     The last finished production geometry, keyed by everything it was built
     from: the plan, the border geometry, the ordered size and fit, the
     silhouette's identity and the artwork. The preview draws THIS cut, and
     an export whose inputs produce the same key ships THIS cut - the same
     object, so the CutContour the customer saw and the one in the file are
     one string, not two that should agree. Different inputs, different key:
     the export simply computes, exactly as before. */
  const prodGeoRef = useRef(null);
  const slKeyHash = (str) => {
    let h1 = 5381, h2 = 52711;
    for (let i = 0; i < str.length; i++) {
      const c = str.charCodeAt(i);
      h1 = ((h1 << 5) + h1) ^ c; h2 = ((h2 << 5) + h2) ^ c;
    }
    return (h1 >>> 0).toString(36) + (h2 >>> 0).toString(36) + ':' + str.length;
  };

  const renderProductionSvg = (designId, ropts) => new Promise(resolve => {
    /* `warm`: build the geometry for the preview and remember it - no file,
       no telemetry. Add to Cart then finds it already made. */
    const warm = !!(ropts && ropts.warm);
    /* `quiet` (D-199): build the file only to MEASURE its kiss cut for a sheet layout - no telemetry. */
    const quiet = !!(ropts && ropts.quiet);
    const PA = window.SLProductionArtwork;
    const core = window.slBuilder;
    const g = proofGeometry();
    if (!PA || !PA.buildProductionSvg || !core || !core.proofDrawPlan || !g.src) { resolve(null); return; }
    /* A shaped cart graphic prints from its production file, so it is built
       by `cgRenderPrintFile` (above) instead of as the blueprint below. */
    if (CG && !g.dieCut) { cgRenderPrintFile(designId, g, resolve); return; }
    const img = new Image();
    /* Die-cut art is an outlined face on Shopify's CDN, so the canvas would be
       tainted and `toDataURL` would throw — the same reason the rasterizer asks
       as a browser. A throw here means no production file, never a substitute. */
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        /* ── PRODUCTION IS PLANNED IN THE PRODUCTION FRAME (10K) ──────────
           `g` is the on-screen model, and its box is the measured stage. Every
           production consumer downstream of this line reads `plan.canvas`:
           `doubleDipGeometry` divides it by `widthIn` for `unitsPerInch`,
           `buildDoubleDip` reconstructs in it, `uX`/`uY`/`tolU` convert the
           physical tolerance through it, and it becomes the exported viewBox.
           Planning in the stage box is what made all of those layout-dependent.

           Only the BOX is restated. The offsets are box-relative by
           construction - `x` is `w * (centreFrac - 0.5)` - so they carry the
           same fraction into the new frame; `scale` is dimensionless
           (`contourScale * fitFactor`) and `fit: 'width'` sizes the art from
           the box itself, so both are already frame-independent.

           DIE-CUT ONLY, and that is ownership rather than caution: the contour
           pipeline is the only thing that constructs geometry, and it is also
           the only mode whose `padPx` and corner radii are pinned to zero. A
           shaped sticker carries those in BOX PIXELS, so moving its frame
           without rescaling them would change the ring it prints. Shaped
           exports keep exactly the frame they have always had. */
        let pg = g;
        const pf = g.dieCut ? productionFrame(g.boxW, g.boxH) : null;
        if (pf && g.boxW > 0 && g.boxH > 0) {
          pg = Object.assign({}, g, {
            boxW: pf.w, boxH: pf.h,
            x: (+g.x || 0) * (pf.w / g.boxW),
            y: (+g.y || 0) * (pf.h / g.boxH)
          });
        }
        const plan = core.proofDrawPlan(pg, img.naturalWidth, img.naturalHeight);
        /* Everything the cut is a function of. Long strings (a data-URL
           source, say) travel as a hash; nothing else is abbreviated. A
           bordered cut is built from the published silhouette, never from the
           displayed picture (which, once the preview is repainted from this
           very cut, is a different string) - so the picture keys only a
           border-none cut, which IS traced from it. */
        const silNow = silhouetteRef.current;
        let geoKey = null;
        try {
          geoKey = slKeyHash(JSON.stringify({
            plan: plan, geom: doubleDipGeometry(plan), w: widthIn, h: heightIn,
            fitting: cfit ? { fw: cfit.finishedW, fh: cfit.finishedH, ff: cfit.fitFactor, r: cfit.artworkRectIn } : null,
            sil: silNow ? silNow.id : null, artwork: silNow ? null : g.src, dieCut: !!g.dieCut
          }, function (k, v) { return (typeof v === 'string' && v.length > 160) ? ('#' + slKeyHash(v)) : v; }));
        } catch (eK) { geoKey = null; }
        /* THE BLUEPRINT EMBEDS THE COMPOSITED PROOF (D-105).
           ---------------------------------------------------------------
           It used to embed the raw artwork and let SVG clip it to the shape,
           via two NESTED clip-path groups around a transformed <image>.
           Illustrator refused that:

               "The following items could not be imported properly:
                Clipping will be lost on roundtrip to Tiny"

           and after continuing the clip was NOT applied — the artwork escaped
           its shape and the full-bleed white face and border paths rendered as
           a giant white object over the sticker and its metadata.

           So the visual is flattened by the CANONICAL rasterizer instead.
           `rasterizeFace` executes the same `proofDrawPlan` this function
           already computed — the crop, zoom, offset, rotation, flip, shape,
           border and face treatment are applied to PIXELS, exactly as the
           customer approved them. No second renderer, and no clipping left in
           the document for Illustrator to object to.

           Bounded by the same blueprint policy, so the file stays small: the
           composite is rendered at the blueprint long edge, not at the
           preview's 3x supersample. */
        const longEdge = PA.blueprintLongEdge(Math.max(widthIn, heightIn));
        /* A DIE-CUT CART GRAPHIC (D-183) is traced at its own resolution and
           printed from its own file. The trace, the gates and the correction
           below are the sticker's, unchanged; only the evidence is finer, and
           at the end the geometry goes to the Cart Graphics print file instead
           of the blueprint. Nothing is encoded that only the blueprint uses. */
        const evidenceEdge = cgEvidenceEdge(longEdge);
        const cgDie = !!(CG && g.dieCut);
        /* Captured from the SAME render that becomes the blueprint, so the
           mask and the embedded picture cannot describe different sticker. */
        let composite = null;
        rasterizeFace({
          longEdgePx: evidenceEdge, skipEncode: cgDie,
          onComposite: function (cv, cvCtx, artPx) { composite = { width: cv.width, height: cv.height, ctx: cvCtx, artPx: artPx }; }
        }).then(function (proofUrl) {
          /* A tainted canvas returns null. A blueprint with no picture is not
             worth shipping, and substituting the raw upload would put
             unclipped artwork back in the file. */
          if (!proofUrl) { resolve(null); return; }

          /* THE DOUBLE DIP IS ADDITIVE AND ISOLATED.
             Only die-cut, and wrapped so that no failure inside it can cost
             the production file — let alone the sale. Shaped stickers keep
             their deterministic ring and never reach this. When it declines,
             `href` stays the bordered composite and no CutContour is emitted,
             which is byte-for-byte today's behaviour. */
          const finish = function (cut, artUrl) {
            /* remembered for the preview and for the export that follows */
            if (g.dieCut && geoKey) {
              prodGeoRef.current = { key: geoKey, done: true, cut: cut, artUrl: artUrl,
                                     fitReport: cfitReportRef.current, fitTrace: window.__slFitTrace,
                                     sil: silNow, at: Date.now() };
            }
            const meta = productionMeta(designId);
            /* The customer was told before checkout - in the builder, or by the
               save stage that follows this refusal (D-176). Recorded in the file
               so HQ can say so. */
            if (g.dieCut && !warm && !(cut && cut.accepted)) meta.customerTold = 'manual cut review';
            if (cut && cut.accepted && cut.note) meta.cutPathNote = cut.note;
            /* THE OUTCOME IS RECORDED WHETHER OR NOT IT SUCCEEDED.
               `cutPathNote` above is written only on acceptance, so a refusal
               left the production file completely silent about the cut — which
               is how SL-1M1T3-85QBP-M94S4 shipped with zero path elements and
               no way to tell, from the artefact, which stage declined. The
               engine's own refusal reasons are precise; they simply never
               reached the file. Diagnostic only: this adds a text row and
               touches no geometry. */
            meta.cutDiag = !cut ? 'not-run'
              : (cut.accepted
                  ? ('accepted ' + ((cut.evidence && (cut.evidence.pLane || cut.evidence.pFamily)) || '?'))
                  : ((cut.ran ? 'refused ' : 'skipped ') + (cut.reason || '?')));
            /* the manufacturing rebuild, recorded whether it ran, declined or
               was not offered — the same reasoning as `cutDiag` above */
            try {
              var cz = cut && cut.cleanup;
              meta.cleanDiag = !cz ? 'not-run'
                : (cz.refused
                    ? ('declined ' + cz.refused + '  (would have been ' +
                       cz.anchorsBefore + '->' + cz.anchorsAfter + ')')
                    : ('anchors ' + cz.anchorsBefore + '->' + cz.anchorsAfter +
                       '  lines ' + cz.lines + ' axis ' + cz.snappedAxis +
                       '  dev ' + cz.maxDeviation + ' bbox ' + cz.bboxShift +
                       ' tol ' + (+cz.tolerance).toFixed(3)));
            } catch (xz) { meta.cleanDiag = null; }
            try {
              console.info('[sl-cutpath] double dip',
                           cut && cut.accepted ? 'ACCEPTED' : ('skipped: ' + (cut && cut.reason)),
                           cut && cut.evidence ? cut.evidence : '');
              if (!warm && !quiet && window.slTelemetry && window.slTelemetry.track) {
                window.slTelemetry.track('cutpath_double_dip', Object.assign({
                  designId: designId || null,
                  accepted: !!(cut && cut.accepted),
                  reason: (cut && cut.reason) || null
                }, (cut && cut.evidence) || {}));
              }
            } catch (x) {}
            /* Recorded BEFORE the resolve so it is available the instant the
               caller has its SVG, and outside any promise the caller waits on. */
            try { lastShadowEvidence = (cut && cut.shadowEvidence) || null; }
            catch (xS) { lastShadowEvidence = null; }
            /* a background build answers WHICH design it built, so the preview
               can never publish a cut that belongs to another one */
            if (warm) { resolve({ warmKey: geoKey }); return; }
            /* A die-cut cart graphic is printed from its own file, built from
               the original artwork and this cut (or refused without one). */
            if (cgDie) { cgFinishDieCut(designId, g, cut, resolve); return; }
            /* MOTO GVE: a shape held for review is written into the file for
               HQ; any other file is exactly what it was */
            const withReview = (svg) => (g.dieCut && cut && !cut.accepted && cut.review)
              ? slWithGeometryReview(svg, cut.review) : svg;
            resolve(withReview(PA.buildProductionSvg(plan, {
              widthIn, heightIn,
              /* WHEN THE BORDER IS VECTOR, THE PICTURE MUST NOT ALSO CARRY IT.
                 `artUrl` is the artwork composited WITHOUT its baked border, so
                 the only border in the file is the one drawn from B. Without
                 the swap the raster border would sit on top of the clean one
                 and the staircase would still be printed. */
              href: (cut && cut.accepted && artUrl) ? artUrl : proofUrl,
              /* One unclipped image; the exporter emits no clipPath at all. */
              flattened: true,
              designId: designId || null,
              title: art ? art.name : null,
              borderBands: (cut && cut.accepted) ? cut.bands : null,
              cutPath: (cut && cut.accepted) ? cut.cutPath : null,
              meta: meta
            })));
          };

          if (!g.dieCut) { finish(null, null); return; }

          /* THE SAME INPUTS ALREADY BUILT: ship that cut (D-176). The fit
             report and its trace are restored exactly as that build left them,
             because Add to Cart reads them to patch the order's rect. */
          const memo = prodGeoRef.current;
          if (geoKey && memo && memo.done && memo.key === geoKey) {
            if (memo.fitReport) cfitReportRef.current = slPublishFit(memo.fitReport);
            window.__slFitTrace = memo.fitTrace;
            finish(memo.cut, memo.artUrl);
            return;
          }

          const silh = silhouetteRef.current;
          const geom = doubleDipGeometry(plan);
          /* Border=None bakes no silhouette, so the artwork's own alpha IS the
             un-dilated silhouette — the same object, one step earlier. The
             composite carries no border in that case, by definition. */
          const silSource = silh ? silh.sil : null;
          const artSource = silh ? silh.base : null;
          const fitted = !!cfit;

          /* ── ONE BUILD, AT A GIVEN PLACEMENT ────────────────────────────
             Everything downstream of the geometry: composite the silhouette,
             composite the artwork, reconstruct the cut path. Factored out so
             the correction pass runs the SAME code as the first pass rather
             than a second version of it. */
          /* The die-cut cart graphic's fit tolerance, stated in inches (D-183). */
          const cgDieEngine = (cgDie && CG.dieCutEngineOpts)
            ? CG.dieCutEngineOpts(widthIn, heightIn, border === 'none' ? 0 : +thick) : null;
          /* A BORDERED STICKER GETS D-187'S RESCUE, AND ONLY THE RESCUE (P-45).
             `bodyInkUnion` builds every rung exactly as before and rebuilds a rung
             with the body's ink in the border's source only when that rung fails
             border_too_thin or ink_outside_cut. So a sticker that passes today is
             the cut it was. The cart graphic's 1/90in grid floor is NOT carried
             over: it would move stickers that pass. */
          const cgDieOffset = (cgDie && CG.dieCutOffsetOpts)
            ? CG.dieCutOffsetOpts(widthIn, heightIn, border === 'none' ? 0 : +thick)
            : ((!CG && border !== 'none') ? { bodyInkUnion: true } : null);
          /* the engine a size rescue asks for (P-45); null is the normal build.
             Set by runFitted, which runs one build sequence at a time. */
          let engineOverride = null;
          const buildAt = function (fitAdjust) {
            return new Promise(function (done) {
              let silPlan = null;
              const dip = function (silComposite, artUrl) {
                const cutOpts = { fitted: fitted, uLong: uLongU, engineOpts: cgDieEngine, offsetOpts: cgDieOffset, cgNormalize: cgDie,
                                  /* MOTO GVE: the fit's unrounded finished box, in inches */
                                  orderedIn: cfit ? { w: cfit.finishedW, h: cfit.finishedH } : null,
                                  traceNoisePx: cgDie ? SL_CG_TRACE_NOISE_PX : 0 };
                /* the size rescue's engine (P-45; stickers only, see rescueFrom) */
                if (engineOverride) cutOpts.engineOpts = engineOverride;
                /* a borderless die-cut sticker's cut may not trim its ink (P-45) */
                cutOpts.inkProtect = !CG && contour && border === 'none';
                const onPage = function () {
                  let cut = null;
                  try { cut = buildDoubleDip(plan, silComposite, geom, cutOpts); }
                  catch (e) { cut = { ran: false, accepted: false, reason: 'double_dip_threw' }; }
                  return cut;
                };
                const finish = function (cut) {
                  /* how this cut maps back onto the preview's own bake (D-176) */
                  if (cut && silPlan) cut.map = silPlan;
                  /* Where the silhouette this cut was traced from was drawn, moved
                     by the cut's final placement if it had one, so the artwork
                     and the cut stay exactly as registered as when traced. */
                  if (cut && silComposite && silComposite.artPx) {
                    const a0 = silComposite.artPx, nt = cut.normalized && cut.normalized.transform;
                    const placed = !nt ? a0 : {
                      w: a0.w, h: a0.h, scale: a0.scale * nt.sx, rotateRad: a0.rotateRad, flipX: a0.flipX,
                      translate: { x: nt.sx * a0.translate.x + nt.tx, y: nt.sy * a0.translate.y + nt.ty }
                    };
                    cut.tracedArt = { canvas: { w: silComposite.width, h: silComposite.height }, art: placed };
                  }
                  done({ cut: cut, artUrl: artUrl });
                };
                if (!silComposite) { finish(onPage()); return; }
                /* off the page's thread (see slCutWorkerRun); the same code either way */
                slCutWorkerRun(buildDoubleDip, plan, silComposite, geom, cutOpts, warm).then(function (cut) {
                  if (window.__slCutWorkerVerify) {
                    try {
                      const mine = onPage(), diffs = [], seen = new WeakSet();
                      const walk = function (a, b, p) {
                        if (diffs.length >= 40) return;
                        if (a && typeof a === 'object') { if (seen.has(a)) return; seen.add(a); }
                        if (ArrayBuffer.isView(a) || ArrayBuffer.isView(b)) {
                          let eq = !!a && !!b && a.length === b.length;
                          for (let i = 0; eq && i < a.length; i++) if (a[i] !== b[i]) eq = false;
                          if (!eq) diffs.push(p);
                          return;
                        }
                        if (a && b && typeof a === 'object' && typeof b === 'object') {
                          const ks = {};
                          Object.keys(a).forEach(function (k) { ks[k] = 1; });
                          Object.keys(b).forEach(function (k) { ks[k] = 1; });
                          Object.keys(ks).forEach(function (k) { walk(a[k], b[k], p + '.' + k); });
                        } else if (a !== b && !(a !== a && b !== b) &&
                                   !/(Ms|\.ms)$/.test(p)) diffs.push(p);   /* durations differ run to run */
                      };
                      walk(mine, cut, 'cut');
                      (window.__slCutVerify = window.__slCutVerify || []).push({
                        warm: warm, same: diffs.length === 0, diffs: diffs,
                        cutSame: !!(mine && cut && mine.cutPath === cut.cutPath && mine.accepted === cut.accepted &&
                                    mine.reason === cut.reason),
                        accepted: !!(cut && cut.accepted), at: Date.now() });
                    } catch (eV) {}
                  }
                  finish(cut);
                }, function (why) {
                  /* a stale preview build, or a preview build that has no worker
                     to run in: nothing is published, the preview keeps its pixel
                     border, and Add to Cart builds the cut as it always has */
                  if (warm || (why && why.superseded)) { resolve(null); return; }
                  finish(onPage());
                });
              };
              if (!silSource) { dip(composite, null); return; }
              let silComposite = null;
              rasterizeFace({
                longEdgePx: evidenceEdge, sourceOverride: silSource, skipEncode: true, fitAdjust: fitAdjust,
                onPlan: function (pm) { silPlan = pm; },
                onComposite: function (cv, cvCtx, artPx) { silComposite = { width: cv.width, height: cv.height, ctx: cvCtx, artPx: artPx }; }
              }).then(function (okSil) {
                if (!okSil || !silComposite) { dip(null, null); return; }
                rasterizeFace({ longEdgePx: evidenceEdge, sourceOverride: artSource, fitAdjust: fitAdjust, skipEncode: cgDie })
                  .then(function (artUrl) { dip(silComposite, artUrl); })
                  .catch(function () { dip(silComposite, null); });
              }).catch(function () { dip(null, null); });
            });
          };

          const cwU = +plan.canvas.w, chU = +plan.canvas.h;
          const uX = cwU / (cfit ? cfit.finishedW : (widthIn || 1));
          const uY = chU / (cfit ? cfit.finishedH : (heightIn || 1));
          /* The ordered LONG edge lives on the canvas's long axis, so that is
             the axis whose scale converts the physical tolerance. Declared
             before `tolU` because `tolU` is now derived from it. */
          const uLongU = (cwU >= chU) ? uX : uY;
          const tolU = slContourTolUnits(cwU, chU, uLongU);
          const residualsOf = function (bb) {
            if (!bb) return null;
            return {
              left: +(bb.minX / uX).toFixed(5), top: +(bb.minY / uY).toFixed(5),
              right: +((cwU - bb.maxX) / uX).toFixed(5), bottom: +((chU - bb.maxY) / uY).toFixed(5)
            };
          };
          const runFitted = function (finish, eo) {
          engineOverride = eo || null;
          const fitReport = {
            contourFitPass: 1, correctionRan: false,
            correctionScale: 1, correctionDeltaX: 0, correctionDeltaY: 0,
            requestedLongEdgeIn: +Math.max(widthIn, heightIn).toFixed(5),
            /* WHAT THE CLOSED FORM ASKED FOR, before anything was built. The
               ordered inches are rounded to one decimal for the customer;
               these are not, so a shortfall can be attributed to the fit or to
               the rounding rather than blamed on whichever is nearer. */
            targetFinishedIn: cfit ? { w: +cfit.finishedW.toFixed(5), h: +cfit.finishedH.toFixed(5) } : null,
            orderedIn: { w: widthIn, h: heightIn },
            canvasUnits: { w: cwU, h: chU },
            distanceUnits: +(+geom.distance).toFixed(4),
            unitsPerInch: { x: +uX.toFixed(4), y: +uY.toFixed(4) },
            /* The number the hard gate actually compares against, not the one
               the formula suggests. */
            toleranceUnits: +tolU.toFixed(4),
            measuredFinishedIn: null,
            gateReason: null
          };

          buildAt(null).then(function (first) {
            fitReport.firstPassCutBBox = first.cut && first.cut.cutBBox
              ? { x: +(first.cut.cutBBox.minX / uX).toFixed(4), y: +(first.cut.cutBBox.minY / uY).toFixed(4),
                  w: +((first.cut.cutBBox.maxX - first.cut.cutBBox.minX) / uX).toFixed(4),
                  h: +((first.cut.cutBBox.maxY - first.cut.cutBBox.minY) / uY).toFixed(4) }
              : null;
            /* THE OLD AUTHORITY, KEPT VISIBLE. Every size number above is now
               the flattened measurement; this is what the control-hull bound
               would have said, and the gap between them is the whole reason
               the correction pass used to fire. A reader comparing a new
               export against an old one needs to see both. */
            fitReport.firstPassCutBBoxConservative = first.cut && first.cut.cutBBoxConservative
              ? { x: +(first.cut.cutBBoxConservative.minX / uX).toFixed(4),
                  y: +(first.cut.cutBBoxConservative.minY / uY).toFixed(4),
                  w: +((first.cut.cutBBoxConservative.maxX - first.cut.cutBBoxConservative.minX) / uX).toFixed(4),
                  h: +((first.cut.cutBBoxConservative.maxY - first.cut.cutBBoxConservative.minY) / uY).toFixed(4) }
              : null;
            fitReport.overReportUnits = first.cut ? (first.cut.overReportUnits || 0) : null;
            /* WHICH GEOMETRY PRODUCED THE PERIMETER. Kept so the on-device
               readout can state whether the border a customer is looking at is
               MOTO's vector reconstruction or the preview's raster bake — two
               different edges that nobody could previously tell apart.
               Diagnostic only: nothing reads this to make a decision. */
            fitReport.cutpath = first.cut ? {
              accepted: !!first.cut.accepted,
              reason: first.cut.reason || null,
              /* die-cut cart graphics: the final placement, and what it measured */
              normalized: first.cut.normalized ? { scale: first.cut.normalized.scale, shiftX: first.cut.normalized.shiftX,
                shiftY: first.cut.normalized.shiftY, budgetUnits: first.cut.normalized.budgetUnits } : null,
              longEdge: first.cut.longEdge || null,
              guardChose: (first.cut.guard && first.cut.guard.chose) || null,
              evidence: first.cut.evidence ? {
                pLane: first.cut.evidence.pLane,
                pFamily: first.cut.evidence.pFamily,
                pConfidence: first.cut.evidence.pConfidence
              } : null
            } : null;
            /* READ OFF THE GLOBAL, NOT OFF `CPX`. `CPX` is a const scoped to
               `buildDoubleDip`; this handler lives in `renderProductionSvg` and
               never had it. The reference threw a ReferenceError on EVERY
               die-cut export, the blanket catch below turned that into
               `finish(null, null)`, and the whole double dip was discarded
               after it had already succeeded — reported only as
               "double dip skipped: null" with no error and no evidence. */
            fitReport.measureFlattenTol = (window.SLCutPath && window.SLCutPath.MEASURE_FLATTEN_TOL) || null;

            /* Only a FITTED contour job corrects. A fixed shape, a border=none
               design and any job whose silhouette could not be measured are
               finished exactly as they were before this existed. */
            if (!fitted || !first.cut || !first.cut.cutBBox) {
              cfitReportRef.current = slPublishFit(fitReport);
              finish(first.cut, first.artUrl); return;
            }

            const corr = slContourCorrect(first.cut.cutBBox, cwU, chU, geom.distance, tolU);
            fitReport.firstPassOverflow = corr ? {
              left: +(corr.overflow.left / uX).toFixed(5), top: +(corr.overflow.top / uY).toFixed(5),
              right: +(corr.overflow.right / uX).toFixed(5), bottom: +(corr.overflow.bottom / uY).toFixed(5)
            } : null;

            if (first.cut.accepted && corr && !corr.needed) {
              /* The closed form landed it. No second build, and the report says
                 so rather than leaving a reader to infer it from a scale of 1. */
              fitReport.finalCutBBox = fitReport.firstPassCutBBox;
              const r0 = residualsOf(first.cut.cutBBox);
              fitReport.finalResidualLeft = r0.left; fitReport.finalResidualTop = r0.top;
              fitReport.finalResidualRight = r0.right; fitReport.finalResidualBottom = r0.bottom;
              fitReport.finalLongEdgeIn = +Math.max(
                (first.cut.cutBBox.maxX - first.cut.cutBBox.minX) / uX,
                (first.cut.cutBBox.maxY - first.cut.cutBBox.minY) / uY).toFixed(5);
              fitReport.measuredFinishedIn = {
                w: +((first.cut.cutBBox.maxX - first.cut.cutBBox.minX) / uX).toFixed(5),
                h: +((first.cut.cutBBox.maxY - first.cut.cutBBox.minY) / uY).toFixed(5)
              };
              fitReport.gateReason = first.cut.accepted ? 'accepted-first-pass' : (first.cut.reason || null);
              cfitReportRef.current = slPublishFit(fitReport);
              finish(first.cut, first.artUrl); return;
            }

            /* ── THE ONE CORRECTION ───────────────────────────────────────
               Solved from the box that was actually built, on the long axis so
               the finished contour still consumes the selected long edge. The
               short axis is checked, not assumed: a short axis that will not
               fit means the canvas aspect disagrees with the reconstruction by
               more than tolerance, and that is a refusal rather than something
               to absorb by shrinking. */
            if (!corr || !corr.shortFits) {
              fitReport.contourFitPass = 2;
              cfitReportRef.current = slPublishFit(fitReport);
              finish({ ran: true, accepted: false,
                       reason: corr ? 'contour_fit_short_axis_will_not_fit' : 'contour_fit_unsolvable',
                       evidence: first.cut.evidence }, first.artUrl);
              return;
            }

            const T1x = cwU / 2 + (g.x || 0), T1y = chU / 2 + (g.y || 0);
            /* ── THE CENTRING IS A FRACTION, NOT A SUBTRACTION OF PIXELS ──
                   gx = -m * (centre.x - T1x)
               subtracts two numbers of order half the viewBox to produce a
               fraction of a pixel, so the answer's RELATIVE precision follows
               the magnitudes it came from - and those magnitudes are the
               preview box, which is a display quantity. Milestone 10F proved
               this is the sole carrier of the mobile/desktop divergence: the
               normalised centring differed by 1.033e-2, which reached the
               pass-4 draw as 2.7e-3 of a canvas pixel against a rasteriser
               threshold of about 5e-4.

               The same value is now formed as a FRACTION of the box, from the
               cut centre measured in the canonical frame where the path is the
               same on every device, and only then multiplied back into
               production units. The algebra is identical -
               gx/cwU = -m * (centreFrac - 0.5 - gFrac) - but every term is now
               layout-independent, so the cancellation cancels the same numbers
               everywhere instead of merely proportional ones.

               If the canonical box is unavailable the old form runs unchanged;
               a fit that silently used a different rule would be worse than one
               that is occasionally imprecise. */
            const bbC = first.cut.cutBBoxCanon;
            let adjust;
            if (bbC && bbC.boxW > 0 && bbC.boxH > 0) {
              const centreFracX = ((bbC.minX + bbC.maxX) / 2) / bbC.boxW;
              const centreFracY = ((bbC.minY + bbC.maxY) / 2) / bbC.boxH;
              const gFracX = (g.x || 0) / cwU, gFracY = (g.y || 0) / chU;
              adjust = {
                scaleMul: corr.scaleMul,
                gx: -corr.scaleMul * (centreFracX - 0.5 - gFracX) * cwU,
                gy: -corr.scaleMul * (centreFracY - 0.5 - gFracY) * chU
              };
            } else {
              adjust = {
                scaleMul: corr.scaleMul,
                /* The new centring, absolute. Scaling the artwork by m about its
                   own draw point moves the cut centre to T1 + m*(C1 - T1); this
                   puts that on the canvas centre in one step. */
                gx: -corr.scaleMul * (corr.centre.x - T1x),
                gy: -corr.scaleMul * (corr.centre.y - T1y)
              };
            }
            /* DIE-CUT CART GRAPHICS: THE CENTRING IN THE UNITS IT IS DRAWN IN
               (D-183). `rasterizeFace` reads `gx`/`gy` as offsets in the
               PREVIEW box and scales them onto the canvas by
               kFit = canvasW / boxW. The two forms above state them in canvas
               units, so the shift actually drawn is kFit times the one solved
               for. On a 40 in graphic kFit is about 8: a 0.021 in correction
               moved the artwork 0.17 in and the second pass was refused. Here
               the same solution is restated so the drawn offset is the solved
               one. Stickers are left exactly as they were. */
            if (cgDie && g.boxW > 0 && g.boxH > 0) {
              const kx = cwU / g.boxW;
              const fx = (bbC && bbC.boxW > 0) ? ((bbC.minX + bbC.maxX) / 2) / bbC.boxW : corr.centre.x / cwU;
              const fy = (bbC && bbC.boxH > 0) ? ((bbC.minY + bbC.maxY) / 2) / bbC.boxH : corr.centre.y / chU;
              adjust.gx = -corr.scaleMul * (fx - 0.5 - (g.x || 0) * kx / cwU) * cwU / kx;
              adjust.gy = -corr.scaleMul * (fy - 0.5 - (g.y || 0) * kx / chU) * chU / kx;
            }
            fitReport.contourFitPass = 2;
            fitReport.correctionRan = true;
            fitReport.correctionScale = +corr.scaleMul.toFixed(6);
            fitReport.correctionDeltaX = +(((adjust.gx - (g.x || 0)) / uX)).toFixed(5);
            fitReport.correctionDeltaY = +(((adjust.gy - (g.y || 0)) / uY)).toFixed(5);
            /* ── THE CORRECTION'S OWN NUMBERS, PUBLISHED ─────────────────
               Milestone 10F proved the layout divergence is carried by one
               value — the pass-4 horizontal translation — and traced it back to
               this centring, whose NORMALISED form differs by 1.03% between
               layouts. It could not see which input carried that in, because
               these are all locals.

               Milestone 10E is the reason this exists rather than an argument:
               two fixes were reasoned out, deployed, and changed `adjust.gx` by
               nothing at all, and there was no way to tell whether the new code
               had run or had run and not mattered. Everything the correction
               reads and writes is published here so that question is a
               measurement. Diagnostic only; nothing consumes it.

               `k` is the canonical scale, so `centre.x * k` is the cut centre in
               the frame where both layouts are supposed to agree exactly. If
               THAT disagrees, the error arrived before the arithmetic. */
            try {
              const kD = (first.cut.canonicalFrame && first.cut.canonicalFrame.k) || null;
              const bbD = first.cut.cutBBox;
              const traceFit = {
                cwU: cwU, chU: chU, distance: geom.distance, k: kD,
                bbox: { minX: bbD.minX, maxX: bbD.maxX, minY: bbD.minY, maxY: bbD.maxY },
                bboxCanon: kD ? { minX: bbD.minX * kD, maxX: bbD.maxX * kD,
                                  minY: bbD.minY * kD, maxY: bbD.maxY * kD } : null,
                /* which centring rule actually ran, and what it read - without
                   this the next milestone would again be unable to tell "the
                   patch did nothing" from "the patch never ran" */
                rule: first.cut.cutBBoxCanon ? 'normalised' : 'legacy-subtraction',
                cutBBoxCanon: first.cut.cutBBoxCanon || null,
                centreFracCanon: first.cut.cutBBoxCanon
                  ? { x: ((first.cut.cutBBoxCanon.minX + first.cut.cutBBoxCanon.maxX) / 2) /
                         first.cut.cutBBoxCanon.boxW,
                      y: ((first.cut.cutBBoxCanon.minY + first.cut.cutBBoxCanon.maxY) / 2) /
                         first.cut.cutBBoxCanon.boxH }
                  : null,
                centre: { x: corr.centre.x, y: corr.centre.y },
                centreCanon: kD ? { x: corr.centre.x * kD, y: corr.centre.y * kD } : null,
                centreFrac: { x: corr.centre.x / cwU, y: corr.centre.y / chU },
                scaleMul: corr.scaleMul,
                gFirst: { x: g.x || 0, y: g.y || 0 },
                gFirstFrac: { x: (g.x || 0) / cwU, y: (g.y || 0) / chU },
                T1: { x: T1x, y: T1y },
                adjust: { gx: adjust.gx, gy: adjust.gy },
                adjustFrac: { x: adjust.gx / cwU, y: adjust.gy / chU }
              };
              window.__slFitTrace = traceFit;
              try { sessionStorage.setItem('__slFitTrace', JSON.stringify(traceFit)); } catch (eS2) {}
            } catch (eTrace) { /* diagnostics never cost an order */ }

            buildAt(adjust).then(function (second) {
              const bb2 = second.cut && second.cut.cutBBox;
              fitReport.finalCutBBox = bb2
                ? { x: +(bb2.minX / uX).toFixed(4), y: +(bb2.minY / uY).toFixed(4),
                    w: +((bb2.maxX - bb2.minX) / uX).toFixed(4), h: +((bb2.maxY - bb2.minY) / uY).toFixed(4) }
                : null;
              const r = residualsOf(bb2);
              if (r) {
                fitReport.finalResidualLeft = r.left; fitReport.finalResidualTop = r.top;
                fitReport.finalResidualRight = r.right; fitReport.finalResidualBottom = r.bottom;
              }
              if (bb2) {
                fitReport.finalLongEdgeIn = +Math.max((bb2.maxX - bb2.minX) / uX,
                                                      (bb2.maxY - bb2.minY) / uY).toFixed(5);
                fitReport.measuredFinishedIn = {
                  w: +((bb2.maxX - bb2.minX) / uX).toFixed(5),
                  h: +((bb2.maxY - bb2.minY) / uY).toFixed(5)
                };
              }
              fitReport.gateReason = (second.cut && second.cut.accepted)
                ? 'accepted-after-correction'
                : ((second.cut && second.cut.reason) || 'unknown');
              /* THE CORRECTED RECT IS THE ONE PRODUCTION WAS BUILT FROM, so it
                 is the one the order must carry. Publishing the first-pass
                 estimate would send Illustrator to a placement that no file
                 anywhere actually used. */
              /* ── ONE SPACE, NOT TWO ───────────────────────────────────
                 This used to set `artHU` from an INCH aspect while holding
                 UNIT values, then convert the two axes back with DIFFERENT
                 scales — `/uX` for width, `/uY` for height. The published
                 aspect came out as the true aspect multiplied by `uY/uX`, so
                 whenever the canvas units were anisotropic the order carried a
                 rect that was not the shape of the customer's artwork.

                 Measured on a pre-effAR order (canvas 398x265, uY/uX = 1.110):
                 the source is 1536x1024, aspect 1.500, and the order shipped
                 2.906483 x 1.737357 — aspect 1.673. HQ scales the original
                 uniformly to that rect's WIDTH, so an 11% aspect error put the
                 cut line through the artwork on the vertical axis.

                 The height is now derived from the width IN INCHES, using the
                 fit's own aspect. The artwork's shape is the artwork's shape;
                 it cannot depend on how square the canvas units happen to be.
                 `artHU` is kept for the y-centring, which is a unit-space
                 quantity and correctly stays one. */
              const artWU = cwU * ((cfit.fitFactor) * corr.scaleMul);
              const artHU = artWU * (cfit.artworkRectIn.height / cfit.artworkRectIn.width);
              const widthIn = artWU / uX;
              const heightIn = widthIn * (cfit.artworkRectIn.height / cfit.artworkRectIn.width);
              fitReport.correctedRectIn = {
                x: +(((cwU / 2 + adjust.gx) - artWU / 2) / uX).toFixed(6),
                y: +(((chU / 2 + adjust.gy) - artHU / 2) / uY).toFixed(6),
                width: +widthIn.toFixed(6),
                height: +heightIn.toFixed(6)
              };
              cfitReportRef.current = slPublishFit(fitReport);

              /* ── WHY THERE IS NO THIRD PASS ───────────────────────────────
                 There was one, briefly: a secant through the two measured
                 builds, on the reasoning that if the response is not the line
                 pass 2 assumes, two real points would find the real line. It
                 overshot. So did the additive model before it.

                 THE REASON BOTH FAILED WAS THE RULER, NOT THE RESPONSE.
                 Sweeping m = 0.980..1.010, the long edge measured with
                 `pathBBox` wandered between 397.1 and 408.6 against a 398
                 target and changed slope sign SEVEN times — shrinking the
                 artwork appeared to make the cut path longer. Re-measured with
                 `measuredBBox` over the identical sweep: ZERO sign changes,
                 strictly monotonic, and a straight-line fit with an rms
                 deviation of 0.34 units. The wander was the control-hull bound
                 breathing as control points moved, and it was never in the
                 geometry at all.

                 (An earlier note here blamed raster quantisation. That was
                 wrong and is recorded as wrong: raising the blueprint to 1200
                 and 2400 px changed nothing, which it would have if the cause
                 were the grid.)

                 A third pass is still not wanted, but for the opposite reason
                 to the one first written down: measured honestly the closed
                 form lands the real bear at 3.0018in and the correction reports
                 `needed: false`. There is nothing left for a solver to do. Pass
                 2 remains as the safety net for shapes the closed form misses,
                 and the long-edge gate below reports an off-target result
                 rather than pretending to fix it. */
              if (!second.cut || !second.cut.accepted) {
                finish({ ran: true, accepted: false,
                         reason: (second.cut && second.cut.reason) || 'contour_fit_correction_failed',
                         evidence: second.cut && second.cut.evidence,
                         overflow: second.cut && second.cut.overflow,
                         longEdge: second.cut && second.cut.longEdge }, second.artUrl);
                return;
              }
              finish(second.cut, second.artUrl);
            }).catch(function (e) {
              /* SAME FALLBACK, NO LONGER SILENT. Shipping the uncorrected
                 first pass is still the right answer — but a correction that
                 THREW and a correction that was not NEEDED used to look
                 identical from outside, which is how a programming error hides
                 inside a geometry pipeline. Verdict unchanged; the error is
                 now on the record. */
              slNoteHandlerError('contour-fit-second-pass', e, fitReport);
              finish(first.cut, first.artUrl);
            });
          }).catch(function (e) {
            /* This catch spans the entire first-pass handler, so it catches
               PROGRAMMING errors as readily as pipeline ones and reports both
               as "not attempted". It cost a full browser session once. The
               fallback is unchanged — no cut, order continues — but the error
               is captured rather than dropped. */
            slNoteHandlerError('contour-fit-first-pass', e, fitReport);
            finish(null, null);
          });
          };
          /* stickers only: no Cart Graphics job of any kind is rescued */
          const rescueFrom = (!CG && fitted) ? SL_SIZE_RESCUE_ENGINES : [];
          runFitted(function (cut0, art0) {
            if (!rescueFrom.length || !cut0 || cut0.accepted || SL_SIZE_RESCUE_REASONS.indexOf(cut0.reason) < 0) {
              finish(cut0, art0); return;
            }
            /* NO SILENT RETRACE (Golden Rule #3, Ryan 27 Oct 2026). Every rescue
               engine turns the geometric lane off, so a confident primitive the
               size gate refused would come back as an organic trace (the path
               exists in the code; whether #1083 took it on live is not proven).
               A CONFIDENT geometric first pass
               (the engine's own primitiveConfidentAt, the same line the engine
               draws for its review) therefore keeps its refusal - no CutContour,
               so HQ shows NO CUT LINE and the shape goes to Geometry Review. A
               low-confidence geometric or an organic first pass is rescued as
               before: Path B may still succeed on its own (Ryan, dual path). */
            const confAt = (window.SLCutPath && window.SLCutPath.DEFAULTS && +window.SLCutPath.DEFAULTS.primitiveConfidentAt) || 0.95;
            if (cut0.evidence && cut0.evidence.pLane === 'geometric' && +cut0.evidence.pConfidence >= confAt) {
              const r0 = cfitReportRef.current;
              if (r0) { r0.sizeRescue = { from: cut0.reason, skipped: 'geometric_first_pass', accepted: false }; cfitReportRef.current = slPublishFit(r0); }
              cut0.sizeRescue = { from: cut0.reason, skipped: 'geometric_first_pass' };
              finish(cut0, art0); return;
            }
            const report0 = cfitReportRef.current;
            const tried = [];
            const next = function (k) {
              if (k >= rescueFrom.length) {
                /* nothing passed every gate: the original refusal, and its report, stand */
                if (report0) { report0.sizeRescue = { from: cut0.reason, tried: tried, accepted: false }; cfitReportRef.current = slPublishFit(report0); }
                finish(cut0, art0); return;
              }
              runFitted(function (cut, art) {
                tried.push({ engine: rescueFrom[k].name, accepted: !!(cut && cut.accepted), reason: (cut && cut.reason) || null });
                if (cut && cut.accepted) {
                  if (cfitReportRef.current) cfitReportRef.current.sizeRescue = { from: cut0.reason, tried: tried, accepted: true, engine: rescueFrom[k].name };
                  cut.sizeRescue = { from: cut0.reason, engine: rescueFrom[k].name };
                  finish(cut, art); return;
                }
                next(k + 1);
              }, rescueFrom[k].eo);
            };
            next(0);
          }, null);
        });
      } catch (e) { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = g.src;
  });
  // Open the sheet workspace with the flattened sticker.
  /* ── THE PREVIEW IS THE PRODUCTION CUT (D-176) ───────────────────────
     Once the design has settled - the bake has published, nothing is being
     chosen, no order is in flight - the production geometry for exactly this
     design is built in the background by the export's own code, and kept
     (`prodGeoRef`). Two things follow from it:

       - the preview's border is repainted FROM that cut, so the outline the
         customer approves is the CutContour the file will carry;
       - if no faithful cut exists, the customer is told before checkout that
         a person will draw it, and may still check out.

     Add to Cart then finds the same inputs already built and ships that very
     cut. Anything it cannot match it simply builds, as it always has. */
  const renderProdRef = useRef(null);
  const warmDesignRef = useRef(null);
  renderProdRef.current = renderProductionSvg;
  /* THE STICKER'S REAL KISS CUT, FOR SHEETS (D-199, Ryan 22 Oct 2026). A sheet's gaps and edges are
     0.20 in between COMPLETE kiss-cut boundaries, so the layout needs the cut as it will be cut, not
     the ordered box: a rounded corner reaches a little past it, a traced die cut can sit inside or
     outside it. The production file is built once per design state (quiet: no file is kept, no
     telemetry) and its CutContour measured. Keyed on everything the cut depends on; until it lands
     the outline estimate below stands in, and Add to cart re-checks it before anything is ordered. */
  const sheetCutKey = [baseArt, shownArt, border, bsize, borderCss, faceColorCss, contour, widthIn, heightIn,
    shape, corners, zoom, pos && pos.x, pos && pos.y, rot, flipX ? 1 : 0, cfitRectKey, dieCut ? 1 : 0,
    /* a die cut's cut is the one the background build made for THIS design; a new build re-measures */
    dieCut ? ((prodGeoRef.current && prodGeoRef.current.done && prodGeoRef.current.key) || 'pending') : 'shape', prodTick].join('|');
  const measureSheetCut = key => {
    const run = renderProdRef.current;
    if (!run) return Promise.resolve(null);
    return run(null, { quiet: true }).then(svg => {
      const SP = window.SLSheetProduction;
      const b = (typeof svg === 'string' && SP && SP.cutBounds) ? SP.cutBounds(svg) : null;
      /* diagnostic only, like __slPreviewCut: what the measuring build gave */
      try { window.__slSheetCut = { at: Date.now(), svg: typeof svg, hasCut: typeof svg === 'string' && svg.indexOf('id="CutContour"') >= 0, bounds: b || null }; } catch (xD) {}
      return b && b.widthIn > 0 && b.heightIn > 0 ? { key: key, wIn: b.widthIn, hIn: b.heightIn } : null;
    }).catch((e) => { try { window.__slSheetCut = { at: Date.now(), error: String(e && (e.message || e)) }; } catch (xD) {} return null; });
  };
  useEffect(() => {
    if (!(sheetIntent || sheetOpen) || !shownArt || choosingWorkflow || orderBusy) return undefined;
    if (sheetCutTrue && sheetCutTrue.key === sheetCutKey) return undefined;
    /* A DIE CUT IS MEASURED FROM THE CUT THE ORDER WILL CARRY: only once the background build for this
       design has finished (a bordered design first waits for its bake), so the measuring build reuses
       that exact cut instead of tracing an earlier one. Add to cart reuses it too. */
    if (dieCut) {
      const P0 = prodGeoRef.current;
      if (!P0 || !P0.done) return undefined;
      if (border !== 'none') { const sh = silhouetteRef.current; if (!sh || sh.key !== [baseArt, border, bsize, contour].join('|')) return undefined; }
    }
    let dead = false;
    const t = setTimeout(() => {
      if (dead || window.__slCartAddInFlight) return;
      measureSheetCut(sheetCutKey).then(r => { if (!dead && r) setSheetCutTrue(r); });
    }, 700);
    return () => { dead = true; clearTimeout(t); };
  }, [sheetIntent, sheetOpen, shownArt, choosingWorkflow, orderBusy, sheetCutKey]);
  const sheetCutLive = (sheetCutTrue && sheetCutTrue.key === sheetCutKey) ? sheetCutTrue : null;
  const sheetCutW = sheetCutLive ? sheetCutLive.wIn : cutWIn;
  const sheetCutH = sheetCutLive ? sheetCutLive.hIn : cutHIn;
  useEffect(() => {
    if (!dieCut || choosingWorkflow || !shownArt || bakeBusy || orderBusy) return undefined;
    /* a bordered design waits for the bake of THIS design: the silhouette the
       cut is built from must be the one on screen */
    if (border !== 'none') {
      const sh = silhouetteRef.current;
      if (!sh || sh.key !== [baseArt, border, bsize, contour].join('|')) return undefined;
    }
    /* THE DESIGN CHANGED: what was shown for the last one is withdrawn now,
       not when the next build lands. The build runs in a worker (seconds on a
       phone), and until it answers the preview shows its pixel border and no
       verdict - never the previous design's cut or notice. */
    const designNow = [baseArt, shownArt, border, bsize, borderCss, faceColorCss, contour, widthIn, heightIn].join('|');
    if (warmDesignRef.current !== designNow) {
      warmDesignRef.current = designNow;
      setCutReview(null);
      try { window.__slPreviewCut = null; } catch (eQ0) {}
    }
    let dead = false;
    const t = setTimeout(() => {
      if (dead || window.__slCartAddInFlight) return;
      const run = renderProdRef.current;
      if (!run) return;
      run(null, { warm: true }).then((built) => {
        if (dead) return;
        const P = prodGeoRef.current;
        /* only the cut this build made, for the design on screen: a build that
           was replaced, or that had no worker to run in, publishes nothing */
        if (!built || !built.warmKey || !P || !P.done || P.key !== built.warmKey) return;
        setCutReview((P && P.done && !(P.cut && P.cut.accepted))
          ? { reason: (P.cut && P.cut.reason) || 'no_cut' } : null);
        /* what the preview now shows, published for QA and the regression
           harness - diagnostic only, nothing reads it back */
        try {
          window.__slPreviewCut = P && P.done ? {
            key: P.key, accepted: !!(P.cut && P.cut.accepted), reason: (P.cut && P.cut.reason) || null,
            cutPath: (P.cut && P.cut.accepted) ? P.cut.cutPath : null,
            chose: (P.cut && P.cut.guard && P.cut.guard.chose) || null,
            rung: (P.cut && P.cut.evidence && P.cut.evidence.traceRung) || null,
            painted: false, at: P.at
          } : null;
        } catch (eQ) {}
        setProdTick(n => n + 1);
      }).catch(() => {});
    }, 700);
    return () => { dead = true; clearTimeout(t); };
  }, [bakeStamp, dieCut, contour, border, bsize, borderCss, faceColorCss, baseArt, shownArt,
      widthIn, heightIn, choosingWorkflow, bakeBusy, orderBusy]);

  /* The production cut, in the bake's own pixels. Production drew the bake's
     silhouette canvas into its composite with one affine - the box-to-canvas
     scale and centring, then the plan's translate, uniform scale and flip -
     and reported it (`rasterizeFace` onPlan). Its exact inverse carries every
     production path back onto the bake, so nothing is re-traced, re-fitted or
     re-derived: the strings drawn are the strings shipped. A die cut is never
     rotated; if one ever were, this declines and the raster border stays. */
  const slProductionBacking = (L, cut) => {
    const M = cut && cut.map, ev = cut && cut.evidence;
    if (!M || !M.art || !ev || !(ev.maskW > 0) || !(ev.viewBoxW > 0) || !(ev.maskH > 0) || !(ev.viewBoxH > 0)) return null;
    if (Math.abs(+M.art.rotateRad || 0) > 1e-9) return null;
    if (M.srcW !== L.cw || M.srcH !== L.ch || !(M.kFit > 0) || !(M.art.scale > 0)) return null;
    if (!cut.cutPath || !cut.P || typeof Path2D === 'undefined') return null;
    const a = M.art, fx = a.flipX ? -1 : 1;
    const SX = (ev.maskW / ev.viewBoxW) / M.kFit / a.scale * fx * M.srcW / a.w;
    const TX = ((-M.offX / M.kFit - a.translate.x) / a.scale * fx + a.w / 2) * M.srcW / a.w;
    const SY = (ev.maskH / ev.viewBoxH) / M.kFit / a.scale * M.srcH / a.h;
    const TY = ((-M.offY / M.kFit - a.translate.y) / a.scale + a.h / 2) * M.srcH / a.h;
    if (![SX, TX, SY, TY].every(isFinite)) return null;
    const mb = document.createElement('canvas');
    mb.width = L.cw; mb.height = L.ch;
    const mx = mb.getContext('2d');
    mx.setTransform(SX, 0, 0, SY, TX, TY);
    const PB = new Path2D(cut.cutPath), PP = new Path2D(cut.P);
    /* the same three layers the preview has always drawn: the die-cut band in
       the border colour, the white kiss-line on the silhouette, the face */
    mx.fillStyle = L.col;
    mx.fill(PB, 'nonzero');
    if (L.sep > 0) {
      mx.strokeStyle = '#ffffff';
      mx.lineWidth = (L.sep * 2) / Math.max(1e-9, Math.abs(SX));
      mx.lineJoin = 'round';
      mx.stroke(PP);
    }
    mx.fillStyle = '#' + L.fhex;
    mx.fill(PP, 'nonzero');
    return { canvas: mb, map: { sx: SX, sy: SY, tx: TX, ty: TY } };
  };

  useEffect(() => {
    const P = prodGeoRef.current, L = bakeLayersRef.current;
    if (!P || !P.done || !L || !P.cut || !P.cut.accepted) return;
    /* only onto the bake the cut was built from, and only while it is current */
    if (!P.sil || P.sil !== L.silObj || L.silObj !== silhouetteRef.current) return;
    let b = null;
    try { b = slProductionBacking(L, P.cut); } catch (e) { b = null; }
    if (!b) return;
    const out = document.createElement('canvas');
    out.width = L.cw; out.height = L.ch;
    const octx = out.getContext('2d');
    octx.drawImage(b.canvas, 0, 0);
    octx.drawImage(L.base, 0, 0);
    /* the painted extent, measured exactly as the bake measures it, so the
       layout fits the production cut's outer edge rather than the raster's */
    let extent = null;
    try {
      const ST = 3, eid = octx.getImageData(0, 0, L.cw, L.ch).data;
      let ex0 = L.cw, ey0 = L.ch, ex1 = -1, ey1 = -1;
      for (let py = 0; py < L.ch; py += ST) {
        const row = py * L.cw;
        for (let px = 0; px < L.cw; px += ST) {
          if (eid[(row + px) * 4 + 3] > 8) {
            if (px < ex0) ex0 = px; if (px > ex1) ex1 = px;
            if (py < ey0) ey0 = py; if (py > ey1) ey1 = py;
          }
        }
      }
      if (ex1 >= 0) {
        ex0 = Math.max(0, ex0 - ST); ey0 = Math.max(0, ey0 - ST);
        ex1 = Math.min(L.cw - 1, ex1 + ST); ey1 = Math.min(L.ch - 1, ey1 + ST);
        extent = { wFrac: (ex1 - ex0 + 1) / L.cw, hFrac: (ey1 - ey0 + 1) / L.ch };
      }
    } catch (e) { extent = null; }
    slPngDataUrl(out, 'paint').then(function (url) {
      /* still the cut and the bake on screen? the encode was asynchronous */
      if (prodGeoRef.current !== P || bakeLayersRef.current !== L || L.silObj !== silhouetteRef.current) return;
      setOutlineFrame(Object.assign({}, L.frame, { extent: extent }));
      setOutlinedUrl(url);
      try { if (window.__slPreviewCut && window.__slPreviewCut.key === P.key) window.__slPreviewCut.painted = true; } catch (eQ) {}
      contourSrcRef.current = Object.assign({}, contourSrcRef.current, {
        source: 'production',
        production: { key: P.key, chose: (P.cut.guard && P.cut.guard.chose) || 'offset',
                      rung: (P.cut.evidence && P.cut.evidence.traceRung) || null, map: b.map }
      });
    }, function () {});
  }, [prodTick]);

  /* `sheetArt` IS THE RENDERED PROOF, OR IT IS NOTHING.
     ---------------------------------------------------------------------
     This used to assign `url || displayArt || shownArt`, and `orderSheets`
     submits `sheetArt` as the checkout preview. So a failed flatten quietly
     put the customer's raw upload on the order under `_artwork_preview` —
     order #1014's defect, still live in the sheet lane after the die-cut path
     was fixed, because the substitution happened HERE rather than at the
     submit call the guard test inspects.

     A render failure now leaves it null and the order carries no preview,
     which is the rule everywhere else: a file that is not a proof must never
     be stored under the proof's name. The sheet WORKSPACE still falls back to
     the upload for display (see the SheetBuilder `design.url` below) — that is
     a picture on a screen, not an asset on an order, and the two must not
     share a variable that blurs which one it is. */
  const openSheet = async () => {
    let url = null;
    try {
      url = await rasterizeFace();
    } catch (e) {}
    if (!url) {
      try { console.warn('[sl-cart] sheet proof render unavailable — the order will carry no preview'); } catch (x) {}
    }
    setSheetArt(url);
    setSheetOpen(true);
  };
  // Sticker Sheets — canonical payload from the SHARED orchestrator (SLBuilderCore) using the customer's
  // sheet size + number of sheets. Same builder/engine/pricing/adapter as die-cut; no separate system (D-050).
  const buildSheetPayload = (cutOverride) => {
    try {
      if (!window.slBuilder) return null;
      const A0 = resolveOrderArtwork();          /* same reconciliation as die-cut */
      return window.slBuilder.buildPayload({
        productType: 'sheet',
        /* a paid order reopened in HQ is rebuilt at the sheet count it was bought at */
        sizeRule: ORDER_RULES_APPLY ? undefined : 'none',
        bytes: A0.bytes,
        fileName: A0.name,
        fileSize: A0.size,
        width: widthIn,
        height: heightIn,
        sheetSize: sheetSizeId,
        sheetQuantity: numSheets,
        cutWidth: (cutOverride && cutOverride.wIn) || sheetCutW,
        cutHeight: (cutOverride && cutOverride.hIn) || sheetCutH,
        material,
        finish: laminate,
        border
      }, {
        /* THE ARTWORK REFERENCE IS THE ARTWORK — never the proof.
           `extra.artworkRef` lands in `payload.artwork.reference`, which the
           core documents as the artwork handle. This used to read
           `sheetArt || shownArt`, so whenever the flatten SUCCEEDED the
           canonical config recorded the rendered proof in the slot reserved
           for the customer's file: the #1014 substitution running in reverse,
           and the one direction nothing else checks for. The die-cut path
           always passed `shownArt` here; the two now agree. */
        artworkRef: durableArtworkRef()
      });
    } catch (e) {
      return null;
    }
  };
  // Add the Sticker Sheet to the REAL cart via the SAME transport as die-cut (D-050 — reverses the
  // D-024/D-038 checkout deferral). Sheets are priced (by total stickers) and orderable. Preview is the
  // flattened sheet-face the builder already rendered (sheetArt), falling back to the sticker face.
  /* Sheets carry the SAME kind of proof: `sheetArt` is the flattened face
     `rasterizeFace` produced when the sheet workspace opened. The old
     `|| displayArt || shownArt` fallback is gone for the same reason it went
     from the die-cut path — falling back to the customer's upload files a
     non-proof under the proof's name, which is precisely the defect. */
  /* Sheets already hold their flattened proof in `sheetArt`, so the proof step
     is a no-op rather than a second render — but the saving state, the
     duplicate-tap lock and the timing report are identical. */
  /* Add to cart lays the sheet out with the REAL kiss cut (D-199). If it is not measured yet it is
     measured now; when it changes what the customer was shown (stickers per sheet, so the total and
     the price), nothing is ordered: the screen updates to the real numbers and they add again. */
  const orderSheets = () => {
    const key = sheetCutKey;
    const shownW = sheetCutW, shownH = sheetCutH;
    const fresh = (sheetCutTrue && sheetCutTrue.key === key) ? Promise.resolve(sheetCutTrue) : measureSheetCut(key);
    return fresh.then(r => {
      if (r && (Math.abs(r.wIn - shownW) > 1e-4 || Math.abs(r.hIn - shownH) > 1e-4)) {
        const S = window.SLSheets;
        const lay = (w, h) => S ? S.layout(widthIn, heightIn, 0, { sheet: sheetSizeId, sheetQuantity: numSheets, cutWidth: w, cutHeight: h }) : null;
        const a = lay(shownW, shownH), b = lay(r.wIn, r.hIn);
        setSheetCutTrue(r);
        if (!a || !b || a.totalStickers !== b.totalStickers || a.status !== b.status) {
          try { console.info('[sl-sheet] the measured kiss cut changed the sheet; showing the real count before ordering', a && a.totalStickers, '->', b && b.totalStickers); } catch (x) {}
          return null;
        }
      }
      return runAddToCart(() => buildSheetPayload(r), {
        navigate: true,
        proof: function () { return Promise.resolve(sheetArt || null); }
      });
    });
  };
  const startOver = () => {
    setOrdered(false);
    setDecision(false);
    setSheetOpen(false);
    setSheetArt(null);
    setSheetIntent(false);
    if (PRODUCTION_HOST) {
      /* The job's artwork stays. Its TRANSFORM goes back to default, which is
         what the canvas Fit and Center tools do one at a time. */
      fitArt();
      setFlipX(false);
    } else {
      setArt(null);
      setArtRes(null);
    }
    setShape(CG ? 'square' : 'custom');
    setCorners(CG ? 'sharp' : 'round');
    setSize(3);
    setSizeMode(CG ? 'custom' : 'preset');
    setQty(CG ? CG.QUANTITY.defaultQty : 100);
    setQtyMode('preset');
    setMaterial('vinyl');
    setLaminate('matte');
    setBorder('none');
    setBsize('standard');
    setSurface('light');
    setOpenOpt(null);
    if (CG) {
      setCustomW(String(CG.SIZE.defaultWidthIn));
      setCustomH(String(CG.SIZE.defaultHeightIn));
      setLockAR(false);
      setCgPlace(null);
      setCgShapeNote('');
      setCgQtyText(null);
    }
    /* `workflow` is what gets a production host past the storefront's choice
       screen, and `resetArtState` discards the measured artwork state the
       driver depends on. Both are about the ARTWORK, which a production reset
       is deliberately not touching. */
    if (!PRODUCTION_HOST) {
      setWorkflow(null);
      resetArtState();
    }
    setSaved(false);
  };
  // Intelligent onboarding: configure the builder from the chosen sticker workflow.
  /* THE STICKER SHEET CARD IS A DIE CUT THAT ENDS IN THE SHEET WORKSPACE.
     It is not a fourth workflow. It puts the builder in the state a customer
     from the Home page sheet card already reaches by picking Die-cut here:
     the die-cut setup plus `sheetIntent`, so the button reads "Build the
     sheet" and opens the existing SheetBuilder with the same artwork, size,
     cut line, price and cart path. Nothing is uploaded again and no second
     sheet flow exists.

     `sheetPickRef` records that THIS screen raised the intent, so going back
     and picking another card lowers it again. An intent the customer arrived
     with (Home / My Lab hint) is never lowered here. */
  /* the Home page's category, applied the moment the artwork lands (see hintWf). A LAYOUT
     effect: it runs before the browser paints, so the chooser - which still renders on the one
     fact choosingWorkflow - is never seen. */
  React.useLayoutEffect(() => {
    if (!art || workflow || !hintWf) return;
    const wf = hintWf;
    setHintWf(null);
    chooseWorkflow(wf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [art, workflow, hintWf]);
  const chooseWorkflow = wf => {
    /* The fourth card is another PRODUCT, not a way of making a sticker. The
       original file is handed to the Cart Graphics builder (bytes, name and
       type unchanged, so resolution and transparency are whatever was
       uploaded) and the customer is not asked to upload again. Nothing below
       runs for it, and nothing below changes for the other three. */
    /* The pack is one sticker design: no sheet, and no other product. */
    if (PROMO && (wf === 'sheet' || wf === 'cartgraphics')) wf = 'photo';
    if (wf === 'cartgraphics') { cgSwitchProduct(SL_ROUTE_CART_GRAPHICS); return; }
    if (wf === 'sheet') {
      wf = 'photo';
      if (!sheetIntent) sheetPickRef.current = true;
      setSheetIntent(true);
    } else if (sheetPickRef.current) {
      sheetPickRef.current = false;
      setSheetIntent(false);
    }
    setWorkflow(wf);
    setOpenOpt(null);
    fitArt();
    // If the guide was running when they uploaded, resume it at "Set it up" (builder) or finish (chooser tour).
    setTourI(t => t < 0 ? t : tourMode === 'builder' ? 1 : -1);
    if (wf === 'photo') {
      // die-cut: contour cut with a standard white border.
      setShape('custom');
      setBorder('white');
      setBsize('standard');
      setFaceColor('white');
    } else if (wf === 'ready') {
      // print-ready: use exactly as uploaded, keep transparency
      setShape('custom');
      setBorder('none');
    } else if (wf === 'shaped') {
      // shaped: clip artwork inside a chosen shape
      setShape('circle');
      setCorners('round');
      setBorder('none');
      setOpenOpt('shape');
    }
  };
  /* ── A PRODUCTION HOST OPENS STRAIGHT INTO THE BUILDER ─────────────────
     On the storefront the chooser teaches a first-time customer what kind of
     sticker they are making, and configures the builder from the answer. In
     production the operator has already decided — the job exists — and the
     screen is a click between selecting a file and seeing it.

     So the workflow is marked chosen the moment artwork arrives, and NOTHING
     ELSE IS PRE-SET. The builder keeps its own defaults (die cut, the shape
     control's initial value); MOTO then drives shape, size, border and
     background through the same controls a human uses. Choosing silently here
     would be exactly the second interaction model this work exists to remove,
     and it would make the recorded workflow a guess: 'production' is honest
     about the fact that nobody picked a storefront workflow.

     `fitArt` matches what the chooser did, so a second upload into a live
     bench starts from the same transform a first one does. */
  useEffect(() => {
    if (!PRODUCTION_HOST || !art || workflow) return;
    fitArt();
    setWorkflow('production');
  }, [art, workflow]);

  /* ── A HANDOFF ENTERS ITS PATH ONCE THE ARTWORK HAS SETTLED ───────────
     The twin above, for the other arrival that already knows its answer.

     WHY IT WAITS. `readFile` finishes asynchronously and its image handler
     calls `setArt(...)` and then `resetArtState()`, which clears `workflow`.
     Anything chosen before that point is erased by the artwork it was chosen
     for. Waiting for `art` means running AFTER the reset rather than racing
     it, which is why the production host waits too.

     WHY A LAYOUT EFFECT. `art` and the cleared `workflow` commit together,
     and that combination IS the gateway's open condition. A passive effect
     runs after the browser may have painted, so the customer would see "How
     should we make it?" flash over a design that had already answered it --
     briefly, and long enough to click. A layout effect runs before paint, so
     the screen is simply never wrong.

     ONCE. The ref is cleared as it is spent, so a later Start Over genuinely
     returns the customer to the question instead of being overruled by a
     handoff they finished with. */
  /* CART GRAPHICS OPENS STRAIGHT INTO THE BUILDER, as the production host
     does and for the same reason: the product is already decided. The shape
     the customer has — a rectangle by default — is left exactly as it is. A
     layout effect, so the sticker chooser never paints for a frame. */
  React.useLayoutEffect(() => {
    if (!CG || !art || workflow) return;
    setWorkflow(shape === 'custom' ? 'photo' : 'shaped');
  }, [art, workflow]);
  /* …and it opens as a RECTANGLE. The shape state's initial value is the
     sticker builder's and stays that literal; this sets the product's own
     default before the first paint, and the saved-design restore (a passive
     effect, so it runs after this) still wins over it. */
  React.useLayoutEffect(() => {
    if (CG) setShape('square');
  }, []);
  React.useLayoutEffect(() => {
    if (!art || workflow || !handoffPathRef.current) return;
    const path = handoffPathRef.current;
    handoffPathRef.current = null;
    /* The same door a click uses. A throw here must not take the builder
       down: the worst honest outcome is the question being asked again. */
    try { chooseWorkflow(path); }
    catch (e) { try { console.warn('[sl-handoff] path transition failed'); } catch (x) {} }
  }, [art, workflow]);
  /* A reopened design goes back to the path it was saved on. Only the answer
     is restored: every setting was already applied from the record. Once. */
  React.useLayoutEffect(() => {
    if (!art || workflow || !resumePathRef.current) return;
    const path = resumePathRef.current;
    resumePathRef.current = null;
    setWorkflow(path);
  }, [art, workflow]);

  /* FOLDERS ARE NOT A FEATURE. The selector that used to sit in the save
     sheet listed folders nothing could file anything into: the account app
     stored them in localStorage, the save service has no folder field, and a
     design "filed" into one appeared nowhere different afterwards. It is gone
     from the sheet, and the list that fed it is gone with it.

     `saveFolder` stays declared and null so the legacy local-only record the
     preview harness writes keeps the shape it has always had. Nothing reads
     it. */
  const navTo = url => {
    try {
      (window.top || window).location.href = url;
    } catch (e) {
      window.location.href = url;
    }
  };
  // Leaving the builder with an uploaded design → ask to save first
  const guardedNav = url => {
    setBrandMenu(false);
    if (art) {
      setPendingNav(url);
    } else {
      navTo(url);
    }
  };
  const openSave = () => {
    if (!art) return;
    /* ── THE NAME THE CUSTOMER ALREADY CHOSE WINS ──────────────────────────
       A design reopened from My Designs carries the name it was saved under.
       Recomputing from the filename here would silently rename it back on
       the next save, which is the kind of change nobody notices until their
       library is full of `IMG_4821` again. */
    var suggested = savedNameRef.current;
    if (!suggested) {
      var SD = window.SLSaveDesign;
      /* A tidy-up of the filename, not a namer. Falls back to the old
         extension strip if the module is not on the page. */
      suggested = (SD && SD.cleanName)
        ? SD.cleanName(art && art.name)
        : ((art && art.name) ? art.name.replace(/\.[a-z0-9]+$/i, '') : SL_UNTITLED);
    }
    setSaveName(suggested);
    /* A previous attempt's message must never outlive the attempt. */
    setSaveError(null);
    setSaveBusy(false);
    setSaveOpen(true);
  };
  /* The customer-editable part of a design, in the shape the save service
     already allow-lists. Nothing production-side is in here on purpose:
     the cut path, the production SVG and the readiness flags belong to the
     order, not to a thing the customer reopens and changes. */
  /* A SHEET SAYS SO, AND SAYS WHICH SHEET. The save service keeps a fixed
     list of fields and drops the rest, and it has no field for the sheet
     size or the number of sheets. `productType` is on the list, so the two
     travel inside it ('sheet:8.5x10:10') and assets/sl-mysl-designs.js takes
     them apart again. `sheetSize` and `sheetQuantity` are sent beside it
     under their own names: dropped today, and read in preference the day the
     service lists them. */
  const savedConfig = () => ({
    productType: sheetIntent ? 'sheet:' + sheetSizeId + ':' + numSheets : 'diecut',
    sheetSize: sheetIntent ? sheetSizeId : null,
    sheetQuantity: sheetIntent ? numSheets : null,
    shape,
    size,
    widthIn: Number(widthIn),
    heightIn: Number(heightIn),
    material,
    finish: laminate,
    border,
    borderColor,
    quantity: effQty
  });
  /* A cart graphic is saved as the same record, under its own product type,
     with what it needs to reopen. The sticker record above is not touched. */
  const cgSavedConfig = () => Object.assign(savedConfig(),
    { productType: CG.PRODUCT_TYPE, sheetSize: null, sheetQuantity: null, corners: corners, bsize: bsize }, cgSavedState());

  /** The single place that closes the save modal and runs the pending nav. */
  const finishSave = () => {
    setSaveBusy(false);
    setSaveOpen(false);
    setSaved(true);
    if (pendingNav) {
      const dest = pendingNav;
      setPendingNav(null);
      setTimeout(() => navTo(dest), 450);
      return;
    }
    setTimeout(() => setSaved(false), 2400);
  };

  /* ── SAVE, FOR REAL ──────────────────────────────────────────────────────
     The design goes to the customer's account, server-side, under a Design ID
     that survives editing. What used to happen here — a record pushed into
     `stickr_saved_designs` — was a gallery that died with the browser profile
     and held a `blob:` preview that died with the tab. It is kept ONLY for a
     theme with no design service configured, where it is the local preview
     harness and not a customer. */
  const saveToAccount = async (opts) => {
    const SD = window.SLSaveDesign;

    if (!saveIdRef.current) {
      saveIdRef.current = (window.slBuilder && window.slBuilder.newDesignId)
        ? window.slBuilder.newDesignId() : null;
    }
    if (!saveIdRef.current) { setSaveError('unavailable'); return; }

    /* NOT SIGNED IN IS A PROMPT, NOT A FAILURE, and it is checked again by the
       server — this only decides which screen to show first.

       AND IT IS SKIPPED AFTER A SIGN-IN. The marker it reads was rendered by
       Liquid when this page loaded; the customer just signed in somewhere
       ELSE, and this tab has not reloaded, so the marker is stale and still
       says "guest". Trusting it here would bounce them back to the sign-in
       prompt they have already completed, for ever. The server is the
       authority on this and always was — so on the retry we simply ask it,
       and a 401 puts the prompt back. */
    if (!(opts && opts.trustServer) && !SD.signedIn()) { setSaveError('signin'); return; }

    setSaveBusy(true);
    setSaveError(null);

    /* The SAME flattened preview the cart and HQ already show, built by the
       same function. A preview that cannot be built costs a thumbnail. */
    let previewDataUrl = null;
    try { previewDataUrl = await rasterizeFace({ frame: 'content' }); } catch (e) {}

    const r = await SD.save({
      designId: saveIdRef.current,
      name: saveName.trim() || SL_UNTITLED,
      fileName: art ? art.name : null,
      source: artEntryRef.current || null,
      config: CG ? cgSavedConfig() : savedConfig(),
      artworkBytes: artBytesRef.current,
      previewDataUrl
    });

    if (r.ok) { finishSave(); return; }
    setSaveBusy(false);
    if (r.reason === 'signed_out') { setSaveError('signin'); return; }
    setSaveError(r.reason === 'no_artwork' ? 'no_artwork' : 'unavailable');
  };

  /** Sign in beside the builder, then finish the save without losing anything. */
  const saveSignIn = () => {
    const SD = window.SLSaveDesign;
    if (!SD.openSignIn()) { setSaveError('popup'); return; }
    setSaveError('waiting');
    SD.onReturn(() => {
      /* Back in this tab. The artwork and every option are still here,
         untouched — nothing was ever stashed, so nothing has to be restored. */
      setSaveError(null);
      saveToAccount({ trustServer: true });
    });
  };

  const confirmSave = () => {
    if (saveBusy) return;
    const SD = window.SLSaveDesign;
    if (SD && SD.available()) return saveToAccount();

    const typeLabel = shape === 'custom' ? 'Die Cut' : (SHAPES.find(s => s.id === shape) || {}).label;
    const rec = {
      id: 'sv' + Date.now(),
      name: saveName.trim() || SL_UNTITLED,
      folder: saveFolder,
      type: typeLabel,
      material: matLabel,
      laminate: lamLabel,
      size: Number(widthIn).toFixed(1) + ' × ' + Number(heightIn).toFixed(1) + ' in',
      shape,
      sizePreset: size,
      qty: effQty,
      border,
      preview: displayArt || shownArt || null,
      at: Date.now(),
      // full state so Edit Design restores exactly where they left off
      state: {
        ...(CG ? cgSavedState() : {}),
        artUrl: art ? art.url : null,
        artName: art ? art.name : 'artwork.png',
        workflow: sheetIntent ? 'sheet' : workflow,
        sheetSize: sheetIntent ? sheetSizeId : null,
        numSheets: sheetIntent ? numSheets : null,
        shape,
        corners,
        size,
        sizeMode,
        customW,
        customH,
        material,
        laminate,
        border,
        borderColor,
        bsize,
        qty,
        qtyMode,
        customQty,
        faceColor,
        faceCustom
      }
    };
    try {
      const all = JSON.parse(localStorage.getItem('stickr_saved_designs') || '[]');
      all.push(rec);
      localStorage.setItem('stickr_saved_designs', JSON.stringify(all));
      localStorage.setItem('stickr_saved_design', JSON.stringify(rec));
    } catch (e) {}
    finishSave();
  };
  const toggle = id => setOpenOpt(o => o === id ? null : id);
  // Apple-style: let the selection register (pop) for a beat, then snap the section closed.
  const closeSoon = () => setTimeout(() => setOpenOpt(null), 170);
  // Click anywhere outside the options list collapses the open section.
  useEffect(() => {
    if (openOpt === null) return;
    const onDown = e => {
      if (e.target.closest && !e.target.closest('.pb-optlist')) setOpenOpt(null);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [openOpt]);
  const shapeLabel = CG ? CG.shapeLabel(shape, widthIn, heightIn) : (SHAPES.find(s => s.id === shape) || {}).label;
  const matLabel = (MATERIALS.find(m => m.id === material) || {}).label;
  const lamLabel = (LAMINATES.find(l => l.id === laminate) || {}).label;
  const borderLabel = border === 'none' ? 'None' : border.charAt(0).toUpperCase() + border.slice(1) + ' · ' + bsz.label +
    (CG ? ' · ' + String(+(+thick).toFixed(3)) + ' in' : '');
  /* ── THE PROMO PACK'S CONTROLS ─────────────────────────────────────────
     Two sizes measured on the longest side, the quantity stated rather than
     chosen, and the price as the landing page promised it: the regular price
     for these dimensions struck through in red, the pack price in the normal
     price style, and the saving in green beneath. */
  const promoDims = s => arForSize >= 1 ? [s, +(s / arForSize).toFixed(1)] : [+(s * arForSize).toFixed(1), s];
  const promoDimText = s => { const d = promoDims(s); return Number(d[0]).toFixed(2) + '″ × ' + Number(d[1]).toFixed(2) + '″'; };
  const promoWas = () => promoRegular > PROMO.price50 && /*#__PURE__*/React.createElement("s", {
    className: "pb-promo-was", "aria-label": "Regular price $" + promoRegular.toFixed(2), style: { color: '#8A94A6', fontWeight: 600, fontSize: 'max(12px, 0.55em)', marginRight: '8px', textDecorationThickness: '1.5px', whiteSpace: 'nowrap' }
  }, "$" + promoRegular.toFixed(2));
  const promoSaveEl = () => (promoRule && promoRule.cheaper && !promoWindowBlock)
    /* Friendly, not an error: the customer is about to get a better price. */
    ? /*#__PURE__*/React.createElement("span", { className: "pb-promo-cheaper", role: "status", style: { fontSize: 'max(12px, 1em)', color: 'var(--ink)', fontWeight: 600 } },
        "Good news: at this size, 50 stickers are just $" + promoRegular.toFixed(2) + " at our regular price, less than this pack. ",
        /*#__PURE__*/React.createElement("a", { href: SL_ROUTE_STICKERS, style: { color: '#2E86E3', fontWeight: 800, textDecoration: 'underline' },
          /* Carries the customer's artwork to the regular builder through the
             same IndexedDB handoff as switching products, so they do not upload
             again; if the handoff is unavailable the unsaved-design guard takes
             over, so the artwork is never dropped silently. Size, finish and
             quantity start fresh there: that builder is not changed for the promo. */
          onClick: e => {
            e.preventDefault();
            promoTrack('promo_regular_click', { widthIn: widthIn, heightIn: heightIn, regular: promoRegular, pack: PROMO.price50 });
            cgSwitchProduct(SL_ROUTE_STICKERS);
          } }, "Order at the regular price"))
    : promoBlock
    ? /*#__PURE__*/React.createElement("span", { className: "pb-promo-block", role: "status", style: { fontSize: 'max(12px, 1em)', color: '#E5484D', fontWeight: 700 } }, promoBlock)
    : promoSave > 0 && /*#__PURE__*/React.createElement("span", { className: "pb-promo-save", style: { display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: 'max(12px, 0.85em)', lineHeight: 1.3, color: '#1F9D55', fontWeight: 700, whiteSpace: 'nowrap' } }, /*#__PURE__*/React.createElement("svg", { viewBox: "0 0 16 16", width: 12, height: 12, "aria-hidden": "true", style: { flex: '0 0 12px' } }, /*#__PURE__*/React.createElement("path", { d: "M8 1.5c.4 2.9 1.6 4.1 4.5 4.5-2.9.4-4.1 1.6-4.5 4.5-.4-2.9-1.6-4.1-4.5-4.5 2.9-.4 4.1-1.6 4.5-4.5Zm4.6 7.6c.2 1.3.7 1.8 2 2-1.3.2-1.8.7-2 2-.2-1.3-.7-1.8-2-2 1.3-.2 1.8-.7 2-2Z", fill: "currentColor" })), "You save $" + promoSave.toFixed(2));
  const promoPriceEl = () => /*#__PURE__*/React.createElement(React.Fragment, null, promoWas(), /*#__PURE__*/React.createElement("b", { className: "pb-promo-now", style: { fontWeight: 900, fontSize: '1.15em', whiteSpace: 'nowrap' } }, "$" + total.toFixed(2)));
  /* 2.0–3.0 in on the longest side, 0.1 in steps: − / slider / +, one control
     for desktop and phone. */
  const promoSetSize = v => { setSizeMode('preset'); setSize(promoSnapSize(v)); };
  const promoCur = promoSnapSize(size);
  const promoSizeControl = () => /*#__PURE__*/React.createElement("div", { className: "pb-promo-size", style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
    /*#__PURE__*/React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '10px' } },
      /*#__PURE__*/React.createElement("button", { type: "button", className: "pbm-step", "aria-label": "Smaller", disabled: promoCur <= PROMO_SIZE_MIN, onClick: () => promoSetSize(promoCur - PROMO_SIZE_STEP), style: { minWidth: '40px', minHeight: '40px' } }, "−"),
      /*#__PURE__*/React.createElement("input", { type: "range", min: String(PROMO_SIZE_MIN), max: String(PROMO_SIZE_MAX), step: String(PROMO_SIZE_STEP), value: promoCur,
        "aria-label": "Size on the longest side, in inches", "aria-valuetext": promoCur.toFixed(1) + " inches",
        onChange: e => promoSetSize(parseFloat(e.target.value)), style: { flex: 1, accentColor: '#2E86E3' } }),
      /*#__PURE__*/React.createElement("button", { type: "button", className: "pbm-step", "aria-label": "Larger", disabled: promoCur >= PROMO_SIZE_MAX, onClick: () => promoSetSize(promoCur + PROMO_SIZE_STEP), style: { minWidth: '40px', minHeight: '40px' } }, "+")),
    /*#__PURE__*/React.createElement("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' } },
      /*#__PURE__*/React.createElement("b", { style: { fontSize: '19px', fontWeight: 900 } }, promoCur.toFixed(1) + "″ longest side"),
      /*#__PURE__*/React.createElement("span", { style: { fontSize: '13px', color: 'var(--mut)' } }, promoDimText(promoCur))));
  const promoSizeOpt = () => /*#__PURE__*/React.createElement(Opt, {
    id: "size", open: openOpt === 'size', onToggle: toggle,
    icon: "M14.5 3.5l6 6L9 21l-6-6L14.5 3.5zM8 9l2 2M11 6l2 2M5 12l2 2",
    k: "Size", v: sizeLabel
  }, promoSizeControl(),
  /*#__PURE__*/React.createElement("p", { className: "pb-bgnote" }, "Measured on the longest side. The other side follows your artwork, so nothing is stretched. The " + promoMoney(PROMO.price50) + " pack applies to eligible sizes; if yours costs less at our regular price, we show you that price instead."));
  const promoQtyOpt = () => /*#__PURE__*/React.createElement(Opt, {
    id: "qty", open: openOpt === 'qty', onToggle: toggle,
    icon: "M4 6h16M4 12h16M4 18h10", k: "Quantity", v: PROMO.packQty + ' Stickers'
  }, /*#__PURE__*/React.createElement("p", { className: "pb-bgnote" }, PROMO.packQty + " stickers of one design, for " + promoMoney(PROMO.price50) + " on eligible sizes from 2″ to 3″, with free shipping. Another design? Add another pack."
    /* The 100 upgrade is only offered where it beats our regular price for 100
       at this size (Ryan, option (a)); the note promises it only then. */
    + (slPrice(widthIn, heightIn, 100) >= (+PROMO.price100 || Infinity) ? " Want 100? You can double this pack in the cart." : "")));
  const promoQtyFixed = () => /*#__PURE__*/React.createElement("b", { style: { marginLeft: '6px' } }, PROMO.packQty);
  const sizeLabel = CG ? CG.fmtIn(widthIn) + ' × ' + CG.fmtIn(heightIn) + ' in' : sizeMode === 'custom' ? widthIn + '″ × ' + heightIn + '″' : Number(widthIn).toFixed(2) + '″ × ' + Number(heightIn).toFixed(2) + '″';
  // === CART GRAPHICS (D-177) — product-mode presentation ================================
  // Everything below is reached only when `CG` is set, which is only on the Cart Graphics
  // route. It adds no state the sticker builder reads and changes none of its geometry: the
  // graphic is drawn by the same `renderSticker`, priced by SLCartGraphics, and submitted by
  // the same `runAddToCart`.
  const cgEl = React.createElement;
  const cgNoun = n => (n === 1 ? 'graphic' : 'graphics');
  const cgMatFin = CG ? CG.materialLine(laminate) : '';
  const cgFinishShown = !!(CG && CG.LAUNCH.finishConfirmed);
  const cgShapeKind = shape === 'custom' ? 'custom'
    : shape === 'circle' ? (lockAR ? 'circle' : 'oval')
    : (lockAR ? 'square' : 'rectangle');
  /* The ratio a locked shape holds: 1:1 for a circle or a square, the finished
     sticker's own ratio for a die cut, and none for a rectangle or an oval. */
  const cgLockedRatio = () => !lockAR ? 0 : (contour ? (dieLockAR > 0 ? dieLockAR : 0) : 1);
  /* ONE DOOR FOR EVERY WAY A DIMENSION CHANGES — typing, pasting and the
     steppers all land here, so all three are validated by the same rule and
     none of them is clamped. The raw text is kept exactly as entered. */
  const cgSetDim = (axis, raw) => {
    if (!CG) return;
    setSizeMode('custom');
    setCgShapeNote('');
    const r = cgLockedRatio();
    const v = CG.validateDimension(axis, raw);
    const other = axis === 'width' ? 'height' : 'width';
    (axis === 'width' ? setCgWText : setCgHText)(raw);
    if (!v.ok) return;                                   // shown as an error; nothing is changed
    (axis === 'width' ? setCustomW : setCustomH)(String(v.value));
    if (!r) return;
    /* A locked shape moves its partner. The partner is SHOWN whatever it comes
       to, and only taken as the size if it is itself valid — so a square that
       would need a 34 in height says so instead of quietly becoming 20. */
    const o = +(axis === 'width' ? v.value / r : v.value * r).toFixed(2);
    (other === 'width' ? setCgWText : setCgHText)(CG.fmtIn(o));
    if (CG.validateDimension(other, o).ok) (other === 'width' ? setCustomW : setCustomH)(String(o));
  };
  const cgStepTarget = (axis, dir) => {
    const cur = axis === 'width' ? widthIn : heightIn, s = CG.SIZE.stepIn;
    const n = dir > 0 ? (Math.floor(cur / s + 1e-9) + 1) * s : (Math.ceil(cur / s - 1e-9) - 1) * s;
    return +n.toFixed(4);
  };
  const cgCanStep = (axis, dir) => !!CG && CG.validateDimension(axis, cgStepTarget(axis, dir)).ok;
  const cgStep = (axis, dir) => { if (cgCanStep(axis, dir)) cgSetDim(axis, CG.fmtIn(cgStepTarget(axis, dir))); };
  /* SHAPES KEEP THEIR OWN RULES. A circle and a square have equal sides, so
     choosing one sets both dimensions to the shorter of the two — never
     stretched into an oval — and says so. A rectangle or an oval is the same
     shape with the sides left free, exactly as unlocking does for a sticker. */
  const cgPickShape = kind => {
    if (!CG) return;
    setCgShapeNote('');
    if (kind === 'rectangle') { setShape('square'); setLockAR(false); return; }
    if (kind === 'oval') { setShape('circle'); setLockAR(false); return; }
    if (kind === 'square' || kind === 'circle') {
      const m = Math.min(widthIn, heightIn);
      setShape(kind);
      lockRatioRef.current = 1;
      setLockAR(true);
      setSizeMode('custom');
      if (Math.abs(widthIn - heightIn) > 1e-6) {
        setCustomW(CG.fmtIn(m));
        setCustomH(CG.fmtIn(m));
        setCgShapeNote((kind === 'circle' ? 'A circle' : 'A square') + ' has equal sides, so the size is now ' +
          CG.fmtIn(m) + ' × ' + CG.fmtIn(m) + ' in. Choose ' + (kind === 'circle' ? 'Oval' : 'Rectangle') + ' to set width and height separately.');
        setOpenOpt('size');
      }
      return;
    }
    /* Die cut: the cut follows the artwork, so the proportions are the
       artwork's. The long side the customer chose is kept and the other
       follows; if that lands outside the range the field says how to fix it. */
    setShape('custom');
    lockRatioRef.current = dieLockAR > 0 ? dieLockAR : 1;
    setLockAR(true);
    setSizeMode('custom');
    if (dieLockAR >= 1) setCustomH(CG.fmtIn(+(widthIn / dieLockAR).toFixed(2)));
    else setCustomW(CG.fmtIn(+(heightIn * dieLockAR).toFixed(2)));
    setCgShapeNote('A die-cut graphic keeps your artwork’s proportions, so width and height change together.');
  };
  const cgSetQty = n => {
    setQtyMode('preset');
    setQty(n);
    setCgQtyText(CG.QUANTITY.presets.indexOf(n) >= 0 ? null : String(n));
  };
  const cgTypeQty = raw => {
    setCgQtyText(raw);
    const v = CG.validateQuantity(raw);
    if (v.ok) { setQtyMode('preset'); setQty(v.value); }
  };
  /* WHY THE ORDER CANNOT BE PLACED, IF IT CANNOT. One sentence, chosen once,
     shown beside the size, in the summary and on the phone's review sheet. */
  const cgBlockMsg = !CG ? ''
    : !cgSize.width.ok ? cgSize.width.message
    : !cgSize.height.ok ? cgSize.height.message
    : !cgQty.ok ? cgQty.message
    : !cgAvail.orderable ? cgAvail.message
    : !CG_CHECKOUT_OPEN ? 'Cart Graphics ordering isn’t open yet. You can design, preview and save your graphic now.'
    : '';
  /* Every valid size has a price: the area price, or the approved minimum
     unit price where the area price would fall below it. */
  const cgQuotable = !!CG && cgInputOk;
  const cgMinNote = (cgQuotable && cgPrice && cgPrice.atMinimum)
    ? ' Our minimum price of $' + (CG.PRICE.minimumUnitCents / 100).toFixed(2) + ' per graphic applies at this size.' : '';
  const cgPriceText = (cgQuotable && cgTotal > 0) ? '$' + cgTotal.toFixed(2) : '—';
  const cgUnitText = (cgQuotable && cgPrice && !cgPrice.error) ? '$' + cgPrice.perUnit.toFixed(2) : '—';
  /* The border's PHYSICAL width, said out loud: a customer choosing between
     Thin and Thick is owed the number each one actually prints at. */
  const cgBorderIn = lvl => (_szCore() && _szCore().borderInchesForLevel) ? _szCore().borderInchesForLevel(lvl, longestIn) : 0;
  const cgBorderNote = !CG ? null : cgEl("p", { className: "cg-hint", style: { marginTop: '10px' } },
    BSIZES.map(b => b.label + ' ' + String(+(+cgBorderIn(b.id)).toFixed(3)) + ' in').join(' · '), '.');

  /* ---- what the order records about this product ---- */
  const cgPreviewRec = () => CG.previewRecord(cgPanel.w, cgPanel.h, widthIn, heightIn, cgPlace);
  const cgOrderRecord = () => {
    const pv = cgPreviewRec();
    const bw = border === 'none' ? null : +(+thick).toFixed(4);
    return {
      vinylFamily: CG.VINYL_FAMILY,
      vinylFinish: CG.resolveFinish(laminate),
      finishConfirmed: !!CG.LAUNCH.finishConfirmed,
      laminate: CG.LAMINATE,
      includes: CG.INCLUDES,
      shape: shape,
      shapeLabel: CG.shapeLabel(shape, widthIn, heightIn),
      corners: shape === 'square' ? corners : null,
      borderType: border,
      borderColor: border === 'none' ? null : (borderCss || null),
      borderLevel: border === 'none' ? null : bsize,
      borderWidthIn: bw,
      borderSummary: border === 'none' ? 'no border' : (bsize + ' ' + border + ' border ' + bw + ' in'),
      backgroundRemoved: !!(bgOn && bgUrl),
      preview: pv,
      previewSummary: CG.previewSummary(pv)
    };
  };
  const cgSavedState = () => ({
    productType: CG.PRODUCT_TYPE,
    lockAR: lockAR,
    cartPanelW: cgPanel.w, cartPanelH: cgPanel.h,
    cartPlace: cgPlace ? { x: cgPlace.x, y: cgPlace.y } : null,
    /* the widths this design is drawn with, so reopening it never changes them */
    borderWidthsIn: ['thin', 'standard', 'thick'].reduce((o, k) => { o[k] = +cgBorderIn(k); return o; }, {})
  });

  /* ---- the size fields (desktop Opt and the phone's Size panel share these) ---- */
  const cgDimField = (axis, mobile) => {
    const isW = axis === 'width';
    const v = isW ? cgSize.width : cgSize.height;
    const id = 'cg-' + axis + (mobile ? '-m' : '');
    const label = isW ? 'Width' : 'Height';
    return cgEl("div", { className: "cg-field" + (v.ok ? "" : " bad") },
      cgEl("label", { htmlFor: id }, label, cgEl("span", { className: "cg-range" },
        (isW ? CG.SIZE.minWidthIn + '–' + CG.SIZE.maxWidthIn : CG.SIZE.minHeightIn + '–' + CG.SIZE.maxHeightIn) + ' in')),
      cgEl("div", { className: "cg-in" },
        cgEl("button", { type: "button", className: "cg-step", "aria-label": "Decrease " + label.toLowerCase() + " by " + CG.SIZE.stepIn + " inch",
          disabled: !cgCanStep(axis, -1), onClick: () => cgStep(axis, -1) }, "−"),
        cgEl("input", {
          id: id, type: "text", inputMode: "decimal", autoComplete: "off", spellCheck: false,
          value: isW ? cgWText : cgHText,
          "aria-invalid": v.ok ? "false" : "true",
          "aria-describedby": v.ok ? undefined : id + '-err',
          onChange: e => cgSetDim(axis, e.target.value),
          onKeyDown: e => {
            if (e.key === 'ArrowUp') { e.preventDefault(); cgStep(axis, 1); }
            else if (e.key === 'ArrowDown') { e.preventDefault(); cgStep(axis, -1); }
          }
        }),
        cgEl("span", { className: "cg-unit", "aria-hidden": "true" }, "in"),
        cgEl("button", { type: "button", className: "cg-step", "aria-label": "Increase " + label.toLowerCase() + " by " + CG.SIZE.stepIn + " inch",
          disabled: !cgCanStep(axis, 1), onClick: () => cgStep(axis, 1) }, "+")),
      !v.ok && cgEl("p", { className: "cg-err", id: id + '-err', role: "alert" }, v.message));
  };
  const cgSizeBody = mobile => cgEl(React.Fragment, null,
    cgEl("div", { className: "cg-dims" }, cgDimField('width', mobile), cgDimField('height', mobile)),
    cgShapeNote && cgEl("p", { className: "cg-note", role: "status" }, cgShapeNote),
    (cgSize.ok && cgAvail && !cgAvail.orderable) && cgEl("p", { className: "cg-note warn", role: "status" }, cgAvail.message),
    cgEl("p", { className: "cg-hint" }, "This is the finished size of the printed graphic. Decimals and fractions both work, such as 34.5 or 34 1/2."));

  const cgOpts = !CG ? {} : {
    size: cgEl(Opt, {
      id: 'size', open: openOpt === 'size', onToggle: toggle,
      icon: "M14.5 3.5l6 6L9 21l-6-6L14.5 3.5zM8 9l2 2M11 6l2 2M5 12l2 2",
      k: "Graphic size", v: sizeLabel
    }, cgSizeBody(false)),
    finish: cgEl(Opt, {
      id: 'laminate', open: openOpt === 'laminate', onToggle: toggle,
      icon: "M12 3C7 3 3 7 3 12s4 9 9 9M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18M3.5 9h17M3.5 15h17",
      k: "Vinyl Finish", v: (CG.finishById(CG.resolveFinish(laminate)) || {}).label
    }, cgEl("div", { className: "pb-grid", role: "radiogroup", "aria-label": "Vinyl finish" },
        CG.FINISHES.map(f => cgEl("button", {
          key: f.id, type: "button", role: "radio",
          className: 'pb-pick' + (f.available && laminate === f.id ? ' on' : '') + (f.available ? '' : ' cg-soon'),
          "aria-checked": f.available && laminate === f.id,
          disabled: !f.available, "aria-disabled": f.available ? undefined : "true",
          "aria-label": f.available ? f.label : f.label + ', coming soon',
          onClick: () => { if (f.available) { setLaminate(f.id); closeSoon(); } }
        }, !f.available && cgEl("span", { className: "pop cg-soonpop" }, "Coming soon"),
           cgEl("span", { className: "nm", style: { fontSize: '14.5px' } }, f.label),
           cgEl("span", { className: "sub" }, f.sub)))),
      cgEl("p", { className: "cg-hint" }, "Printed on white vinyl. This product is not laminated.")),
    qty: cgEl(Opt, {
      id: 'qty', open: openOpt === 'qty', onToggle: toggle,
      icon: "M4 6h16M4 12h16M4 18h10",
      k: "Quantity", v: effQty + ' ' + cgNoun(effQty)
    }, cgEl("div", { className: "pb-rows" },
        CG.QUANTITY.presets.map(q => {
          const pr = cgSize.ok ? CG.price(widthIn, heightIn, q) : null;
          return cgEl("button", {
            key: q, type: "button",
            className: 'pb-row' + (cgQtyText == null && qty === q ? ' on' : ''),
            onClick: () => { cgSetQty(q); closeSoon(); }
          }, cgEl("span", { className: "qn" }, q),
             cgEl("span", { className: "qr" }, cgEl("span", { className: "qp" }, (cgQuotable && pr && !pr.error) ? '$' + pr.price.toFixed(2) : '—')));
        }),
        cgEl("button", {
          type: "button", className: 'pb-row' + (cgQtyText != null ? ' on' : ''),
          onClick: () => { if (cgQtyText == null) setCgQtyText(String(effQty)); }
        }, cgEl("span", { className: "qn" }, "Custom quantity"),
           cgEl("span", { className: "qr", style: { fontSize: '12px', color: 'var(--mut)' } }, cgQtyText != null ? 'editing' : 'up to ' + CG.QUANTITY.max))),
      cgQtyText != null && cgEl("div", { className: "pb-num" + (cgQty.ok ? "" : " cg-bad") },
        cgEl("input", {
          type: "text", inputMode: "numeric", autoComplete: "off", value: cgQtyText,
          "aria-label": "Custom quantity, a whole number from " + CG.QUANTITY.min + " to " + CG.QUANTITY.max,
          "aria-invalid": cgQty.ok ? "false" : "true",
          "aria-describedby": cgQty.ok ? undefined : "cg-qty-err",
          onChange: e => cgTypeQty(e.target.value)
        }), cgEl("span", null, "graphics")),
      (cgQtyText != null && !cgQty.ok) && cgEl("p", { className: "cg-err", id: "cg-qty-err", role: "alert" }, cgQty.message),
      cgEl("p", { className: "cg-hint" }, (cgQuotable && cgPrice && !cgPrice.error ? CG.priceLine(cgPrice) : cgUnitText + " each × " + effQty + " = " + cgPriceText) + ". Standard shipping included; tax is added at checkout." + cgMinNote))
  };

  /* ---- the order summary additions (desktop right rail) ---- */
  const cgSummaryExtra = () => cgEl("div", { className: "cg-sum" },
    cgEl("div", { className: "cg-sum-only" }, "The cart, sign board, installation and artwork design are not included."),
    cgEl("div", { className: "cg-sum-foot" }, "Standard shipping included. Tax is added at checkout."),
    cgBlockMsg && cgEl("p", { className: "cg-note warn", id: "cg-block", role: "status" }, cgBlockMsg));

  /* ---- THE CART PREVIEW ------------------------------------------------
     A code-drawn cart, front view, whose panel takes whatever proportions the
     customer measured. The graphic on it is `renderSticker` itself — the real
     shape, border, transparency and background-removal result — scaled by one
     uniform factor so that it is exactly graphic-inches over panel-inches of
     the panel. It is never stretched to fill the panel and never clipped to
     it: a graphic that is too big for the panel overhangs it and says so. */
  const cgPanelFields = mobile => {
    const P = CG.placement(cgPanel.w, cgPanel.h, widthIn, heightIn, cgPlace);
    const f = (axis, val, set, st) => {
      const id = 'cg-panel-' + axis + (mobile ? '-m' : '');
      return cgEl("label", { className: "cg-pf" + (st.ok ? "" : " bad"), htmlFor: id },
        cgEl("span", null, axis === 'width' ? "Width" : "Height"),
        cgEl("span", { className: "cg-pin" },
          cgEl("input", { id: id, type: "text", inputMode: "decimal", autoComplete: "off", value: val,
            "aria-label": (axis === 'width' ? "Cart front panel width" : "Cart front panel height") + " in inches, preview only",
            "aria-invalid": st.ok ? "false" : "true", onChange: e => set(e.target.value) }),
          cgEl("em", { "aria-hidden": "true" }, "in")));
    };
    return cgEl("div", { className: "cg-panelbar" + (mobile ? " m" : ""), role: "group", "aria-label": "Cart front panel, preview only" },
      cgEl("div", { className: "cg-panelhead" },
        cgEl("b", null, "Cart front panel"),
        cgEl("span", null, "Preview only. Enter your cart’s own panel size. It never changes the graphic you order or its price.")),
      cgEl("div", { className: "cg-panelrow" },
        f('width', cgPanelW, setCgPanelW, cgPanelWv), f('height', cgPanelH, setCgPanelH, cgPanelHv),
        cgEl("button", { type: "button", className: "cg-center", disabled: !P.fits || P.centered, onClick: () => setCgPlace(null) }, "Center graphic")),
      (!cgPanelWv.ok || !cgPanelHv.ok)
        ? cgEl("p", { className: "cg-err", role: "alert" }, (!cgPanelWv.ok ? cgPanelWv.message : cgPanelHv.message) +
            ' Showing the last valid panel, ' + CG.fmtIn(cgPanel.w) + ' × ' + CG.fmtIn(cgPanel.h) + ' in.')
        : !P.fits
          ? cgEl("p", { className: "cg-err", role: "alert" }, P.message)
          : cgEl("p", { className: "cg-hint", role: "status" },
              'The ' + CG.fmtIn(widthIn) + ' × ' + CG.fmtIn(heightIn) + ' in graphic covers ' +
              (P.widthFrac * 100).toFixed(1).replace(/\.0$/, '') + '% of the panel width and ' +
              (P.heightFrac * 100).toFixed(1).replace(/\.0$/, '') + '% of its height. Drag it to try a position.'));
  };
  /* THE CART, drawn as the landing page draws it (Claude Design handoff v2,
     20 Oct 2026): a white event cart laid out in inches around the front
     panel. Counter 3 in deep across the full width; body 2 in in from each
     end, with a 3 in frame round the panel and a 3 in kick below it; wheels
     min(7, (w + 6) / 3.4) in. Total (w + 10) × (h + 16), plus room for the
     floor shadow. The same numbers as cartgfx-lab.css, so the two carts match.
     Decoration only: nothing here is measured, ordered or printed. */
  const cgLabCart = (sw, sh, pw, ph) => {
    /* The geometry is shared with the product chooser's Cart Graphics card
       (slWhiteCartGeometry, beside WorkflowChooser). */
    const geometry = slWhiteCartGeometry(pw, ph);
    const ppi = Math.max(0.01, Math.min(sw / geometry.width, sh / geometry.height));
    return { ppi: ppi, widthPx: geometry.width * ppi, heightPx: geometry.height * ppi, geometry: geometry };
  };
  const cgRenderCart = () => {
    const sw = Math.max(120, (stageBox ? stageBox.w : 600) - (isMobile ? 20 : 64));
    const sh = Math.max(90, (stageBox ? stageBox.h : 400) - (isMobile ? 62 : 96));
    const L = cgLabCart(sw, sh, cgPanel.w, cgPanel.h);
    const g = L.geometry, ppi = L.ppi;
    const P = CG.placement(cgPanel.w, cgPanel.h, widthIn, heightIn, cgPlace);
    const gw = widthIn * ppi, gh = heightIn * ppi;
    const move = (x, y) => { const c = CG.placement(cgPanel.w, cgPanel.h, widthIn, heightIn, { x: x, y: y }); setCgPlace({ x: c.x, y: c.y }); };
    const onDown = e => {
      if (!P.fits) return;
      if (e.button != null && e.button !== 0) return;
      e.preventDefault(); e.stopPropagation();
      movedRef.current = true;
      const el = e.currentTarget, sx = e.clientX, sy = e.clientY, ox = P.x, oy = P.y;
      try { el.setPointerCapture(e.pointerId); } catch (x) {}
      const mv = ev => move(ox + (ev.clientX - sx) / ppi, oy + (ev.clientY - sy) / ppi);
      const up = () => {
        el.removeEventListener('pointermove', mv);
        el.removeEventListener('pointerup', up);
        el.removeEventListener('pointercancel', up);
      };
      el.addEventListener('pointermove', mv);
      el.addEventListener('pointerup', up);
      el.addEventListener('pointercancel', up);
    };
    const onKey = e => {
      if (!P.fits) return;
      const s = e.shiftKey ? 1 : 0.25;
      const d = e.key === 'ArrowLeft' ? [-s, 0] : e.key === 'ArrowRight' ? [s, 0]
              : e.key === 'ArrowUp' ? [0, -s] : e.key === 'ArrowDown' ? [0, s] : null;
      if (!d) return;
      e.preventDefault();
      move(P.x + d[0], P.y + d[1]);
    };
    const n = v => +(+v).toFixed(3);
    return cgEl("div", { className: "cg-cart", style: { width: L.widthPx + 'px', height: L.heightPx + 'px' },
        onClick: e => e.stopPropagation() },
      cgEl("svg", { className: "cg-cartsvg", viewBox: '0 0 ' + n(g.width) + ' ' + n(g.height), width: L.widthPx, height: L.heightPx,
          role: "img", "aria-label": "Illustration of a food cart seen from the front, with a " + CG.fmtIn(cgPanel.w) + " by " + CG.fmtIn(cgPanel.h) + " inch front panel" },
        /* the drawing is shared with the product chooser's card (slWhiteCartParts) */
        slWhiteCartParts(g, 'cgCart')),
      art && cgEl("div", {
          className: "cg-graphic" + (P.fits ? "" : " nofit"),
          style: { left: (g.panel.x + P.x) * ppi + 'px', top: (g.panel.y + P.y) * ppi + 'px', width: gw + 'px', height: gh + 'px' },
          tabIndex: P.fits ? 0 : -1, role: "group",
          "aria-label": P.fits
            ? "Your graphic on the cart panel. Drag it, or use the arrow keys, to reposition it. This does not change the artwork or the order."
            : "Your graphic does not fit this panel.",
          onPointerDown: onDown, onKeyDown: onKey
        },
        cgEl("div", { className: "cg-graphic-in", style: { width: w + 'px', height: h + 'px', transform: 'scale(' + (gw / Math.max(1, w)) + ')' } },
          renderSticker(true))));
  };
  const cgSurfNote = !CG ? '' : surface === 'cart'
    ? 'Cart preview · illustration only, not an exact match to your cart'
    : (surface === 'dark' ? 'Dark' : 'White') + ' preview background · not printed';

  /* ---- the phone ---- */
  const cgMobileViews = () => cgEl("div", { className: "cg-views" + ((review || (activeTool && activeTool !== 'cartpanel')) ? " cg-hide" : ""), role: "group", "aria-label": "Preview", onClick: e => e.stopPropagation() },
    CG_SURFACES.map(s => cgEl("button", { key: s.id, type: "button", className: "cg-view" + (surface === s.id ? " on" : ""),
      "aria-pressed": surface === s.id, onClick: () => setSurface(s.id) }, s.label)),
    surface === 'cart' && cgEl("button", { type: "button", className: "cg-view cg-panelchip", "aria-haspopup": "dialog",
      "aria-expanded": activeTool === 'cartpanel',
      onClick: () => { setReview(false); setActiveTool(t => t === 'cartpanel' ? null : 'cartpanel'); } },
      "Panel " + CG.fmtIn(cgPanel.w) + " × " + CG.fmtIn(cgPanel.h) + " in"));
  const cgMobileShape = () => cgEl(React.Fragment, null,
    cgEl("div", { className: "pbm-grp" }, "Shape"),
    cgEl("div", { className: "pbm-shapes cg-shapes", role: "radiogroup", "aria-label": "Shape" },
      CG_SHAPES.map(s => cgEl("button", {
        key: s.id, className: "pbm-shape" + (cgShapeKind === s.id ? " on" : ""), type: "button",
        role: "radio", "aria-checked": cgShapeKind === s.id, onClick: () => cgPickShape(s.id)
      }, cgEl(Ic, { d: s.icon }), cgEl("span", null, s.label)))),
    shp.roundable && cgEl("div", { className: "pbm-cornmod" },
      cgEl("span", { className: "pbm-cornmod-l" }, "Corners"),
      cgEl("div", { className: "pbm-cornseg", role: "radiogroup", "aria-label": "Corner style" },
        cgEl("button", { className: "pbm-corn" + (corners === 'round' ? " on" : ""), type: "button", role: "radio", "aria-checked": corners === 'round', onClick: () => setCorners('round') }, "Rounded"),
        cgEl("button", { className: "pbm-corn" + (corners === 'sharp' ? " on" : ""), type: "button", role: "radio", "aria-checked": corners === 'sharp', onClick: () => setCorners('sharp') }, "Sharp"))),
    cgShapeNote && cgEl("p", { className: "cg-note", role: "status" }, cgShapeNote));
  const cgMobileSize = () => cgEl("div", { className: "cg-msize" },
    cgSizeBody(true),
    cgEl("div", { className: "pbm-sizemeta" },
      cgEl("span", null, cgUnitText + " each"),
      cgEl("span", { className: "pbm-sizeprice" }, cgPriceText)));
  const cgMobileQty = () => cgEl(React.Fragment, null,
    cgEl("div", { className: "pbm-qgrp" }, "How many graphics?"),
    cgEl("div", { className: "pbm-qchips", role: "group", "aria-label": "Preset quantities" },
      CG.QUANTITY.presets.map(t => cgEl("button", {
        key: t, type: "button",
        className: "pbm-qchip" + (cgQtyText == null && qty === t ? " on" : ""),
        "aria-pressed": cgQtyText == null && qty === t,
        "aria-label": t + ' ' + cgNoun(t), onClick: () => cgSetQty(t)
      }, cgEl("span", { "aria-hidden": "true" }, t)))),
    cgEl("div", { className: "pbm-qcustom" },
      cgEl("label", { className: "pbm-qcustom-l", htmlFor: "cg-qty-m" }, "Custom quantity"),
      cgEl("div", { className: "pbm-qinwrap" },
        cgEl("input", {
          id: "cg-qty-m", className: "pbm-qinput", type: "text", inputMode: "numeric", autoComplete: "off",
          value: cgQtyText != null ? cgQtyText : String(qty),
          "aria-invalid": cgQty.ok ? "false" : "true",
          onChange: e => cgTypeQty(e.target.value),
          onKeyDown: e => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); if (cgQty.ok) setActiveTool(null); } }
        }),
        cgEl("span", { className: "pbm-qinunit" }, "graphics")),
      cgQty.ok
        ? cgEl("div", { className: "pbm-qhint" }, (cgQuotable && cgPrice && !cgPrice.error ? CG.priceLine(cgPrice) : cgUnitText + " each × " + effQty + " = " + cgPriceText) + (cgMinNote ? '.' + cgMinNote : ''))
        : cgEl("div", { className: "pbm-qerr", role: "alert" }, cgQty.message)),
    cgEl("button", { className: "pbm-qdone", type: "button", disabled: !cgQty.ok, onClick: () => setActiveTool(null) }, "Done"));
  /* Carry the artwork across when the customer moves between products. The file
     goes through the SAME IndexedDB handoff the home page uses, so the other
     builder receives it by the path it already trusts; if that is unavailable
     the ordinary unsaved-design guard takes over and nothing is lost quietly. */
  const cgSwitchProduct = url => {
    setBrandMenu(false);
    const HO = window.SLUploadHandoff, bytes = artOriginalRef.current || artBytesRef.current, a = artRef.current;
    if (!a || !bytes || !HO || !HO.available || !HO.available() || typeof File === 'undefined') { guardedNav(url); return; }
    let file = null;
    try {
      const DA = window.SLDesignAssets;
      file = new File([bytes], a.name || 'artwork', { type: (DA && DA.mimeOf) ? DA.mimeOf(a.name || '') : '' });
    } catch (e) { file = null; }
    if (!file) { guardedNav(url); return; }
    HO.put(file).then(() => navTo(url)).catch(() => guardedNav(url));
  };

  // === Mobile Builder V2 — presentation shell (D-045, Stage 1) ==========================
  // Same component, same SLBuilderCore state/handlers, same pricing/upload/payload/cart.
  // isMobile (declared above, next to the pricing derives) selects the presentation tree;
  // desktop tree (below) is unchanged.
  /* Stage 2: open Glass Dock tool (null = resting).
     FRESH MOBILE VISITS START ON ARTWORK. The first thing a customer has to do is
     upload, so the dock opens straight onto that tool instead of making them tap
     Artwork to discover it. This is an INITIAL VALUE, not a behaviour: it is a lazy
     useState initialiser that runs once on mount, so nothing ever re-opens the panel
     afterwards - closing it, switching tools, uploading, Review and back/forward all
     behave exactly as before.

     Deliberately NOT auto-opened, because those visits are not 'fresh' and the
     customer is being returned to something they already built:
       - stickr_resume_design   Edit Design is restoring a saved design
       - stickr_incoming_art    legacy handoff is about to inject artwork
       - stickr_product_hint    'sheet' routes to the sticker-sheet workspace
     Storage access is wrapped: private mode throws, and a throw means we know
     nothing about a previous session, which is exactly the fresh case.

     isMobile is the builder's OWN definition (matchMedia '(max-width:820px)',
     declared with the pricing derives) - no second breakpoint is introduced, and
     desktop keeps returning null. */
  const [activeTool, setActiveTool] = useState(() => {
    if (!isMobile) return null;
    try {
      if (localStorage.getItem('stickr_resume_design')) return null;
      if (sessionStorage.getItem('stickr_incoming_art')) return null;
      /* a sheet arrival opens the upload controls too (WG2, 24 Oct 2026): the customer came to upload */
    } catch (e) {}
    return 'artwork';
  });
  const [review, setReview] = useState(false); // Stage 7: Review Order dock state (UI-only; reuses shared summary/cart)
  // Content-driven open-panel height (D-045). The glass panel rises ONLY as tall as the active tool's actual
  // controls: measure the rendered content (.pbm-panelc) and animate the panel to exactly that height, so there
  // is never an empty glass region and each tool (Artwork/Shape/Size/Style/Review) hugs its own content —
  // including dynamic states (Size lock 1↔2 dims, Style border/thickness rows, Artwork with/without art). A
  // ResizeObserver keeps it exact when content changes within a tool. Closed → 0 (drives the existing open/close
  // height animation). The panel is an ABSOLUTE overlay, so its height never moves/resizes/recenters the sticker.
  const [panelH, setPanelH] = useState(0);
  /* THE DECK'S REAL HEIGHT, PUBLISHED.
     Measured at 390x844 the source advisory sat at 651-828 and the deck --
     tool tabs, quantity, Review Order -- occupies 671-844. The advisory was
     lying directly on the controls, so a customer reaching for "Shape" hit a
     paragraph of the advisory instead and the builder appeared to ignore them.
     Seating it needs the deck's height, and a guessed number would drift the
     moment the deck changes; this is the same measured approach panelH uses. */
  const [deckH, setDeckH] = useState(0);
  const deckRef = useRef(null);
  /* One-shot, and only for the auto-opened first paint. panelH is measured AFTER
     mount, so an auto-opened panel would otherwise render at height 0 and then
     animate 0 -> H through the .34s height transition: a visible drawer-opening
     flash on arrival, which is precisely what an initial state must not do.
     While this is set the panel lays out at height:auto with the transition off,
     so the very first frame is already the open state. It is cleared once the real
     height is known, restoring the normal animation for every user-initiated open.
     Always false on desktop and on any non-fresh visit. */
  const [panelInstant, setPanelInstant] = useState(activeTool !== null);
  const panelcRef = useRef(null);
  useEffect(() => {
    const el = deckRef.current;
    if (!isMobile || !el) { setDeckH(0); return undefined; }
    const measure = () => setDeckH(el.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => { try { ro.disconnect(); } catch (e) {} };
  }, [isMobile, activeTool, review, art]);
  useEffect(() => {
    const el = panelcRef.current;
    if (!(activeTool || review) || !el) { setPanelH(0); return undefined; }
    const measure = () => setPanelH(el.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeTool, review]);
  // Hand the panel back to the normal height animation once its real height is in.
  useEffect(() => {
    if (!panelInstant || !panelH) return undefined;
    const id = requestAnimationFrame(() => setPanelInstant(false));
    return () => cancelAnimationFrame(id);
  }, [panelInstant, panelH]);

  /* ── THE STAGE MAKES ROOM FOR THE SHEET ────────────────────────────────
     `.pbm-panel` is `position:absolute; bottom:100%` — it rises OVER the
     stage rather than taking flex space from it, which is exactly why an open
     sheet could sit on top of the sticker. The stage never knew the panel
     existed.

     So the stage is told, in pixels, how much of itself the sheet is about to
     cover, and shifts by that much. PRESENTATION ONLY: this is a translate on
     the canvas region. No customer state is written, `pos` is not touched,
     Center is not called, and nothing downstream of the transform — the
     payload, artworkRectIn, the cut path, production — can see it.

     ── MEASURED, NOT GUESSED ──────────────────────────────────────────────
     A per-tab constant would be wrong the moment a panel's content changed,
     and the four panels differ by ~90px already. The overlap is read off the
     real rendered rects each time.

     ── AND ALWAYS FROM THE RESTING POSITION ───────────────────────────────
     `getBoundingClientRect()` returns the sticker where it is NOW, including
     any shift already applied. Feeding that back in would make each pass
     measure its own output and walk the sticker up the screen. Adding the
     current shift back gives the resting rect, so every calculation starts
     from the same place and repeated opens cannot drift. */
  const [avoidY, setAvoidY] = useState(0);
  const avoidRef = useRef(0);
  avoidRef.current = avoidY;

  useEffect(() => {
    if (!isMobile) { if (avoidRef.current) setAvoidY(0); return undefined; }
    const stage = canvasRef.current;
    if (!stage) return undefined;

    /* A gap below, so the sticker is not glued to the glass. */
    const CLEARANCE = 16;
    /* And a smaller one above. These are not the same measurement: the gap
       above the sticker is headroom the avoidance SPENDS, and spending 16 of
       it bought nothing — measured at 390px the sticker has 103px of slack
       above it and the tallest panel (Style) needs all 103. Charging 16 of
       that to a margin nobody sees left the sticker touching the glass
       instead. 8 is enough to keep it off the header. */
    const TOP_CLEARANCE = 8;

    let raf = 0;
    const measure = () => {
      raf = 0;
      const el = stage.querySelector('.pb-sticker');
      const panel = document.querySelector('.pbm-panel');
      const applied = avoidRef.current;

      if (!el || !panel || !(activeTool || review)) {
        if (applied !== 0) setAvoidY(0);
        return;
      }
      const sr = el.getBoundingClientRect();
      const pr = panel.getBoundingClientRect();
      const st = stage.getBoundingClientRect();
      if (!(sr.height > 0) || !(pr.height > 0)) {
        if (applied !== 0) setAvoidY(0);
        return;
      }

      /* ── THE VISIBLE BOUNDS, NOT THE BOX ────────────────────────────
         The stage is `overflow:hidden`, so a sticker whose box is taller
         than the stage is CLIPPED -- the customer sees it end at the stage
         edge. getBoundingClientRect reports the full box regardless, and
         using that would compute a collision against pixels nobody can see
         and try to move the sticker by hundreds of pixels it does not need.
         What is visible is the box intersected with the stage. */
      const visibleBottom = Math.min(sr.bottom, st.bottom);

      /* undo the shift already on screen: measure the resting layout */
      const restBottom = visibleBottom + applied;
      const restTop = sr.top + applied;
      const stageTop = st.top + applied;

      const need = (restBottom + CLEARANCE) - pr.top;
      /* never above the stage's own top — the sticker must not climb toward
         the header just because a tall sheet opened */
      const headroom = Math.max(0, restTop - (stageTop + TOP_CLEARANCE));
      const next = Math.max(0, Math.min(Math.round(need), Math.round(headroom)));

      if (Math.abs(next - applied) >= 1) setAvoidY(next);
    };

    const schedule = () => { if (!raf) raf = requestAnimationFrame(measure); };
    schedule();

    /* ── MEASURE AFTER THE SHEET HAS FINISHED MOVING ───────────────────
       The panel animates its height over .34s. Measuring mid-flight reads a
       panel that is still short, computes "no collision", and then never
       looks again — which is exactly how a 1px overlap survived the first
       pass. So the real trigger is the panel's own transitionend, and the
       timeouts are the backstop for when no transition fires (reduced motion,
       or a panel that was already at the target height). */
    const t1 = setTimeout(schedule, 60);
    const t2 = setTimeout(schedule, 180);
    const t3 = setTimeout(schedule, 400);
    const t4 = setTimeout(schedule, 700);
    const panelEl = document.querySelector('.pbm-panel');
    const onEnd = (e) => { if (!e || e.propertyName === 'height') schedule(); };
    if (panelEl) panelEl.addEventListener('transitionend', onEnd);

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(schedule);
      const el = stage.querySelector('.pb-sticker');
      if (el) ro.observe(el);
      ro.observe(stage);
      const panel = document.querySelector('.pbm-panel');
      if (panel) ro.observe(panel);
    }
    window.addEventListener('resize', schedule);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', schedule);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4);
      if (panelEl) panelEl.removeEventListener('transitionend', onEnd);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', schedule);
      if (window.visualViewport) window.visualViewport.removeEventListener('resize', schedule);
    };
    /* Recomputed whenever anything can move the sticker or resize the sheet:
       which sheet is open and how tall it is, and every customer action that
       changes what the sticker looks like. */
  }, [isMobile, activeTool, review, panelH, zoom, rot, flipX, pos, shape,
      w, h, bgOn, bgUrl, fitScale, border, bsize, dieCut, art]);
  // --- Mobile quantity editor (opens inside the Glass Dock as the 'qty' tool) ------------------------
  // Reuses the shared qty state (qty/qtyMode/customQty → effQty → pricing/payload/cart) and the pricing
  // engine's own supported range. Presets come from SLPricing.tiers (not hard-coded). Custom values are
  // fully priceable (the engine interpolates any integer 10–1000), so we validate = round + clamp to the
  // engine's range and surface an inline note when a value is adjusted — never a fake/quote price.
  const QTY_TIERS_UI = (typeof window !== 'undefined' && window.SLPricing && window.SLPricing.tiers) || [10, 25, 50, 100, 200, 500, 1000];
  const QTY_MIN_UI = QTY_TIERS_UI[0], QTY_MAX_UI = QTY_TIERS_UI[QTY_TIERS_UI.length - 1];
  const qtyText = n => Number(n).toLocaleString() + ' ' + (CG ? cgNoun(n) : (n === 1 ? 'sticker' : 'stickers'));
  const [qtyInput, setQtyInput] = useState('');
  const [qtyErr, setQtyErr] = useState('');
  const qtyFocusRef = useRef(false);
  // Keep the editor's input mirrored to the live quantity (from ± or presets) EXCEPT while it's being typed.
  useEffect(() => {
    if (activeTool !== 'qty' || qtyFocusRef.current) return;
    setQtyInput(String(effQty)); setQtyErr('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTool, effQty]);
  const openQtyEditor = () => { setReview(false); setActiveTool(t => t === 'qty' ? null : 'qty'); };
  const setQtyPreset = n => { setQtyMode('preset'); setQty(n); setQtyInput(String(n)); setQtyErr(''); };
  const commitQty = close => {
    const raw = parseFloat(qtyInput);
    if (!isFinite(raw) || raw <= 0) { setQtyErr('Enter a quantity between ' + QTY_MIN_UI + ' and ' + QTY_MAX_UI.toLocaleString() + '.'); return; }
    let n = Math.round(raw), note = '';
    if (n < QTY_MIN_UI) { n = QTY_MIN_UI; note = 'Minimum order is ' + QTY_MIN_UI + ' stickers.'; }
    else if (n > QTY_MAX_UI) { n = QTY_MAX_UI; note = 'Maximum is ' + QTY_MAX_UI.toLocaleString() + ' stickers.'; }
    else if (n !== raw) { note = 'Rounded to ' + qtyText(n) + '.'; }
    setQtyMode('custom'); setCustomQty(String(n)); setQtyInput(String(n)); setQtyErr(note);
    if (!note && close) setActiveTool(null);
  };
  // Subtle purchasing insight shown beneath the Quantity control. Priority: (1) per-sticker price — the
  // engine's own perUnit; (2) savings vs the previous supported tier — compared from the SAME engine's
  // per-unit at two real quantities (no invented percentages); (3) a quiet "most popular" note when no
  // savings applies. Falls back to just the unit price if the engine can't price it.
  const qtyUnit = slUnit(widthIn, heightIn, effQty);
  const qtyPrevTier = QTY_TIERS_UI.filter(t => t < effQty).pop();
  let qtySavePct = 0;
  if (qtyPrevTier) { const pu = slUnit(widthIn, heightIn, qtyPrevTier); if (pu > 0) qtySavePct = Math.round((1 - qtyUnit / pu) * 100); }
  const qtyInsight = CG ? (cgInputOk ? cgUnitText + ' each · shipping included' : '') : qtyUnit > 0
    ? '$' + qtyUnit.toFixed(2) + ' per sticker'
      + (qtySavePct >= 1 ? ' · save ' + qtySavePct + '% vs ' + Number(qtyPrevTier).toLocaleString()
         : effQty === QTY_POP ? ' · most popular' : '')
    : '';
  // Trust Banner (D-046) — rotating reassurance between the preview and the dock. trustShow drives the fade
  // (opacity only). Rotation pauses while a tool/review panel is open (i.e. while the customer is interacting)
  // and is disabled entirely under prefers-reduced-motion — the banner still shows, just without auto-rotation.
  const [trustI, setTrustI] = useState(0);
  const [trustShow, setTrustShow] = useState(true);
  const trustPaused = !!activeTool || review;
  useEffect(() => {
    if (!isMobile || trustPaused) return undefined;   // banner is mobile-only → no timer on desktop
    if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion:reduce)').matches) return undefined;
    let swap;
    const iv = setInterval(() => {                       // ~7s cadence
      setTrustShow(false);                               // fade current out (opacity 300ms)
      swap = setTimeout(() => { setTrustI(i => (i + 1) % TRUST_LIST.length); setTrustShow(true); }, 300); // swap + fade in
    }, 7000);
    return () => { clearInterval(iv); clearTimeout(swap); setTrustShow(true); };
  }, [trustPaused, isMobile]);
  // Single live-preview source of truth for BOTH desktop and mobile (verbatim-extracted .pb-sticker subtree).
  /* `cartView` draws the SAME sticker for the Cart Graphics cart preview: no
     stage fit-scale (the cart scales it instead) and no artwork handlers,
     because moving the graphic on the cart must never pan the artwork. */
  const renderSticker = (cartView) => /*#__PURE__*/React.createElement("div", {
    className: (cartView && dieCut) ? "pb-sticker cg-die" : "pb-sticker",
    style: dieCut ? {
      width: w + 'px',
      height: h + 'px',
      background: 'transparent',
      transform: `scale(${fitScale})`,
      ...(cartView ? { transform: 'none' } : null),
      transformOrigin: 'center',
      /* Computed from the CLIPPED silhouette, so the sticker's shadow traces
         what will actually be produced. */
      filter: contourFilter
    } : {
      width: w + 'px',
      height: h + 'px',
      borderRadius: shapeRad,
      background: stickerBg,
      padding: padPx + 'px',
      transform: `scale(${fitScale})`,
      ...(cartView ? { transform: 'none' } : null),
      transformOrigin: 'center',
      boxShadow: '0 40px 70px -26px rgba(15,23,42,.5), 0 16px 32px -18px rgba(15,23,42,.4), 0 0 0 1px rgba(15,23,42,.05)'
    }
  }, /*#__PURE__*/React.createElement("div", {
    ref: faceRef,
    /* THE CURSOR ADVERTISES WHAT THE HANDLERS ALLOW, and reads it from the
       same predicate they do. `movable` was keyed on `art`, which is true
       for a die cut the handlers will not pan. */
    className: 'pb-face' + (canPanArt && dragging.current ? ' grabbing' : '') +
               (canPanArt ? ' movable' : ''),
    onMouseDown: cartView ? undefined : onFaceDown,
    onTouchStart: cartView ? undefined : onFaceTouch,
    /* THE PREVIEW SHOWS THE EXPORT BOUNDARY.
       This was 'visible', and that made the die-cut preview the only view in
       the system that did not clip: rasterizeFace draws into a canvas of
       exactly boxW x boxH and the production SVG carries that same viewBox,
       so anything outside the box is gone the moment the design is exported.
       A customer could therefore see a complete sticker on screen and buy a
       cropped one, with nothing in the UI ever showing the line being
       crossed. The transform is pinned now, so nothing SHOULD reach the edge
       — but 'should' is not a boundary, and the preview is where a boundary
       has to be visible. The contact shadow moves to the wrapper below so it
       still renders outside the clip. */
    style: dieCut ? {
      /* ── A DIE-CUT PREVIEW HAS NO BOUNDARY TO SHOW ──────────────────────
         This was `hidden`, to make the preview reveal the export boundary.
         For a shaped sticker that is still right. For a die-cut it is not:
         the exporter writes ONE UNCLIPPED IMAGE and emits no clipPath at all
         (`flattened: true` in `renderProductionSvg`), so there is no boundary
         here for production to enforce — and the clip was therefore hiding an
         edge that ships. What it hid was the worst possible couple of pixels:
         the outer band and the cut line, up to 4.56px of them on a thick
         border, which is the "clipped by the preview edge" defect.

         The sticker cannot escape the stage instead: `fitScale` now fits the
         PAINTED extent, so what this stops clipping is already known to fit. */
      borderRadius: 0,
      background: 'transparent',
      overflow: 'visible'
    } : {
      borderRadius: faceRad,
      background: faceBg,
      overflow: 'hidden'
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: displayArt,
    alt: "art",
    draggable: false,
    style: dieCut ? {
      // GPU-layer promotion (translateZ(0)+backface/will-change) rasterizes the die-cut drop-shadow once so
      // it MOVES with the layer during drag instead of re-rasterizing each pointermove frame (stale shadow
      // ghosting). But promoting the layer AT REST composites it on first paint — before a freshly-uploaded
      // base64 data-URL has decoded — so mobile shows an EMPTY layer that never repaints until a later style
      // change (e.g. Shape) invalidates it: the "upload preview blank until you change Shape" bug. The hints
      // are only needed WHILE DRAGGING, so gate them on that: at rest the img uses normal compositing and
      // paints the moment the image decodes (immediately after upload). Desktop is unaffected (never blank).
      /* Same canonical geometry the exported proof is drawn from — see
         `proofGeometry`. Typing the transform twice is what let the order's
         "preview" become the raw upload without anybody noticing. */
      transform: proofTransformCss(proofGeometry()) + (dragging.current ? ' translateZ(0)' : ''),
      /* Die-cut's contact shadow is applied to .pb-sticker instead, which is
         outside the clip — a filter on this element would be cut off with it
         and put a hard rectangle edge around a die-cut sticker. */
      filter: dieCut ? 'none' : contourFilter,
      willChange: dragging.current ? 'transform' : 'auto',
      backfaceVisibility: dragging.current ? 'hidden' : 'visible',
      WebkitBackfaceVisibility: dragging.current ? 'hidden' : 'visible',
      maxWidth: 'none',
      width: w + 'px',
      height: 'auto'
    } : {
      /* The shaped geometry: letterboxed into the box, same transform source
         as above and as the exporter. (`dieCut === contour`, so the old third
         branch here was unreachable and is gone.) */
      transform: proofTransformCss(proofGeometry()),
      filter: 'none',
      width: w + 'px',
      height: h + 'px',
      objectFit: 'contain',
      maxWidth: 'none',
      maxHeight: 'none'
    }
  })));
  // ---- Sticker Sheet mode (mobile) — reuses the SHARED SLSheets catalog + engine and SLPricing (D-050).
  // Active when the customer arrived wanting a sheet (Home / My Lab hint). Desktop shows the SheetBuilder
  // overlay; mobile presents the SAME capabilities/state inline in the Glass Dock (Sheet tool + panel).
  const sheetMode = !!sheetIntent;
  const sheetPlan = (typeof window !== 'undefined' && window.SLSheets && sheetMode)
    ? window.SLSheets.layout(widthIn, heightIn, 0, { sheet: sheetSizeId, sheetQuantity: numSheets, cutWidth: sheetCutW, cutHeight: sheetCutH }) : null;
  const sheetSizes = (typeof window !== 'undefined' && window.SLSheets && window.SLSheets.SHEET_SIZES) || [];
  const sheetPerSheet = sheetPlan ? sheetPlan.stickersPerSheet : 0;
  const sheetTotal = (sheetPlan && sheetPlan.totalStickers != null) ? sheetPlan.totalStickers : 0;
  const sheetSingle = !!(sheetPlan && sheetPlan.single);
  /* RAISED TO THE MINIMUM, WHEREVER THE COUNT CAME FROM: a size change, a saved design, a
     smaller sticker that now fits fewer per sheet. Never below the size's minimum or the sheets
     needed for 10 stickers, so a sheet is always priced. */
  const sheetMinNow = sheetMode ? slSheetMin(sheetSizeId, sheetPerSheet) : 1;
  useEffect(() => {
    if (!sheetMode || !ORDER_RULES_APPLY) return;
    if ((parseInt(numSheets, 10) || 0) < sheetMinNow) setNumSheets(sheetMinNow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetMode, sheetMinNow, numSheets]);
  const sheetEligible = !!(sheetPlan && sheetPlan.status === 'ok' && (sheetPlan.stickersPerSheet > 1 || sheetPlan.single));
  const sheetFits = !!(sheetPlan && sheetPlan.status === 'ok');
  /* A single backed sticker is priced as a regular die-cut of the same size and count (D-198). */
  const sheetPriceInfo = (typeof window !== 'undefined' && window.SLPricing && window.SLPricing.sheet && sheetMode)
    ? (sheetSingle ? window.SLPricing.diecut(widthIn, heightIn, sheetTotal) : window.SLPricing.sheet(widthIn, heightIn, sheetTotal)) : null;
  const sheetPriced = !!(sheetPriceInfo && !sheetPriceInfo.error && sheetEligible);
  const sheetTotalPrice = sheetPriced ? sheetPriceInfo.price : 0;
  const sheetPerSheetPrice = sheetPriced ? Math.round(sheetPriceInfo.price / Math.max(1, numSheets) * 100) / 100 : null;
  const sheetSizeLabel = ((typeof window !== 'undefined' && window.SLSheets && (window.SLSheets.resolveSheet(sheetSizeId) || {}).label)) || sheetSizeId;
  /* The finished, trimmed sheet (D-179), straight from the engine. The size the customer
     picks stays the advertised sheet size; this is what arrives. */
  const sheetFinished = (sheetPlan && sheetPlan.status === 'ok' && sheetPlan.finished) || null;
  /* SEE STICKER / SEE SHEET (WG3, Ryan 24 Oct 2026): on the phone, a sheet order can show the
     finished sheet itself, drawn from the SAME engine layout the order carries (positions, the
     real sticker size, turned stickers, the trimmed edge and its corner). View-only: switching
     never changes the design, the layout or any control, and the sticker view is the default. */
  const [mSheetView, setMSheetView] = useState('sticker');
  const mSheetViewable = !!(isMobile && sheetMode && art && sheetFinished && !CG);
  const showMSheet = mSheetViewable && mSheetView === 'sheet';
  /* THE PICTURE'S OWN EXTENT (phone See sheet accuracy, 24 Oct 2026). The phone's preview raster
     carries a transparent margin the production file does not (Shop SB-2: 1.3-2.2% a side), so
     drawing the whole image into the sticker box drew every sticker 3% small. The opaque extent
     is read ONCE per image, off screen, and the sheet view maps that extent onto the sticker's
     real box. Display only: no file, size, cut or price reads this. If the image cannot be read
     (or is fully opaque) the view draws the whole image exactly as before. */
  const [mSheetFit, setMSheetFit] = useState(null);
  const mSheetUrl = showMSheet ? (sheetArt || displayArt || shownArt) : null;
  useEffect(() => {
    if (!mSheetUrl) return undefined;
    let gone = false;
    const im = new Image();
    im.onload = () => {
      if (gone) return;
      try {
        const iw = im.naturalWidth, ih = im.naturalHeight;
        if (!(iw > 0 && ih > 0)) return;
        const k = Math.min(1, 1600 / Math.max(iw, ih)), w = Math.max(1, Math.round(iw * k)), h = Math.max(1, Math.round(ih * k));
        const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
        const cx = cv.getContext('2d'); cx.drawImage(im, 0, 0, w, h);
        const d = cx.getImageData(0, 0, w, h).data;
        let x0 = w, y0 = h, x1 = -1, y1 = -1;
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (d[(y * w + x) * 4 + 3] > 8) {
          if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
        }
        if (x1 < 0) return;
        setMSheetFit({ url: mSheetUrl, ar: iw / ih, fx0: x0 / w, fy0: y0 / h, fx1: (x1 + 1) / w, fy1: (y1 + 1) / h });
      } catch (e) { /* a tainted or unreadable image keeps the plain whole-image drawing */ }
    };
    im.src = mSheetUrl;
    return () => { gone = true; };
  }, [mSheetUrl]);
  const mSheetToggle = () => mSheetViewable ? /*#__PURE__*/React.createElement("div", {
      className: "pbm-sheetviews" + ((review || activeTool) ? " pbm-sv-hide" : ""), role: "group", "aria-label": "Preview" },
    [['sticker', 'See sticker'], ['sheet', 'See sheet']].map(v => /*#__PURE__*/React.createElement("button", {
      key: v[0], type: "button", className: "pbm-sheetview" + (mSheetView === v[0] ? " on" : ""), "aria-pressed": mSheetView === v[0],
      onClick: e => { e.stopPropagation(); setMSheetView(v[0]); } }, v[1]))) : null;
  const renderMobileSheet = () => {
    const f = sheetFinished, W = f.widthIn, H = f.heightIn, url = sheetArt || displayArt || shownArt;
    const r = f.cornerRadiusIn || 0;
    return /*#__PURE__*/React.createElement("div", { className: "pbm-sheetprev", role: "img", "aria-label": f.label + " sheet",
      style: { aspectRatio: W + ' / ' + H, '--pbm-sheet-ar': (W / H).toFixed(4), borderRadius: (r / W * 100) + '% / ' + (r / H * 100) + '%' } },
      f.stickers.map((s, i) => {
        const sw = f.stickerWidthIn, sh = f.stickerHeightIn;
        /* a turned sticker: the upright picture in its own box, rotated about the cell's centre */
        const bw = f.rotated ? sh : sw, bh = f.rotated ? sw : sh;
        const cx = s.xIn + sw / 2, cy = s.yIn + sh / 2;
        const fit = mSheetFit && mSheetFit.url === url ? mSheetFit : null;
        if (fit) {
          /* the picture's opaque extent, fitted (one uniform scale, centred) to the sticker's box,
             turned about that extent's own centre when the layout turns the sticker */
          const ew = (fit.fx1 - fit.fx0) * fit.ar, eh = (fit.fy1 - fit.fy0);   // extent, in image-height units
          const u = Math.min(bw / ew, bh / eh);                                // inches per image-height unit
          const iwIn = fit.ar * u, ihIn = u;
          const ocx = (fit.fx0 + fit.fx1) / 2, ocy = (fit.fy0 + fit.fy1) / 2;
          return /*#__PURE__*/React.createElement("img", { key: i, className: "pbm-sheetcell pbm-sheetcell-fit", src: url, alt: "", draggable: false,
            style: { left: ((cx - ocx * iwIn) / W * 100) + '%', top: ((cy - ocy * ihIn) / H * 100) + '%',
                     width: (iwIn / W * 100) + '%', height: (ihIn / H * 100) + '%',
                     transformOrigin: (ocx * 100) + '% ' + (ocy * 100) + '%', transform: f.rotated ? 'rotate(90deg)' : undefined } });
        }
        return /*#__PURE__*/React.createElement("img", { key: i, className: "pbm-sheetcell", src: url, alt: "", draggable: false,
          style: { left: ((cx - bw / 2) / W * 100) + '%', top: ((cy - bh / 2) / H * 100) + '%',
                   width: (bw / W * 100) + '%', height: (bh / H * 100) + '%', transform: f.rotated ? 'rotate(90deg)' : undefined } });
      }));
  };
  const mSheetStep = d => setNumSheets(n => Math.max(slSheetMin(sheetSizeId, sheetPerSheet), (parseInt(n, 10) || 1) + d));
  // Stage 1: layout shell only — resting Glass Dock (tools inert until Stage 2), fixed action bar.
  const mQtyStep = d => { if (CG) { cgSetQty(Math.max(CG.QUANTITY.min, Math.min(CG.QUANTITY.max, effQty + d))); return; } const T = [10, 25, 50, 100, 250, 500, 1000]; const i = T.indexOf(qty); const ni = i < 0 ? 3 : Math.max(0, Math.min(T.length - 1, i + d)); setQtyMode('preset'); setQty(T[ni]); };
  const TOOL_LABEL = { artwork: 'Artwork', shape: 'Shape', sheet: 'Sheet', size: 'Size', style: 'Style', qty: 'Quantity', cartpanel: 'Cart preview' };
  const Mtool = (id, d) => /*#__PURE__*/React.createElement("button", {
    className: "pbm-tool" + (activeTool === id ? " on" : ""), type: "button", "aria-pressed": activeTool === id,
    onClick: () => { setReview(false); setActiveTool(t => t === id ? null : id); }
  }, /*#__PURE__*/React.createElement(Ic, { d: d }), /*#__PURE__*/React.createElement("span", null, TOOL_LABEL[id]));
  // Single upload element (same fileRef + readFile pipeline as desktop) — rendered in whichever tree is active.
  const fileInput = /*#__PURE__*/React.createElement("input", {
    ref: fileRef, type: "file",
    /* FROM THE VALIDATOR'S OWN LIST. It was "image/*,.pdf,.svg", which offered
       the customer WEBP, HEIC and GIF and then rejected them after they had
       chosen. The picker and the validator now read the same array. */
    accept: (window.SLUpload && window.SLUpload.limits) ? window.SLUpload.limits().acceptAttr : ".png,.jpg,.jpeg,.svg,.pdf",
    style: { display: 'none' },
    onChange: e => { readFile(e.target.files[0]); e.target.value = ''; }
  });

  /* ARTWORK QUALITY CONFIRMATION (D-099).
     ---------------------------------------------------------------------
     ONE component for both devices — same words, same buttons, same
     behaviour. Only the CSS differs: a centred dialog on desktop, a
     bottom-sheet on a phone. Building two would be building two chances to
     disagree.

     It informs; it does not punish. There is no "bad file", no "invalid", and
     no block: "Use this file anyway" is a real, equal choice, and the primary
     action is simply the one most customers will be better served by.

     Accessibility: role=dialog + aria-modal, labelled by its own heading,
     Escape closes it as "anyway" (dismissing a warning is accepting it), and
     focus moves to the dialog so a keyboard lands inside it. */
  const qualityDialogRef = useRef(null);

  /* ONE DIALOG, TWO QUESTIONS (D-103).
     The source advisory and the resolution warning are separate DECISIONS with
     separate acknowledgements, but they are the same conversation with the
     customer and share one component — same layout, same buttons, same
     accessibility, same bottom sheet on a phone. Two components would be two
     chances for the tone to drift.

     `activeDialog` guarantees they can never stack: the source question is
     asked first because it is about the file just chosen, and the resolution
     warning simply waits its turn. */
  const dialogKind = activeDialog === 'source' ? 'source'
    : (qualityOpen && activeDialog === 'quality') ? 'quality' : null;
  const dialogOpen = dialogKind !== null;
  const onDismiss = dialogKind === 'source' ? sourceProceed : qualityProceed;
  const onBetter = dialogKind === 'source' ? sourceChooseBetter : qualityChooseBetter;
  const COPY = dialogKind === 'source' ? {
    title: 'Want the cleanest print?',
    /* ONE LINE ON A PHONE, THE WHOLE EXPLANATION BEHIND Why?.
       The full sentence below is the approved copy and is what desktop shows
       and what Why? reveals — nothing is lost. But it wraps to four lines on a
       390px screen, and with the two actions that made a 249px card standing
       between the customer and their sticker. This says the same thing in the
       space a phone has. */
    bodyShort: 'This image can be used, but a higher-quality original may print cleaner.',
    body: 'This image can still be used, but logos and graphics usually print cleanest from the original PDF, SVG, or a high-resolution PNG.',
    body2: 'If you have the original artwork file, we recommend uploading that instead.',
    soft: 'You can continue with this file if you\'re happy with it.'
  } : {
    /* CALM GUIDANCE, NOT CORRECTION. The customer chose this file; the job here
       is to make sure they are choosing it KNOWINGLY, not to grade it. Nothing
       in this copy calls the artwork poor, bad or low quality, and nothing asks
       for a better one — the previous wording said "Choose a Better File",
       which tells someone their work fell short on the way to asking a
       question. One short paragraph: what we noticed, that we will still make
       it, and that the call is theirs. */
    title: 'Quick heads up',
    body: 'This image may look a little soft or pixelated when printed at this size. We can still make it \u2014 just make sure you\u2019re happy with the preview.',
    body2: null,
    soft: null
  };

  useEffect(() => {
    if (!dialogOpen) return undefined;
    const el = qualityDialogRef.current;
    if (el && el.focus) { try { el.focus(); } catch (e) {} }
    /* Dismissing a warning IS accepting it, for whichever one is showing. */
    const onKey = ev => { if (ev.key === 'Escape') onDismiss(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dialogOpen, dialogKind]);
  /* AN INLINE CARD, NOT A MODAL.
     This was a full-screen scrim with a backdrop blur and an amber warning
     triangle — the visual language of an error, for something that is not an
     error. The decision it asks for is unchanged and still required before Add
     to Cart; only the way it asks has changed. It sits beside the artwork it is
     about, the page stays usable behind it, and the amber is an attention
     signal rather than a verdict.

     `role="status"` rather than `role="dialog"`: it is not modal, so claiming
     to be would lie to a screen reader about whether the rest of the page is
     reachable. `aria-live="polite"` announces it when it appears without
     interrupting. */
  /* ── ON A PHONE IT IS A SHEET; ON A DESKTOP IT STAYS AN INLINE CARD ──────
     The paragraph above still holds for desktop, where there is room to put
     the card beside the artwork and leave the page working. A phone has no
     such room: the card competed with the sticker for the same 720px and was
     permanently in the customer's face, which is the complaint this answers.

     So on mobile it becomes a focused sheet, shown once after analysis and
     gone the moment a decision is made. What must NOT come back with it is the
     old treatment this card was created to replace: a hazard triangle, red, an
     opaque blocker. The scrim is a soft dim over a still-visible builder, the
     dot stays a dot, and both answers remain one tap away.

     The a11y role follows the behaviour rather than being asserted, and the
     behaviour changed: the phone sheet used to sit over a dimmed page that it
     genuinely held, so it reported itself modal. It no longer holds anything
     -- taps pass through it to the builder -- so `aria-modal="true"` would now
     be exactly the false claim this rule exists to prevent. Both shells are
     the same thing again: a status region that speaks when it appears. */
  const advA11y = { role: "status", "aria-live": "polite" };
  const qualityCardBody = dialogOpen ? /*#__PURE__*/React.createElement("div", Object.assign({
    className: "pbq-card" + (qualityNudge ? " is-nudged" : ""),
    "aria-labelledby": "pbq-title", "aria-describedby": "pbq-body",
    tabIndex: -1, ref: qualityDialogRef
  }, advA11y),
    /* A DOT, NOT A TRIANGLE. The hazard triangle is the strongest symbol in the
       interface vocabulary and this is the mildest message in the builder. */
    /*#__PURE__*/React.createElement("span", { className: "pbq-dot", "aria-hidden": "true" }),
    /*#__PURE__*/React.createElement("h2", { className: "pbq-title", id: "pbq-title" }, COPY.title),
    /*#__PURE__*/React.createElement("p", { className: "pbq-body", id: "pbq-body" },
      (isMobile && COPY.bodyShort && !advWhy) ? COPY.bodyShort : COPY.body),
    /* THE LONGER EXPLANATION IS DISCLOSURE, NOT DELETION.
       On a phone this card was 353px of a 720px screen and pushed the sticker
       out of the useful area. The headline, the one-line summary and BOTH
       actions stay exactly where they were; only the follow-up paragraphs move
       behind Why?. On desktop nothing changes — there is room, so it is all
       shown. The quality warning has no follow-up paragraphs at all, so it was
       already this compact and is untouched. */
    ((!isMobile || advWhy) && COPY.body2) ? /*#__PURE__*/React.createElement("p", { className: "pbq-body" }, COPY.body2) : null,
    ((!isMobile || advWhy) && COPY.soft) ? /*#__PURE__*/React.createElement("p", { className: "pbq-body pbq-soft" }, COPY.soft) : null,
    /*#__PURE__*/React.createElement("div", { className: "pbq-actions" },
      /* CONTINUING IS THE NEUTRAL ACTION. It used to be the ghost button while
         "Choose a Better File" was solid blue — the interface leaning on the
         customer to change their mind about their own artwork. Amber says
         "look"; it must not also say "go". So continuing wears the site's
         ordinary dark action styling and the alternative is an outlined
         secondary: both obvious, neither pushed. */
      /*#__PURE__*/React.createElement("button", {
        type: "button", className: "pbq-btn pbq-btn-neutral", onClick: onDismiss
      }, "Use this image"),
      /*#__PURE__*/React.createElement("button", {
        type: "button", className: "pbq-btn pbq-btn-outline", onClick: onBetter
      /* TWO BUTTONS, ONE ROW. At 390px "Choose another image" wrapped the
         actions onto a second row and cost the card another 52px — more than
         the sentence above it. The shorter label says the same thing where the
         space is actually scarce; desktop keeps the full one. */
      }, isMobile ? "Choose another" : "Choose another image")),
    /* Below the actions, so the decision comes first and the reading is
       optional. Only where something is actually hidden. */
    (isMobile && (COPY.body2 || COPY.soft)) ? /*#__PURE__*/React.createElement("button", {
      type: "button", className: "pbq-why", onClick: () => setAdvWhy(v => !v),
      "aria-expanded": advWhy ? 'true' : 'false'
    }, advWhy ? 'Hide details' : 'Why?') : null) : null;

  /* ONE COMPONENT; THE DEVICE DIFFERENCE IS CSS.
     The wrapper is always rendered and is `display:contents` by default, so on
     desktop it is not a box at all and the inline card lays out exactly as it
     did. Only inside the phone media query does it become a seated scrim. That
     keeps the rule this card has always followed — one card, responsive
     presentation — rather than growing a second dialog for small screens.

     NOT A TRAP. On a phone this became position:fixed; inset:0 with a dimmed
     backdrop, and it ate every tap aimed at the builder underneath -- measured
     at 390x844, a tap on "Shape" landed on this card's own body paragraph and
     nothing happened. The customer concluded the builder was broken.

     It is now non-blocking, which is what the paragraph above always claimed:
     the backdrop passes pointer events straight through, only the card itself
     takes them, and the card is seated ABOVE the deck so the controls it used
     to cover are reachable. The dimming went with the blocking -- a surface
     that darkens the page while not actually holding it is a lie about what a
     tap will do.

     Still no click-to-dismiss, and now that costs nothing: there is no dead
     zone left to dismiss. The two buttons remain the decision, Escape still
     accepts it, and Add to Cart still brings this back for anyone who never
     answered. */
  const qualityCard = qualityCardBody
    ? /*#__PURE__*/React.createElement("div", {
        className: "pbq-scrim",
        /* The deck's measured height travels with the element that has to
           clear it, so the CSS never guesses. */
        style: isMobile ? { '--pbm-deck-h': (deckH || 0) + 'px' } : undefined
      }, qualityCardBody)
    : null;

  /* AN ON-DEVICE READOUT, OFF BY DEFAULT (D-102).
     Safari on a phone has no practical console, so a verdict that only exists
     in console.info cannot be read by the person holding the device. This
     renders the SAME values on screen, but only when the URL carries
     ?qadebug=1 — a customer can never see it, and it ships no differently
     from any other build. */
  /* THE BUILD, SAID OUT LOUD. A theme can serve an asset older than the one
     in the working copy, and when it does every conclusion drawn from a device
     test is about code that is not running. This string is rendered in the QA
     panel and mirrored onto `window.SL_BUILD_ID`, so "is the phone running the
     build I think it is" is answered by looking, not by assuming. */
  const qaDebug = (typeof window !== 'undefined') &&
    /[?&]qadebug=1/.test(window.location.search || '');
  /* THE READOUT IS AN INSTRUMENT, NOT A LAYER OVER THE PRODUCT.
     At 82vw and fourteen lines it covered most of the sticker on a phone —
     which is the one thing a visual acceptance pass needs to see. So on a
     phone it collapses to a badge and opens on a tap; on desktop, where it
     costs nothing, it stays open as before. Nothing is removed: every line is
     still there, one tap away. Still only ever present with ?qadebug=1. */
  try { if (typeof window !== 'undefined') window.SL_BUILD_ID = 'D-104 cart-snapshot'; } catch (e) {}

  /* ── THE HQ PRODUCTION GATEWAY, AND NOTHING ELSE ──────────────────────
     HQ is a second ENTRY POINT, not a second engine. MOTO is canvas work, so
     it has to run in a browser; HQ therefore serves these same asset files
     over localhost and drives THIS component, which is why there is exactly
     one implementation of the frozen pipeline and no HQ fork of it.

     This publishes two things and adds no behaviour of its own:

       state()   what HQ needs to decide whether it may commit — in particular
                 `busy`, which is `orderBusy`, the 10J settle gate. HQ refuses
                 to build production against a silhouette that has not caught
                 up with the artwork, for the same reason Add to Cart does.
                 Re-creating that race inside HQ is explicitly out of bounds.

       create()  the EXISTING `renderProductionSvg`. Not a copy of it, not a
                 variant: the same function the cart path calls, so the file
                 HQ writes is built by the frozen 10K frame and the frozen 10L
                 exporter, and a parity harness can prove it.

     Deliberately NOT here: any setter. Size, border, artwork and Remove BG are
     driven through the real controls, exactly as the cross-layout acceptance
     harness drives them, so HQ cannot reach a state the customer path cannot.
     Adding setters would be a second way to configure an order, which is the
     thing this architecture exists to avoid. */
  try {
    if (typeof window !== 'undefined') {
      window.SLProductionGateway = {
        version: 1,
        state: function () {
          return {
            hasArtwork: !!art,
            /* the 10J gate, verbatim */
            busy: !!orderBusy,
            ready: !!art && !orderBusy && !!contour,
            contour: !!contour,
            widthIn: widthIn, heightIn: heightIn,
            border: border, borderSize: bsize,
            backgroundRemoved: !!(bgOn && bgUrl),
            fileName: art ? art.name : null
          };
        },
        /**
         * shadowEvidence() — what the last build offered and what 7B said.
         *
         * ── A SEPARATE METHOD, ON PURPOSE ─────────────────────────────────
         * `create()`'s payload is untouched, so no existing consumer sees a
         * byte of difference and nothing that reads a production result can
         * accidentally start depending on learning data.
         *
         * ── A DEEP COPY, ALSO ON PURPOSE ──────────────────────────────────
         * The caller is handed its own object. There is no reference back into
         * the builder's candidates, so HQ cannot reach in and change what the
         * engine holds even by accident - which is what makes this genuinely
         * read-only rather than merely documented as such.
         */
        shadowEvidence: function () {
          try {
            return lastShadowEvidence
              ? JSON.parse(JSON.stringify(lastShadowEvidence)) : null;
          } catch (e) { return null; }
        },
        create: function (designId) {
          if (!art) return Promise.resolve({ ok: false, reason: 'no_artwork' });
          if (orderBusy) return Promise.resolve({ ok: false, reason: 'not_settled' });
          return renderProductionSvg(designId || null).then(function (svg) {
            return svg
              ? { ok: true, svg: svg, widthIn: widthIn, heightIn: heightIn,
                  border: border, borderSize: bsize,
                  backgroundRemoved: !!(bgOn && bgUrl),
                  fileName: art ? art.name : null,
                  fit: (typeof window !== 'undefined' && window.__slFitTrace) || null }
              /* A refusal is a RESULT. The engine declined to build a safe cut
                 line and HQ must report that, not retry it into existence. */
              : { ok: false, reason: 'moto_refused',
                  fit: (typeof window !== 'undefined' && window.__slFitTrace) || null };
          }).catch(function (e) {
            return { ok: false, reason: 'production_threw', detail: String((e && e.message) || e) };
          });
        }
      };
    }
  } catch (eG) {}

  const qaShowPanel = qaDebug && (qaOpen || !isMobile);
  const qaBadge = (qaDebug && isMobile) ? /*#__PURE__*/React.createElement('button', {
    type: 'button',
    onClick: () => setQaOpen(v => !v),
    'aria-expanded': qaOpen ? 'true' : 'false',
    'aria-label': qaOpen ? 'Hide QA diagnostics' : 'Show QA diagnostics',
    style: {
      position: 'fixed', left: '8px', bottom: '8px', zIndex: 2001,
      background: 'rgba(15,23,42,.92)', color: '#e2e8f0',
      font: '600 11px/1 ui-monospace,SFMono-Regular,Menlo,monospace',
      padding: '9px 11px', borderRadius: '999px', border: '0',
      pointerEvents: 'auto', cursor: 'pointer'
    }
  }, qaOpen ? 'QA ×' : 'QA') : null;
  const qaPanel = qaShowPanel ? /*#__PURE__*/React.createElement('div', {
    style: {
      position: 'fixed', left: '8px', bottom: isMobile ? '46px' : '8px', zIndex: 2000,
      background: 'rgba(15,23,42,.92)', color: '#e2e8f0', font: '11px/1.45 monospace',
      padding: '8px 10px', borderRadius: '8px', maxWidth: '82vw',
      maxHeight: isMobile ? '50dvh' : 'none',
      overflowY: isMobile ? 'auto' : 'visible',
      whiteSpace: 'pre-wrap', pointerEvents: isMobile ? 'auto' : 'none'
    }
  }, (function () {
    const m = (dpiInfo && dpiInfo.metadata) || {};
    return [
      'build   D-104 cart-snapshot',
      'file    ' + (art ? art.name : '(none)'),
      'kind    ' + (m.kind || '-') + '  ' + (m.format || '-'),
      'source  ' + (m.widthPx || '-') + ' x ' + (m.heightPx || '-') + ' px',
      'ordered ' + widthIn + ' x ' + heightIn + ' in',
      'effDPI  ' + (m.effectivePrintDpi != null ? m.effectivePrintDpi : 'null'),
      'level   ' + quality.level + '   vector=' + quality.vector,
      'measured ' + qualityMeasured + '   bytes=' + (artBytes ? artBytes.length : 0),
      'token   ' + artToken,
      'acked   ' + qualityAcked + '   open=' + qualityOpen,
      'willWarn ' + !!(qualityLow && !qualityAcked),
      'source  ' + sourceAdvice.category + '  advise=' + sourceAdvise + '  acked=' + sourceAcked,
      'dialog  ' + (activeDialog || 'none'),
      /* ── WHY DID ADD TO CART REFUSE? ────────────────────────────────────
         The customer-facing sentence is the same for every cause, and on a
         phone there is no console to tell them apart. These lines are blank
         until an add is attempted, and after one they say exactly what the
         guard saw. `react` vs `store` vs `payload` are printed separately
         because a disagreement between them IS the suspected defect. */
      'cart    ' + (qaCart
        ? (qaCart.refusalReason ? ('REFUSED — ' + qaCart.refusalReason) : 'accepted (no refusal)')
        : 'no add attempted yet'),
      /* REACT vs PAYLOAD is the comparison that matters: the payload is built
         from React's own values at click time, so a disagreement between these
         two is a genuine stale-state bug. The STORE's own width/height are
         printed last and marked unused on purpose - React calls
         `buildPayload(inputs)` as a pure function and never drives the store's
         setters, so the store legitimately still reads 3x3@100 on a 1.25in
         order. Measured, and noted here so it is not mistaken for the defect. */
      'cart-sz ' + (qaCart
        ? ('react ' + qaCart.reactWidth + 'x' + qaCart.reactHeight + '@' + qaCart.reactQty +
           '  payload ' + qaCart.payloadWidth + 'x' + qaCart.payloadHeight + '@' + qaCart.payloadQty +
           (qaCart.reactWidth === qaCart.payloadWidth && qaCart.reactHeight === qaCart.payloadHeight &&
            qaCart.reactQty === qaCart.payloadQty ? '  MATCH' : '  ** MISMATCH **'))
        : '-'),
      'cart-st ' + (qaCart
        ? ('store(unused) ' + qaCart.storeWidth + 'x' + qaCart.storeHeight + '@' + qaCart.storeQty +
           '  upload ' + qaCart.uploadStatus)
        : '-'),
      'cart-ok ' + (qaCart
        ? ('price ' + qaCart.price + (qaCart.priceError ? ('/' + qaCart.priceError) : '') +
           '  canOrder ' + qaCart.canOrder + '  reasons[' + (qaCart.readinessReasons || '') + ']')
        : '-'),
      'cart-tx ' + (qaCart
        ? ('variant ' + qaCart.variantId + '  payload ' + qaCart.payloadPresent +
           '  line ' + qaCart.lineItemPresent + '  id ' + qaCart.lineItemId +
           '  qty ' + qaCart.lineItemQty + '  proof ' + qaCart.proof + '  bytes ' + qaCart.bytes)
        : '-'),
      'cart-wn ' + (qaCart ? (qaCart.warnings || '(none)') + '  bg=' + qaCart.bgOn + '  at ' + qaCart.at : '-'),
      /* ── WHY IS THERE (NO) REMOVE BACKGROUND BUTTON? ────────────────────
         "The control did not appear" has three completely different causes and
         they are indistinguishable from the outside: the artwork was not
         eligible, the analysis failed, or it was eligible and the button did
         not render. Only the last is a bug, and guessing which one happened
         wastes a real person's time. These four lines say it outright. */
      'bg      state=' + bgState + '  offered=' + ((bgState !== 'none' || bgOn) ? 'yes' : 'NO') +
        '  on=' + bgOn + '  derived=' + (bgUrl ? 'yes' : 'no'),
      'bgwhy   ' + (bgInfo ? (bgInfo.verdict + ' — ' + bgInfo.reason) : 'engine not consulted'),
      'bgpix   ' + (bgInfo
        ? ((bgInfo.width || '?') + 'x' + (bgInfo.height || '?') +
           '  edgeUniformity=' + (bgInfo.uniformity != null ? (+bgInfo.uniformity).toFixed(3) : '?') +
           '  alreadyTransparent=' + (bgInfo.transparentFrac != null ? (+bgInfo.transparentFrac).toFixed(3) : '?'))
        : '-'),
      'bgerr   ' + (bgError || 'none') + '  engine=' +
        ((typeof window !== 'undefined' && window.SLBgRemove) ? window.SLBgRemove.METHOD : 'NOT LOADED'),
      /* ── WHAT IS ACTUALLY DRAWING THE STICKER PERIMETER? ────────────────
         Two different things produce a border in this product and they do not
         look the same. The PREVIEW bakes one as a raster: the alpha is blurred
         1.1px, thresholded at 128, then grown and shrunk by a disk. That is a
         pixel mask, so its edge steps — visibly so on a phone, and worse on a
         background-removed JPEG where the flood boundary follows compression
         noise. PRODUCTION reconstructs the same perimeter as vector geometry
         through MOTO/double-dip, which only runs at export.

         So "the edge looks fuzzy" and "the cut line is clean" can both be true
         at once, and nobody could tell which they were looking at. This says
         it outright, and reports MOTO's verdict once it has run. */
      'contour visible=' + (usingOutline
          ? ((contourSrcRef.current && contourSrcRef.current.source) || 'raster-bake')
          : (dieCut ? 'none(no border selected)' : 'shaped-face')) +
        '  preview-moto=' + ((contourSrcRef.current && contourSrcRef.current.moto)
          ? (contourSrcRef.current.moto.accepted
              ? ('accepted/' + (contourSrcRef.current.moto.pLane || '?') +
                 '/conf=' + (contourSrcRef.current.moto.pConfidence != null
                    ? (+contourSrcRef.current.moto.pConfidence).toFixed(3) : '?'))
              : ('refused/' + (contourSrcRef.current.moto.reason || '?') + ' -> raster fallback'))
          : 'not-run'),
      'frame   stage=' + (_stW ? (Math.round(_stW) + 'x' + Math.round(_stH)) : 'unmeasured') +
        '  safe=' + (_stW ? (Math.round(_stW * SAFE_FRAME) + 'x' + Math.round(_stH * SAFE_FRAME)) : '-') +
        '  sticker=' + w + 'x' + h +
        '  occupancy=' + (_stW ? Math.round(Math.max(w / (_stW * SAFE_FRAME), h / (_stH * SAFE_FRAME)) * 100) + '%' : '-') +
        ((_stH > 0 && _stH < STAGE_SHORT_H)
          ? '  STAGE SQUEEZED (<' + STAGE_SHORT_H + 'px tall) — layout defect, sticker is correct but tiny'
          : ''),
      'export  moto=' + (cfitReportRef.current && cfitReportRef.current.cutpath
          ? (cfitReportRef.current.cutpath.accepted
              ? ('accepted/' + (cfitReportRef.current.cutpath.evidence &&
                                cfitReportRef.current.cutpath.evidence.pLane || '?'))
              : ('refused/' + (cfitReportRef.current.cutpath.reason || '?')))
          : 'not-run-until-export')
    ].join(String.fromCharCode(10));
  })()) : null;
  /* One node for both, so every existing `qaPanel` placement keeps working. */
  const qaUi = (qaBadge || qaPanel)
    ? /*#__PURE__*/React.createElement(React.Fragment, null, qaBadge, qaPanel)
    : null;

  /* The quiet reminder that replaces the modal once it has been answered.
     Present for exactly as long as the artwork is below the floor, and it
     carries a word as well as a colour — colour alone is not a message. */
  /* REPLACES the dialog — so not WHILE the dialog is up. The chip was gated on
     the artwork alone, which was invisible while the card sat inline elsewhere
     on the page. Seated as a sheet, the two land on the same corner of the
     screen and the chip printed straight across the sentence asking the
     question it summarises. */
  /* CART GRAPHICS DOES NOT FLOAT IT. Fixed above the dock, the chip sat on the
     quantity, price and Review Order row of the phone shell. Cart Graphics
     shows the same warning, on the same condition, inline: in the Artwork
     panel under the file's controls and in Review Order (`cgSoftNote`). On
     desktop the right rail's quality card already says it in place. It is
     text, not a button: once the question has been answered `activeDialog` is
     null, so the chip's `setQualityOpen(true)` opened nothing. */
  const qualityChip = (qualityLow && art && !dialogOpen) && !CG ? /*#__PURE__*/React.createElement("button", {
    type: "button", className: "pbq-chip", onClick: () => setQualityOpen(true),
    "aria-label": "Artwork may print soft. Open quality details."
  },
    /*#__PURE__*/React.createElement("span", { "aria-hidden": "true" }, "⚠"),
    /*#__PURE__*/React.createElement("span", null, "Artwork may print soft")) : null;
  const cgSoftNote = (qualityLow && art && !dialogOpen) && CG ? cgEl("p", { className: "cg-softnote", role: "status" },
    cgEl("span", { "aria-hidden": "true" }, "⚠"),
    cgEl("span", null, "Artwork may print soft.")) : null;
  const aBtn = (label, d, onClick, active, aria) => /*#__PURE__*/React.createElement("button", {
    className: "pbm-abtn" + (active ? " on" : ""), type: "button", "aria-label": aria || label,
    "aria-pressed": typeof active === 'boolean' ? active : undefined, onClick: onClick
  }, /*#__PURE__*/React.createElement(Ic, { d: d }), /*#__PURE__*/React.createElement("span", null, label));
  // Stage 3: real Artwork controls, wired to the existing shared transform/upload logic (no new state).
  const renderArtworkPanel = () => art
    ? /*#__PURE__*/React.createElement(React.Fragment, null,
        /* No zoom row for a die-cut sticker: the artwork IS the sticker, so
           the only size that means anything is the physical one, chosen on the
           Size step. */
        !dieCut && /*#__PURE__*/React.createElement("div", { className: "pbm-slrow" },
          /*#__PURE__*/React.createElement("span", { className: "pbm-sllbl" }, "Zoom"),
          /*#__PURE__*/React.createElement("input", { className: "pbm-slider", type: "range", min: "0.4", max: "4", step: "0.01", value: zoom, "aria-label": "Zoom", onChange: e => setZoom(parseFloat(e.target.value)) }),
          /*#__PURE__*/React.createElement("span", { className: "pbm-slval" }, Math.round(zoom * 100) + "%")
        ),
        /*#__PURE__*/React.createElement("div", { className: "pbm-artrow" },
          /* ── ONE ROTATE, THE SAME ONE DESKTOP HAS ─────────────────────────
             Was Rotate left AND Rotate right, two of the six slots in this row
             for a control most people press until the artwork looks right
             rather than because they wanted a specific direction. One button,
             `spin(90)` per tap, four taps back to where you started — and it
             is the SAME call and the SAME glyph desktop uses, so the two trees
             now describe rotation identically instead of nearly.

             No new transform state: `spin` is untouched, and it still returns
             early in contour mode, which is why this is gated on `!dieCut`
             exactly as the pair before it was. */
          !dieCut && aBtn("Rotate", "M21 12a9 9 0 1 0-3 6.7M21 21v-5h-5", () => spin(90)),
          aBtn("Flip", "M12 3v18M8 7l-4 5 4 5M16 7l4 5-4 5", () => setFlipX(f => !f), flipX),
          // Center: reset ONLY the positional offset to the shared face-centre ({x:0,y:0}); zoom/rotation/flip/size/shape untouched.
          /* Nothing to centre when nothing can be moved. */
          !dieCut && aBtn("Center", "M12 3v18M3 12h18M9 12a3 3 0 1 0 6 0 3 3 0 1 0-6 0", () => setPos({ x: 0, y: 0 }), undefined, "Center artwork horizontally and vertically"),
          aBtn("Reset", "M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4", () => { fitArt(); setFlipX(false); }),
          /* REPLACE MOVED INTO THE ROW, and Remove Background took the large
             slot below. Replacement is not gone — it is the same hidden input
             it always was, one tap away, in the row where the other artwork
             actions already live. The prominent slot belongs to the action a
             customer actually reaches for, and re-uploading is the rarer of
             the two by a wide margin.

             This costs no height: Remove Background used to be a sixth icon in
             this row and is not one any more, so the row holds the same number
             of tools it did. */
          aBtn("Replace", "M4 4v6h6M20 20v-6h-6M20 8a8 8 0 0 0-14-3M4 16a8 8 0 0 0 14 3",
               () => fileRef.current && fileRef.current.click(), undefined,
               "Replace this artwork with a different file")
        ),
        cgSoftNote,
        bgState === 'failed' && bgError && /*#__PURE__*/React.createElement("p", { className: "pbm-bgnote" }, bgError),
        bgOn && /*#__PURE__*/React.createElement("p", { className: "pbm-bgnote ok" },
          "Background removed. Your original file is kept."),
        halo && /*#__PURE__*/React.createElement("p", { className: "pbm-bgnote" }, haloNote(null)),
        /* ── THE LARGE SLOT IS REMOVE BACKGROUND ────────────────────────────
           It ALWAYS renders. The old control appeared only for artwork the
           engine had already judged removable, so on anything else the sheet
           simply had no such button and the question "can I remove this
           background?" had no answer on screen at all — which reads as a
           missing feature rather than as a considered refusal.

           So the slot is permanent and the LABEL carries the truth, from the
           same `bgState` the engine has always published. Nothing new is
           computed here and no second state machine exists: every branch below
           is a reading of state `toggleBg` and `analyzeUrl` already own. */
        /*#__PURE__*/React.createElement("button", {
            className: "pbm-replace pbm-bgaction" + (bgOn ? " on" : "") + (bgBusy ? " busy" : ""),
            type: "button",
            onClick: toggleBg,
            disabled: !art || bgBusy || (bgState === 'none' && !bgOn),
            "aria-pressed": bgOn ? 'true' : 'false',
            title: bgLabel.title
          },
          /*#__PURE__*/React.createElement(Ic, { d: bgOn
            ? "M4 4v6h6M20 20v-6h-6M20 8a8 8 0 0 0-14-3M4 16a8 8 0 0 0 14 3"
            : "M4 4h6v6H4zM14 14h6v6h-6zM14 4l6 6M20 4l-6 6M4 14l6 6M10 14l-6 6" }),
          bgLabel.text)
      )
    : /*#__PURE__*/React.createElement("div", { className: "pbm-upload" },
        /*#__PURE__*/React.createElement("button", { className: "pbm-uploadbtn", type: "button", onClick: () => fileRef.current && fileRef.current.click() },
          /*#__PURE__*/React.createElement(Ic, { d: "M12 16V4M7 9l5-5 5 5M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" }), "Upload image"),
        /*#__PURE__*/React.createElement("div", { className: "pbm-uploadhint" }, SL_UPLOAD_HINT())
      );
  // Stage 4: Shape panel — buttons map over the shared SHAPES enum; corner control reuses corners/setCorners.
  const SHAPE_ICON = { custom: "M12 3l3 5 5.5.8-4 4 1 5.7L12 21l-5.5-2.5 1-5.7-4-4L9 8z", circle: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18", square: "M5 5h14v14H5z" };
  const renderShapePanel = () => CG ? cgMobileShape() : /*#__PURE__*/React.createElement(React.Fragment, null,
    /*#__PURE__*/React.createElement("div", { className: "pbm-grp" }, "Shape"),
    /*#__PURE__*/React.createElement("div", { className: "pbm-shapes", role: "radiogroup", "aria-label": "Shape" },
      SHAPES.map(s => /*#__PURE__*/React.createElement("button", {
        key: s.id, className: "pbm-shape" + (shape === s.id ? " on" : ""), type: "button",
        role: "radio", "aria-checked": shape === s.id, onClick: () => setShape(s.id)
      }, /*#__PURE__*/React.createElement(Ic, { d: SHAPE_ICON[s.id] }), /*#__PURE__*/React.createElement("span", null, s.label)))
    ),
    shp.roundable && /*#__PURE__*/React.createElement("div", { className: "pbm-cornmod" },
      /*#__PURE__*/React.createElement("span", { className: "pbm-cornmod-l" }, "Corners"),
      /*#__PURE__*/React.createElement("div", { className: "pbm-cornseg", role: "radiogroup", "aria-label": "Corner style" },
        /*#__PURE__*/React.createElement("button", { className: "pbm-corn" + (corners === 'round' ? " on" : ""), type: "button", role: "radio", "aria-checked": corners === 'round', onClick: () => setCorners('round') }, "Rounded"),
        /*#__PURE__*/React.createElement("button", { className: "pbm-corn" + (corners === 'sharp' ? " on" : ""), type: "button", role: "radio", "aria-checked": corners === 'sharp', onClick: () => setCorners('sharp') }, "Sharp")
      )
    )
  );
  // Stage 5/9: Size panel — Lock Proportions ON shows one unified "Size" control (Width & Height linked); OFF
  // reveals independent Width & Height. All through the shared custom-size pipeline (sizeMode/customW/customH →
  // widthIn/heightIn → pricing/payload/cart). Never resizes the artwork itself; it changes the sticker/cut
  // canvas only. Locked ratio: 1:1 for Circle/Square, the art ratio for Die Cut.
  const mDim = (label, valIn, apply) => /*#__PURE__*/React.createElement("div", { className: "pbm-dim" },
    /*#__PURE__*/React.createElement("div", { className: "pbm-dim-h" },
      /*#__PURE__*/React.createElement("span", { className: "pbm-dim-l" }, label),
      /*#__PURE__*/React.createElement("span", { className: "pbm-dim-v" }, Number(valIn).toFixed(2), /*#__PURE__*/React.createElement("span", { className: "pbm-sizeunit" }, "″"))
    ),
    /*#__PURE__*/React.createElement("div", { className: "pbm-dim-row" },
      /*#__PURE__*/React.createElement("button", { className: "pbm-step", type: "button", "aria-label": "Decrease " + label, disabled: valIn <= mDimMin(label) + 1e-6, onClick: () => apply(valIn - SZ_STEP) }, "−"),
      /*#__PURE__*/React.createElement("input", {
        className: "pbm-dimslider", type: "range", min: String(SZ_MIN), max: String(SZ_MAX), step: String(SZ_STEP),
        value: Math.min(SZ_MAX, Math.max(SZ_MIN, valIn)), "aria-label": label + " in inches",
        "aria-valuetext": Number(valIn).toFixed(2) + " inches", onChange: e => apply(parseFloat(e.target.value))
      }),
      /*#__PURE__*/React.createElement("button", { className: "pbm-step", type: "button", "aria-label": "Increase " + label, disabled: valIn >= SZ_MAX - 1e-6, onClick: () => apply(valIn + SZ_STEP) }, "+")
    )
  );
  const promoMobileSize = () => /*#__PURE__*/React.createElement(React.Fragment, null,
    /*#__PURE__*/React.createElement("div", { className: "pbm-qgrp" }, "Size on the longest side · 2″ to 3″"),
    promoSizeControl(),
    /*#__PURE__*/React.createElement("div", { className: "pbm-sizemeta" },
      /*#__PURE__*/React.createElement("span", null, "The other side follows your artwork"),
      /*#__PURE__*/React.createElement("span", { className: "pbm-sizeprice" }, promoPriceEl())),
    /* A size that is cheaper at regular price says so where it was chosen,
       with the lower price and the way to it, not only in the price bar. */
    (promoRule && promoRule.cheaper && !promoWindowBlock) && /*#__PURE__*/React.createElement("div", { className: "pbm-qhint", style: { marginTop: '10px' } }, promoSaveEl()));
  const renderSizePanel = () => CG ? cgMobileSize() : PROMO ? promoMobileSize() : /*#__PURE__*/React.createElement(React.Fragment, null,
    sizeLocked
      ? mDim("Size", Math.max(widthIn, heightIn), applySize)
      : /*#__PURE__*/React.createElement(React.Fragment, null,
          mDim("Width", widthIn, applyWidth),
          mDim("Height", heightIn, applyHeight)),
    /*#__PURE__*/React.createElement("button", {
      className: "pbm-lockrow" + (sizeLocked ? " on" : ""),
      type: "button", role: "switch", "aria-checked": sizeLocked,
      "aria-label": "Lock proportions", onClick: toggleSizeLock
    },
      /*#__PURE__*/React.createElement(Ic, { d: sizeLocked ? "M6 11V8a6 6 0 0 1 12 0v3M5 11h14v9H5z" : "M6 11V8a6 6 0 0 1 11.6-2M5 11h14v9H5z" }),
      /*#__PURE__*/React.createElement("span", { className: "pbm-lockrow-t" }, "Lock proportions"),
      /*#__PURE__*/React.createElement("span", { className: "pbm-lockrow-s" }, sizeLocked ? "On" : "Off")
    ),
    /*#__PURE__*/React.createElement("div", { className: "pbm-sizemeta" },
      /*#__PURE__*/React.createElement("span", null, sizeMinNote || (indSizeRule === 'diecut' ? "Range 1.00″ (longest side) – 9.00″" : indSizeRule === 'shape' ? "Range 1.00″ – 9.00″" : "Range 0.50″ – 9.00″")),
      /*#__PURE__*/React.createElement("span", { className: "pbm-sizeprice" }, "$", total.toFixed(2))
    )
  );
  // Quantity panel — preset chips (from SLPricing.tiers) + a numeric custom-entry field, all feeding the
  // shared qty state. Presets highlight the active value; custom entry validates via commitQty (round +
  // clamp to the engine's 10–1000 range) with an inline note. Live price/Review/payload update through effQty.
  const renderQtyPanel = () => CG ? cgMobileQty() : PROMO ? /*#__PURE__*/React.createElement("div", { className: "pbm-qgrp" }, PROMO.packQty + " stickers of one design. You can double your pack in the cart.") : /*#__PURE__*/React.createElement(React.Fragment, null,
    /*#__PURE__*/React.createElement("div", { className: "pbm-qgrp" }, "Choose an amount"),
    /*#__PURE__*/React.createElement("div", { className: "pbm-qchips", role: "group", "aria-label": "Preset quantities" },
      QTY_TIERS_UI.map(t => /*#__PURE__*/React.createElement("button", {
        key: t, type: "button",
        className: "pbm-qchip" + (qtyMode === 'preset' && effQty === t ? " on" : ""),
        "aria-pressed": qtyMode === 'preset' && effQty === t,
        "aria-label": qtyText(t), onClick: () => setQtyPreset(t)
      }, /*#__PURE__*/React.createElement("span", { "aria-hidden": "true" }, Number(t).toLocaleString())))
    ),
    /*#__PURE__*/React.createElement("div", { className: "pbm-qcustom" },
      /*#__PURE__*/React.createElement("span", { className: "pbm-qcustom-l" }, "Or enter an exact amount"),
      /*#__PURE__*/React.createElement("div", { className: "pbm-qinwrap" },
        /*#__PURE__*/React.createElement("input", {
          className: "pbm-qinput", type: "text", inputMode: "numeric", pattern: "[0-9]*",
          value: qtyInput,
          "aria-label": "Custom quantity in stickers, from " + QTY_MIN_UI + " to " + QTY_MAX_UI.toLocaleString(),
          onFocus: e => { qtyFocusRef.current = true; e.target.select(); },
          onBlur: () => { qtyFocusRef.current = false; if (qtyInput.trim() !== '') commitQty(false); },
          onChange: e => setQtyInput(e.target.value.replace(/[^0-9]/g, '')),
          onKeyDown: e => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); commitQty(true); } }
        }),
        /*#__PURE__*/React.createElement("span", { className: "pbm-qinunit" }, "stickers")
      ),
      qtyErr
        ? /*#__PURE__*/React.createElement("div", { className: "pbm-qerr", role: "status" }, qtyErr)
        : /*#__PURE__*/React.createElement("div", { className: "pbm-qhint" }, QTY_MIN_UI + "–" + QTY_MAX_UI.toLocaleString() + " stickers · any amount")
    ),
    /*#__PURE__*/React.createElement("button", { className: "pbm-qdone", type: "button", onClick: () => commitQty(true) }, "Done")
  );
  // Stage 6: Style panel — Finish (primary) + Border, wired to the exact shared enums.
  // Finish = laminate (LAMINATES: matte/gloss). Border = border/BORDERS (none/white/black/custom) with
  // borderColor for custom; thickness = bsize (BSIZES: thin/standard/thick), a property shown only when a
  // border is on. Material (single vinyl) and proof (cart-level) are intentionally not exposed here.
  // Premium control-sheet layout (D-048): quiet inline label + control per row, hairline grouping, generous
  // whitespace, no card chrome. Same shared enums/handlers/state as before — only the presentation changed.
  const renderStylePanel = () => /*#__PURE__*/React.createElement("div", { className: "pbm-style" },
    (CG && !cgFinishShown) ? null : /*#__PURE__*/React.createElement("div", { className: "pbm-srow" },
      /*#__PURE__*/React.createElement("span", { className: "pbm-slabel" }, CG ? "Vinyl Finish" : "Finish"),
      /*#__PURE__*/React.createElement("div", { className: "pbm-cornseg", role: "radiogroup", "aria-label": CG ? "Vinyl finish" : "Finish" },
        (CG ? CG.FINISHES : LAMINATES).map(l => /*#__PURE__*/React.createElement("button", {
          key: l.id, className: "pbm-corn" + (laminate === l.id && l.available !== false ? " on" : "") + (l.available === false ? " cg-soon" : ""), type: "button",
          role: "radio", "aria-checked": laminate === l.id && l.available !== false,
          disabled: l.available === false, "aria-disabled": l.available === false ? "true" : undefined,
          onClick: () => { if (l.available !== false) setLaminate(l.id); }
        }, l.available === false ? l.label + " · Coming soon" : l.label))
      )
    ),
    /* ── THE FOUR CANONICAL CHOICES, AS ONE ROW ──────────────────────────
       Mobile used to offer None only as an unlabelled swatch in a COLOUR row,
       with Thin/Standard/Thick in a separate Thickness row that disappeared
       entirely once None was chosen. The four choices desktop presents
       together were never on screen together, and a customer reading the
       labels could not find "None" at all.

       This is desktop's group, with desktop's actions: None clears the border,
       and picking a thickness re-arms a cleared one exactly as `pb-pick` does.
       No mobile-only border state, no second owner, no extra row -- the
       Thickness row became this one. */
    /*#__PURE__*/React.createElement("div", { className: "pbm-srow" },
      /*#__PURE__*/React.createElement("span", { className: "pbm-slabel" }, "Border"),
      /*#__PURE__*/React.createElement("div", { className: "pbm-cornseg", role: "radiogroup", "aria-label": "Border" },
        /*#__PURE__*/React.createElement("button", {
          className: "pbm-corn" + (border === 'none' ? " on" : ""), type: "button",
          role: "radio", "aria-checked": border === 'none', onClick: () => setBorder('none')
        }, "None"),
        BSIZES.map(b => /*#__PURE__*/React.createElement("button", {
          key: b.id, className: "pbm-corn" + (border !== 'none' && bsize === b.id ? " on" : ""), type: "button",
          role: "radio", "aria-checked": border !== 'none' && bsize === b.id,
          onClick: () => { setBsize(b.id); if (border === 'none') setBorder('white'); }
        }, b.label))
      )
    ),
    /* Colour is meaningless without a border, so it is not offered without one
       -- which also removes the second, unlabelled way to say None. */
    border !== 'none' && /*#__PURE__*/React.createElement("div", { className: "pbm-srow" },
      /*#__PURE__*/React.createElement("span", { className: "pbm-slabel" }, "Colour"),
      /*#__PURE__*/React.createElement("div", { className: "pbm-swrow", role: "radiogroup", "aria-label": "Border colour" },
        BORDERS.map(b => b.id === 'custom'
          ? /*#__PURE__*/React.createElement("button", {
              key: b.id, className: "pbm-sw pbm-sw-custom" + (border === 'custom' ? " on" : ""), type: "button",
              role: "radio", "aria-checked": border === 'custom', "aria-label": "Custom color",
              style: { background: borderColor }, onClick: () => setBorder('custom')
            })
          : /*#__PURE__*/React.createElement("button", {
              key: b.id, className: "pbm-sw" + (border === b.id ? " on" : ""), type: "button",
              role: "radio", "aria-checked": border === b.id, "aria-label": b.label,
              style: { background: b.sw }, onClick: () => setBorder(b.id)
            })
        )
      )
    ),
    (CG && border !== 'none') ? cgBorderNote : null,
    border === 'custom' && /*#__PURE__*/React.createElement("label", { className: "pbm-swcustom" },
      /*#__PURE__*/React.createElement("span", null, "Custom colour"),
      /*#__PURE__*/React.createElement("input", { type: "color", value: borderColor, "aria-label": "Border colour", onChange: e => setBorderColor(e.target.value) })
    ),
    null
  );
  // Sheet panel (D-050) — Sheet Size (shared catalog chips) + Number of Sheets stepper + live production
  // readout. Every value comes from the SHARED SLSheets engine + SLPricing (no mobile-only math, no
  // hardcoded sizes). Identical capabilities to the desktop SheetBuilder, mobile-native presentation.
  const renderSheetPanel = () => /*#__PURE__*/React.createElement(React.Fragment, null,
    /*#__PURE__*/React.createElement("div", { className: "pbm-sheetf" },
      /*#__PURE__*/React.createElement("span", { className: "pbm-sllbl" }, "Sheet size"),
      /*#__PURE__*/React.createElement("div", { className: "pbm-sizes" },
        sheetSizes.map(s => /*#__PURE__*/React.createElement("button", {
          key: s.id, type: "button",
          className: "pbm-schip" + (sheetSizeId === s.id ? " on" : ""),
          "aria-pressed": sheetSizeId === s.id,
          onClick: () => chooseSheetSize(s.id)
        }, s.label)))),
    /*#__PURE__*/React.createElement("div", { className: "pbm-sheetf" },
      /*#__PURE__*/React.createElement("span", { className: "pbm-sllbl" }, sheetSingle ? "Number of stickers" : "Number of sheets"),
      /*#__PURE__*/React.createElement("div", { className: "pbm-qty" },
        /*#__PURE__*/React.createElement("button", { className: "pbm-sb", type: "button", "aria-label": sheetSingle ? "Fewer stickers" : "Fewer sheets", disabled: (parseInt(numSheets, 10) || 0) <= slSheetMin(sheetSizeId, sheetPerSheet), onClick: () => mSheetStep(-1) }, "−"),
        /*#__PURE__*/React.createElement("input", {
          className: "pbm-sheetinput", type: "text", inputMode: "numeric",
          value: String(numSheets), "aria-label": sheetSingle ? "Number of stickers" : "Number of sheets",
          onChange: e => { const v = e.target.value.replace(/[^0-9]/g, ''); const min = slSheetMin(sheetSizeId); setNumSheets(v ? Math.max(min, parseInt(v, 10)) : min); }
        }),
        /*#__PURE__*/React.createElement("button", { className: "pbm-sb", type: "button", "aria-label": sheetSingle ? "More stickers" : "More sheets", onClick: () => mSheetStep(1) }, "+"))),
    /*#__PURE__*/React.createElement("div", { className: "pbm-sheetinfo" },
      /*#__PURE__*/React.createElement("div", { className: "pbm-sheetrow" }, /*#__PURE__*/React.createElement("span", null, sheetSingle ? "Stickers per backing" : "Stickers per sheet"), /*#__PURE__*/React.createElement("b", null, sheetFits ? sheetPerSheet : "—")),
      /*#__PURE__*/React.createElement("div", { className: "pbm-sheetrow" }, /*#__PURE__*/React.createElement("span", null, "Estimated total"), /*#__PURE__*/React.createElement("b", null, sheetFits ? (sheetTotal.toLocaleString() + " stickers") : "—")),
      sheetFinished && /*#__PURE__*/React.createElement("div", { className: "pbm-sheetrow" }, /*#__PURE__*/React.createElement("span", null, sheetSingle ? "Backing size (finished)" : "Finished size (trimmed)"), /*#__PURE__*/React.createElement("b", null, sheetFinished.label)),
      sheetPerSheetPrice != null && /*#__PURE__*/React.createElement("div", { className: "pbm-sheetrow" }, /*#__PURE__*/React.createElement("span", null, sheetSingle ? "Per sticker" : "Per sheet"), /*#__PURE__*/React.createElement("b", null, "$" + sheetPerSheetPrice.toFixed(2))),
      (sheetFits && !sheetEligible) && /*#__PURE__*/React.createElement("div", { className: "pbm-sheetnote" }, "This size fits one sticker per sheet — try a larger sheet or a smaller sticker."),
      !sheetFits && /*#__PURE__*/React.createElement("div", { className: "pbm-sheetnote" }, sheetSingle ? "This sticker is too large to back on its own — choose a smaller sticker." : "This sticker is too large for this sheet — choose a larger sheet or a smaller sticker."))
  );
  // Stage 7: Review Order — the closing confirmation. Lives ENTIRELY inside the Glass Dock panel overlay
  // (same surface as the tools) so the deck's flow height never changes and the sticker never moves. Every
  // value is read verbatim from the shared model (no recompute). Sheet mode (D-050) summarizes the sheet
  // configuration and adds to cart via orderSheets; die-cut uses goCheckout — both are the shared submit.
  const revRow = (k, v) => /*#__PURE__*/React.createElement("div", { className: "pbm-revrow" },
    /*#__PURE__*/React.createElement("span", { className: "pbm-revk" }, k),
    /*#__PURE__*/React.createElement("span", { className: "pbm-revv" }, v));
  const renderReviewPanel = () => {
    const chip = border === 'none' ? null : border === 'custom' ? borderColor : (BORDERS.find(b => b.id === border) || {}).sw;
    const borderVal = /*#__PURE__*/React.createElement(React.Fragment, null,
      chip && /*#__PURE__*/React.createElement("span", { className: "pbm-revchip", style: { background: chip } }),
      borderLabel + (border === 'custom' ? ' · ' + borderColor : ''));
    /* CART GRAPHICS: the same sheet, with this product's rows. Declared HERE,
       inside the mobile shell, because it wires Add to Cart — and a control
       that reaches the cart must live where a production host never renders. */
    const cgMobileReview = borderVal => cgEl(React.Fragment, null,
      cgEl("div", { className: "pbm-rev" },
        revRow("Product", "Cart Graphics · Graphic only"),
        revRow("Graphic size", CG.fmtIn(widthIn) + ' × ' + CG.fmtIn(heightIn) + ' in'),
        revRow("Shape", CG.shapeLabel(shape, widthIn, heightIn) + (shp.roundable ? ' · ' + (corners === 'sharp' ? 'Sharp' : 'Rounded') : '')),
        revRow(cgFinishShown ? "Vinyl finish" : "Material", cgFinishShown ? CG.finishById(CG.resolveFinish(laminate)).label + " · No laminate" : cgMatFin),
        revRow("Border", borderVal),
        revRow("Quantity", effQty + ' ' + cgNoun(effQty)),
        revRow("Price each", cgUnitText),
        cgEl("div", { className: "pbm-revrow pbm-revtot" },
          cgEl("span", { className: "pbm-revk" }, "Total"),
          cgEl("span", { className: "pbm-revv" }, cgPriceText))),
      cgSoftNote,
      cgEl("p", { className: "cg-hint cg-revnote" }, "Standard shipping included. Tax is added at checkout. The cart, sign board and installation are not included."),
      cgBlockMsg && cgEl("p", { className: "cg-note warn", role: "status" }, cgBlockMsg),
      cgEl("div", { className: "pbm-revact" },
        cgEl("button", { className: 'pbm-revadd' + (saveStage ? " is-saving" : ""), type: "button",
          disabled: !!saveStage || orderBusy || !cgCanOrder || !art,
          "aria-busy": (saveStage || orderBusy) ? "true" : "false", onClick: goCheckout }, saveBtnContent("Add to Cart"))));
    if (CG) return cgMobileReview(borderVal);
    if (PROMO) {
      return /*#__PURE__*/React.createElement(React.Fragment, null,
        /*#__PURE__*/React.createElement("div", { className: "pbm-rev" },
          revRow("Product", PROMO.packQty + "-sticker pack"),
          revRow("Shape", shapeLabel + (shp.roundable ? ' · ' + (corners === 'sharp' ? 'Sharp' : 'Rounded') : '')),
          revRow("Size", widthIn + '″ × ' + heightIn + '″'),
          revRow("Finish", lamLabel),
          revRow("Border", borderVal),
          revRow("Quantity", String(PROMO.packQty)),
          revRow("Shipping", "Free shipping"),
          promoRegular > PROMO.price50 && revRow("Regular price", promoWas()),
          /*#__PURE__*/React.createElement("div", { className: "pbm-revrow pbm-revtot" },
            /*#__PURE__*/React.createElement("span", { className: "pbm-revk" }, "Total"),
            /*#__PURE__*/React.createElement("span", { className: "pbm-revv" }, "$" + total.toFixed(2))),
          (promoBlock || promoSave > 0) && /*#__PURE__*/React.createElement("div", { className: "pbm-revrow" }, promoSaveEl())),
        cutReviewNote(),
        /*#__PURE__*/React.createElement("div", { className: "pbm-revact" },
          /*#__PURE__*/React.createElement("button", { className: "pbm-revadd" + (saveStage ? " is-saving" : ""), type: "button", disabled: !!saveStage || orderBusy || !!promoBlock, "aria-busy": (saveStage || orderBusy) ? "true" : "false", onClick: goCheckout }, saveBtnContent("Add to Cart"))));
    }
    if (sheetMode) {
      return /*#__PURE__*/React.createElement(React.Fragment, null,
        /*#__PURE__*/React.createElement("div", { className: "pbm-rev" },
          revRow("Product", "Sticker Sheets"),
          revRow("Sticker shape", shapeLabel + (shp.roundable ? " · " + (corners === "sharp" ? "Sharp" : "Rounded") : "")),
          revRow("Sheet size", sheetSizeLabel),
          sheetFinished && revRow(sheetSingle ? "Backing size" : "Finished size", sheetFinished.label + (sheetFinished.trimmed ? " (trimmed)" : "")),
          revRow("Sticker size", widthIn + '″ × ' + heightIn + '″'),
          revRow("Finish", lamLabel),
          revRow("Border", borderVal),
          revRow("Layout", sheetFits ? (sheetSingle ? 'One sticker per backing' : sheetPerSheet + ' per sheet') : '—'),
          revRow("Quantity", numSheets + (sheetSingle ? (numSheets === 1 ? ' sticker' : ' stickers') : (numSheets === 1 ? ' sheet' : ' sheets'))),
          revRow("Est. stickers", sheetFits ? sheetTotal.toLocaleString() : '—'),
          /*#__PURE__*/React.createElement("div", { className: "pbm-revrow pbm-revtot" },
            /*#__PURE__*/React.createElement("span", { className: "pbm-revk" }, "Total"),
            /*#__PURE__*/React.createElement("span", { className: "pbm-revv" }, sheetPriced ? ("$" + sheetTotalPrice.toFixed(2)) : "—"))
        ),
        /*#__PURE__*/React.createElement("div", { className: "pbm-revact" },
          /* A sheet is priced and laid out from the same `widthIn`/`heightIn`
             the die-cut order uses — the row above this one prints them — so
             it waits on the same settled state. */
          /*#__PURE__*/React.createElement("button", { className: "pbm-revadd" + (saveStage ? " is-saving" : ""), type: "button", disabled: !sheetPriced || !!saveStage || orderBusy, "aria-busy": (saveStage || orderBusy) ? "true" : "false", onClick: orderSheets }, saveBtnContent("Add to Cart"))
        )
      );
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null,
      /*#__PURE__*/React.createElement("div", { className: "pbm-rev" },
        revRow("Shape", shapeLabel + (shp.roundable ? ' · ' + (corners === 'sharp' ? 'Sharp' : 'Rounded') : '')),
        revRow("Size", widthIn + '″ × ' + heightIn + '″'),
        revRow("Finish", lamLabel),
        revRow("Border", borderVal),
        revRow("Quantity", String(effQty)),
        /*#__PURE__*/React.createElement("div", { className: "pbm-revrow pbm-revtot" },
          /*#__PURE__*/React.createElement("span", { className: "pbm-revk" }, "Total"),
          /*#__PURE__*/React.createElement("span", { className: "pbm-revv" }, "$" + total.toFixed(2)))
      ),
      cutReviewNote(),
      // Single primary action. Closing is handled by the dock CTA (now "Close Review"), so the old
      // "Back to edit" button is redundant and removed — less clutter, one clear next step.
      /*#__PURE__*/React.createElement("div", { className: "pbm-revact" },
        /* `orderBusy` gates this the same way it gates desktop's Continue:
           while the background-removed picture is being checked or built, or
           its silhouette not yet measured, WHICH ARTWORK WOULD SHIP — AND AT
           WHAT SIZE — IS NOT YET DECIDED, and an order placed in that moment
           could carry either one. */
        /*#__PURE__*/React.createElement("button", { className: "pbm-revadd" + (saveStage ? " is-saving" : ""), type: "button", disabled: !!saveStage || orderBusy, "aria-busy": (saveStage || orderBusy) ? "true" : "false", onClick: goCheckout }, saveBtnContent("Add to Cart"))
      )
    );
  };
  /* "How should we make it?", ON A PHONE. The same component, the same three
     cards and the same `chooseWorkflow` the desktop tree uses; only the
     layout differs, and that is CSS.

     AFTERWARDS EVERY TOOL PANEL IS CLOSED. The customer has just said what
     they are making and the next thing they should see is all of it: the
     whole sticker above the dock, nothing covering it. The Artwork panel
     they uploaded from is still open underneath this screen, and a Shape or
     Sheet panel opened on their behalf covers the same picture, so the pick
     closes whatever is open. A panel opens when its button is tapped. */
  const mobileChooser = choosingWorkflow && /*#__PURE__*/React.createElement(WorkflowChooser, {
    art: art,
    onPick: id => {
      chooseWorkflow(id);
      setReview(false);
      setActiveTool(null);
    },
    onBack: () => {
      setArt(null);
      setArtRes(null);
      resetArtState();
    }
  });
  /* The same rule for an arrival that answered the question somewhere else
     (a ChatGPT handoff): it never meets the chooser, so nothing above closes
     the Artwork panel a fresh phone visit opens. Only that auto-opened panel
     is closed, and only at the moment the answer lands. */
  useEffect(() => {
    if (isMobile && art && workflow) setActiveTool(t => t === 'artwork' ? null : t);
  }, [workflow]);
  const renderMobileShell = () => /*#__PURE__*/React.createElement("div", { className: "pbm" },
    fileInput, qaUi, mobileChooser,
    /*#__PURE__*/React.createElement("header", { className: "pbm-hdr" },
      /*#__PURE__*/React.createElement("a", { className: "pbm-brand", href: "/", "aria-label": "Stickr Lab home" }, /*#__PURE__*/React.createElement("img", { className: "pbm-logo", src: SLA + 'sl-logo-dark.png', alt: "STICKR LAB" })),
      /* THE BUILDER'S HEADING ON THE PHONE (Ryan, 18 Oct 2026; one style for every builder): inside the
         existing header row, so the preview and controls do not move. The promo adds its price line. */
      /*#__PURE__*/React.createElement("div", { className: "pbm-head", style: { flex: 1, minWidth: 0, padding: '0 8px', textAlign: 'center', fontSize: CG ? 'clamp(11px, 3.1vw, 15px)' : 'clamp(12px, 3.4vw, 15px)', fontWeight: 500, letterSpacing: '-.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.15 } },
        /*#__PURE__*/React.createElement("span", { style: { display: 'block' } }, CG ? "Create your cart graphics" : "Create your stickers"),
        PROMO && /*#__PURE__*/React.createElement("span", { style: { display: 'block', fontSize: '11.5px', fontWeight: 500, color: '#5B6676', letterSpacing: 0 } }, "50 custom stickers for ", /*#__PURE__*/React.createElement("span", { style: { color: '#2E86E3', fontWeight: 600 } }, promoMoney(PROMO.price50)))),
      /*#__PURE__*/React.createElement("a", { className: "pbm-cart", href: "/cart", "aria-label": "Cart" }, /*#__PURE__*/React.createElement(Ic, { d: "M9 21h.01M18 21h.01M3 4h2l2.4 12.4a1.5 1.5 0 0 0 1.5 1.2h8.7a1.5 1.5 0 0 0 1.5-1.2L22 8H6" }))
    ),
    qualityCard,
    /* The preview switcher is a row of its own between the header and the
       stage, not a layer on the stage: the stage slides up under an open
       sheet, and anything pinned to it would slide under the header with it. */
    CG ? cgMobileViews() : mSheetToggle(),
    /*#__PURE__*/React.createElement("div", { className: "pbm-stage" + (CG ? " cg-stage" : ""), ref: canvasRef, "data-surface": CG ? surface : undefined,
      /* How much of the stage the open sheet is about to cover. The CSS turns
         it into a transform; nothing else reads it. */
      style: { '--pbm-avoid-y': avoidY + 'px' },
      onPointerDown: () => { movedRef.current = false; },   // fresh each interaction (bubbles from the sticker too)
      onClick: () => {                                        // deliberate tap on the sticker OR empty stage closes the dock;
        const moved = movedRef.current; movedRef.current = false;  // a drag/pinch (moved) never closes it
        if (moved) return;
        if (activeTool || review) { setActiveTool(null); setReview(false); }
      } },
      processing,
      art ? (showMSheet ? renderMobileSheet() : (CG && surface === 'cart') ? cgRenderCart() : renderSticker())
          : uploadBusy
          ? /*#__PURE__*/React.createElement("div", { className: "pbm-empty pbm-upbusy", role: "status", "aria-live": "polite" },
              /*#__PURE__*/React.createElement("span", { className: "pbm-upspin", "aria-hidden": "true" }), "Opening your artwork…")
          : uploadFail
          ? /*#__PURE__*/React.createElement("div", { className: "pbm-empty pbm-upfail", role: "alert" },
              /*#__PURE__*/React.createElement("b", null, "That file didn’t upload"),
              /*#__PURE__*/React.createElement("span", null, uploadFail),
              /*#__PURE__*/React.createElement("button", { type: "button", className: "pbm-upretry",
                onClick: e => { e.stopPropagation(); try { if (fileRef.current) fileRef.current.click(); } catch (x) {} } }, "Choose another file"))
          : /*#__PURE__*/React.createElement("div", { className: "pbm-empty" }, CG ? "Your cart graphic will appear here" : "Your sticker will appear here"),
      (CG && art) ? /*#__PURE__*/React.createElement("span", { className: "cg-mnote" }, cgSurfNote) : null,
      /* a replacement that failed: the previous sticker stays, and the reason is said over it */
      (art && uploadFail) ? /*#__PURE__*/React.createElement("div", { className: "pbm-upfailbar", role: "alert" },
        uploadFail, /*#__PURE__*/React.createElement("button", { type: "button", "aria-label": "Dismiss", onClick: e => { e.stopPropagation(); setUploadFail(null); } }, "×")) : null
    ),
    /* THE SOFT-PRINT WARNING SITS UNDER THE PREVIEW ON THE PHONE (Ryan, 28 Oct 2026). Fixed above the
       dock it covered the bottom navigation and took its taps. Here it is in the page flow, between the
       sticker and the trust strip: the same chip, the same condition, never over a control. */
    qualityChip ? /*#__PURE__*/React.createElement("div", { className: "pbm-softnote" }, qualityChip) : null,
    /* hidden while a tool sheet or Review is open: the glass sheet is translucent and the strip
       read through it as stray text (Shop check, 24 Oct 2026) */
    /*#__PURE__*/React.createElement("div", { className: "pbm-trust" + ((trustShow && !activeTool && !review) ? "" : " hide") },
      /*#__PURE__*/React.createElement("span", { className: "pbm-trust-ic", "aria-hidden": "true" }, /*#__PURE__*/React.createElement(Ic, { d: TRUST_LIST[trustI].d, w: 1.7 })),
      /*#__PURE__*/React.createElement("span", { className: "pbm-trust-tx" },
        /*#__PURE__*/React.createElement("b", { className: "pbm-trust-tt" }, TRUST_LIST[trustI].title),
        " ", TRUST_LIST[trustI].body
      )
    ),
    /*#__PURE__*/React.createElement("div", {
      className: "pbm-deckwrap" + ((activeTool || review) ? " open" : ""), ref: deckRef,
      style: { '--pbm-deck-h': (deckH || 0) + 'px' } },
      /*#__PURE__*/React.createElement("div", { className: "pbm-panel" + (panelInstant ? " pbm-panel--instant" : ""), style: { height: (activeTool || review) ? (panelInstant ? 'auto' : panelH + 'px') : '0px' } },
        (activeTool || review) && /*#__PURE__*/React.createElement("div", { className: "pbm-panelc", ref: panelcRef, key: review ? 'review' : activeTool },
          /*#__PURE__*/React.createElement("div", { className: "pbm-panel-h" + (review ? " pbm-panel-h-rev" : "") },
            /*#__PURE__*/React.createElement("span", { className: "pbm-grab" }),
            /*#__PURE__*/React.createElement("span", { className: "pbm-panel-t" }, review ? "Order Summary" : TOOL_LABEL[activeTool])
          ),
          review ? renderReviewPanel()
            : activeTool === 'artwork' ? renderArtworkPanel()
            : activeTool === 'shape' ? renderShapePanel()
            : activeTool === 'sheet' ? renderSheetPanel()
            : activeTool === 'size' ? renderSizePanel()
            : activeTool === 'qty' ? renderQtyPanel()
            : activeTool === 'cartpanel' ? cgPanelFields(true)
            : renderStylePanel() /* style is the only remaining tool */
        )
      ),
      /*#__PURE__*/React.createElement("div", { className: "pbm-deck" },
        /*#__PURE__*/React.createElement("div", { className: "pbm-rail" },
          Mtool("artwork", "M3 3h18v18H3zM3 15l5-5 4 4 3-3 6 6"),
          /* Shape is on the rail in BOTH modes. Sticker Sheet mode used to swap
             it out for Sheet (D-050), which left a sheet customer on a phone
             with no way to say "circles" or "squares". It is the same button
             and the same Shape panel the sticker builder uses, so the shape
             reaches the sheet's preview, price and kiss cut through the path
             it always did. Sheet mode ADDS Sheet beside it:
             Artwork · Shape · Sheet · Size · Style. The rail is a flex row of
             equal buttons, so five share the width four did. */
          Mtool("shape", "M12 3l9 6-3.5 10h-11L3 9z"),
          sheetMode && Mtool("sheet", "M12 2 2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"),
          Mtool("size", "M3 8V4h4M21 8V4h-4M3 16v4h4M21 16v4h-4"),
          Mtool("style", "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M12 8.8a3.2 3.2 0 1 0 0 6.4 3.2 3.2 0 0 0 0-6.4")
        ),
        /*#__PURE__*/React.createElement("div", { className: "pbm-act" },
          /*#__PURE__*/React.createElement("span", { className: "pbm-qtylbl" }, sheetMode ? (sheetSingle ? "Stickers" : "Sheets") : "Quantity"),
          /*#__PURE__*/React.createElement("div", { className: "pbm-actmain" },
            // Quantity control: sheets in sheet mode (opens the Sheet panel), stickers in die-cut mode.
            sheetMode
              ? /*#__PURE__*/React.createElement("div", { className: "pbm-qty" + (activeTool === 'sheet' ? " on" : "") },
                  /*#__PURE__*/React.createElement("button", { className: "pbm-sb", type: "button", "aria-label": "Fewer sheets", onClick: () => mSheetStep(-1) }, "−"),
                  /*#__PURE__*/React.createElement("button", {
                    className: "pbm-qtyval", type: "button", "aria-haspopup": "dialog", "aria-expanded": activeTool === 'sheet',
                    "aria-label": numSheets + (sheetSingle ? " stickers" : " sheets") + ". Edit sheet options", onClick: () => { setReview(false); setActiveTool(t => t === 'sheet' ? null : 'sheet'); }
                  }, /*#__PURE__*/React.createElement("b", null, Number(numSheets).toLocaleString()), /*#__PURE__*/React.createElement("small", null, sheetSingle ? (numSheets === 1 ? "sticker" : "stickers") : (numSheets === 1 ? "sheet" : "sheets"))),
                  /*#__PURE__*/React.createElement("button", { className: "pbm-sb", type: "button", "aria-label": "More sheets", onClick: () => mSheetStep(1) }, "+"))
              : PROMO ? /*#__PURE__*/React.createElement("div", { className: "pbm-qty" }, /*#__PURE__*/React.createElement("div", { className: "pbm-qtyval", "aria-label": PROMO.packQty + " stickers" }, /*#__PURE__*/React.createElement("b", null, PROMO.packQty), /*#__PURE__*/React.createElement("small", null, "stickers")))
              : /*#__PURE__*/React.createElement("div", { className: "pbm-qty" + (activeTool === 'qty' ? " on" : "") },
                  /*#__PURE__*/React.createElement("button", { className: "pbm-sb", type: "button", "aria-label": "Fewer stickers, currently " + qtyText(effQty), onClick: () => mQtyStep(-1) }, "−"),
                  /*#__PURE__*/React.createElement("button", {
                    className: "pbm-qtyval", type: "button", "aria-haspopup": "dialog", "aria-expanded": activeTool === 'qty',
                    "aria-label": "Quantity " + qtyText(effQty) + ". Edit quantity", onClick: openQtyEditor
                  }, /*#__PURE__*/React.createElement("b", null, Number(effQty).toLocaleString()), /*#__PURE__*/React.createElement("small", null, CG ? cgNoun(effQty) : (effQty === 1 ? "sticker" : "stickers"))),
                  /*#__PURE__*/React.createElement("button", { className: "pbm-sb", type: "button", "aria-label": "More stickers, currently " + qtyText(effQty), onClick: () => mQtyStep(1) }, "+")),
            sheetMode
              ? /*#__PURE__*/React.createElement("div", { className: "pbm-price" }, /*#__PURE__*/React.createElement("div", { className: "pbm-pv" }, sheetPriced ? ("$" + sheetTotalPrice.toFixed(2)) : "—"), /*#__PURE__*/React.createElement("small", null, sheetSizeLabel))
              : /*#__PURE__*/React.createElement("div", { className: "pbm-price" }, /*#__PURE__*/React.createElement("div", { className: "pbm-pv" }, CG ? cgPriceText : "$" + total.toFixed(2)), PROMO ? /*#__PURE__*/React.createElement("small", null, promoWas(), promoSaveEl()) : /*#__PURE__*/React.createElement("small", null, widthIn, " × ", heightIn, " in")),
            /*#__PURE__*/React.createElement("button", { className: "pbm-cta" + (review ? " on" : ""), type: "button", "aria-pressed": review, onClick: () => { setActiveTool(null); setReview(r => !r); } }, review ? "Close Review" : "Review Order")
          ),
          // Subtle, educational per-sticker insight directly beneath the quantity control (die-cut only).
          (!sheetMode && !PROMO && qtyInsight) && /*#__PURE__*/React.createElement("div", { className: "pbm-qinsight" },
            /*#__PURE__*/React.createElement("span", { key: qtyInsight }, qtyInsight))
        )
      )
    )
  );

  return isMobile ? renderMobileShell() : /*#__PURE__*/React.createElement("div", {
    className: "pb"
  }, /* WEBSITE CHROME: logo menu, FAQ, My Sticker Lab, cart. HQ has its own
     navigation and none of these destinations exist inside it. */
  !PRODUCTION_HOST && /*#__PURE__*/React.createElement("header", {
    className: "pb-header"
  }, /*#__PURE__*/React.createElement("button", {
    className: "pb-brand",
    onClick: () => setBrandMenu(true),
    "aria-label": "Open menu"
  }, /*#__PURE__*/React.createElement("img", {
    src: "https://cdn.shopify.com/s/files/1/0760/6245/7993/files/stickrlablogo-thinborder.png?v=1778899622",
    alt: "The Sticker Lab"
  })), /*#__PURE__*/React.createElement("nav", {
    className: "pb-nav"
  }, /*#__PURE__*/React.createElement("a", {
    href: "/?view=faq",
    onClick: e => {
      e.preventDefault();
      guardedNav('/?view=faq');
    }
  }, "FAQ"), /*#__PURE__*/React.createElement("a", {
    href: "/?view=mysl",
    onClick: e => {
      e.preventDefault();
      guardedNav('/?view=mysl');
    }
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M3 3h7v7H3z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M14 3h7v7h-7z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M14 14h7v7h-7z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3 14h7v7H3z"
  })), "My Sticker Lab")), /*#__PURE__*/React.createElement("a", {
    className: "pb-cart",
    href: "/cart",
    "aria-label": "Cart",
    onClick: e => {
      e.preventDefault();
      guardedNav('/cart');
    }
  }, cartN > 0 && /*#__PURE__*/React.createElement("span", {
    className: "pb-cartcount"
  }, cartN), /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "9",
    cy: "21",
    r: "1.4"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "18",
    cy: "21",
    r: "1.4"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3 4h2l2.4 12.4a1.5 1.5 0 0 0 1.5 1.2h8.7a1.5 1.5 0 0 0 1.5-1.2L22 8H6"
  })))), /*#__PURE__*/React.createElement("main", {
    className: "pb-main"
  }, fileInput, qualityChip, qaUi, /*#__PURE__*/React.createElement("div", {
    className: "pb-col pb-col-left"
  }, !PRODUCTION_HOST && /*#__PURE__*/React.createElement("div", {
    className: "pb-starthere"
  }, /*#__PURE__*/React.createElement("svg", {
    className: "sh-arrow",
    viewBox: "0 0 24 34",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.4",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M10 3C8 11 9 18 15 24"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M20 15l-5 9-6-5"
  })), /*#__PURE__*/React.createElement("span", {
    className: "sh-txt"
  }, "Start here")), /*#__PURE__*/React.createElement("div", {
    className: "pb-optlist"
  }, CG ? cgOpts.size : PROMO ? promoSizeOpt() : /*#__PURE__*/React.createElement(Opt, {
    id: "size",
    open: openOpt === 'size',
    onToggle: toggle,
    icon: "M14.5 3.5l6 6L9 21l-6-6L14.5 3.5zM8 9l2 2M11 6l2 2M5 12l2 2",
    k: "Size",
    v: sizeLabel
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-grid"
  }, SIZES.map(s => /*#__PURE__*/React.createElement("button", {
    key: s.id,
    className: 'pb-pick' + (sizeMode === 'preset' && size === s.id ? ' on' : ''),
    onClick: () => {
      setSizeMode('preset');
      setSize(s.id);
      closeSoon();
    }
  }, s.pop && /*#__PURE__*/React.createElement("span", {
    className: "pop"
  }, "Popular"), /*#__PURE__*/React.createElement("span", {
    className: "big",
    style: {
      fontWeight: 800,
      fontSize: '19px'
    }
  }, s.label), /*#__PURE__*/React.createElement("span", {
    className: "sub"
  }, s.sub)))), /*#__PURE__*/React.createElement("button", {
    className: 'pb-row' + (sizeMode === 'custom' ? ' on' : ''),
    style: {
      width: '100%',
      marginTop: '9px'
    },
    onClick: () => {
      if (sizeMode !== 'custom') {
        const ar = arForSize;
        setCustomW(String(ar >= 1 ? size : +(size * ar).toFixed(2)));
        setCustomH(String(ar >= 1 ? +(size / ar).toFixed(2) : size));
      }
      setSizeMode('custom');
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "qn"
  }, /*#__PURE__*/React.createElement(Ic, {
    d: "M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"
  }), " Custom size"), /*#__PURE__*/React.createElement("span", {
    className: "qr",
    style: {
      fontSize: '12px',
      color: 'var(--mut)'
    }
  }, sizeMode === 'custom' ? 'editing' : 'set W × H')), sizeMode === 'custom' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "pb-csrow"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-csf"
  }, /*#__PURE__*/React.createElement("label", null, "Width"), /*#__PURE__*/React.createElement("div", {
    className: "in"
  }, /*#__PURE__*/React.createElement("input", {
    type: "number",
    min: indSizeRule === 'shape' ? "1" : "0.5",
    max: "10.5",
    step: "0.25",
    value: customW,
    onChange: e => {
      setCustomW(e.target.value);
      const v = parseFloat(e.target.value);
      if (lockAR && contour && dieLockAR > 0 && !isNaN(v)) setCustomH(String(+(v / dieLockAR).toFixed(2)));
      /* A CIRCLE OR SQUARE KEEPS ITS SHAPE WHEN A SIDE IS TYPED. The lock is
         always on for them here (the lock button is disabled off die cut), and
         the phone's applyWidth already honours it; these fields did not, so a
         desktop "circle" could be ordered 3 x 3.25 and cut as an oval. */
      else if (lockAR && !contour && !isNaN(v)) setCustomH(String(+(v / shapeLockRatio()).toFixed(2)));
    },
    onBlur: e => {
      const v = parseFloat(e.target.value);
      if (!isNaN(v)) {
        const cv = Math.min(10.5, Math.max(0.5, v));
        setCustomW(String(cv));
        if (lockAR && contour && dieLockAR > 0) setCustomH(String(+(cv / dieLockAR).toFixed(2)));
        else if (lockAR && !contour) setCustomH(String(+(cv / shapeLockRatio()).toFixed(2)));
      }
      setSizeFixTick(t => t + 1);
    }
  }), /*#__PURE__*/React.createElement("span", null, "in"))), /*#__PURE__*/React.createElement("button", {
    className: 'pb-lock' + (lockAR ? ' on' : ''),
    onClick: () => setLockAR(l => !l),
    title: lockAR ? 'Width & height locked to the shape' : 'Locked ratio off',
    disabled: !contour
  }, lockAR ? /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("rect", {
    x: "5",
    y: "11",
    width: "14",
    height: "10",
    rx: "2"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8 11V7a4 4 0 0 1 8 0v4"
  })) : /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("rect", {
    x: "5",
    y: "11",
    width: "14",
    height: "10",
    rx: "2"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8 11V7a4 4 0 0 1 7-2"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "pb-csf"
  }, /*#__PURE__*/React.createElement("label", null, "Height"), /*#__PURE__*/React.createElement("div", {
    className: "in"
  }, /*#__PURE__*/React.createElement("input", {
    type: "number",
    min: indSizeRule === 'shape' ? "1" : "0.5",
    max: "10.5",
    step: "0.25",
    value: customH,
    onChange: e => {
      setCustomH(e.target.value);
      const v = parseFloat(e.target.value);
      if (lockAR && contour && dieLockAR > 0 && !isNaN(v)) setCustomW(String(+(v * dieLockAR).toFixed(2)));
      else if (lockAR && !contour && !isNaN(v)) setCustomW(String(+(v * shapeLockRatio()).toFixed(2)));
    },
    onBlur: e => {
      const v = parseFloat(e.target.value);
      if (!isNaN(v)) {
        const cv = Math.min(10.5, Math.max(0.5, v));
        setCustomH(String(cv));
        if (lockAR && contour && dieLockAR > 0) setCustomW(String(+(cv * dieLockAR).toFixed(2)));
        else if (lockAR && !contour) setCustomW(String(+(cv * shapeLockRatio()).toFixed(2)));
      }
      setSizeFixTick(t => t + 1);
    }
  }), /*#__PURE__*/React.createElement("span", null, "in")))), contour && /*#__PURE__*/React.createElement("div", {
    className: "pb-lockhint"
  }, lockAR ? 'Width & height scale together to keep your shape' : 'Ratio unlocked, set each side freely'), indSizeRule && /*#__PURE__*/React.createElement("div", {
    className: "pb-lockhint"
  }, sizeMinNote || indMinText))), /*#__PURE__*/React.createElement(Opt, {
    id: "shape",
    open: openOpt === 'shape',
    onToggle: toggle,
    icon: "M12 3l9 9-9 9-9-9 9-9z",
    k: "Shape",
    v: shapeLabel
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-grid"
  }, (CG ? CG_SHAPES : SHAPES).map(s => /*#__PURE__*/React.createElement("button", {
    key: s.id,
    className: 'pb-pick' + ((CG ? cgShapeKind === s.id : shape === s.id) ? ' on' : ''),
    onClick: () => {
      if (CG) { cgPickShape(s.id); return; }
      setShape(s.id);
      if (!s.roundable) closeSoon();
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "big"
  }, s.big), /*#__PURE__*/React.createElement("span", {
    className: "nm"
  }, s.label)))), shp.roundable ? /*#__PURE__*/React.createElement("div", {
    className: "pb-seg"
  }, /*#__PURE__*/React.createElement("button", {
    className: corners === 'sharp' ? 'on' : '',
    onClick: () => {
      setCorners('sharp');
      closeSoon();
    }
  }, /*#__PURE__*/React.createElement(Ic, {
    d: "M4 20V4h16"
  }), "Sharp"), /*#__PURE__*/React.createElement("button", {
    className: corners === 'round' ? 'on' : '',
    onClick: () => {
      setCorners('round');
      closeSoon();
    }
  }, /*#__PURE__*/React.createElement(Ic, {
    d: "M4 20v-9a7 7 0 0 1 7-7h9"
  }), "Round")) : /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: '12.5px',
      color: 'var(--mut)',
      margin: '10px 2px 0'
    }
  }, shp.hint || 'A clean cut around your art.')), /*#__PURE__*/React.createElement(Opt, {
    id: "border",
    open: openOpt === 'border',
    onToggle: toggle,
    icon: "M4 4h16v16H4zM8 8h8v8H8z",
    k: "Border",
    v: borderLabel
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-grid"
  }, /*#__PURE__*/React.createElement("button", {
    className: 'pb-pick' + (border === 'none' ? ' on' : ''),
    onClick: () => {
      setBorder('none');
      closeSoon();
    },
    style: {
      padding: '8px'
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "pb-bswatch"
  }, /*#__PURE__*/React.createElement("img", {
    src: SLA + 'border-none.png',
    alt: "",
    style: {
      width: '42px',
      height: '42px',
      objectFit: 'contain'
    }
  })), /*#__PURE__*/React.createElement("span", {
    className: "nm"
  }, "None")), BSIZES.map(s => /*#__PURE__*/React.createElement("button", {
    key: s.id,
    className: 'pb-pick' + (border !== 'none' && bsize === s.id ? ' on' : ''),
    onClick: () => {
      setBsize(s.id);
      if (border === 'none') setBorder('white');
      closeSoon();
    },
    style: {
      padding: '8px'
    }
  }, s.pop && /*#__PURE__*/React.createElement("span", {
    className: "pop"
  }, "Popular"), /*#__PURE__*/React.createElement("span", {
    className: "pb-bswatch"
  }, /*#__PURE__*/React.createElement("img", {
    src: s.img,
    alt: "",
    style: {
      width: '42px',
      height: '42px',
      objectFit: 'contain'
    }
  })), /*#__PURE__*/React.createElement("span", {
    className: "nm"
  }, s.label)))), border !== 'none' && /*#__PURE__*/React.createElement("div", {
    className: "pb-swrow"
  }, BORDERS.map(b => /*#__PURE__*/React.createElement("button", {
    key: b.id,
    className: 'pb-swb' + (border === b.id ? ' on' : ''),
    onClick: () => {
      if (b.id === 'custom') {
        pickBorderColor();
      } else {
        setBorder(b.id);
        closeSoon();
      }
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "d",
    style: {
      background: b.id === 'custom' ? border === 'custom' ? borderColor : 'conic-gradient(from 0deg,#ff4d4d,#ffd24d,#4dff88,#4dd2ff,#b04dff,#ff4d4d)' : b.sw
    }
  }), b.label))), (CG && border !== 'none') ? cgBorderNote : null), CG ? (cgFinishShown ? cgOpts.finish : null) : !PRODUCTION_HOST && /*#__PURE__*/React.createElement(Opt, {
    id: "laminate",
    open: openOpt === 'laminate',
    onToggle: toggle,
    icon: "M12 3C7 3 3 7 3 12s4 9 9 9M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18M3.5 9h17M3.5 15h17",
    k: "Laminate",
    v: lamLabel
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-grid"
  }, LAMINATES.map(l => /*#__PURE__*/React.createElement("button", {
    key: l.id,
    className: 'pb-pick' + (laminate === l.id ? ' on' : ''),
    onClick: () => {
      setLaminate(l.id);
      closeSoon();
    }
  }, l.pop && /*#__PURE__*/React.createElement("span", {
    className: "pop"
  }, "Popular"), /*#__PURE__*/React.createElement("span", {
    className: "nm",
    style: {
      fontSize: '14.5px'
    }
  }, l.label), /*#__PURE__*/React.createElement("span", {
    className: "sub"
  }, l.sub))))), /* QUANTITY IS A PURCHASE, NOT A CUT LINE. One approved cut line is one
     approved cut line; how many get printed is the order's business. */
  CG ? cgOpts.qty : PROMO ? promoQtyOpt() : !PRODUCTION_HOST && /*#__PURE__*/React.createElement(Opt, {
    id: "qty",
    open: openOpt === 'qty',
    onToggle: toggle,
    icon: "M4 6h16M4 12h16M4 18h10",
    k: "Quantity",
    v: effQty + ' Stickers'
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-rows"
  }, QTYS.map(q => {
    const rt = slPrice(widthIn, heightIn, q);
    const save = Math.round((1 - rt / q / perUnit10) * 100);
    return /*#__PURE__*/React.createElement("button", {
      key: q,
      className: 'pb-row' + (qtyMode === 'preset' && qty === q ? ' on' : ''),
      onClick: () => {
        setQtyMode('preset');
        setQty(q);
        closeSoon();
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "qn"
    }, q, q === QTY_POP && /*#__PURE__*/React.createElement("span", {
      className: "tag"
    }, "Popular")), /*#__PURE__*/React.createElement("span", {
      className: "qr"
    }, /*#__PURE__*/React.createElement("span", {
      className: "qp"
    }, "$", rt.toFixed(2)), save > 0 && /*#__PURE__*/React.createElement("span", {
      className: "qs"
    }, " Save ", save, "%")));
  }), /*#__PURE__*/React.createElement("button", {
    className: 'pb-row' + (qtyMode === 'custom' ? ' on' : ''),
    onClick: () => {
      if (qtyMode !== 'custom') setCustomQty(String(qty));
      setQtyMode('custom');
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "qn"
  }, "Custom"), /*#__PURE__*/React.createElement("span", {
    className: "qr",
    style: {
      fontSize: '12px',
      color: 'var(--mut)'
    }
  }, qtyMode === 'custom' ? 'editing' : '10 min →'))), qtyMode === 'custom' && /*#__PURE__*/React.createElement("div", {
    className: "pb-num"
  }, /*#__PURE__*/React.createElement("input", {
    type: "number",
    min: "10",
    max: "1000",
    step: "1",
    value: customQty,
    onChange: e => setCustomQty(e.target.value),
    onBlur: e => {
      const n = Math.round(parseFloat(e.target.value) || 0);
      setCustomQty(String(Math.min(1000, Math.max(10, n))));
    }
  }), /*#__PURE__*/React.createElement("span", null, "pcs")))), /*#__PURE__*/React.createElement("div", {
    className: "pb-mini"
  }, !PRODUCTION_HOST && /*#__PURE__*/React.createElement("button", {
    className: "pb-minibtn",
    onClick: startTour
  }, /*#__PURE__*/React.createElement(Ic, {
    d: "M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
  }), "Guide me"), !PRODUCTION_HOST && /*#__PURE__*/React.createElement("button", {
    className: "pb-minibtn",
    onClick: () => setHelpOpen(true)
  }, /*#__PURE__*/React.createElement(Ic, {
    d: "M12 17v.01M12 13.5a2 2 0 0 0 .914-3.782 2 2 0 1 0-2.414 3.03M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z"
  }), "Help"), /*#__PURE__*/React.createElement("button", {
    className: "pb-minibtn",
    onClick: startOver
  }, /*#__PURE__*/React.createElement(Ic, {
    d: "M3 12a9 9 0 1 1 3 6.7M3 21v-5h5"
  }), "Reset")), !PRODUCTION_HOST && /*#__PURE__*/React.createElement("div", {
    className: "card pb-trust"
  }, /*#__PURE__*/React.createElement("span", {
    className: "ic"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12 2l7 3v6c0 5-3.5 8-7 9-3.5-1-7-4-7-9V5l7-3z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9 12l2 2 4-4"
  }))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h4", null, "We've got you"), /*#__PURE__*/React.createElement("p", null, "Every order is reviewed, and you can request a free digital proof before printing right from your cart.")))), /*#__PURE__*/React.createElement("div", {
    className: "pb-col pb-col-center"
  }, qualityCard, /*#__PURE__*/React.createElement("div", {
    className: "pb-hero"
  }, !PRODUCTION_HOST && /*#__PURE__*/React.createElement("div", {
    className: "pb-center-head"
  }, PROMO
    /* THE PROMO'S HEADER (Ryan, 18 Oct 2026; final direction: Apple-style, no photo).
       A centered headline and one line with the price, on plain white. Styles live in
       sections/sl-builder.liquid (promo only). The regular builder keeps its own
       heading (the branch below). */
    ? /*#__PURE__*/React.createElement(React.Fragment, null,
        /*#__PURE__*/React.createElement("div", { className: "slp-bh" },
          /*#__PURE__*/React.createElement("h1", null, "Create your stickers"),
          /*#__PURE__*/React.createElement("p", null, "50 custom stickers for ", /*#__PURE__*/React.createElement("span", { className: "slp-bh-price" }, promoMoney(PROMO.price50)), ". Upload your design to begin.")))
    : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h1", null, CG ? "Create your cart graphics" : "Create your stickers"), /*#__PURE__*/React.createElement("p", null, CG ? "Custom cart graphics for your cart’s front panel. Graphic only." : "Upload. Experiment. Preview."))), !(CG && surface === 'cart') && !dieCut && /*#__PURE__*/React.createElement("div", {
    className: "pb-canvasctrls"
  }, /*#__PURE__*/React.createElement("button", {
    className: "pb-cbtn",
    onClick: () => bumpZoom(0.2),
    disabled: !art
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "11",
    cy: "11",
    r: "7"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M11 8v6M8 11h6M21 21l-4.3-4.3"
  })), "Zoom In"), /*#__PURE__*/React.createElement("button", {
    className: "pb-cbtn",
    onClick: () => bumpZoom(-0.2),
    disabled: !art
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "11",
    cy: "11",
    r: "7"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8 11h6M21 21l-4.3-4.3"
  })), "Zoom Out"), /*#__PURE__*/React.createElement("button", {
    className: "pb-cbtn",
    onClick: fitArt,
    disabled: !art
  }, /*#__PURE__*/React.createElement(Ic, {
    d: "M4 9V5a1 1 0 0 1 1-1h4M20 9V5a1 1 0 0 0-1-1h-4M4 15v4a1 1 0 0 0 1 1h4M20 15v4a1 1 0 0 1-1 1h-4"
  }), "Fit to Canvas")), /*#__PURE__*/React.createElement("div", {
    className: "pb-canvas",
    "data-surface": surface,
    ref: canvasRef
  }, !(CG && surface === 'cart') && art && !dieCut && /*#__PURE__*/React.createElement("div", {
    className: "pb-canvas-tools"
  }, /*#__PURE__*/React.createElement("span", {
    className: "pb-ctool on",
    role: "img",
    title: "Grab: drag to move your artwork",
    "aria-label": "Grab and move artwork"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M18 11V6a1.5 1.5 0 0 0-3 0M15 6V4.5a1.5 1.5 0 0 0-3 0V6M12 6V5a1.5 1.5 0 0 0-3 0v7"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9 12V9.5a1.5 1.5 0 0 0-3 0V14c0 3.3 2.2 6 6 6s6-2.7 6-6v-2"
  }))), /*#__PURE__*/React.createElement("button", {
    className: "pb-ctool",
    onClick: () => setPos({
      x: 0,
      y: 0
    }),
    title: "Center artwork",
    "aria-label": "Center artwork"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.9",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "12",
    cy: "12",
    r: "3.4"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M12 2.5v3.6M12 17.9v3.6M2.5 12h3.6M17.9 12h3.6"
  }))), /*#__PURE__*/React.createElement("button", {
    className: "pb-ctool",
    onClick: () => setPos(p => ({
      x: 0,
      y: p.y
    })),
    title: "Align horizontally (center left–right)",
    "aria-label": "Align horizontally"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.9",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12 3v18"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "5",
    y: "8",
    width: "14",
    height: "8",
    rx: "1.4"
  }))), /*#__PURE__*/React.createElement("button", {
    className: "pb-ctool",
    onClick: () => setPos(p => ({
      x: p.x,
      y: 0
    })),
    title: "Align vertically (center top–bottom)",
    "aria-label": "Align vertically"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.9",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M3 12h18"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "8",
    y: "5",
    width: "8",
    height: "14",
    rx: "1.4"
  }))), /*#__PURE__*/React.createElement("button", {
    className: "pb-ctool",
    onClick: fitArt,
    title: "Fit to canvas",
    "aria-label": "Fit to canvas"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.9",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M4 9V5a1 1 0 0 1 1-1h4M20 9V5a1 1 0 0 0-1-1h-4M4 15v4a1 1 0 0 0 1 1h4M20 15v4a1 1 0 0 1-1 1h-4"
  })))), /*#__PURE__*/React.createElement("div", {
    className: "pb-stage"
  }, processing, !art ? /*#__PURE__*/React.createElement("div", {
    className: 'pb-dropzone-c' + (drag ? ' drag' : ''),
    onClick: () => fileRef.current && fileRef.current.click(),
    onDragOver: e => {
      e.preventDefault();
      setDrag(true);
    },
    onDragLeave: () => setDrag(false),
    onDrop: onDrop,
    style: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '14px',
      padding: '48px 40px',
      border: '2px dashed ' + (drag ? 'var(--blue)' : 'rgba(120,140,170,.4)'),
      borderRadius: '20px',
      cursor: 'pointer',
      background: drag ? 'rgba(46,134,227,.06)' : 'rgba(255,255,255,.5)',
      backdropFilter: 'blur(2px)',
      minWidth: '320px'
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      width: '62px',
      height: '62px',
      borderRadius: '16px',
      background: 'var(--blue)',
      display: 'grid',
      placeItems: 'center',
      color: '#fff',
      boxShadow: '0 12px 24px -8px var(--blue-ring)'
    }
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    width: "28",
    height: "28",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12 16V4M7 9l5-5 5 5"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"
  }))), /*#__PURE__*/React.createElement("span", {
    style: {
      fontWeight: 800,
      fontSize: '18px'
    }
  }, "Drag & drop your file"), /*#__PURE__*/React.createElement("span", {
    style: {
      color: 'var(--mut)',
      fontSize: '14px'
    }
  }, "or ", /*#__PURE__*/React.createElement("b", {
    style: {
      color: 'var(--blue)'
    }
  }, "browse files")), /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: '12px',
      color: 'var(--mut-2)'
    }
  }, SL_UPLOAD_HINT()), uploadBusy && /*#__PURE__*/React.createElement("span", {
    role: "status", "aria-live": "polite",
    style: { marginTop: '10px', fontSize: '13px', fontWeight: 700, color: 'var(--blue)' }
  }, "Opening your artwork…"), !uploadBusy && uploadFail && /*#__PURE__*/React.createElement("span", {
    role: "alert",
    style: { marginTop: '10px', fontSize: '13px', fontWeight: 700, color: '#b42318', maxWidth: '34ch', lineHeight: 1.35 }
  }, uploadFail), !uploadFail && handoffError && /*#__PURE__*/React.createElement("span", {
    style: {
      marginTop: '10px',
      fontSize: '13px',
      fontWeight: 700,
      color: '#b42318',
      maxWidth: '30ch',
      lineHeight: 1.35
    }
  }, handoffError === 'format'
    ? "This photo format isn’t supported yet. Try JPG, PNG, PDF, or SVG."
    : handoffError === 'decode'
    ? "We received the file, but couldn’t open it. Try another copy or format."
    : handoffError === 'chatgpt_expired'
    ? "The sticker you set up in ChatGPT has expired. Your options are still here, so just add your artwork again."
    : handoffError === 'chatgpt_artwork'
    ? "Your sticker options came across from ChatGPT. Add your artwork here to see it."
    : handoffError === 'chatgpt'
    ? "We couldn’t carry your sticker over from ChatGPT. Choose your artwork here to keep going."
    : "We couldn’t carry your file over from the home page. Please choose it again.")) : ((CG && surface === 'cart') ? cgRenderCart() : renderSticker())), (art && uploadFail) && /*#__PURE__*/React.createElement("div", {
    className: "pb-upfailbar", role: "alert"
  }, uploadFail, /*#__PURE__*/React.createElement("button", {
    type: "button", "aria-label": "Dismiss", onClick: e => { e.stopPropagation(); setUploadFail(null); }
  }, "×")), (CG || surface !== 'light') && /*#__PURE__*/React.createElement("span", {
    className: "surf-note"
  }, CG ? cgSurfNote : (SURFACES.find(s => s.id === surface) || {}).label + ' preview · not printed'))),
  /* THE PREVIEW-BACKGROUND LABEL SITS OUTSIDE THE PREVIEW (WG3, 24 Oct 2026): drawn over the stage
     it covered large or zoomed artwork. The in-stage copy above stays in the tree (hidden by CSS)
     so nothing that reads it changes; this caption is the one customers see. */
  (CG || surface !== 'light') && /*#__PURE__*/React.createElement("span", {
    className: "surf-note-out", "aria-hidden": "true"
  }, CG ? cgSurfNote : (SURFACES.find(s => s.id === surface) || {}).label + ' preview · not printed'), /*#__PURE__*/React.createElement("div", {
    className: "pb-surfaces"
  }, (CG ? CG_SURFACES : SURFACES).map(s => /*#__PURE__*/React.createElement("button", {
    key: s.id,
    className: 'pb-surf' + (surface === s.id ? ' on' : ''),
    onClick: () => setSurface(s.id)
  }, /*#__PURE__*/React.createElement("span", {
    className: "dot",
    style: {
      background: s.dot
    }
  }), s.label))), (CG && surface === 'cart') ? cgPanelFields(false) : null, /*#__PURE__*/React.createElement("div", {
    className: "pb-dpibar"
  }, hiRes ? /*#__PURE__*/React.createElement("span", {
    className: "q-ok"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.4",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12 2l7 3v6c0 5-3.5 8-7 9-3.5-1-7-4-7-9V5l7-3z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9 12l2 2 4-4"
  })), " High Quality") : /*#__PURE__*/React.createElement("span", {
    className: "q-warn"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.4",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
  })), " Low Resolution"), /*#__PURE__*/React.createElement("span", {
    className: "sep"
  }), /*#__PURE__*/React.createElement("span", null, dpi == null ? '300 DPI' : Math.min(dpi, 300) + ' DPI'), /*#__PURE__*/React.createElement("span", {
    className: "sep"
  }), /*#__PURE__*/React.createElement("span", null, shapeLabel), halo && /*#__PURE__*/React.createElement("span", {
    className: "sep"
  }), haloNote(null))), /*#__PURE__*/React.createElement("div", {
    className: "pb-col pb-col-right"
  }, /*#__PURE__*/React.createElement("div", {
    className: 'card pb-quality ' + (hiRes ? 'ok' : 'low')
  }, /*#__PURE__*/React.createElement("div", {
    className: "qhead"
  }, hiRes ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.4",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M20 6 9 17l-5-5"
  })), " High Resolution") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.4",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
  })), " Low Resolution")), /*#__PURE__*/React.createElement("div", {
    /* ── PRINT QUALITY, IN ONE LINE ────────────────────────────────────
       Was two padded chips on a row of their own. The number and what it
       means for the print are one fact, read together, so they are one
       line. Nothing about how either is CALCULATED changed. */
    className: "qline"
  }, /*#__PURE__*/React.createElement("b", null, dpi == null ? '300 DPI' : Math.min(dpi, 300) + ' DPI'), /*#__PURE__*/React.createElement("span", {
    className: "qsep"
  }, "\u00B7"), /*#__PURE__*/React.createElement("span", null, hiRes ? 'Ready for print' : 'May print soft')), (hasBgWarn || !hiRes) ? /*#__PURE__*/React.createElement("div", {
    /* ── BACKGROUND IS A SEPARATE CONDITION AND MUST LOOK LIKE ONE ──────
       It used to be a third paragraph inside a warning card, which made a
       neutral observation read as part of a resolution problem. The two are
       unrelated: artwork can be low-resolution and already cut out, or
       perfectly sharp with a white surround. So it sits on its own row under
       a hairline, in the muted ink used for statements of fact, never in the
       warning colour.

       REMOVING the background is still Edit Your Image's job. This reports. */
    className: "qfoot"
  }, hasBgWarn ? /*#__PURE__*/React.createElement("span", {
    className: "qbg"
  }, /*#__PURE__*/React.createElement("span", {
    className: "qdot"
  }), "Background detected") : null, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "pbq-why qdetails",
    onClick: () => setQMore(v => !v),
    "aria-expanded": qMore ? 'true' : 'false'
  }, qMore ? 'Hide details' : 'Details')) : null, (qMore && (hasBgWarn || !hiRes)) ? /*#__PURE__*/React.createElement("div", {
    /* THE COPY IS NOT GONE, IT IS FOLDED. Both sentences are the approved
       wording and are exactly what was on screen before; they simply no
       longer occupy five permanent lines of the right rail. */
    className: "qmore"
  }, !hiRes ? /*#__PURE__*/React.createElement("p", {
    className: "qtip"
  }, "This image is small for a ", longestIn, "\u2033 print. Try a higher-resolution file for crisp edges.") : null, hasBgWarn ? /*#__PURE__*/React.createElement("p", {
    className: "qtip"
  }, "This artwork contains a background. We'll review it before production and contact you if anything needs attention.") : null) : null), /*#__PURE__*/React.createElement("div", {
    className: "card pb-finished"
  }, /*#__PURE__*/React.createElement("h4", null, CG ? "Finished Graphic" : "Finished Sticker"), /*#__PURE__*/React.createElement("div", {
    className: "fsize"
  }, widthIn.toFixed ? widthIn.toFixed(2) : widthIn, " × ", heightIn.toFixed ? heightIn.toFixed(2) : heightIn, /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: '15px',
      color: 'var(--mut)',
      fontWeight: 700
    }
  }, "\xA0in")), /*#__PURE__*/React.createElement("div", {
    className: "frow"
  }, /*#__PURE__*/React.createElement("span", {
    className: "fchip"
  }, shapeLabel), /*#__PURE__*/React.createElement("span", {
    className: "fchip"
  }, CG ? cgMatFin : [matLabel, " · ", lamLabel]), CG ? null : /*#__PURE__*/React.createElement("span", {
    className: "fchip"
  }, effQty, " stickers")), CG ? cgSummaryExtra() : null), /*#__PURE__*/React.createElement("div", {
    className: "card pb-edit"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-edithead"
  }, /*#__PURE__*/React.createElement("h3", null, "Edit Your Image"),
     /* REPLACE LIVES HERE NOW. The same hidden input it has always used — no
        second uploader, no new state — demoted from a tool slot to a secondary
        action, because swapping the file is a different KIND of act from
        editing the one you have. It stays one click away and the card keeps
        its dimensions: this sits on the heading line, which was already
        there. */
     /*#__PURE__*/React.createElement("button", {
       className: "pb-replacelink",
       type: "button",
       onClick: () => fileRef.current && fileRef.current.click(),
       disabled: !art,
       title: "Replace this artwork with a different file"
     }, /*#__PURE__*/React.createElement(Ic, {
       d: "M4 4v6h6M20 20v-6h-6M20 8a8 8 0 0 0-14-3M4 16a8 8 0 0 0 14 3"
     }), "Replace image")),
  /*#__PURE__*/React.createElement("div", {
    className: "pb-editgrid"
  }, !dieCut && /* ROTATE IS REFUSED FOR A DIE CUT, so it is not offered for one.
     `spin()` returns early in contour mode because the preview box comes from
     the UNROTATED artwork and the proof canvas never grows — see its own
     comment. This gate has been here since the contour-crop work and
     `builder-contour-nocrop.test.js` holds its exact shape; the comment is the
     only thing added, after a mapping pass briefly mistook the control for an
     ungated one. */
  /*#__PURE__*/React.createElement("button", {
    className: "pb-etool",
    onClick: () => spin(90),
    disabled: !art
  }, /*#__PURE__*/React.createElement(Ic, {
    d: "M21 12a9 9 0 1 0-3 6.7M21 21v-5h-5"
  }), "Rotate"), /*#__PURE__*/React.createElement("button", {
    className: 'pb-etool' + (flipX ? ' on' : ''),
    onClick: () => setFlipX(f => !f),
    disabled: !art
  }, /*#__PURE__*/React.createElement(Ic, {
    d: "M12 3v18M8 7l-4 5 4 5M16 7l4 5-4 5"
  }), "Flip"), /* ── THE THIRD TOOL IS REMOVE BACKGROUND ──────────────────
     It used to be Replace, with Remove Background as an OPTIONAL fourth that
     only appeared for artwork the engine had already judged removable. So the
     common case showed Rotate / Flip / Replace and background removal was
     nowhere on the screen — present in the code, invisible in the product.

     Now it is permanent and the LABEL carries the truth (see `bgLabel`, which
     both trees read). Replace moved to the card heading: still one click, no
     longer occupying the slot a customer reaches for. */
  /*#__PURE__*/React.createElement("button", {
    className: 'pb-etool pb-bgtool' + (bgOn ? ' on' : '') + (bgBusy ? ' busy' : ''),
    onClick: toggleBg,
    disabled: !art || bgBusy || (bgState === 'none' && !bgOn),
    "aria-pressed": bgOn ? 'true' : 'false',
    title: bgLabel.title
  }, /*#__PURE__*/React.createElement(Ic, {
    d: bgOn ? "M4 4v6h6M20 20v-6h-6M20 8a8 8 0 0 0-14-3M4 16a8 8 0 0 0 14 3"
            : "M4 4h6v6H4zM14 14h6v6h-6zM14 4l6 6M20 4l-6 6M4 14l6 6M10 14l-6 6"
  }), bgLabel.text)), bgState === 'failed' && bgError &&
    /*#__PURE__*/React.createElement("p", {
      className: "pb-bgnote"
    }, bgError), bgOn && /*#__PURE__*/React.createElement("p", {
      className: "pb-bgnote ok"
    }, "Background removed. Your original file is kept and is what we print from if you switch back.")),
  !PRODUCTION_HOST && /*#__PURE__*/React.createElement("div", {
    className: "card pb-protip"
  }, /*#__PURE__*/React.createElement("span", {
    className: "ic"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "currentColor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12 2l2.4 5.9L21 9l-5 4 1.3 7L12 16.9 6.7 20 8 13 3 9l6.6-1.1L12 2z"
  }))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h4", null, "Pro Tip"), /*#__PURE__*/React.createElement("p", null, "Use high-resolution images with a clear subject. Artwork with a transparent background cuts cleanest, but we review every file before printing."))))), /* THE SHOP BAR. Quantity, price, shipping, Save Design and Add to cart —
     everything that takes an order. Not rendered in a production host, so the
     cart path cannot be reached rather than merely being hidden. */
  !PRODUCTION_HOST && /*#__PURE__*/React.createElement("div", {
    className: "pb-bar"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-bar-in"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-bar-qty"
  }, PROMO ? /*#__PURE__*/React.createElement("b", { className: "pb-qty-num" }, PROMO.packQty) : /*#__PURE__*/React.createElement("select", {
    "aria-label": "Quantity",
    value: CG ? (cgQtyText != null ? 'custom' : String(qty)) : qtyMode === 'custom' ? 'custom' : String(qty),
    onChange: e => {
      if (CG) {
        if (e.target.value === 'custom') { setCgQtyText(String(effQty)); setOpenOpt('qty'); }
        else cgSetQty(+e.target.value);
        return;
      }
      if (e.target.value === 'custom') {
        setQtyMode('custom');
        setOpenOpt('qty');
      } else {
        setQtyMode('preset');
        setQty(+e.target.value);
      }
    }
  }, (CG ? CG.QUANTITY.presets : QTYS).map(q => /*#__PURE__*/React.createElement("option", {
    key: q,
    value: q
  }, q)), /*#__PURE__*/React.createElement("option", {
    value: "custom"
  }, (CG ? cgQtyText != null : qtyMode === 'custom') ? effQty : 'Custom')), /*#__PURE__*/React.createElement("span", {
    className: "pb-qty-noun"
  }, CG ? cgNoun(effQty) : (effQty === 1 ? "sticker" : "stickers"))),
  /* THE PRICE SUMMARY (Ryan, 18 Oct 2026): one group. The total is the strongest thing; a genuine
     comparison price (the promo's regular price, only when it is higher) sits small beside it; the line
     beneath carries the per-unit price, or the promo's saving / notice. Same values as before, only
     arranged; nothing here is calculated. */
  /*#__PURE__*/React.createElement("div", {
    className: "pb-bar-total"
  }, /*#__PURE__*/React.createElement("span", {
    className: "k"
  }, "Total"), /*#__PURE__*/React.createElement("span", {
    className: "pb-sum-line"
  }, /*#__PURE__*/React.createElement("span", {
    className: "v"
  }, CG ? cgPriceText : "$" + total.toFixed(2)), PROMO ? promoWas() : null), (PROMO || !(CG && effQty === 1)) && /*#__PURE__*/React.createElement("span", {
    className: "ea"
  }, PROMO ? promoSaveEl() : CG ? cgUnitText + " each" : "$" + (total / effQty).toFixed(2) + " each")), /*#__PURE__*/React.createElement("div", {
    className: "pb-bar-ship"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.6",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M20 6 9 17l-5-5"
  })), " Free proof on request"), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.6",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M20 6 9 17l-5-5"
  })), " Printed in California"), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.6",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M20 6 9 17l-5-5"
  })), CG ? " Standard shipping included" : PROMO ? " Free shipping" : " Ships in 2–3 business days")), /*#__PURE__*/React.createElement("button", {
    className: 'pb-save' + (saved ? ' saved' : ''),
    onClick: openSave
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: saved ? 'currentColor' : 'none',
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M19 21l-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"
  })), saved ? 'Saved' : 'Save Design'), /*#__PURE__*/React.createElement("button", {
    className: "pb-addcart",
    onClick: () => sheetIntent ? openSheet() : setDecision(true),
    /* WHAT WOULD SHIP IS NOT YET DECIDED. While the derivative is being
       checked or built — or while the silhouette that decides the ordered
       size is still being measured from it — the effective artwork can still
       change under the order, so the order cannot be placed. */
    disabled: !art || orderBusy || !cgCanOrder || (!!CG && !!saveStage) || (!!PROMO && (!!promoBlock || !!saveStage)),
    /* a cart graphic has no chooser and no sheet: the button adds to cart */
    ...(CG ? { onClick: () => addToCart(), "aria-describedby": cgBlockMsg ? "cg-block" : undefined } : PROMO ? { onClick: () => addToCart() } : null)
  }, ((CG || PROMO) && saveStage) ? null : /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.4",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M5 12h14M13 6l6 6-6 6"
  })), (CG || PROMO) ? saveBtnContent('Add to cart') : (sheetIntent ? 'Build the sheet' : 'Continue')))), /*#__PURE__*/React.createElement("input", {
    ref: colorRef,
    type: "color",
    value: borderColor,
    onChange: e => {
      setBorderColor(e.target.value);
      setBorder('custom');
    },
    style: {
      display: 'none'
    }
  }), brandMenu && /*#__PURE__*/React.createElement("div", {
    className: "pb-glass-scrim",
    onMouseDown: e => {
      if (e.target === e.currentTarget) setBrandMenu(false);
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-glass-card"
  }, /*#__PURE__*/React.createElement("button", {
    className: "pb-glass-x",
    onClick: () => setBrandMenu(false),
    "aria-label": "Close"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.2",
    strokeLinecap: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M18 6 6 18M6 6l12 12"
  }))), /*#__PURE__*/React.createElement("img", {
    className: "pb-glass-logo",
    src: "https://cdn.shopify.com/s/files/1/0760/6245/7993/files/stickrlablogo-thinborder.png?v=1778899622",
    alt: "Stickr Lab"
  }), /*#__PURE__*/React.createElement("div", {
    className: "pb-glass-menu"
  }, /* Cart Graphics is reached from the product cards (after upload, and on
       the home page), not from this menu. The Cart Graphics builder keeps its
       way back to stickers here. */
  !CG ? null : /*#__PURE__*/React.createElement("button", {
    className: "pb-glass-item",
    onClick: () => cgSwitchProduct(CG ? SL_ROUTE_STICKERS : SL_ROUTE_CART_GRAPHICS)
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.9",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: CG ? "M12 3l9 9-9 9-9-9 9-9z" : "M3 6h18v2H3zM5 8v8h14V8M7.5 20a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3M16.5 20a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3"
  })), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, CG ? "Stickers" : "Cart Graphics"), /*#__PURE__*/React.createElement("em", null, CG ? "Switch to the sticker builder" : "Graphics for a cart’s front panel")), /*#__PURE__*/React.createElement("svg", {
    className: "go",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M9 6l6 6-6 6"
  }))), /*#__PURE__*/React.createElement("button", {
    className: "pb-glass-item",
    onClick: () => guardedNav('/')
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.9",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M3 11l9-8 9 8M5 9.5V21h14V9.5"
  })), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, "Home"), /*#__PURE__*/React.createElement("em", null, "Back to the start")), /*#__PURE__*/React.createElement("svg", {
    className: "go",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M9 6l6 6-6 6"
  }))), /*#__PURE__*/React.createElement("button", {
    className: "pb-glass-item",
    onClick: () => guardedNav('/?view=mysl')
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.9",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z"
  })), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, "My Stickr Lab"), /*#__PURE__*/React.createElement("em", null, "Your saved designs & orders")), /*#__PURE__*/React.createElement("svg", {
    className: "go",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M9 6l6 6-6 6"
  }))), /*#__PURE__*/React.createElement("button", {
    className: "pb-glass-item",
    onClick: () => {
      setBrandMenu(false);
      startOver();
    }
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.9",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12 5v14M5 12h14"
  })), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, "New sticker"), /*#__PURE__*/React.createElement("em", null, "Start a fresh design")), /*#__PURE__*/React.createElement("svg", {
    className: "go",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M9 6l6 6-6 6"
  }))), /*#__PURE__*/React.createElement("button", {
    className: "pb-glass-item",
    // Track Order is live (D-194): /?view=orderstatus. Same leave-the-builder guard as the other items.
    onClick: () => guardedNav('/?view=orderstatus')
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.9",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4zM3 6h18M16 10a4 4 0 0 1-8 0"
  })), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, "Track order"), /*#__PURE__*/React.createElement("em", null, "See where your prints are")), /*#__PURE__*/React.createElement("svg", {
    className: "go",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M9 6l6 6-6 6"
  })))))), tourI >= 0 && tourSteps.length > 0 && !(tourMode === 'builder' && art && !workflow) && /*#__PURE__*/React.createElement(BuilderTour, {
    steps: tourSteps,
    i: Math.min(tourI, tourSteps.length - 1),
    onPrev: () => setTourI(v => Math.max(0, v - 1)),
    onNext: () => setTourI(v => v + 1),
    onClose: () => setTourI(-1)
  }), helpOpen && /*#__PURE__*/React.createElement("div", {
    className: "pb-ordered",
    onMouseDown: e => {
      if (e.target === e.currentTarget) setHelpOpen(false);
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-savecard"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-save-head"
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h3", null, "Need a hand?"), /*#__PURE__*/React.createElement("p", null, "Quick tips for a print-perfect sticker.")), /*#__PURE__*/React.createElement("button", {
    className: "pb-save-x",
    onClick: () => setHelpOpen(false)
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.2",
    strokeLinecap: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M18 6 6 18M6 6l12 12"
  })))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      margin: '4px 0 6px'
    }
  }, [{
    t: 'Upload',
    s: 'Use a PNG, JPG, SVG or PDF. Transparent artwork cuts cleanest. We review every file before printing.',
    d: 'M12 3v12M8 7l4-4 4 4M5 21h14'
  }, {
    t: 'Resolution',
    s: 'Aim for 300 DPI at print size. The quality chip warns you if it may print soft.',
    d: 'M3 3h18v18H3zM3 15l5-5 4 4 3-3 5 5'
  }, {
    t: 'Size & border',
    s: 'Longest side up to 10.5″. Add a white border so it’s easy to peel.',
    d: 'M4 4h16v16H4zM8 8h8v8H8z'
  }, {
    t: 'Free proof',
    s: 'Want a proof first? Tick “Add a free digital proof” in your cart (free account needed). We email it, and your order waits for your approval.',
    d: 'M4 4h16v12H4zM8 20h8M12 16v4M8 9l2.5 2.5L16 6'
  }].map((it, i) => /*#__PURE__*/React.createElement("div", {
    key: i,
    style: {
      display: 'flex',
      gap: '12px',
      alignItems: 'flex-start'
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      flex: '0 0 34px',
      width: '34px',
      height: '34px',
      borderRadius: '10px',
      background: 'var(--blue-l)',
      color: 'var(--blue)',
      display: 'grid',
      placeItems: 'center'
    }
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.9",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    style: {
      width: '18px',
      height: '18px'
    }
  }, /*#__PURE__*/React.createElement("path", {
    d: it.d
  }))), /*#__PURE__*/React.createElement("span", {
    style: {
      minWidth: 0
    }
  }, /*#__PURE__*/React.createElement("b", {
    style: {
      display: 'block',
      fontSize: '14.5px',
      fontWeight: 800,
      color: 'var(--ink)'
    }
  }, it.t), /*#__PURE__*/React.createElement("span", {
    style: {
      display: 'block',
      fontSize: '13px',
      lineHeight: 1.5,
      color: 'var(--mut)',
      fontWeight: 550,
      marginTop: '2px'
    }
  }, it.s))))), /*#__PURE__*/React.createElement("div", {
    className: "pb-save-foot"
  }, /*#__PURE__*/React.createElement("a", {
    className: "pb-save-cancel",
    href: "#",
    style: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      textDecoration: 'none'
    }
  }, "Read the FAQ"), /*#__PURE__*/React.createElement("a", {
    className: "pb-save-confirm",
    href: "#",
    style: {
      textDecoration: 'none'
    }
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("rect", {
    x: "3",
    y: "5",
    width: "18",
    height: "14",
    rx: "2"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m3 7 9 6 9-6"
  })), "Contact us")))), saveOpen && /*#__PURE__*/React.createElement("div", {
    className: "pb-ordered",
    onMouseDown: e => {
      if (e.target === e.currentTarget) setSaveOpen(false);
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-savecard"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-save-head"
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h3", null, "Save to your lab"), /*#__PURE__*/React.createElement("p", null, "Name your design so you can find it later.")), /*#__PURE__*/React.createElement("button", {
    className: "pb-save-x",
    onClick: () => setSaveOpen(false)
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.2",
    strokeLinecap: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M18 6 6 18M6 6l12 12"
  })))), /*#__PURE__*/React.createElement("div", {
    className: "pb-save-preview"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-save-thumb",
    style: contour ? {
      background: 'transparent'
    } : {
      background: border === 'none' ? faceColorCss : borderCss,
      padding: (border === 'none' ? 6 : 8) + 'px',
      borderRadius: contour ? '12px' : shpR
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: '100%',
      height: '100%',
      borderRadius: contour ? '8px' : innerR,
      background: contour ? 'transparent' : faceColorCss,
      display: 'grid',
      placeItems: 'center',
      overflow: 'hidden'
    }
  }, shownArt ? /*#__PURE__*/React.createElement("img", {
    src: displayArt,
    alt: "",
    style: {
      maxWidth: '86%',
      maxHeight: '86%',
      filter: contour ? contourFilter : 'none'
    }
  }) : null)), /*#__PURE__*/React.createElement("div", {
    className: "pb-save-fields"
  }, /*#__PURE__*/React.createElement("label", {
    className: "pb-save-label"
  }, "Design name"), /*#__PURE__*/React.createElement("input", {
    className: "pb-save-input",
    autoFocus: true,
    value: saveName,
    maxLength: 40,
    onChange: e => setSaveName(e.target.value),
    onKeyDown: e => {
      if (e.key === 'Enter') confirmSave();
    },
    placeholder: SL_UNTITLED
  }), /*#__PURE__*/React.createElement("div", {
    className: "pb-save-spec"
  }, shapeLabel, " · ", matLabel, " · ", effQty, " pcs"))), saveError && /*#__PURE__*/React.createElement("div", {
    /* SAVING CAN NOW FAIL, SO IT CAN NOW SAY SO. Signing in and waiting are
       not failures and do not read like them. */
    className: 'pb-save-msg' + (saveError === 'signin' || saveError === 'waiting' ? ' info' : ' err'),
    role: "status"
  }, saveError === 'signin'
      ? 'Sign in to save this design to your account — your design stays right here.'
      : saveError === 'waiting'
      ? 'Waiting for you to sign in. Come back to this tab when you’re done.'
      : saveError === 'popup'
      ? 'Your browser blocked the sign-in window. Allow pop-ups for this site, or sign in from the account menu and press Save again.'
      : saveError === 'no_artwork'
      ? 'We couldn’t read your artwork file. Please add it again and try once more.'
      : 'Sorry — we couldn’t save that just now. Please try again.'), /*#__PURE__*/React.createElement("div", {
    className: "pb-save-foot"
  }, /*#__PURE__*/React.createElement("button", {
    className: "pb-save-cancel",
    onClick: () => setSaveOpen(false)
  }, "Cancel"), (saveError === 'signin' || saveError === 'popup')
    ? /*#__PURE__*/React.createElement("button", {
        className: "pb-save-confirm",
        onClick: saveSignIn
      }, "Sign in to save")
    : /*#__PURE__*/React.createElement("button", {
    className: "pb-save-confirm",
    /* A SECOND CLICK MUST NOT BECOME A SECOND UPLOAD. */
    disabled: saveBusy || saveError === 'waiting',
    onClick: confirmSave
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M19 21l-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"
  })), saveBusy ? 'Saving…' : 'Save')))), pendingNav && !saveOpen && /*#__PURE__*/React.createElement("div", {
    className: "pb-ordered",
    onMouseDown: e => {
      if (e.target === e.currentTarget) setPendingNav(null);
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-guardcard"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-guard-ic"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M19 21l-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"
  }))), /*#__PURE__*/React.createElement("h3", null, "Save your design first?"), /*#__PURE__*/React.createElement("p", null, "You’ve got a sticker in progress. Leaving now will discard it. Save it to your lab so you can pick up right where you left off."), /*#__PURE__*/React.createElement("div", {
    className: "pb-guard-actions"
  }, /*#__PURE__*/React.createElement("button", {
    className: "pb-guard-save",
    onClick: openSave
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M19 21l-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"
  })), "Save design"), /*#__PURE__*/React.createElement("button", {
    className: "pb-guard-leave",
    onClick: () => {
      const d = pendingNav;
      setPendingNav(null);
      navTo(d);
    }
  }, "Leave without saving"), /*#__PURE__*/React.createElement("button", {
    className: "pb-guard-stay",
    onClick: () => setPendingNav(null)
  }, "Keep editing")))), choosingWorkflow && /*#__PURE__*/React.createElement(WorkflowChooser, {
    art: art,
    onPick: chooseWorkflow,
    onBack: () => {
      setArt(null);
      setArtRes(null);
      resetArtState();
    }
  }), decision && !PRODUCTION_HOST && !sheetOpen && !ordered && /*#__PURE__*/React.createElement("div", {
    className: "pb-decide",
    onMouseDown: e => {
      if (e.target === e.currentTarget) setDecision(false);
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-decide-card"
  }, /*#__PURE__*/React.createElement("button", {
    className: "pb-decide-close",
    onClick: () => setDecision(false),
    "aria-label": "Close"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M18 6 6 18M6 6l12 12"
  }))), /*#__PURE__*/React.createElement("span", {
    className: "pb-decide-badge"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.6",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M20 6 9 17l-5-5"
  })), " Your sticker is ready"), /*#__PURE__*/React.createElement("h2", null, "What would you like to do next?"), /*#__PURE__*/React.createElement("p", {
    className: "pb-decide-sub"
  }, "No rush to check out. Keep creating, or turn this design into a full sticker sheet."), /*#__PURE__*/React.createElement("div", {
    className: "pb-decide-grid"
  }, /*#__PURE__*/React.createElement("button", {
    className: "pb-dcard primary" + (saveStage ? " is-saving" : ""),
    disabled: !!saveStage,
    "aria-busy": saveStage ? "true" : "false",
    onClick: () => {
      /* The decision card is NOT dismissed first any more. Hiding it and then
         doing several seconds of work looked like the design had been thrown
         away; the card now stays put and shows the saving state on itself. */
      addToCart();
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "pb-dtag"
  }, saveStage ? "Saving" : "Fastest"), /*#__PURE__*/React.createElement("span", {
    className: "pb-dic"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "9",
    cy: "21",
    r: "1.5"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "18",
    cy: "21",
    r: "1.5"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3 4h2l2.4 12.4a1.5 1.5 0 0 0 1.5 1.2h8.7a1.5 1.5 0 0 0 1.5-1.2L22 8H6"
  }))), /*#__PURE__*/React.createElement("h3", null, "Order this sticker"), /*#__PURE__*/React.createElement("p", null, "Add it to your cart at the size and quantity you chose."), cutReviewNote(), /*#__PURE__*/React.createElement("span", {
    className: "pb-dgo"
  }, "Add to cart ", /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.4",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M5 12h14M13 6l6 6-6 6"
  })))), /*#__PURE__*/React.createElement("button", {
    className: "pb-dcard",
    onClick: () => {
      setDecision(false);
      openSheet();
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "pb-dic"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M4 4h16v16H4zM4 9h16M4 14h16M9 4v16M14 4v16"
  }))), /*#__PURE__*/React.createElement("h3", null, "Turn into a Sticker Sheet"), /*#__PURE__*/React.createElement("p", null, "Fill an 11″ sheet with copies of this design. We lay it out for you."), /*#__PURE__*/React.createElement("span", {
    className: "pb-dgo"
  }, "Open sheet ", /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.4",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M5 12h14M13 6l6 6-6 6"
  })))), /*#__PURE__*/React.createElement("button", {
    className: "pb-dcard",
    onClick: () => {
      setDecision(false);
      startOver();
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "pb-dic"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12 5v14M5 12h14"
  }))), /*#__PURE__*/React.createElement("h3", null, "Create another design"), /*#__PURE__*/React.createElement("p", null, "Start a fresh sticker. Your work stays saved in your lab."), /*#__PURE__*/React.createElement("span", {
    className: "pb-dgo"
  }, "New design ", /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.4",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M5 12h14M13 6l6 6-6 6"
  }))))))), sheetOpen && !PRODUCTION_HOST && window.SheetBuilder && /*#__PURE__*/React.createElement(window.SheetBuilder, {
    design: {
      url: sheetArt || displayArt || shownArt,
      name: saveName || 'Your design',
      circle: shape === 'circle'
    },
    widthIn: widthIn,
    heightIn: heightIn,
    cutWidthIn: sheetCutW,
    cutHeightIn: sheetCutH,
    sheetSizeId: sheetSizeId,
    numSheets: numSheets,
    onSheetSize: chooseSheetSize,
    onNumSheets: setNumSheets,
    onBack: () => setSheetOpen(false),
    onOrder: orderSheets
  }), ordered && !PRODUCTION_HOST && /*#__PURE__*/React.createElement("div", {
    className: "pb-ordered"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-ordercard"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-orderchk"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.6",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M20 6 9 17l-5-5"
  }))), /*#__PURE__*/React.createElement("h3", null, "Added to your cart"), /*#__PURE__*/React.createElement("p", {
    className: "pb-ordersub"
  }, "Nice work. Here's what you made."), /*#__PURE__*/React.createElement("div", {
    className: "pb-orderitem"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-orderthumb",
    style: {
      background: border === 'none' ? '#fff' : borderCss,
      padding: (border === 'none' ? 6 : Math.max(4, Math.round(borderPx * 0.5))) + 'px',
      borderRadius: contour ? '14px' : shpR
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: '100%',
      height: '100%',
      borderRadius: contour ? '10px' : innerR,
      background: faceColorCss,
      display: 'grid',
      placeItems: 'center',
      overflow: 'hidden'
    }
  }, shownArt ? /*#__PURE__*/React.createElement("img", {
    src: displayArt,
    alt: "",
    style: {
      maxWidth: '82%',
      maxHeight: '82%',
      filter: contour ? contourFilter : 'none'
    }
  }) : /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: '11px',
      color: 'var(--mut)'
    }
  }, "Art to follow"))), /*#__PURE__*/React.createElement("div", {
    className: "pb-ordermeta"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-ordername"
  }, CG ? "Cart Graphics · Graphic only" : PROMO ? (PROMO.packQty + "-sticker pack") : "Custom Sticker"), /*#__PURE__*/React.createElement("div", {
    className: "pb-orderspec"
  }, shapeLabel, " · ", widthIn, "″×", heightIn, "″ · ", CG ? cgMatFin : [matLabel, " · ", lamLabel]), /*#__PURE__*/React.createElement("div", {
    className: "pb-orderprice"
  }, "$", (CG ? cgTotal : total).toFixed(2), " ", /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: '12px',
      color: 'var(--mut)',
      fontWeight: 600
    }
  }, "· ", effQty, " pcs")))), /*#__PURE__*/React.createElement("button", {
    className: "pb-checkout",
    onClick: goToCart
  }, "Go to cart ", /*#__PURE__*/React.createElement(Ic, {
    d: "M5 12h14M13 6l6 6-6 6",
    w: "2.2"
  })), /*#__PURE__*/React.createElement("button", {
    className: "pb-buildmore",
    onClick: startOver
  }, CG ? "Build another graphic" : "Build another sticker"), /*#__PURE__*/React.createElement("p", {
    className: "pb-ordernote"
  }, CG ? "Your cart graphic is in your cart. Building another won’t remove it." : "Your sticker is in your cart. Building another won’t remove it."), /*#__PURE__*/React.createElement("p", {
    className: "pb-ordernote"
  }, "Want a free digital proof? Tick the box on any design in your cart."))));
}

// Left-column collapsible option row
function Opt({
  id,
  open,
  onToggle,
  icon,
  k,
  v,
  children
}) {
  const ref = React.useRef(null);
  // When an option opens, reveal the whole section so no one has to scroll down for it.
  React.useEffect(() => {
    if (!open || !ref.current) return;
    const t = setTimeout(() => {
      try {
        ref.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      } catch (e) {
        try {
          ref.current.scrollIntoView();
        } catch (e2) {}
      }
    }, 90);
    return () => clearTimeout(t);
  }, [open]);
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    className: 'pb-opt' + (open ? ' open' : '')
  }, /*#__PURE__*/React.createElement("button", {
    className: "pb-opt-head",
    onClick: () => onToggle(id)
  }, /*#__PURE__*/React.createElement("span", {
    className: "pb-opt-ic"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.9",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: icon
  }))), /*#__PURE__*/React.createElement("span", {
    className: "pb-opt-tx"
  }, /*#__PURE__*/React.createElement("span", {
    className: "k"
  }, k), /*#__PURE__*/React.createElement("span", {
    className: "v"
  }, v)), /*#__PURE__*/React.createElement("span", {
    className: "pb-opt-chev"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.4",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M9 6l6 6-6 6"
  })))), open && /*#__PURE__*/React.createElement("div", {
    className: "pb-opt-body"
  }, children));
}

// Guided mini tour: spotlights key areas of the builder with a bubble.
function BuilderTour({
  steps,
  i,
  onPrev,
  onNext,
  onClose
}) {
  const [box, setBox] = React.useState(null);
  React.useLayoutEffect(() => {
    const measure = () => {
      const el = document.querySelector(steps[i].sel);
      if (!el) {
        setBox(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setBox({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height
      });
    };
    measure();
    const id = setTimeout(measure, 60); // settle after any layout change
    const iv = setInterval(measure, 250); // keep the spotlight glued as content changes (e.g. after upload)
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      clearTimeout(id);
      clearInterval(iv);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [i, steps]);
  const last = i === steps.length - 1;
  const pad = 8;
  const hole = box ? {
    top: box.top - pad + 'px',
    left: box.left - pad + 'px',
    width: box.width + pad * 2 + 'px',
    height: box.height + pad * 2 + 'px'
  } : {
    top: '50%',
    left: '50%',
    width: '0px',
    height: '0px'
  };
  let bubble;
  if (box) {
    const bw = 300,
      vw = window.innerWidth,
      vh = window.innerHeight,
      place = steps[i].place || 'auto';
    let left, top;
    const spaceBelow = vh - (box.top + box.height);
    const wantTop = place === 'top' || place === 'auto' && spaceBelow < 210;
    if (place === 'right' && box.left + box.width + bw + 24 < vw) {
      left = box.left + box.width + pad + 14;
      top = Math.max(16, box.top);
    } else if (place === 'left' && box.left - bw - 24 > 0) {
      left = box.left - bw - pad - 14;
      top = Math.max(16, box.top);
    } else {
      left = Math.min(Math.max(box.left, 16), vw - bw - 16);
      top = wantTop ? box.top - pad - 14 - 200 : box.top + box.height + pad + 14;
    }
    top = Math.max(14, Math.min(top, vh - 210));
    left = Math.min(Math.max(left, 16), vw - bw - 16);
    bubble = {
      left: left + 'px',
      top: top + 'px'
    };
  } else {
    bubble = {
      left: '50%',
      top: '50%',
      transform: 'translate(-50%,-50%)'
    };
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "pb-tour"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-tour-hole",
    style: hole
  }), /*#__PURE__*/React.createElement("div", {
    className: "pb-tour-bubble",
    style: bubble
  }, /*#__PURE__*/React.createElement("span", {
    className: "pb-tour-step"
  }, "Step ", i + 1, " of ", steps.length), /*#__PURE__*/React.createElement("h4", null, steps[i].title), /*#__PURE__*/React.createElement("p", null, steps[i].body), /*#__PURE__*/React.createElement("div", {
    className: "pb-tour-foot"
  }, /*#__PURE__*/React.createElement("span", {
    className: "pb-tour-dots"
  }, steps.map((_, k) => k < i ? /*#__PURE__*/React.createElement("span", {
    key: k,
    className: "pb-tour-dot done"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "3.2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M20 6 9 17l-5-5"
  }))) : /*#__PURE__*/React.createElement("i", {
    key: k,
    className: k === i ? 'on' : ''
  }))), /*#__PURE__*/React.createElement("div", {
    className: "pb-tour-btns"
  }, !last && /*#__PURE__*/React.createElement("button", {
    className: "pb-tour-skip",
    onClick: onClose
  }, "Skip"), i > 0 && /*#__PURE__*/React.createElement("button", {
    className: "pb-tour-back",
    onClick: onPrev
  }, "Back"), /*#__PURE__*/React.createElement("button", {
    className: "pb-tour-next",
    onClick: last ? onClose : onNext
  }, last ? 'Done' : 'Next')))));
}

/* THE WHITE CART, ONE DRAWING. The Cart Graphics builder's cart preview
   (cgRenderCart) and the "How should we make it?" Cart Graphics card both draw
   it from here, so the card shows the cart the customer will see in the
   builder. Geometry in inches around a panel of pw x ph (Claude Design handoff
   v2, 20 Oct 2026; the same numbers as cartgfx-lab.css): counter 3 in deep
   across the full width; body 2 in in from each end, with a 3 in frame round
   the panel and a 3 in kick below it; wheels min(7, (w + 6) / 3.4) in. Total
   (w + 10) x (h + 16), plus room for the floor shadow. Decoration only:
   nothing here is measured, ordered or printed. */
function slWhiteCartGeometry(pw, ph) {
  const W = pw + 10, H = ph + 16, floorIn = 1.4;
  const d = Math.min(7, (pw + 6) / 3.4);
  return {
    width: W, height: H + floorIn,
    counter: { x: 0, y: 0, w: W, h: 3 },
    body: { x: 2, y: 3, w: W - 4, h: ph + 6, r: 0.8 },
    panel: { x: 5, y: 6, w: pw, h: ph },
    wheels: [{ cx: 4 + d / 2, cy: ph + 9 + d / 2, r: d / 2 }, { cx: W - 4 - d / 2, cy: ph + 9 + d / 2, r: d / 2 }],
    floor: { cx: W / 2, cy: H + 0.2, rx: W * 0.46, ry: 1 }
  };
}
/* The cart's SVG content for geometry g. `idp` prefixes the gradient ids, so
   two carts on one page never share them. `panelContent`, when given, is drawn
   on the panel, under the counter's shadow. */
function slWhiteCartParts(g, idp, panelContent) {
  const e = React.createElement;
  const n = v => +(+v).toFixed(3);
  const b = g.body, r = b.r;
  const d = 'M' + n(b.x) + ' ' + n(b.y) + 'H' + n(b.x + b.w) + 'V' + n(b.y + b.h - r) +
    'Q' + n(b.x + b.w) + ' ' + n(b.y + b.h) + ' ' + n(b.x + b.w - r) + ' ' + n(b.y + b.h) +
    'H' + n(b.x + r) + 'Q' + n(b.x) + ' ' + n(b.y + b.h) + ' ' + n(b.x) + ' ' + n(b.y + b.h - r) + 'Z';
  return e(React.Fragment, null,
    e("defs", null,
      e("linearGradient", { id: idp + "Top", x1: "0", y1: "0", x2: "0", y2: "1" },
        e("stop", { offset: "0", stopColor: "#FFFFFF" }), e("stop", { offset: "1", stopColor: "#E4E8EE" })),
      e("linearGradient", { id: idp + "Body", x1: "0", y1: "0", x2: "0", y2: "1" },
        e("stop", { offset: "0", stopColor: "#F7F8FA" }), e("stop", { offset: "1", stopColor: "#E3E7ED" })),
      e("linearGradient", { id: idp + "TopShade", x1: "0", y1: "0", x2: "0", y2: "1" },
        e("stop", { offset: "0", stopColor: "#0B111F", stopOpacity: "0.30" }), e("stop", { offset: "1", stopColor: "#0B111F", stopOpacity: "0" })),
      e("linearGradient", { id: idp + "Kick", x1: "0", y1: "0", x2: "0", y2: "1" },
        e("stop", { offset: "0", stopColor: "#0B111F", stopOpacity: "0" }), e("stop", { offset: "1", stopColor: "#0B111F", stopOpacity: "0.14" })),
      e("radialGradient", { id: idp + "Wheel" },
        e("stop", { offset: "0", stopColor: "#C9D0DA" }), e("stop", { offset: "0.18", stopColor: "#C9D0DA" }),
        e("stop", { offset: "0.20", stopColor: "#2A3242" }), e("stop", { offset: "0.62", stopColor: "#2A3242" }),
        e("stop", { offset: "0.64", stopColor: "#151B27" }), e("stop", { offset: "1", stopColor: "#151B27" })),
      e("radialGradient", { id: idp + "Floor" },
        e("stop", { offset: "0", stopColor: "#0B111F", stopOpacity: "0.38" }), e("stop", { offset: "1", stopColor: "#0B111F", stopOpacity: "0" }))),
    e("ellipse", { cx: n(g.floor.cx), cy: n(g.floor.cy), rx: n(g.floor.rx), ry: n(g.floor.ry), fill: "url(#" + idp + "Floor)" }),
    /* the white body, rounded at the foot, with a soft kick-plate shadow */
    e("path", { d: d, fill: "url(#" + idp + "Body)", stroke: "rgba(11,17,31,.16)", strokeWidth: "0.12" }),
    e("rect", { x: n(b.x), y: n(b.y + b.h - 2.2), width: n(b.w), height: 2.2, fill: "url(#" + idp + "Kick)" }),
    /* the wheels hang below the body */
    g.wheels.map((wh, i) => e("circle", { key: 'w' + i, cx: n(wh.cx), cy: n(wh.cy), r: n(wh.r), fill: "url(#" + idp + "Wheel)" })),
    /* the front panel: a faint recess the graphic sits on */
    e("rect", { x: n(g.panel.x), y: n(g.panel.y), width: n(g.panel.w), height: n(g.panel.h),
      fill: "#EEF1F5", stroke: "rgba(11,17,31,.18)", strokeWidth: "0.12" }),
    panelContent || null,
    /* the counter, with its shadow falling on the body */
    e("rect", { x: n(g.body.x), y: n(g.counter.h), width: n(g.body.w), height: 1.1, fill: "url(#" + idp + "TopShade)" }),
    e("rect", { x: n(g.counter.x), y: n(g.counter.y), width: n(g.counter.w), height: n(g.counter.h), rx: 0.25,
      fill: "url(#" + idp + "Top)", stroke: "rgba(11,17,31,.16)", strokeWidth: "0.12" }));
}
/* The Cart Graphics card's picture: the white cart on its default 40 x 24 in
   panel (SLCartGraphics PANEL, the builder's opening panel), with the
   customer's whole upload CONTAINED in the panel: centred, aspect kept, as
   large as fits inside a small inset, and clipped to the panel so nothing can
   reach the frame, counter or wheels. Illustration only: it never changes the
   ordered size, the price, the print file or the builder's own placement,
   which follows the graphic's real inches. */
const SL_CARD_PANEL = { w: 40, h: 24, inset: 1.6 };
function slCartCardSvg(url) {
  const e = React.createElement;
  const g = slWhiteCartGeometry(SL_CARD_PANEL.w, SL_CARD_PANEL.h);
  const p = g.panel, i = SL_CARD_PANEL.inset;
  const art = url ? e(React.Fragment, null,
    e("clipPath", { id: "wfCartPanel" }, e("rect", { x: p.x, y: p.y, width: p.w, height: p.h })),
    e("image", { className: "wf-cart-img", href: url, x: p.x + i, y: p.y + i, width: p.w - 2 * i, height: p.h - 2 * i,
      preserveAspectRatio: "xMidYMid meet", clipPath: "url(#wfCartPanel)" })) : null;
  return e("svg", { viewBox: '0 0 ' + g.width + ' ' + +g.height.toFixed(3), "aria-hidden": "true", focusable: "false",
      preserveAspectRatio: "xMidYMid meet", style: { display: 'block', width: '100%', height: '100%' } },
    slWhiteCartParts(g, 'wfCart', art));
}

/* THE STICKER SHEET CARD'S PICTURE. Copies of the customer's upload on a
   tilted sheet, laid out from the upload's real aspect ratio. Illustration
   only: the sheet builder's own layout and quantity, the production sheet and
   the price are untouched and never read here.

   Units are the old CSS card's pixels: a 150 x 176 sheet, 12 in from its
   edges, 8 between stickers. Each sticker is the image plus a 3 white margin,
   and its dashed kiss cut is drawn INSIDE that box, so the box is the
   sticker's whole footprint. The grid is centred, so opposite outer margins
   are equal and the gaps are even. The image is drawn at exactly its own
   aspect (and `meet` besides), so it is never cropped or stretched.

   HOW MANY COPIES. Six when the artwork reads clearly; fewer, larger ones for
   extreme shapes. Counts are tried 6, 4, 3, 2, each in the grid that makes its
   stickers largest; the first whose image keeps a short side of at least
   `minShort` (sheet units, so every screen shows the same count) wins. If none
   does (a very long banner is limited by the sheet's width whatever the
   count), the count with the largest stickers wins, the fewer on a tie. */
const SL_SHEET_CARD = { w: 150, h: 176, pad: 12, gap: 8, margin: 3, cut: 1.5, tilt: -3, counts: [6, 4, 3, 2], minShort: 24 };
function slSheetCardLayout(aspect) {
  const S = SL_SHEET_CARD;
  const a = (+aspect > 0 && isFinite(+aspect)) ? +aspect : 1;
  const aw = a >= 1 ? 1 : a, ah = a >= 1 ? 1 / a : 1;          // the image's shape, longest side 1
  const uw = S.w - 2 * S.pad, uh = S.h - 2 * S.pad;             // the sheet's usable area
  const fit = n => {                                            // the largest stickers n copies can have
    let b = null;
    for (let c = 1; c <= n; c++) {
      if (n % c) continue;
      const r = n / c;
      const s = Math.min(((uw - (c - 1) * S.gap) / c - 2 * S.margin) / aw, ((uh - (r - 1) * S.gap) / r - 2 * S.margin) / ah);
      if (s > 0 && (!b || s > b.s + 1e-9)) b = { c: c, r: r, s: s };
    }
    return b;
  };
  const fits = S.counts.map(fit).filter(Boolean);
  let best = fits.find(f => f.s * Math.min(aw, ah) >= S.minShort);
  if (!best) best = fits.reduce((p, f) => (f.s >= p.s - 1e-9 ? f : p));     // later = fewer copies wins a tie
  const iw = best.s * aw, ih = best.s * ah;                     // image size
  const bw = iw + 2 * S.margin, bh = ih + 2 * S.margin;         // sticker footprint, cut line included
  const gw = best.c * bw + (best.c - 1) * S.gap, gh = best.r * bh + (best.r - 1) * S.gap;
  const x0 = S.pad + (uw - gw) / 2, y0 = S.pad + (uh - gh) / 2;
  const stickers = [];
  for (let r = 0; r < best.r; r++) for (let c = 0; c < best.c; c++) {
    stickers.push({ x: x0 + c * (bw + S.gap), y: y0 + r * (bh + S.gap), w: bw, h: bh,
      img: { x: x0 + c * (bw + S.gap) + S.margin, y: y0 + r * (bh + S.gap) + S.margin, w: iw, h: ih } });
  }
  return { cols: best.c, rows: best.r, count: best.c * best.r, stickers: stickers, imageW: iw, imageH: ih };
}
function slSheetCardSvg(url, aspect) {
  const e = React.createElement, S = SL_SHEET_CARD;
  const L = slSheetCardLayout(aspect);
  const n = v => +(+v).toFixed(3);
  /* The frame holds the TILTED sheet and its shadow, so the whole sheet stays
     inside the card's picture area whatever the box's shape. */
  const t = Math.abs(S.tilt) * Math.PI / 180;
  const bw = S.w * Math.cos(t) + S.h * Math.sin(t), bh = S.w * Math.sin(t) + S.h * Math.cos(t);
  const m = 16, cx = S.w / 2, cy = S.h / 2;
  const vb = [cx - bw / 2 - m, cy - bh / 2 - m * 0.75, bw + 2 * m, bh + 2 * m];
  const rr = Math.min(6, 0.2 * Math.min(L.stickers[0].w, L.stickers[0].h));
  return e("svg", { viewBox: vb.map(n).join(' '), "aria-hidden": "true", focusable: "false",
      preserveAspectRatio: "xMidYMid meet", style: { display: 'block', width: '100%', height: '100%', overflow: 'visible' } },
    e("defs", null, e("filter", { id: "wfSheetShadow", x: "-20%", y: "-20%", width: "140%", height: "150%" },
      e("feDropShadow", { dx: "0", dy: "7", stdDeviation: "6", floodColor: "#0F172A", floodOpacity: "0.28" }))),
    e("g", { transform: 'rotate(' + S.tilt + ' ' + cx + ' ' + cy + ')' },
      e("rect", { x: 0, y: 0, width: S.w, height: S.h, rx: 10, fill: "#fff", stroke: "#E9EDF3", strokeWidth: 1, filter: "url(#wfSheetShadow)" }),
      L.stickers.map((s, i) => e("g", { key: i, className: "wf-sheet-sticker" },
        e("rect", { x: n(s.x + S.cut / 2), y: n(s.y + S.cut / 2), width: n(s.w - S.cut), height: n(s.h - S.cut), rx: n(rr),
          fill: "#fff", strokeWidth: S.cut, strokeDasharray: "3 2.2", style: { stroke: 'var(--blue, #2E86E3)' } }),
        url && e("image", { href: url, x: n(s.img.x), y: n(s.img.y), width: n(s.img.w), height: n(s.img.h), preserveAspectRatio: "xMidYMid meet" })))));
}
/* The upload's aspect ratio, read once from the picture itself (the art
   object carries only its URL). Square until it is known. */
function useSlImageAspect(url) {
  const [aspect, setAspect] = React.useState(1);
  React.useEffect(() => {
    if (!url) return undefined;
    let live = true;
    const im = new Image();
    im.onload = () => { if (live && im.naturalWidth > 0 && im.naturalHeight > 0) setAspect(im.naturalWidth / im.naturalHeight); };
    im.src = url;
    return () => { live = false; };
  }, [url]);
  return aspect;
}

/* ICJ-MINI (Ryan, 26 Oct 2026): the chooser's thumbnail becomes ONE tiny row of six miniature objects, each
   wearing the customer's actual artwork: a water bottle, a shipping box, a drink cup, the die-cut sticker itself,
   a laptop and a skateboard. Examples of what the art could become; nothing here is a product or a choice.
   Every object reuses the SAME small picture (the card's bordered sticker, about 360 px, or the upload until it is
   ready), so the browser decodes it once; no network request, no full-resolution copy. The centre sticker stays
   the one <img> in .pb-choose-thumb, so the page transition (sl-page-transition.js chooserArt) and the guided tour
   still land on it. Static after a short entrance; reduced motion = no entrance. /ICJ-MINI */
function slMiniObj(kind, src, i) {
  const e = React.createElement;
  const art = (x, y, w, h, r) => e("image", { href: src, x: x, y: y, width: w, height: h, preserveAspectRatio: "xMidYMid meet",
    style: r ? { transformOrigin: (x + w / 2) + 'px ' + (y + h / 2) + 'px', transform: 'rotate(' + r + 'deg)' } : null });
  const svg = (w, kids) => e("svg", { className: "pb-mini-obj pb-mini-" + kind, viewBox: "0 0 " + w + " 44", width: w, height: 44,
    "aria-hidden": "true", focusable: "false", style: { '--i': i } }, kids);
  if (kind === 'bottle') return svg(24, [
    e("rect", { key: 'c', x: 8, y: 3, width: 8, height: 6, rx: 1.6, fill: "#2E86E3" }),
    e("path", { key: 'n', d: "M8.5 9h7l1.5 3.5H7z", fill: "#cfd8e3" }),
    e("rect", { key: 'b', x: 5, y: 12, width: 14, height: 31, rx: 4.5, fill: "#e8eef5", stroke: "#c7d2df", strokeWidth: .8 }),
    e("rect", { key: 'h', x: 7, y: 15, width: 1.6, height: 24, rx: .8, fill: "#fff", opacity: .9 }),
    art(6.5, 20, 11, 13)]);
  if (kind === 'box') return svg(30, [
    e("path", { key: 't', d: "M3 18l12-6 12 6-12 6z", fill: "#e6c9a3" }),
    e("path", { key: 'l', d: "M3 18l12 6v19L3 37z", fill: "#cfa877" }),
    e("path", { key: 'r', d: "M27 18l-12 6v19l12-6z", fill: "#dbb688" }),
    e("path", { key: 's', d: "M9 15l12 6", stroke: "#b88e5c", strokeWidth: 1.4 }),
    art(16.5, 26, 9, 9, -8)]);
  if (kind === 'cup') return svg(24, [
    e("path", { key: 's', d: "M14 2l2.4 8", stroke: "#2E86E3", strokeWidth: 1.6, strokeLinecap: "round" }),
    e("rect", { key: 'l', x: 3.5, y: 9, width: 17, height: 4, rx: 1.6, fill: "#cfd8e3" }),
    e("path", { key: 'b', d: "M5 13h14l-2 30H7z", fill: "#f4f7fb", stroke: "#c7d2df", strokeWidth: .8 }),
    art(7.5, 21, 9, 10)]);
  if (kind === 'laptop') return svg(34, [
    e("rect", { key: 'lid', x: 3, y: 15, width: 28, height: 22, rx: 2.4, fill: "#9aa8b8" }),
    e("rect", { key: 'hi', x: 4.5, y: 16.5, width: 25, height: 19, rx: 1.6, fill: "#aeb9c7" }),
    e("path", { key: 'base', d: "M1 38h32l-2.5 4h-27z", fill: "#7d8b9c" }),
    art(11, 19.5, 12, 12, 4)]);
  /* skateboard, standing on its tail and leaning on its neighbour */
  return svg(20, [
    e("rect", { key: 'd', x: 5, y: 3, width: 10, height: 39, rx: 5, fill: "#334155" }),
    e("circle", { key: 'w1', cx: 4, cy: 10, r: 1.8, fill: "#e2e8f0" }), e("circle", { key: 'w2', cx: 16, cy: 10, r: 1.8, fill: "#e2e8f0" }),
    e("circle", { key: 'w3', cx: 4, cy: 35, r: 1.8, fill: "#e2e8f0" }), e("circle", { key: 'w4', cx: 16, cy: 35, r: 1.8, fill: "#e2e8f0" }),
    art(5.5, 15, 9, 13)]);
}
function slMiniShowcase(src, uploadUrl) {
  const e = React.createElement;
  return [
    slMiniObj('bottle', src, 1), slMiniObj('box', src, 2), slMiniObj('cup', src, 3),
    e("img", { key: 'sticker', className: "pb-mini-sticker", src: src || uploadUrl, alt: "your upload", style: { '--i': 0 } }),
    slMiniObj('laptop', src, 4), slMiniObj('skate', src, 5)
  ].map((el, k) => el && React.cloneElement(el, { key: 'm' + k }));
}
/* /ICJ-MINI */

// Post-upload onboarding: choose how to make the sticker (Apple-style).
function WorkflowChooser({
  art,
  onPick,
  onBack
}) {
  const url = art && art.url;
  const artAspect = useSlImageAspect(url);
  const CARDS = [{
    id: 'photo',
    tag: 'Most popular',
    title: 'Die-cut sticker',
    desc: 'Cut precisely around the edges of your artwork.',
    examples: ['Logos', 'Products', 'Icons', 'Art'],
    art: /*#__PURE__*/React.createElement("div", {
      className: "wf-art wf-art-cut"
    }, /*#__PURE__*/React.createElement("div", {
      className: "wf-cutsticker ready"
    }, url && /*#__PURE__*/React.createElement("img", {
      src: url,
      alt: ""
    })), /*#__PURE__*/React.createElement("span", {
      className: "wf-badge wf-badge-cut"
    }, /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.9",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("circle", {
      cx: "6",
      cy: "6",
      r: "3"
    }), /*#__PURE__*/React.createElement("circle", {
      cx: "6",
      cy: "18",
      r: "3"
    }), /*#__PURE__*/React.createElement("path", {
      d: "M20 4L8.5 15.5M14.5 14.5L20 20M8.5 8.5l3 3"
    }))))
  }, {
    id: 'shaped',
    tag: 'Pick a shape',
    title: 'Shape sticker',
    desc: 'Printed inside a circle or square — unlock proportions for an oval or rectangle.',
    examples: ['Circle', 'Square', 'Oval', 'Rectangle'],
    art: /*#__PURE__*/React.createElement("div", {
      className: "wf-art wf-art-keep"
    }, /*#__PURE__*/React.createElement("div", {
      className: "wf-circle"
    }, url && /*#__PURE__*/React.createElement("img", {
      src: url,
      alt: ""
    })), /*#__PURE__*/React.createElement("div", {
      className: "wf-shapechips"
    }, /*#__PURE__*/React.createElement("span", {
      className: "s-c on"
    }), /*#__PURE__*/React.createElement("span", {
      className: "s-s"
    }), /*#__PURE__*/React.createElement("span", {
      className: "s-r"
    }), /*#__PURE__*/React.createElement("span", {
      className: "s-rect"
    })))
  }, {
    /* Not a workflow of its own: `chooseWorkflow` turns this into the die-cut
       setup with the sheet intent raised. The six tiles are the customer's
       own artwork, because one design repeated is what the sheet builder
       makes. */
    id: 'sheet',
    tag: 'Make a sheet',
    title: 'Sticker Sheet',
    desc: 'Turn your artwork into a sheet of stickers. We lay out as many as fit.',
    examples: ['Multiple Stickers', 'Auto Layout', 'Kiss-Cut'],
    art: /*#__PURE__*/React.createElement("div", {
      className: "wf-art wf-art-sheet"
    }, /*#__PURE__*/React.createElement("div", {
      className: "wf-sheet-fit",
      /* Pinned inside the picture area on all four sides, so the tilted sheet
         is contained by both dimensions at every card size (slSheetCardSvg). */
      style: { position: 'absolute', top: '7%', right: '7%', bottom: '7%', left: '7%' }
    }, slSheetCardSvg(url, artAspect)), /*#__PURE__*/React.createElement("span", {
      className: "wf-badge wf-badge-cut"
    }, /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.9",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z"
    }))))
  }, {
    /* A different PRODUCT, not a way of making a sticker: choosing it leaves
       for the Cart Graphics builder and takes the uploaded file along. The
       picture is the customer's own artwork contained in the front panel of
       the same white cart the Cart Graphics builder draws (slCartCardSvg), so
       the card shows what they are choosing. */
    id: 'cartgraphics',
    tag: null,
    title: 'Cart Graphics',
    desc: 'Custom vinyl graphics for your coffee, food or event cart.',
    examples: ['Matte', 'Gloss', 'Custom Sizes'],
    art: /*#__PURE__*/React.createElement("div", {
      className: "wf-art wf-art-cart"
    }, /*#__PURE__*/React.createElement("div", {
      className: "wf-cart",
      /* Pinned inside the art box on all four sides (it is 210, 180 or ~118 px
         tall and up to ~410 px wide across layouts), so the drawing is
         contained by BOTH dimensions and the whole cart always shows. A
         percentage height alone does not resolve in the art box's grid. */
      style: { position: 'absolute', top: '6%', right: '6%', bottom: '6%', left: '6%', width: 'auto', height: 'auto', maxWidth: 'none' }
    }, slCartCardSvg(url)))
  }];
  return /*#__PURE__*/React.createElement("div", {
    className: "pb-choose"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-choose-in"
  }, /*#__PURE__*/React.createElement("button", {
    className: "pb-choose-back",
    onClick: onBack
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M15 6l-6 6 6 6"
  })), "Back to upload"), /*#__PURE__*/React.createElement("div", {
    className: "pb-choose-head"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-choose-thumb pb-mini"   /* ICJ-MINI: six miniatures, one row; the sticker is the one <img> */
  }, url && slMiniShowcase(url, url)), /*#__PURE__*/React.createElement("h1", null, "How should we make it?"), /*#__PURE__*/React.createElement("p", null, "Pick a product and we'll set everything up.")), /*#__PURE__*/React.createElement("div", {
    className: "pb-choose-grid three four"
  }, (PROMO ? CARDS.filter(c => c.id !== 'sheet' && c.id !== 'cartgraphics') : CARDS).map(c => /*#__PURE__*/React.createElement("button", {
    key: c.id,
    className: "pb-wfcard",
    onClick: () => onPick(c.id)
  }, c.tag && /*#__PURE__*/React.createElement("span", {
    className: "pb-wf-tag"
  }, c.tag), c.art, /*#__PURE__*/React.createElement("h3", null, c.title), /*#__PURE__*/React.createElement("p", null, c.desc), /*#__PURE__*/React.createElement("div", {
    className: "pb-wf-ex"
  }, c.examples.map(e => /*#__PURE__*/React.createElement("span", {
    key: e
  }, e))), /*#__PURE__*/React.createElement("span", {
    className: "pb-wf-go"
  }, "Choose ", /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.4",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M5 12h14M13 6l6 6-6 6"
  }))))))));
}
/* ============================================================================
   THE BUILDER MUST NOT BE ABLE TO DISAPPEAR.

   `createRoot(...).render(<PremiumBuilder/>)` with no boundary above it means
   any uncaught error — in a render, or in an effect — unmounts the ENTIRE
   tree. React empties `#root` and the customer is left looking at a white
   page, with no message, no recovery, and no hint that a refresh would bring
   the builder back. That is what a customer reported after choosing a PNG.

   A boundary cannot make a broken render work. What it CAN do is make the
   failure survivable: the page stays, the customer is told the file could not
   be processed, and one click remounts the builder without it. No refresh, no
   lost session, and — because the trigger is nearly always the artwork — a
   route back that does not immediately reproduce the crash.

   THIS IS NOT A FIX FOR THE UNDERLYING BUG. It is the guarantee that the
   underlying bug, and every future one like it, costs a message instead of
   the whole ordering UI.
   ============================================================================ */
class SLBuilderBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, attempt: 0 };
    this.recover = this.recover.bind(this);
  }
  static getDerivedStateFromError(error) { return { error: error }; }
  componentDidCatch(error, info) {
    /* Reported loudly, and reported ONCE with everything a diagnosis needs:
       the message, the component stack, and the last upload stage that ran. */
    try {
      console.error('[sl-builder] uncaught error — builder recovered',
                    error, info && info.componentStack);
      if (window.slTelemetry && window.slTelemetry.track) {
        window.slTelemetry.track('builder_crash', {
          message: String(error && error.message || error),
          stack: String((error && error.stack) || '').slice(0, 900),
          componentStack: String((info && info.componentStack) || '').slice(0, 900)
        });
      }
    } catch (e) {}
  }
  /* Remounting with a fresh key gives the builder a clean slate without a page
     load — the artwork that triggered the crash is not carried back in. */
  recover() { this.setState(function (st) { return { error: null, attempt: st.attempt + 1 }; }); }
  render() {
    if (!this.state.error) {
      return React.createElement(PremiumBuilder, { key: this.state.attempt });
    }
    return React.createElement('div', { className: 'pb-crash', role: 'alert',
      style: { maxWidth: '560px', margin: '48px auto', padding: '28px 24px',
               fontFamily: 'inherit', textAlign: 'center' } },
      React.createElement('h2', { style: { margin: '0 0 10px', fontSize: '20px' } },
        'That file could not be processed'),
      React.createElement('p', { style: { margin: '0 0 20px', lineHeight: '1.5', opacity: 0.8 } },
        'Something went wrong while preparing your artwork, so the builder stopped ' +
        'before it could show it. Your order has not been affected. Try again, or ' +
        'upload a different file — a flattened PNG usually works best.'),
      React.createElement('button', { type: 'button', onClick: this.recover,
        style: { padding: '12px 22px', fontSize: '15px', cursor: 'pointer',
                 borderRadius: '999px', border: '0', background: '#0b1220', color: '#fff' } },
        'Back to the builder')
    );
  }
}

ReactDOM.createRoot(document.getElementById('root')).render(/*#__PURE__*/React.createElement(SLBuilderBoundary, null));
